/* ============================================================
   JV Runsheet Builder -- Shopify Bridge Mode v870
   (v870 2026-07-18: Part A1 durable cold-open fix. loadWorkspace paints the
    roster INSTANTLY from the runsheet_workspace_cache metaobject (Storefront
    read, zero GAS) then background-refreshes via bridge_runsheet_getWorkspace
    (stale-while-revalidate), so a bride opened under GAS execution-slot
    saturation no longer waits ~45s on a spinner. Kill flag: JV_RSB_FASTPATH.)
   (v869 2026-07-08: finding #4 part 2 - lazy modals. The notify popup renders
    instantly and upgrades to update-mode in place when getLastNotification
    returns; the calendar-diff check shows a light "checking..." overlay and
    only confirms when attendees actually change. No more ~48s modal wait.)
   (v868 2026-07-08: finding #4 - conflict gate moved to the very top of
    publishRunsheet so a conflicted runsheet blocks instantly, zero network,
    before the notify + calendar-diff modals.)
   (v867 2026-07-08: fire-and-forget publish + instant client-side
    conflict gate + status poll. bridge_runsheet_publishAsync.)
   Full transplant from GAS RunsheetApp.html
   google.script.run calls replaced with jvBridge.callAdmin()
   ============================================================ */

/* ============================================================
   GOOGLE.SCRIPT.RUN CALL SITE MAP (preserved from RunsheetApp.html)
   1. appGetBrides()                                    -- load bride list
   2. appGetWorkspace(brideName, overrides)              -- hydrate workspace
   3. appSaveDraft(payload)                              -- save draft to sheet
   4. appPublishRunsheet(payload)                        -- publish + PDF export
   5. appRepairRunsheetSources(brideName)                -- repair source data
   6. guestOnboarding_getArtistBriefAlerts_(id, email)  -- safety alerts
   7. appGetInspirationFormUrl(brideName)                -- inspiration form URL
   ============================================================ */

/* ============================================================
   ⚠️  LOCKED ARCHITECTURE — Bride + Bridal Party cards (Step 2)
   ============================================================
   The bride card (role === 'bride') and party-member card render shells
   are SYNCED as of 2026-05-19 across many sessions of debugging. Future
   work that touches these cards MUST preserve the contract below or it
   will silently regress (the failure modes are subtle — empty galleries,
   chip vs sub-text mismatches, save handlers that no-op, etc.).

   See:
     - CLAUDE.md → "Runsheet Builder Step-2 Cards LOCKED"
     - memory: project_runsheet_builder_cards_locked.md

   ─── Contract: Person object shape ───────────────────────────
   @typedef {Object} RsbPerson
   @property {string}   personId               Stable engine ID (V4-frozen for bride)
   @property {string}   name                   V4 person label (e.g. "Bride", "Bridesmaid 1")
   @property {string}   profileName            Display name (e.g. "Jessica Ferguson-Green")
   @property {string}   personLabel            Editable label, defaults to displayName
   @property {string}   role                   "Bride" | "Bridesmaid" | "Mother" | "Guest" | "Bridal Party"
   @property {string[]} servicesBooked         ["Hair", "Hair Prep", "Makeup"]
   @property {string}   hairstyleChosen        Planner override (one of HAIRSTYLE_OPTIONS or legacy)
   @property {string}   inspirationFolder      Drive URL. Bride: CRM col 27 (INSPIRATION FOLDER).
                                               Party: _PartyMembers row + blob + jv_bridal_party_member
                                               metaobject inspiration_folder field, via
                                               bridge_runsheet_savePartyMemberHubFields.
   @property {Array}    inspirationGallery     Thumb URLs from Drive folder scan
   @property {string}   trialFolderUrl         Bride only — CRM col 26 (TRIAL FOLDER)
   @property {Array}    trialGallery           Bride only — Drive folder scan
   @property {boolean}  trialAssistSubmitted   Bride only
   @property {boolean}  inspirationSubmitted   Legacy Google-form flag (use isInspoCompleteForPerson_ instead)
   @property {string}   hairArtist             Per-person artist override
   @property {string}   makeupArtist           Per-person artist override
   @property {string}   notes                  Free text

   ─── Contract: Render function split ─────────────────────────
   renderBridalPartyCards_      — common card shell + Inspiration Folder field
   renderHubBrideDetails_       — bride hub panel (Partner, Beauty notes, Makeup vibe, vision chips)
   renderHubMemberDetails_      — party hub panel (Allergies, Skin concerns, Colour palette, Hair notes, Inspo notes)

   ─── Contract: Save routes ───────────────────────────────────
   Bride hub fields  -> bridge_runsheet_saveBrideHubFields
                        -> runsheetBuilder_saveBrideHubFields_ (RunsheetBuilderHubWriteback.gs)
                        -> keys: hairstylePref, makeupVibe, beautyNotes, inspirationFolder
                        -> inspirationFolder writes to CRM col 27
   Party hub fields  -> bridge_runsheet_savePartyMemberHubFields
                        -> keys: hair_style, allergies, skin_concerns, colour_palette, hair_notes,
                          inspo_notes, inspirationFolder
                        -> writes to _PartyMembers row + blob + metaobject
                          (inspirationFolder writes _PartyMembers.INSPIRATION_FOLDER and the
                          jv_bridal_party_member metaobject's inspiration_folder field)

   ─── Contract: Synchronicity rules (do not break) ────────────
   1. The Inspiration Folder input field at the top of each card is unconditional
      (renders for both bride + party). DO NOT add an isBrideCard gate.
   2. The sub-text under that field uses isInspoCompleteForPerson_(person), NOT
      person.inspirationSubmitted directly. The two were misaligned pre-fix.
   3. updatePartyField('inspirationFolder', ...) MUST call saveBrideHubField_('inspirationFolder', ...)
      when isBride, otherwise the bride paste no-ops on reload. For party members it calls
      savePartyMemberHubField_(index, 'inspirationFolder', ...) instead.
   4. renderHubBrideDetails_ does NOT render inspo thumbnails — the Inspiration Gallery
      section above the panel already does this. Adding them back creates a duplicate.
   5. Cross-roster joins between V4 workspace + hub people MUST key on name.toLowerCase()
      (NOT personId, NOT raw name). V4 sets bride.name = "Bride" so use profileName too.
     (See feedback memory: v4_vs_hub_personid_mismatch + RunsheetPersonSourcer.gs merge-back.)
   ============================================================ */

var hasBridge = (typeof window.jvBridge !== 'undefined' && typeof window.jvBridge.callAdmin === 'function');

/* ============================================================
   P6: Runsheet badges CF read (2026-10-07, Team App lane).

   Reads per-bride availability-poll responses directly from the Cloudflare
   /admin/poll-status relay entity (same backend Admin Calendar's poll chips
   already use, shipped dark under ADMIN_POLL_STATUS_D1_ENABLED_). Caller
   is loadWorkspace below; it fires AFTER the GAS workspace has hydrated
   (fast paint from sheet-sourced artist.availability) and overrides each
   artist's availability ONLY when CF has an answer -- a CF 404/5xx or an
   artist with no CF row leaves the GAS-sourced value untouched. This is
   the deliberate self-healing fallback: if the Worker regresses, every
   badge stays correct via GAS.

   Flag: const JV_RUNSHEET_AVAILABILITY_CF_ENABLED_ (default false; one-line
   follow-up commit flips it after shadow pass). Liquid config may raise it
   via JV_RSB_CF_CONFIG.availabilityReadEnabled.

   See state/findings/SCOPE-runsheet-badges-cf-migration-2026-10-07.md.
   ============================================================ */

/**
 * Fetch availability-poll responses for a bride from Cloudflare /admin/poll-status
 * via the Shopify App Proxy. The App Proxy signs the request with the admin's
 * verified identity server-side; nothing identity-bearing goes in the body.
 *
 * Returns a Promise that resolves to:
 *   { pollMirrored: boolean,
 *     availabilityByName: { [lowercase_display_name]: 'AVAILABLE'|'UNAVAILABLE'|'MAYBE' },
 *     availabilityByEmail: { [lowercase_email]: 'AVAILABLE'|'UNAVAILABLE'|'MAYBE' } }
 * or null on any transport error / non-200 / malformed shape (caller treats null
 * as "CF had nothing" and keeps the GAS-sourced values -- see mergeCFAvailability_).
 *
 * Uses captured native fetch (window._jvNativeFetch) because third-party Shopify
 * apps monkey-patch window.fetch to inject tracking headers, which would turn
 * this simple POST into a non-simple preflight the GAS fallback cannot survive.
 * noRetryOn5xx semantics are a single call, 8s timeout, no retry: a transient
 * backend failure must NOT be hammered 3x on a hot admin workspace open.
 */
function loadAvailabilityForBride_(brideName) {
  var safeBride = String(brideName || '').trim();
  if (!safeBride) return Promise.resolve(null);
  var nf = (window._jvNativeFetch || window.fetch).bind(window);
  var controller = (typeof AbortController === 'function') ? new AbortController() : null;
  var timeoutId = controller ? setTimeout(function () { try { controller.abort(); } catch (e) {} }, 8000) : null;
  var opts = {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ brideName: safeBride })
  };
  if (controller) opts.signal = controller.signal;
  return nf('/apps/jv-api-v2/partner/admin-poll-status/read', opts)
    .then(function (r) {
      if (timeoutId) clearTimeout(timeoutId);
      if (!r || !r.ok) return null;
      return r.json();
    })
    .then(function (body) {
      if (!body || body.ok === false) return null;
      var byName = {};
      var byEmail = {};
      var responses = Array.isArray(body.responses) ? body.responses : [];
      for (var i = 0; i < responses.length; i++) {
        var row = responses[i] || {};
        var v = String(row.response || '').toUpperCase();
        if (v !== 'AVAILABLE' && v !== 'UNAVAILABLE' && v !== 'MAYBE') continue;
        var n = String(row.artist_name || '').trim().toLowerCase();
        var e = String(row.artist_email || '').trim().toLowerCase();
        if (n) byName[n] = v;
        if (e) byEmail[e] = v;
      }
      return {
        pollMirrored: body.pollMirrored === true,
        availabilityByName: byName,
        availabilityByEmail: byEmail
      };
    })
    .catch(function () { if (timeoutId) clearTimeout(timeoutId); return null; });
}

/**
 * Rollout flag for the CF availability read. Default OFF.
 *
 * Flip plan (per DEPLOY-runsheet-badges-cf-first-2026-10-07.md):
 *   1. Shadow pass: 3 brides x 2 time windows x 24h, parity gate = empty divergences.
 *   2. Staleness trade-off acknowledged by Jess + Consolidator (sheet->D1 mirror is hourly).
 *   3. Flip shop metafield custom.runsheet_cf_availability_read to true (preferred, no code
 *      push required), OR flip this const in a one-line follow-up commit.
 * Rollback: flip metafield back to false; no theme push, no secret rotation.
 */
var JV_RUNSHEET_AVAILABILITY_CF_ENABLED_ = false;

function runsheetAvailabilityCFEnabled_() {
  if (JV_RUNSHEET_AVAILABILITY_CF_ENABLED_) return true;
  try {
    var cfg = window.JV_RSB_CF_CONFIG || {};
    return cfg.availabilityReadEnabled === true || String(cfg.availabilityReadEnabled) === 'true';
  } catch (e) { return false; }
}

/**
 * Merge Cloudflare availability lookup maps onto the GAS-enriched workspace team.
 *
 * CF-OVERRIDES-WHEN-CF-HAS-AN-ANSWER semantics (SCOPE §3 Commit 2): for each artist,
 * if CF has a response under their lowercase name or email, that response wins and
 * replaces artist.availability. If CF has nothing for that artist, the GAS-sourced
 * value is preserved untouched -- a CF 404 / 5xx / empty response therefore cannot
 * blank a working badge. This is the deliberate self-healing fallback (same pattern
 * as the Team Dashboard outage self-heal, commit 1517a67ca).
 *
 * The function mutates team in place; the next renderStage() pass picks up the new
 * values via artistAvailStatus_. blockedOnDate is NOT touched (self block-out has
 * hard precedence inside artistAvailStatus_ regardless).
 */
function mergeCFAvailability_(team, maps) {
  if (!team || !maps) return;
  var byName = maps.availabilityByName || {};
  var byEmail = maps.availabilityByEmail || {};
  for (var i = 0; i < team.length; i++) {
    var artist = team[i];
    if (!artist) continue;
    var nameKey = String(artist.name || '').trim().toLowerCase();
    var emailKey = String(artist.email || '').trim().toLowerCase();
    var cfValue = (nameKey && byName[nameKey]) || (emailKey && byEmail[emailKey]) || null;
    if (!cfValue) continue; // CF had nothing for this artist; keep GAS-sourced value.
    artist.availability = cfValue; // UPPERCASE, matches artistAvailStatus_ contract.
  }
}

let state = {
  brides: [],
  currentBride: "",
  currentStage: "booking",
  workspace: null,
  leadArtist: "",
  activeTeam: [],
  sourceServicesDraft: [],
  requestToken: 0,
  dragServiceId: "",
  dragSource: "",
  lastHydrateDebug: null,
  timelineDirty: false,
  debugExpanded: false,
  editingServiceId: null,
  movingServiceId: null,
  dragStarted: false,
  bridalPartyDraft: [],
  customBlocksDraft: [],
  collapsedPartyCards: {},
  // STAGE 1 roster redesign (2026-09-13, state/findings/TRACE-runsheet-page2-
  // matching-2026-09-13.md, BRIEFS/SCOPING-runsheet-page2-redesign-combined-
  // 2026-09-13.md). The selected roster row's cardKey, drives which single
  // person's full card is expanded via the EXISTING collapsedPartyCards
  // mechanism (no new save/collapse logic, this only decides which existing
  // key gets left uncollapsed). Empty string means no selection yet, defaults
  // to the first person on render.
  selectedPartyPersonKey: "",
  // Secondary hub-content "show more" toggle, keyed by the same cardKey as
  // collapsedPartyCards but a SEPARATE map so it never interferes with the
  // main card expand/collapse. Defaults to collapsed (false = hidden) for
  // every person; per the scoping doc, this content stays reachable, just
  // not shown by default.
  rsbSecondaryExpanded: {},
  partyProfiles: [],
  brideProfile: null,
  contextMenu: { open: false, serviceId: "", x: 0, y: 0 },
  skillSelections: { makeup: "", hair: "", additional: [] },
  timelineOverrides: {},
  slotScale: 1,                 // timeline height scaler (Jess 2026-07-22); 1 = full, <1 = shrunk to fit
  hydrateTimedRowsFromBackend: false,
  workspaceLoading: false,
  _hasUserEditedStaffing: false,
  _undoStack: [],
  _redoStack: []
};

    // --- UNDO / REDO STATE STACK ---
    var UNDO_MAX = 30;
    function pushUndoSnapshot_() {
      try {
        var snapshot = {
          sourceServicesDraft: JSON.parse(JSON.stringify(state.sourceServicesDraft)),
          timelineOverrides: JSON.parse(JSON.stringify(state.timelineOverrides)),
          customBlocksDraft: JSON.parse(JSON.stringify(state.customBlocksDraft))
        };
        state._undoStack.push(snapshot);
        if (state._undoStack.length > UNDO_MAX) state._undoStack.shift();
        state._redoStack = [];
      } catch (e) { /* serialisation edge case — skip */ }
    }

    function undo_() {
      if (!state._undoStack.length) { showBanner("Nothing to undo.", "warning"); return; }
      // Save current state to redo stack
      state._redoStack.push({
        sourceServicesDraft: JSON.parse(JSON.stringify(state.sourceServicesDraft)),
        timelineOverrides: JSON.parse(JSON.stringify(state.timelineOverrides)),
        customBlocksDraft: JSON.parse(JSON.stringify(state.customBlocksDraft))
      });
      var snapshot = state._undoStack.pop();
      state.sourceServicesDraft = snapshot.sourceServicesDraft;
      state.timelineOverrides = snapshot.timelineOverrides;
      state.customBlocksDraft = snapshot.customBlocksDraft;
      syncWorkspaceServicesFromSourceDraft_();
      rederiveDraftState_();
      state.timelineDirty = true;
      renderStage();
      showBanner("Undone. " + state._undoStack.length + " step(s) remaining.", "success");
    }

    function redo_() {
      if (!state._redoStack.length) { showBanner("Nothing to redo.", "warning"); return; }
      state._undoStack.push({
        sourceServicesDraft: JSON.parse(JSON.stringify(state.sourceServicesDraft)),
        timelineOverrides: JSON.parse(JSON.stringify(state.timelineOverrides)),
        customBlocksDraft: JSON.parse(JSON.stringify(state.customBlocksDraft))
      });
      var snapshot = state._redoStack.pop();
      state.sourceServicesDraft = snapshot.sourceServicesDraft;
      state.timelineOverrides = snapshot.timelineOverrides;
      state.customBlocksDraft = snapshot.customBlocksDraft;
      syncWorkspaceServicesFromSourceDraft_();
      rederiveDraftState_();
      state.timelineDirty = true;
      renderStage();
      showBanner("Redone. " + state._redoStack.length + " step(s) remaining.", "success");
    }

    // ─── In-DOM confirm / prompt modals (replaces native window.confirm/prompt) ──
    // Chrome suppresses native dialogs after 2+ in an iframe session, so these
    // must be in-DOM to work reliably inside Shopify.

    function confirmModal_(body, okLabel, cancelLabel) {
      // Test hook: auto-accept when window.__testConfirm is set
      if (window.__testConfirm) return Promise.resolve(true);
      return new Promise(function(resolve) {
        var modal = document.getElementById('confirmModal');
        document.getElementById('confirmModalBody').innerText = body || 'Are you sure?';
        document.getElementById('confirmModalOk').innerText = okLabel || 'OK';
        document.getElementById('confirmModalCancel').innerText = cancelLabel || 'Cancel';
        modal.style.display = 'flex';
        var cleanup = function(answer) {
          modal.style.display = 'none';
          document.getElementById('confirmModalOk').onclick = null;
          document.getElementById('confirmModalCancel').onclick = null;
          modal.onclick = null;
          resolve(answer);
        };
        document.getElementById('confirmModalOk').onclick = function() { cleanup(true); };
        document.getElementById('confirmModalCancel').onclick = function() { cleanup(false); };
        modal.onclick = function(e) { if (e.target === modal) cleanup(false); };
      });
    }

    // ─── BBS <-> Runsheet reconciliation diff-preview modal ──────────────────
    // BRIEFS/SCOPING-runsheet-bbs-two-way-reconciliation-2026-09-18.md.
    // Jess's requirement 2: reconciliation is NEVER silent/automatic -- always
    // preview + explicit admin confirm. Shared by Repair Source Data's confirm
    // flow AND the Publish pre-check (both call this with the same diff shape).
    // Follows the publishNotifyModal_ pattern (dynamically-created overlay),
    // NOT confirmModal_ (plain-text body) -- the diff needs structured rows.
    // Deliberately does NOT touch renderBridalPartyCards_ or any locked
    // Step-2 card render function (.claude/rules/runsheet.md).
    var ROLE_LABELS_ = { bride: 'Bride', bridesmaid: 'Bridesmaid', flowergirl: 'Flower Girl', mother: 'Mother', guest: 'Guest' };
    var SERVICE_TYPE_LABELS_ = { hmu: 'Hair & Makeup', hs: 'Hair Only', mu: 'Makeup Only' };

    // DETECT + ADMIN-MANUAL (Jess's decision, gap 4): this modal shows the
    // drift precisely so the admin can go fix it herself -- it never
    // offers to apply anything automatically. applyLabel/skipLabel names
    // are kept (callers still pass their own copy) but default to
    // acknowledge/dismiss wording, not "apply a correction."
    function bbsReconcileDiffModal_(diff, opts) {
      opts = opts || {};
      var title = opts.title || 'Runsheet does not match the Bridal Booking Summary';
      var applyLabel = opts.applyLabel || 'Got it, I\'ll fix this';
      var skipLabel = opts.skipLabel || 'Leave as-is';

      return new Promise(function(resolve) {
        var overlay = document.createElement('div');
        overlay.style.cssText = 'position:fixed;inset:0;background:rgba(30,23,22,0.6);display:flex;align-items:center;justify-content:center;z-index:9999;';

        var rows = diff.map(function(d) {
          var roleLabel = ROLE_LABELS_[d.role] || (d.role.charAt(0).toUpperCase() + d.role.slice(1));
          var svcLabel = SERVICE_TYPE_LABELS_[d.serviceType] || d.serviceType;
          var arrow = d.bbsQty > d.runsheetQty ? 'fa-arrow-up' : 'fa-arrow-down';
          return '<tr>' +
            '<td style="padding:8px 6px;border-bottom:1px solid #f1ebe8;">' + escapeHtml(roleLabel) + '</td>' +
            '<td style="padding:8px 6px;border-bottom:1px solid #f1ebe8;">' + escapeHtml(svcLabel) + '</td>' +
            '<td style="padding:8px 6px;border-bottom:1px solid #f1ebe8;text-align:center;">' + escapeHtml(String(d.runsheetQty)) + '</td>' +
            '<td style="padding:8px 6px;border-bottom:1px solid #f1ebe8;text-align:center;"><i class="fa-solid ' + arrow + '" style="color:var(--brand-primary,#c99591);"></i></td>' +
            '<td style="padding:8px 6px;border-bottom:1px solid #f1ebe8;text-align:center;font-weight:600;">' + escapeHtml(String(d.bbsQty)) + '</td>' +
          '</tr>';
        }).join('');

        overlay.innerHTML =
          '<div style="background:#fff;border-radius:1.5rem;padding:2rem;max-width:560px;width:90%;box-shadow:0 4px 24px rgba(47,37,36,0.15);max-height:90vh;overflow-y:auto;">'
          + '<h3 style="margin:0 0 8px;font-size:18px;color:var(--brand-dark,#1e1716);">' + escapeHtml(title) + '</h3>'
          + '<p style="margin:0 0 16px;font-size:13px;color:var(--brand-text,#2f2524);line-height:1.6;">The Bridal Booking Summary (party roster + service entitlements) is the source of truth. This does not change anything automatically -- update the party/service entries below to match before publishing.</p>'
          + '<table style="width:100%;border-collapse:collapse;font-size:13px;margin-bottom:16px;">'
          + '<thead><tr style="text-align:left;color:var(--brand-text,#2f2524);opacity:0.7;font-size:12px;text-transform:uppercase;letter-spacing:0.02em;">'
          + '<th style="padding:0 6px 8px;">Role</th><th style="padding:0 6px 8px;">Service</th>'
          + '<th style="padding:0 6px 8px;text-align:center;">Runsheet</th><th></th>'
          + '<th style="padding:0 6px 8px;text-align:center;">BBS</th></tr></thead>'
          + '<tbody>' + rows + '</tbody></table>'
          + '<div style="display:flex;gap:10px;justify-content:flex-end;">'
          +   '<button id="bbsrc-skip" style="padding:10px 20px;border:1px solid #e6dad4;background:#fff;border-radius:3rem;cursor:pointer;font-size:14px;color:var(--brand-text,#2f2524);">' + escapeHtml(skipLabel) + '</button>'
          +   '<button id="bbsrc-apply" style="padding:10px 24px;background:var(--brand-primary,#c99591);color:#fff;border:none;border-radius:3rem;cursor:pointer;font-size:14px;font-weight:600;">' + escapeHtml(applyLabel) + '</button>'
          + '</div></div>';

        document.body.appendChild(overlay);

        var cleanup = function(answer) {
          document.body.removeChild(overlay);
          resolve(answer);
        };
        overlay.querySelector('#bbsrc-apply').onclick = function() { cleanup(true); };
        overlay.querySelector('#bbsrc-skip').onclick = function() { cleanup(false); };
        overlay.onclick = function(e) { if (e.target === overlay) cleanup(false); };
      });
    }

    // Runs the read-only BBS reconciliation check for the current bride.
    // Returns a Promise resolving to the check result ({ok, inSync, diff}) --
    // never throws, degrades to {ok:false} on any network/parse failure so a
    // caller can treat a check failure as "skip, don't block" rather than a
    // hard error.
    function bbsReconcileCheck_(brideName) {
      return jvBridge.callAdmin('bridge_runsheet_bbsReconcileCheck', { brideName: brideName })
        .then(function(res) { return (res && res.ok) ? res : { ok: false, inSync: false, checkFailed: true, diff: [] }; })
        .catch(function() { return { ok: false, inSync: false, checkFailed: true, diff: [] }; });
    }

    // FAIL-CLOSED (2026-10-20, BBS reconcile findings): a failed check must
    // never read as "in sync". Callers that gate a publish ask the admin
    // explicitly. Resolves true = "Publish anyway" (admin accepted the risk),
    // false = stop and retry.
    function bbsReconcileUnavailableModal_() {
      return new Promise(function(resolve) {
        var overlay = document.createElement('div');
        overlay.style.cssText = 'position:fixed;inset:0;background:rgba(30,23,22,0.6);display:flex;align-items:center;justify-content:center;z-index:9999;';
        overlay.innerHTML =
          '<div style="background:#fff;border-radius:1.5rem;padding:2rem;max-width:480px;width:90%;box-shadow:0 4px 24px rgba(47,37,36,0.15);">'
          + '<h3 style="margin:0 0 8px;font-size:18px;color:var(--brand-dark,#1e1716);">Could not check against the Bridal Booking Summary</h3>'
          + '<p style="margin:0 0 16px;font-size:13px;color:var(--brand-text,#2f2524);line-height:1.6;">The comparison did not run, so this runsheet has NOT been checked. You can try again, or publish without the check.</p>'
          + '<div style="display:flex;gap:10px;justify-content:flex-end;">'
          +   '<button id="bbsru-publish" style="padding:10px 20px;border:1px solid #e6dad4;background:#fff;border-radius:3rem;cursor:pointer;font-size:14px;color:var(--brand-text,#2f2524);">Publish without check</button>'
          +   '<button id="bbsru-stop" style="padding:10px 24px;background:var(--brand-primary,#c99591);color:#fff;border:none;border-radius:3rem;cursor:pointer;font-size:14px;font-weight:600;">Stop, I\'ll try again</button>'
          + '</div></div>';
        document.body.appendChild(overlay);
        var done = function(a) { document.body.removeChild(overlay); resolve(a); };
        overlay.querySelector('#bbsru-publish').onclick = function() { done(true); };
        overlay.querySelector('#bbsru-stop').onclick = function() { done(false); };
        overlay.onclick = function(e) { if (e.target === overlay) done(false); };
      });
    }

    // Shows the diff modal (if there's a real diff). DETECT + ADMIN-MANUAL
    // (Jess's decision, LANE-bbs-runsheet-reconcile-activation-2026-10-12,
    // gap 4): this NEVER mutates the runsheet. The correction record this
    // used to write (bridge_runsheet_bbsReconcileApply, now CF-routed to
    // /bbs-reconcile/apply) only logs that a drift was acknowledged
    // (bbs_reconcile_corrections, status 'pending_runsheet_sync') -- it is
    // an audit trail, not a fix. The admin fixes the party/service entries
    // herself in this same builder. Always returns false (nothing to
    // reload -- the workspace never changed); the log call is fire-and-
    // forget and never blocks or fails the confirm.
    function bbsReconcileOfferCorrection_(brideName, modalOpts) {
      return bbsReconcileCheck_(brideName).then(function(check) {
        if (check.checkFailed) { showBanner('Could not check against the Bridal Booking Summary. Please try again.', 'warning'); return false; }
        if (!check.ok || check.inSync || !check.diff || !check.diff.length) return false;
        return bbsReconcileDiffModal_(check.diff, modalOpts).then(function(confirmed) {
          if (!confirmed) return false;
          jvBridge.callAdmin('bridge_runsheet_bbsReconcileApply', { brideName: brideName }).catch(function() {});
          showBanner('Noted. Please update the party/service entries above to match the Bridal Booking Summary before publishing.', 'warning');
          return false;
        });
      });
    }

    // Fetch the last notification metadata for the current bride so the
    // publish modal can switch into "update" mode if she has already been
    // notified. Returns { notified, last } on success or { notified: false }
    // on any failure (modal degrades to first-publish flow safely).
    //
    // CF SHADOW/PRIMARY (Runsheet Builder Slice 1 / lane B1, 2026-10-17):
    // window.JV_RUNSHEET_STATUS_READS_MODE ('off' default | 'shadow' | 'primary')
    // controls a parallel CF-native read via bridge_runsheet_getLastNotification
    // on the relay (see DEPOLYMENT/jv-crm-mirror/runsheet-status-reads.mjs).
    // 'off' (or unset): GAS-only, unchanged from before this lane.
    // 'shadow': GAS answer is still authoritative; the CF answer is fetched
    //   in parallel purely to log a mismatch, never affects the return value.
    // 'primary': the CF answer wins when it succeeds; GAS stays the fallback
    //   on any CF failure. Both the CF downstream flag AND this client flag
    //   must be set for any behaviour change — ships fully dark by default.
    function publishNotifyFetchLast_() {
      if (!state.currentBride) return Promise.resolve({ notified: false });
      var mode = window.JV_RUNSHEET_STATUS_READS_MODE || 'off';
      var gasPromise = jvBridge.callAdmin('bridge_runsheet_getLastNotification', { brideName: state.currentBride })
        .then(function(res) {
          if (res && res.ok && res.data) return res.data;
          return { notified: false };
        })
        .catch(function() { return { notified: false }; });

      if (mode !== 'shadow' && mode !== 'primary') return gasPromise;

      var cfPromise = (jvBridge.callAdminRelay ? jvBridge.callAdminRelay('bridge_runsheet_getLastNotification', { brideName: state.currentBride }) : Promise.resolve(null))
        .then(function(res) {
          if (res && res.ok) return { notified: !!res.notified, last: res.last || null };
          return null;
        })
        .catch(function() { return null; });

      if (mode === 'shadow') {
        Promise.all([gasPromise, cfPromise]).then(function(pair) {
          var gasAns = pair[0], cfAns = pair[1];
          if (cfAns && (!!cfAns.notified !== !!gasAns.notified)) {
            console.warn('[runsheet][shadow] getLastNotification mismatch for ' + state.currentBride +
              ': GAS notified=' + gasAns.notified + ' CF notified=' + cfAns.notified);
          }
        });
        return gasPromise;
      }

      // mode === 'primary'
      return cfPromise.then(function(cfAns) { return cfAns || gasPromise; });
    }

    async function publishNotifyModal_() {
      var firstName = (state.currentBride || '').split(' ')[0] || '';
      var defaultSms = 'Hi ' + firstName + ', your Bridal Beauty Runsheet is ready. View it here: {LINK}\n\nJessica Vegas Pro Team';

      // LAZY (finding #4 part 2, 2026-07-08): render the modal IMMEDIATELY in
      // first-publish mode and fetch the prior-notification status in the BACKGROUND.
      // getLastNotification is a ~24s cold read; awaiting it up-front made this popup
      // take up to a minute to appear. Now the popup is instant and, if the bride
      // turns out to have been notified before, the modal upgrades itself to
      // "update" mode in place (banner + required reason + team option) the moment
      // the background fetch returns. If the user clicks Publish before it resolves,
      // the click handler awaits it first, so correctness is never sacrificed for speed.
      var lastInfoPromise = publishNotifyFetchLast_();
      var fetchDone = false;
      var isUpdate = false;
      var lastSentLabel = '';
      var lastChannel = '';

      return new Promise(function(resolve) {
        var overlay = document.createElement('div');
        overlay.style.cssText = 'position:fixed;inset:0;background:rgba(30,23,22,0.6);display:flex;align-items:center;justify-content:center;z-index:9999;';

        // Render first-publish shell immediately. The update-mode extras (banner +
        // reason + team) are injected into #pnm-update-slot when the fetch returns.
        overlay.innerHTML =
          '<div style="background:#fff;border-radius:1.5rem;padding:2rem;max-width:520px;width:90%;box-shadow:0 4px 24px rgba(47,37,36,0.15);max-height:90vh;overflow-y:auto;">'
          + '<h3 id="pnm-headline" style="margin:0 0 12px;font-size:18px;color:var(--brand-dark,#1e1716);">Publish Runsheet</h3>'
          + '<p id="pnm-intro" style="margin:0 0 20px;font-size:14px;color:var(--brand-text,#2f2524);line-height:1.6;">This will update the bride\'s portal and export a PDF.<br>Send notifications?</p>'
          + '<div id="pnm-update-slot"></div>'
          + '<label style="display:flex;align-items:center;gap:10px;margin-bottom:12px;cursor:pointer;font-size:14px;color:var(--brand-text,#2f2524);">'
          +   '<input type="checkbox" id="pnm-email" checked style="width:16px;height:16px;accent-color:var(--brand-primary,#c99591);"> '
          +   '<span id="pnm-email-label">Send email notification to bride</span></label>'
          + '<label style="display:flex;align-items:center;gap:10px;margin-bottom:12px;cursor:pointer;font-size:14px;color:var(--brand-text,#2f2524);">'
          +   '<input type="checkbox" id="pnm-sms" style="width:16px;height:16px;accent-color:var(--brand-primary,#c99591);"> '
          +   '<span id="pnm-sms-label">Send SMS notification</span></label>'
          + '<div id="pnm-sms-box" style="display:none;margin-bottom:16px;">'
          +   '<textarea id="pnm-sms-text" rows="3" style="width:100%;box-sizing:border-box;border:1px solid #e6dad4;border-radius:0.75rem;padding:10px;font-size:13px;font-family:inherit;resize:vertical;"></textarea>'
          + '</div>'
          + '<div id="pnm-team-slot"></div>'
          + '<div style="display:flex;gap:10px;justify-content:flex-end;margin-top:8px;">'
          +   '<button id="pnm-cancel" style="padding:10px 20px;border:1px solid #e6dad4;background:#fff;border-radius:3rem;cursor:pointer;font-size:14px;color:var(--brand-text,#2f2524);">Cancel</button>'
          +   '<button id="pnm-ok" style="padding:10px 24px;background:var(--brand-primary,#c99591);color:#fff;border:none;border-radius:3rem;cursor:pointer;font-size:14px;font-weight:600;">Publish</button>'
          + '</div></div>';

        document.body.appendChild(overlay);
        overlay.querySelector('#pnm-sms-text').value = defaultSms;

        // First-publish team Chat opt-in (decision 2, BRIEF 2026-07-10): render the
        // "Notify team" checkbox on EVERY publish, OFF by default. In update mode
        // applyLastInfo() overwrites #pnm-team-slot with its own copy of this control.
        overlay.querySelector('#pnm-team-slot').innerHTML =
          '<label style="display:flex;align-items:center;gap:10px;margin-bottom:12px;cursor:pointer;font-size:14px;color:var(--brand-text,#2f2524);">'
          +   '<input type="checkbox" id="pnm-team" style="width:16px;height:16px;accent-color:var(--brand-primary,#c99591);"> Notify allocated team members via Google Chat'
          + '</label>';

        overlay.querySelector('#pnm-sms').addEventListener('change', function() {
          overlay.querySelector('#pnm-sms-box').style.display = this.checked ? '' : 'none';
        });

        // Apply update-mode to the already-open modal when the background fetch
        // resolves as "already notified". Idempotent + guarded so a fast Publish
        // click cannot double-apply.
        var updateApplied = false;
        function applyLastInfo(li) {
          fetchDone = true;
          isUpdate = !!(li && li.notified);
          if (isUpdate && li.last) {
            lastSentLabel = String(li.last.timestamp_au || li.last.timestamp || '');
            lastChannel = String(li.last.channel || '');
          }
          if (!isUpdate || updateApplied) return;
          updateApplied = true;
          if (!overlay.parentNode) return; // modal already closed
          overlay.querySelector('#pnm-headline').textContent = 'Publish Updated Runsheet';
          overlay.querySelector('#pnm-intro').innerHTML = 'This bride has already been notified once. Re-publishing will save the changes but will NOT auto-email her again unless you explicitly send an update below.';
          overlay.querySelector('#pnm-ok').innerHTML = 'Publish &amp; Send Update';
          overlay.querySelector('#pnm-email').checked = false; // update-mode default: do not auto-send
          overlay.querySelector('#pnm-email-label').textContent = 'Send updated email to bride';
          overlay.querySelector('#pnm-sms-label').textContent = 'Send updated SMS to bride';
          overlay.querySelector('#pnm-update-slot').innerHTML =
            '<div style="background:#fff7ec;border:1px solid #f0d9a6;border-radius:0.75rem;padding:10px 14px;margin-bottom:16px;font-size:13px;color:#6b4d12;line-height:1.5;">'
            +  '<strong>Previously notified:</strong> ' + (lastSentLabel || '(date unknown)')
            +  (lastChannel ? ' &nbsp;·&nbsp; <strong>Channel:</strong> ' + lastChannel : '')
            + '</div>'
            + '<label style="display:block;margin-bottom:6px;font-size:13px;color:var(--brand-text,#2f2524);font-weight:600;">What changed? <span style="color:#b32727;">*</span></label>'
            + '<textarea id="pnm-reason" rows="3" placeholder="e.g. Ceremony moved to 3pm at bride\'s request, service order updated."'
            + ' style="width:100%;box-sizing:border-box;border:1px solid #e6dad4;border-radius:0.75rem;padding:10px;font-size:13px;font-family:inherit;resize:vertical;margin-bottom:16px;"></textarea>';
          overlay.querySelector('#pnm-team-slot').innerHTML =
            '<label style="display:flex;align-items:center;gap:10px;margin-bottom:12px;cursor:pointer;font-size:14px;color:var(--brand-text,#2f2524);">'
            +   '<input type="checkbox" id="pnm-team" style="width:16px;height:16px;accent-color:var(--brand-primary,#c99591);"> Notify allocated team members via Google Chat'
            + '</label>';
        }
        lastInfoPromise.then(applyLastInfo).catch(function() { fetchDone = true; });

        function cleanup(result) {
          if (overlay.parentNode) document.body.removeChild(overlay);
          resolve(result);
        }

        function finishOk() {
          var reasonEl = overlay.querySelector('#pnm-reason');
          var reason = reasonEl ? String(reasonEl.value || '').trim() : '';
          var teamEl = overlay.querySelector('#pnm-team');
          var notifyTeam = !!(teamEl && teamEl.checked);
          var sendEmail = overlay.querySelector('#pnm-email').checked;
          var sendSms = overlay.querySelector('#pnm-sms').checked;

          // Update flow requires a reason whenever any notification is being sent
          if (isUpdate && (sendEmail || sendSms || notifyTeam) && !reason) {
            if (reasonEl) {
              reasonEl.style.borderColor = '#b32727';
              reasonEl.focus();
            }
            alert('Please describe what changed before sending update notifications.');
            return;
          }

          cleanup({
            ok:         true,
            isUpdate:   isUpdate,
            reason:     reason,
            sendEmail:  sendEmail,
            sendSms:    sendSms,
            notifyTeam: notifyTeam,
            customSms:  overlay.querySelector('#pnm-sms-text').value.trim(),
            lastSentLabel: lastSentLabel,
            lastChannel:   lastChannel
          });
        }

        overlay.querySelector('#pnm-ok').addEventListener('click', function() {
          // Race guard: if the background notification-status fetch has not resolved
          // yet, wait for it (so update-mode is correctly applied) THEN finish. Rare,
          // since the fetch usually resolves before the user clicks; while pending we
          // show a brief "Checking..." on the button.
          if (fetchDone) { finishOk(); return; }
          var okBtn = overlay.querySelector('#pnm-ok');
          okBtn.disabled = true;
          okBtn.innerHTML = '<i class="fa-solid fa-spinner fa-spin"></i> Checking...';
          lastInfoPromise.then(applyLastInfo).catch(function(){ fetchDone = true; }).then(function() {
            okBtn.disabled = false;
            if (!isUpdate) okBtn.innerHTML = 'Publish';
            finishOk();
          });
        });
        overlay.querySelector('#pnm-cancel').addEventListener('click', function() { cleanup({ ok: false }); });
        overlay.addEventListener('click', function(e) { if (e.target === overlay) cleanup({ ok: false }); });
      });
    }

    function promptModal_(body, placeholder) {
      // Test hook: auto-return value when window.__testPrompt is set
      if (window.__testPrompt !== undefined) return Promise.resolve(window.__testPrompt);
      return new Promise(function(resolve) {
        var modal = document.getElementById('promptModal');
        document.getElementById('promptModalBody').innerText = body || '';
        var input = document.getElementById('promptModalInput');
        input.value = '';
        input.placeholder = placeholder || '';
        modal.style.display = 'flex';
        setTimeout(function() { input.focus(); }, 50);
        var cleanup = function(value) {
          modal.style.display = 'none';
          document.getElementById('promptModalOk').onclick = null;
          document.getElementById('promptModalCancel').onclick = null;
          modal.onclick = null;
          resolve(value);
        };
        document.getElementById('promptModalOk').onclick = function() { cleanup(input.value); };
        document.getElementById('promptModalCancel').onclick = function() { cleanup(null); };
        modal.onclick = function(e) { if (e.target === modal) cleanup(null); };
        input.onkeydown = function(e) { if (e.key === 'Enter') { e.preventDefault(); cleanup(input.value); } };
      });
    }

    document.addEventListener("keydown", function(e) {
      // Ctrl+S / Cmd+S = save draft
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        if (state.currentBride && state.workspace) {
          saveDraftChanges();
        }
        return;
      }
      // Ctrl+Z / Cmd+Z = undo, Ctrl+Shift+Z / Cmd+Shift+Z = redo
      if ((e.ctrlKey || e.metaKey) && e.key === "z" && !e.shiftKey) {
        e.preventDefault();
        undo_();
      } else if ((e.ctrlKey || e.metaKey) && e.key === "z" && e.shiftKey) {
        e.preventDefault();
        redo_();
      } else if ((e.ctrlKey || e.metaKey) && e.key === "y") {
        e.preventDefault();
        redo_();
      }
    });

    // --- LOCAL STORAGE AUTOSAVE ---
    var _autosaveTimer = null;
    var AUTOSAVE_KEY_PREFIX = 'jv_runsheet_draft_';

    function autosaveSchedule_() {
      if (_autosaveTimer) clearTimeout(_autosaveTimer);
      _autosaveTimer = setTimeout(autosaveNow_, 2000);
    }

    function autosaveNow_() {
      if (!state.currentBride) return;
      try {
        var key = AUTOSAVE_KEY_PREFIX + state.currentBride.replace(/[^a-zA-Z0-9]/g, '_');
        var draft = {
          bride: state.currentBride,
          savedAt: new Date().toISOString(),
          sourceServicesDraft: state.sourceServicesDraft,
          timelineOverrides: state.timelineOverrides,
          activeTeam: state.activeTeam,
          leadArtist: state.leadArtist,
          bridalPartyDraft: state.bridalPartyDraft,
          customBlocksDraft: state.customBlocksDraft,
          skillSelections: state.skillSelections,
          currentStage: state.currentStage
        };
        localStorage.setItem(key, JSON.stringify(draft));
        showAutosaveIndicator_("Autosaved");
      } catch (e) {
        showBanner("Browser storage full — click Save Draft to persist your changes.", "warning");
      }
    }

    var _autosaveIndicatorTimeout = null;
    function showAutosaveIndicator_(text) {
      var el = document.getElementById("autosaveIndicator");
      if (!el) return;
      el.textContent = text;
      el.style.opacity = "1";
      if (_autosaveIndicatorTimeout) clearTimeout(_autosaveIndicatorTimeout);
      _autosaveIndicatorTimeout = setTimeout(function() { el.style.opacity = "0"; }, 2000);
    }

    function autosaveRestore_(brideName) {
      try {
        var key = AUTOSAVE_KEY_PREFIX + brideName.replace(/[^a-zA-Z0-9]/g, '_');
        var raw = localStorage.getItem(key);
        if (!raw) return null;
        var draft = JSON.parse(raw);
        // Only restore if less than 24 hours old
        if (draft.savedAt) {
          var age = Date.now() - new Date(draft.savedAt).getTime();
          if (age > 24 * 60 * 60 * 1000) {
            localStorage.removeItem(key);
            return null;
          }
        }
        return draft;
      } catch (e) { return null; }
    }

    function autosaveClear_(brideName) {
      try {
        var key = AUTOSAVE_KEY_PREFIX + (brideName || state.currentBride || '').replace(/[^a-zA-Z0-9]/g, '_');
        localStorage.removeItem(key);
      } catch (e) { /* ignore */ }
    }

    /**
     * Discard button for the "Restored local draft" banner (2026-09-14, see
     * URGENT-runsheet-publish-stale-draft-root-cause-2026-09-14.md). Clears
     * the stored draft so it can't silently reappear, then reloads with
     * forceReload:true -- reusing loadWorkspace's EXISTING forceReload path
     * verbatim (skips the whole savedDraft restore branch, and
     * hydrateWorkspace's own existing guard wipes state.timelineOverrides on
     * forceReload), not a new reload mechanism.
     * @param {string} brideName
     */
    function rsbDiscardDraftAndReload_(brideName) {
      autosaveClear_(brideName);
      loadWorkspace(brideName, {}, { forceReload: true });
      showBanner("Discarded the saved draft. Showing the live runsheet.", "success");
    }

    // Hook: warn on page close if there are unsaved changes
    window.addEventListener('beforeunload', function(ev) {
      if (state.timelineDirty) {
        autosaveNow_();
        ev.preventDefault();
        ev.returnValue = '';
      }
    });

    // Periodic autosave every 30 seconds when dirty
    setInterval(function() {
      if (state.timelineDirty && state.currentBride) autosaveNow_();
    }, 30000);

    const STAGES = [
      {
        id: "booking",
        count: "01",
        name: "Booking Details",
        note: "Review prefilled booking info and confirm the details the planner will rely on."
      },
      {
        id: "team",
        count: "02",
        name: "Team + Service Order",
        note: "Set the lead, choose the working artists, and tidy the service list before scheduling."
      },
      {
        id: "timeline",
        count: "03",
        name: "Timeline Preview",
        note: "Drag services between artists and refine the shape of the morning before publishing."
      }
    ];

    // --- DEBOUNCE UTILITY ---
    function debounce_(fn, ms) {
      var timer;
      return function() {
        var ctx = this, args = arguments;
        clearTimeout(timer);
        timer = setTimeout(function() { fn.apply(ctx, args); }, ms);
      };
    }

    var _debouncedRenderBrides = debounce_(renderBrides, 200);

    window.onload = function() {
      document.getElementById("search").addEventListener("input", _debouncedRenderBrides);
      preRenderContextMenu_();
      renderStageBar();
      loadBrides();
    };

    function preRenderContextMenu_() {
      var menu = document.getElementById("svcContextMenu");
      if (!menu) return;
      menu.innerHTML =
        '<button class="context-menu-item" onclick="ctxEditService()"><i class="fa-solid fa-pen-to-square"></i> Edit service</button>' +
        '<button class="context-menu-item" onclick="ctxOverrideTiming()"><i class="fa-solid fa-clock"></i> Override duration</button>' +
        '<button class="context-menu-item" onclick="ctxEditNote()"><i class="fa-solid fa-note-sticky"></i> Add / edit note</button>' +
        '<div class="context-menu-divider"></div>' +
        '<button class="context-menu-item danger" onclick="ctxRemoveService()"><i class="fa-solid fa-trash-can"></i> Remove service</button>';
    }

    // Build the bride list from the CDN card-data island (name + wedding date),
    // so the list renders instantly (~ms) instead of the ~8-10s GAS getBrides
    // cold-start. Returns an array of {name, isoDate, dateStr, dateMs} or null
    // when the island is absent/empty (then loadBrides falls back to GAS).
    function rsbBridesFromIsland_() {
      if (window.JV_RSB_FASTPATH === false) return null;
      try {
        var idx = rsbBuildCardIndex_();               // { emailLower: entry }
        var out = [];
        Object.keys(idx || {}).forEach(function(k) {
          var e = idx[k];
          var name = e && e.name ? String(e.name).trim() : '';
          if (!name) return;                          // skip entries with no name
          var iso = e && e.weddingDate ? String(e.weddingDate).trim() : '';
          var dMs = 0, dStr = iso;
          if (iso) {
            var d = new Date(iso + 'T00:00:00');
            if (!isNaN(d.getTime())) {
              dMs = d.getTime();
              dStr = d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
            }
          }
          out.push({ name: name, isoDate: iso, dateStr: dStr, dateMs: dMs });
        });
        return out.length ? out : null;
      } catch (_) { return null; }
    }

    // Win A (2026-07-12): read the FULL bride list from the shop-level
    // `runsheet_brides_cache_json` island (RunsheetBridesCache.gs). payload.data is
    // byte-identical to the appGetBrides() array bridge_runsheet_getBrides returns
    // ({name, dateStr, dateMs, isoDate}), so it feeds renderBrides() directly.
    // Unlike rsbBridesFromIsland_ (derived from the 50-cap card-data island) this
    // covers every bride. Returns null when the island is absent/empty/unparseable
    // so loadBrides falls back to the card-data island, then to live GAS.
    // Kill flag: window.JV_RSB_FASTPATH === false disables it.
    function rsbBridesFromShopIsland_() {
      if (window.JV_RSB_FASTPATH === false) return null;
      try {
        var el = document.getElementById('jv-rsb-brides-cache');
        if (!el) return null;
        var raw = (el.textContent || el.innerText || '').trim();
        if (!raw || raw === 'null') return null;
        var parsed = JSON.parse(raw);
        var arr = parsed && Array.isArray(parsed.data) ? parsed.data : null;
        return (arr && arr.length) ? arr : null;
      } catch (_) { return null; }
    }

    // Hairstyle consolidation (2026-07-20): canonical 5-hairstyle list, read
    // from the GAS-authored shop metafield island (see
    // sections/jv-runsheet-builder.liquid #jv-rsb-hairstyle-options and GAS
    // HairstyleOptions.gs). No "TBC" entry per Jess's confirmed decision -
    // TBC remains a display-only sentinel elsewhere in this file for
    // unset times/fields, not a selectable hairstyle. Falls back to a
    // hardcoded copy of the same 5 if the metafield hasn't been seeded yet.
    function rsbHairstyleOptions_() {
      var fallback = ["Soft Waves/Curls", "1/2 Updo", "Updo", "Hollywood Waves", "Above Collar/Short Hair"];
      try {
        var el = document.getElementById('jv-rsb-hairstyle-options');
        if (!el) return fallback;
        var raw = (el.textContent || el.innerText || '').trim();
        if (!raw || raw === 'null') return fallback;
        var parsed = JSON.parse(raw);
        var opts = parsed && Array.isArray(parsed.options) ? parsed.options : null;
        return (opts && opts.length) ? opts : fallback;
      } catch (_) { return fallback; }
    }

    /* ── Part A1 CDN roster fast-path (2026-07-18) ────────────────────────────
       Read the viewed bride's runsheet_workspace_cache metaobject by handle via
       the Storefront API so loadWorkspace paints the roster INSTANTLY from the CDN
       (zero GAS, immune to GAS execution-slot saturation), then background-refreshes
       via the live bridge_runsheet_getWorkspace (stale-while-revalidate). The handle
       is derived from the bride name exactly as GAS derives it
       (runsheetRosterCache_handle_), so both sides agree byte-for-byte. The
       `workspace` field may be gzip'd (JVGZ_ prefix) by the shared GAS encoder;
       rsbInflateIslandValue_ inflates it via DecompressionStream, mirroring the
       admin bride profile + pipeline islands. Kill flag: window.JV_RSB_FASTPATH ===
       false disables it (same flag as the bride-list fast-path). */
    var RSB_SF_SHOP = 'jessicavegas.com';
    var RSB_SF_VER  = '2026-01';

    // SECURITY (2026-07-28): the handle is SHA-256(nameKey + '|' + salt), NOT the old
    // guessable 'runsheet-roster-<nameslug>'. The salt is emitted server-side ONLY
    // into this admin-gated runsheet-app page (window._rsbCacheSalt). MUST match GAS
    // runsheetRosterCache_handle_ byte-for-byte: same trimmed-lowercased bride name
    // (NOT the lossy slug, since two different names could collapse to the same
    // slug), same '<key>|<salt>' join, same lowercase SHA-256 hex, same
    // 'runsheet-roster-' prefix. Returns a Promise (crypto.subtle is async).
    // Resolves null if the salt is missing (the page then falls back to the
    // authenticated live GAS call, no guessable read).
    function rsbRosterCacheHandle_(brideName) {
      var salt = (typeof window !== 'undefined' && window._rsbCacheSalt) ? String(window._rsbCacheSalt) : '';
      var key  = String(brideName || '').trim().toLowerCase();
      if (!salt || !key || !(window.crypto && window.crypto.subtle)) return Promise.resolve(null);
      return rsbSha256Hex_(key + '|' + salt).then(function (hex) {
        return hex ? ('runsheet-roster-' + hex) : null;
      });
    }

    // SHA-256 to lowercase hex, matching GAS brideCache_sha256Hex_ byte-for-byte.
    function rsbSha256Hex_(str) {
      try {
        var enc = new TextEncoder().encode(str);
        return window.crypto.subtle.digest('SHA-256', enc).then(function (buf) {
          var b = new Uint8Array(buf), out = '';
          for (var i = 0; i < b.length; i++) out += ('0' + b[i].toString(16)).slice(-2);
          return out;
        }).catch(function () { return null; });
      } catch (e) { return Promise.resolve(null); }
    }

    // Parse a metaobject field value that is EITHER plain JSON OR a JSON string
    // literal "JVGZ_<base64gzip>". Returns a Promise resolving to the parsed object
    // (or null). Mirrors abpInflateIslandValue_ in jv-admin-bride-profile.js.
    function rsbInflateIslandValue_(raw) {
      return new Promise(function (resolve) {
        try {
          if (!raw) { resolve(null); return; }
          var s = String(raw).trim();
          var marker = s;
          if (marker.charAt(0) === '"' && marker.charAt(marker.length - 1) === '"') {
            try { marker = JSON.parse(s); } catch (e) { marker = s.slice(1, -1); }
          }
          if (typeof marker === 'string' && marker.indexOf('JVGZ_') === 0) {
            if (typeof DecompressionStream === 'undefined') { resolve(null); return; }
            var b64 = marker.slice(5);
            var bin = atob(b64);
            var bytes = new Uint8Array(bin.length);
            for (var i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
            var ds = new DecompressionStream('gzip');
            new Response(new Blob([bytes]).stream().pipeThrough(ds)).text()
              .then(function (txt) { try { resolve(JSON.parse(txt)); } catch (e) { resolve(null); } })
              .catch(function () { resolve(null); });
            return;
          }
          resolve(JSON.parse(s)); // plain object
        } catch (e) { resolve(null); }
      });
    }

    // Returns a Promise resolving to the workspace object from the CDN metaobject,
    // or null on any miss / no token / error (caller then uses the live loader).
    // Salted handle is async (SHA-256). A null handle (missing salt / no crypto)
    // means the CDN read cannot be constructed, so fall back to the live
    // authenticated GAS call.
    function rsbTryCdnRoster_(brideName) {
      if (window.JV_RSB_FASTPATH === false) return Promise.resolve(null);
      var tok = (typeof window !== 'undefined' && window._rsbSfToken) ? String(window._rsbSfToken) : '';
      if (!tok || !brideName) return Promise.resolve(null);
      var nf = (window._jvNativeFetch || window.fetch).bind(window);
      return rsbRosterCacheHandle_(brideName).then(function (handle) {
        if (!handle) return null;
        var q = 'query($h:MetaobjectHandleInput!){ metaobject(handle:$h){ ' +
                'w: field(key:"workspace"){ value } } }';
        var body = { query: q, variables: { h: { type: 'runsheet_workspace_cache', handle: handle } } };
        return nf('https://' + RSB_SF_SHOP + '/api/' + RSB_SF_VER + '/graphql.json', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': tok },
          body: JSON.stringify(body)
        })
        .then(function (r) { return r.json(); })
        .then(function (d) {
          var mo = d && d.data && d.data.metaobject;
          if (!mo) return null;
          var raw = mo.w && mo.w.value;
          if (!raw) return null;
          return rsbInflateIslandValue_(raw).then(function (obj) {
            if (!obj || !obj.workspace) return null;
            return obj.workspace;
          });
        });
      })
      .catch(function () { return null; });
    }

    function loadBrides() {
      if (!hasBridge) return;

      // Fast path: render the whole list from a CDN island (zero GAS). Prefer the
      // full shop-level bride-list island (Win A); fall back to the card-data
      // island (first ~50 brides). The GAS getBrides call still fires in the
      // background to refresh the list (catches brand-new brides not yet on the
      // CDN), but the list is usable immediately.
      var islandBrides = rsbBridesFromShopIsland_() || rsbBridesFromIsland_();
      if (islandBrides) {
        state.brides = islandBrides;
        renderBrides();
      }

      function acceptBrideList_(res) {
        if (!res || res.ok !== true) return false;
        state.brides = Array.isArray(res.data) ? res.data : [];
        renderBrides();
        try {
          var hashStr = window.location.hash.replace(/^#/, '');
          var urlParams = new URLSearchParams(hashStr.indexOf('=') !== -1 ? hashStr : window.location.search);
          var deepLinkBride = urlParams.get('bride') || '';
          if (deepLinkBride && !state.currentBride) {
            var searchInput = document.getElementById('search');
            if (searchInput) {
              searchInput.value = deepLinkBride;
              renderBrides();
            }
            var match = state.brides.find(function(b) {
              return b.name && b.name.toLowerCase() === deepLinkBride.toLowerCase();
            });
            if (match) loadWorkspace(match.name);
          }
        } catch (e) { /* deep-link parse error */ }
        return true;
      }

      // V2 owns the list from its first network request. Timeout, disabled
      // flags and ambiguous responses fail closed and never retry GAS.
      if (rsbV2On_()) {
        rsbV2Init_();
        window.jvRunsheetV2.listBrides(false).then(function(r) {
          if (r.kind === 'ok' && acceptBrideList_(r.res)) return;
          if (!(state.brides && state.brides.length)) {
            document.getElementById('brideList').innerHTML =
              '<div class="empty-state"><i class="fa-solid fa-triangle-exclamation"></i><div>' +
              'Could not prove the Cloudflare bride list. Reload to retry.' +
              '</div></div>';
          }
        });
        return;
      }

      jvBridge.callAdmin('bridge_runsheet_getBrides', {})
        .then(function(res) {
          if (!acceptBrideList_(res) && !(state.brides && state.brides.length)) {
            // Only surface the error if the island fast-path did NOT already
            // render a usable list. A background GAS failure must never wipe the
            // instant CDN list.
            document.getElementById("brideList").innerHTML =
              '<div class="empty-state"><i class="fa-solid fa-triangle-exclamation"></i><div>' +
              escapeHtml(res.error || 'Failed to load brides') +
              "</div></div>";
          }
        })
        .catch(function(err) {
          if (state.brides && state.brides.length) return; // keep the island list
          document.getElementById("brideList").innerHTML =
            '<div class="empty-state"><i class="fa-solid fa-triangle-exclamation"></i><div>' +
            escapeHtml(getErrorMessage(err)) +
            "</div></div>";
        });
    }

    function renderBrides() {
      const q = String(document.getElementById("search").value || "").toLowerCase().replace(/[''‚‛′‵`]/g, "'");
      const list = document.getElementById("brideList");
      const filtered = state.brides.filter(function(bride) {
        return String(bride.name || "").toLowerCase().includes(q);
      });

      if (!filtered.length) {
        list.innerHTML = '<div class="empty-state"><i class="fa-solid fa-magnifying-glass"></i><div>No matching booked brides found.</div></div>';
        return;
      }

      // Sort: upcoming weddings (dateMs >= today) soonest-first at the top, then
      // past weddings oldest-to-newest below a divider. Matches Proposal Builder
      // ordering so the next wedding to run is always the first item in the list.
      var nowMs = Date.now();
      var upcoming = filtered.filter(function(b) { return (b.dateMs || 0) >= nowMs; });
      var past     = filtered.filter(function(b) { return (b.dateMs || 0) <  nowMs; });
      upcoming.sort(function(a, b) { return (a.dateMs || 0) - (b.dateMs || 0); });
      past.sort(function(a, b)     { return (a.dateMs || 0) - (b.dateMs || 0); });
      var sorted = upcoming.concat(past);

      list.innerHTML = "";
      var pastDividerAdded = false;
      sorted.forEach(function(bride) {
        var isPast = (bride.dateMs || 0) < nowMs;
        if (isPast && !pastDividerAdded && upcoming.length) {
          pastDividerAdded = true;
          var divider = document.createElement("div");
          divider.className = "bride-list-divider";
          divider.textContent = "Past weddings";
          list.appendChild(divider);
        }
        const div = document.createElement("div");
        div.className = "bride-item" + (state.currentBride === bride.name ? " active" : "") + (isPast ? " bride-item--past" : "");
        div.innerHTML =
          '<div class="bride-name">' + escapeHtml(bride.name) + '</div>' +
          '<div class="muted-text">' + escapeHtml(formatDateDisplay_(bride.dateStr)) + "</div>";
        div.onclick = function() {
          loadWorkspace(bride.name);
        };
        list.appendChild(div);
      });
    }

    function renderStageBar() {
      const bar = document.getElementById("stageBar");
      bar.innerHTML = STAGES.map(function(stage) {
        return (
          '<button class="stage-pill' + (state.currentStage === stage.id ? ' active' : '') + '" onclick="setStage(\'' + stage.id + '\')">' +
            '<div class="stage-count">' + escapeHtml(stage.count) + "</div>" +
            '<div class="stage-name">' + escapeHtml(stage.name) + "</div>" +
            '<div class="stage-note">' + escapeHtml(stage.note) + "</div>" +
          "</button>"
        );
      }).join("");
    }

    function setStage(stageId) {
      var prev = state.currentStage;
      if (prev === "team" && stageId === "timeline") {
        rederiveDraftState_();
        if (typeof autosaveNow_ === "function") {
          try { autosaveNow_(); } catch (e) {}
        }
      }
      state.currentStage = stageId;
      renderStageBar();
      renderStage();
    }

    async function loadWorkspace(brideName, overrides, options) {
      if (!hasBridge) return;
      // Dirty-state guard: warn if switching brides with unsaved changes
      if (state.currentBride && brideName !== state.currentBride && state.timelineDirty) {
        var switchOk = await confirmModal_("You have unsaved changes for " + state.currentBride + ". Switch anyway?", "Switch", "Cancel");
        if (!switchOk) return;
      }
      const opts = options || {};
      // Drop a stale "View published runsheet" link when genuinely switching brides
      // (not on the same-bride reload that fires right after a publish).
      if (brideName !== state.currentBride) {
        var _pvl = document.getElementById("publishedViewerLink");
        if (_pvl) _pvl.remove();
      }
      resetBrideScopedState_(brideName);
      state.currentBride = brideName;
      state.hydrateTimedRowsFromBackend = !!opts.hydrateTimedRowsFromBackend;
      renderBrides();
      showBanner("Loading runsheet workspace...", "success");
      document.getElementById("repairBtn").disabled = true;
      document.getElementById("publishBtn").disabled = true;
      showLoading();

      // Check for autosaved draft
      var savedDraft = autosaveRestore_(brideName);
      if (savedDraft && !opts.forceReload) {
        // DISCARD BUTTON (2026-09-14, state/findings/URGENT-runsheet-publish-
        // stale-draft-root-cause-2026-09-14.md): every audit that has hit this
        // class of bug (2026-08-05, this incident) flagged the same missing
        // affordance -- a passive banner with no discard action, only
        // "Publish to save permanently," which actively invites publishing
        // stale data over a bride's real live schedule. rsbDiscardDraftAndReload_
        // below calls loadWorkspace with forceReload:true, which already (a)
        // skips this whole savedDraft branch entirely and (b) wipes
        // state.timelineOverrides via hydrateWorkspace's own existing
        // forceReload guard -- reusing existing reload logic, not new state.
        showBanner(
          'Restored local draft from ' + new Date(savedDraft.savedAt).toLocaleTimeString() +
          '. Publish to save permanently.' +
          '<button type="button" class="btn btn-ghost" style="margin-left:10px;padding:4px 14px;font-size:12px;" ' +
          'onclick="rsbDiscardDraftAndReload_(\'' + escapeHtml(brideName).replace(/'/g, "\\'") + '\')">Discard draft and load live</button>',
          "warning",
          true
        );
      }

      const token = ++state.requestToken;
      // In-flight guard (Bug A, 2026-07-03 audit): reloading/refreshing mid-wait
      // races the previous request. User-triggered refresh entry points check
      // this flag and refuse to stack a second workspace load.
      state.workspaceLoading = true;

      // P0(1) fix (Sub-D, 2026-10-26, BRIEFS/HANDOFF-runsheet-builder-cassandra-findings-
      // 2026-10-26.md finding #1 -- Cassandra Healey incident, wedding day minus one): the
      // workspace hydrate call below had NO bounded timeout, so on a slow/stuck relay it
      // hung on "Finalising workspace..." forever with no retry affordance (confirmed live:
      // 28s then a reload that also hung 15s). jv-gas-bridge.liquid's own TIMEOUT_MS=150000
      // is an outer transport-level bound, far too long to be a useful UX signal. This is a
      // tighter, UX-facing bound: GAS cold start is documented at ~7-10s (CLAUDE.md "GAS perf
      // wall") and the Cloudflare relay enforces a 30s upstream cap, so 28s gives this call a
      // full fair shot while guaranteeing the admin is never left staring at a frozen spinner.
      // On timeout: bump the request token so a late-arriving real response is silently
      // dropped (treated exactly like any other superseded load), and surface the existing
      // error path with a clear retry affordance (see handleWorkspaceError below).
      var WORKSPACE_LOAD_TIMEOUT_MS = 28000;
      var workspaceTimeoutHandle_ = setTimeout(function () {
        if (token !== state.requestToken) return; // already superseded
        state.requestToken++; // invalidate this token; a late real response is now ignored
        state.workspaceLoading = false;
        var timeoutErr = new Error(
          "Loading this bride's runsheet is taking longer than expected (over " +
          Math.round(WORKSPACE_LOAD_TIMEOUT_MS / 1000) + "s) and timed out. Nothing was lost -- try again."
        );
        timeoutErr.isWorkspaceTimeout = true;
        handleWorkspaceError(timeoutErr);
      }, WORKSPACE_LOAD_TIMEOUT_MS);

      // Part A1 CDN roster fast-path (2026-07-18): paint the roster INSTANTLY from
      // the runsheet_workspace_cache metaobject (zero GAS, immune to GAS execution-
      // slot saturation), then let the live call below repair it (stale-while-
      // revalidate). SKIP when overrides are present (unsaved edits / forceRebuild)
      // or when a local draft is being restored, those want the authoritative live
      // build, not a possibly-stale island (mirrors the GAS cache-skip rule in
      // shopifyBridge_runsheetGetWorkspace). The live call still fires
      // unconditionally; the island only fills the ~45s wait with the correct
      // roster instead of a spinner.
      var _rsbHasOverrides = overrides && typeof overrides === 'object' && Object.keys(overrides).length > 0;
      if (!_rsbHasOverrides && !savedDraft) {
        rsbTryCdnRoster_(brideName).then(function (cachedWs) {
          // Only paint if THIS load is still current AND the live call has not
          // already landed (workspaceLoading flips false when the live response
          // settles). This guarantees the island never overwrites fresher live data.
          if (!cachedWs) return;
          if (token !== state.requestToken) return;
          if (!state.workspaceLoading) return; // live response already painted
          try {
            hydrateWorkspace(cachedWs, { forceReload: !!opts.forceReload });
            rederiveDraftState_();
            renderStage();
            loadBrideAndPartyProfiles_((cachedWs || {}).email || '');
            showBanner("Roster loaded. Refreshing latest...", "success");
            // Problem 2 fix (2026-09-30, HANDOFF-runsheet-builder-step01-stale-
            // party-data): this page paints from a CDN cache exactly like the
            // 6 other admin pages that already set this flag (jv-admin-crm.js,
            // jv-team-dashboard.js, jv-admin-bride-profile-v2.js, jv-team-
            // availability.liquid) but never set it itself, so jv-portal-
            // shell.liquid's showError() had no signal that the page already
            // painted correctly and could show the loud "Could not connect to
            // backend" banner over working data instead of the quiet stale
            // marker. The live bridge_runsheet_getWorkspace call below still
            // fires unconditionally and repairs/repaints on success.
            window._jvIslandPainted = true;
          } catch (e) { /* island paint non-fatal; live call is the source of truth */ }
        });
      }

      // V2 owns its base workspace through a unique fresh-D1 route. That name
      // has no GAS dispatcher entry, so an unavailable mirror fails closed
      // instead of silently reopening from a stale legacy fullResponse blob.
      // V2 edits are persisted by its intent API below; legacy overrides stay
      // on the explicit V2-off rollback path only.
      var workspaceRoute_ = rsbV2On_() ? 'bridge_runsheet_v2_workspace' : 'bridge_runsheet_getWorkspace';
      jvBridge.callAdmin(workspaceRoute_, { brideName: brideName, overrides: rsbV2On_() ? {} : (overrides || {}) })
        .then(function(res) {
          // Clear BEFORE the staleness check (bug found in live E2E verification,
          // 2026-07-03): this specific request has settled either way, so the flag
          // must clear regardless of whether a newer request has since superseded
          // it. Gating the clear behind `token !== state.requestToken` meant a
          // superseded request's response could never clear the flag it itself
          // set; if the newer request then also errored/timed out (confirmed live
          // via a Cloudflare Worker 524) `workspaceLoading` stayed stuck `true`
          // forever, permanently blocking Refresh Timeline/Recalculate until a
          // full page reload, recreating the exact "had to reload multiple
          // times" symptom this guard was meant to fix.
          clearTimeout(workspaceTimeoutHandle_); // P0(1) fix: request settled, cancel the bound
          state.workspaceLoading = false;
          if (token !== state.requestToken) return; // superseded by a newer load (or our own timeout)
          if (res.ok && (!rsbV2On_() || (res.found === true && res.data))) {
            hydrateWorkspace(res.data || {}, { forceReload: !!opts.forceReload });
            // Wedding address: with the CF flag on, D1 is the owner of this value (the GAS workspace reads the Sheet,
            // which is only mirrored from D1 asynchronously). Overlay D1 so a just-saved address never reverts on screen.
            if (window.jvRsbLocationCf === true) { try { rsbOverlayLocationFromD1_(brideName, token); } catch (e) { /* non-fatal */ } }
            // V2 (dark, flag-gated): load revisions + server draft for this bride; no-op when the flag is off.
            if (rsbV2On_()) { try { rsbV2Init_(); window.jvRunsheetV2.onWorkspaceLoaded(); } catch (v2e) { try { console.warn('[runsheet-v2] onWorkspaceLoaded failed', v2e); } catch (_) {} } }
            // P6: CF availability read -- fast paint from GAS already done above
            // (artist.availability is pre-enriched server-side by appEnrichTeamWithAvailability_),
            // this is the background override. CF-overrides-when-CF-has-an-answer;
            // on any failure the GAS values stay. Fire-and-forget, re-renders on return.
            if (runsheetAvailabilityCFEnabled_() && token === state.requestToken) {
              var _cfTokenAtFire = token;
              loadAvailabilityForBride_(brideName).then(function (maps) {
                if (!maps) return;
                if (_cfTokenAtFire !== state.requestToken) return; // workspace moved on
                var team = state.workspace && state.workspace.team;
                mergeCFAvailability_(team, maps);
                try { renderStage(); } catch (e) { /* non-fatal; next render pass picks it up */ }
              });
            }
            if (savedDraft && !opts.forceReload) {
              // STAFFING-RESTORE FIX (2026-08-05, from the runsheet E2E stress
              // test, BRIEFS/RESULTS-runsheet-builder-e2e-stress-2026-08-05.md).
              //
              // THE BUG: these lines used to overwrite the freshly-fetched live
              // team + lead artist with whatever was in localStorage, silently,
              // for ANY stored draft. Reopening Bianca Dibou with zero edits
              // showed 2 artists + hairstylist Jill on one load and 1 artist +
              // Jessica on the next, reproducibly, with NO console error. The
              // only signal was a yellow banner reading "Restored local draft...
              // Publish to save permanently", wording that invites the admin to
              // publish the restored state OVER the live one.
              //
              // WHY A DRAFT EXISTS WITHOUT ANY EDIT: setStage() calls
              // autosaveNow_() on the team to timeline transition (see that
              // function), so merely NAVIGATING between tabs persists activeTeam
              // and leadArtist to localStorage. The draft is therefore not
              // reliably "the admin's unsaved work", it can be a snapshot of a
              // previous session's derived state, up to 24h old, restored over
              // current server truth.
              //
              // WHY THIS MATTERS MORE THAN A DISPLAY GLITCH: publishing rosters
              // real artists onto a real wedding day. A silently-restored stale
              // team can un-roster an artist and double-book another into two
              // roles, and the banner encourages exactly that.
              //
              // THE FIX: only restore staffing when the draft genuinely diverges
              // from the live workspace, and when it does, say so LOUDLY and
              // name the conflict instead of quietly winning.
              //
              // TIMELINE-OVERRIDE RESTORE FIX (2026-09-14, state/findings/URGENT-
              // runsheet-publish-stale-draft-root-cause-2026-09-14.md, Isabelle
              // Sharp's published runsheet came out with swapped/duplicated
              // artist assignments after a real publish). The comment this
              // replaces claimed timelineOverrides had "no live equivalent to
              // compare against" and could stay unconditional -- that was wrong,
              // and this incident is the proof. Each override entry IS keyed by
              // a live serviceId and carries {startMin, endMin, artist}
              // (moveServiceToSameArtistLane_ etc.), so it has an exact live
              // equivalent: whatever service currently sits at that id in the
              // freshly-hydrated state.sourceServicesDraft. A stale override
              // from an earlier, abandoned edit session (the tester's own
              // account: several double-booking-conflict moves, reload,
              // different final edit, publish) was silently reapplied here with
              // no comparison at all, misapplying an old time/artist pairing
              // onto whatever real service now occupies that same composite id.
              //
              // Same posture as the staffing check above: compare, and only
              // keep+warn on a REAL divergence. An override that already
              // matches live state is a no-op restore, so it's kept silently
              // (this is the common case -- most drafts ARE just the live state,
              // per the staffing fix's own finding that navigating tabs alone
              // autosaves). A genuinely stale entry (references an id that no
              // longer exists, or disagrees with the live service's current
              // startMin/artist) is DROPPED, not silently applied -- given this
              // data has already corrupted one real bride's published schedule,
              // dropping is safer than restoring-with-a-warning here, unlike the
              // staffing case where the admin's own typed edits were worth
              // keeping. The admin can always re-make a real edit; they cannot
              // un-publish a wrong schedule.
              if (savedDraft.timelineOverrides && Object.keys(savedDraft.timelineOverrides).length) {
                var liveServicesById_ = {};
                (state.sourceServicesDraft || []).forEach(function(svc) {
                  var sid = getServiceId_(svc);
                  if (sid) liveServicesById_[sid] = svc;
                });

                var keptOverrides_ = {};
                var droppedStaleIds_ = [];
                Object.keys(savedDraft.timelineOverrides).forEach(function(overrideId) {
                  var entry = savedDraft.timelineOverrides[overrideId];
                  var liveSvc = liveServicesById_[overrideId];
                  if (!liveSvc) {
                    // The id this override was keyed against no longer exists in
                    // the live, freshly-hydrated service list at all -- cannot be
                    // the current edit, definitely stale.
                    droppedStaleIds_.push(overrideId);
                    return;
                  }
                  var liveStartMin = Number(liveSvc.startMin !== undefined
                    ? liveSvc.startMin
                    : parseDisplayTimeToMinutes(liveSvc.start || liveSvc.startTime || ""));
                  var liveArtist = String(liveSvc.assignedArtist || liveSvc.artist || "").trim();
                  var draftStartMin = entry && entry.startMin !== undefined ? Number(entry.startMin) : null;
                  var draftArtist = entry && entry.artist ? String(entry.artist).trim() : "";

                  var startMatches = draftStartMin === null || isNaN(draftStartMin) || draftStartMin === liveStartMin;
                  var artistMatches = !draftArtist || draftArtist === liveArtist;

                  if (startMatches && artistMatches) {
                    // Already agrees with live state -- restoring is a genuine
                    // no-op, keep it silently, same rule the staffing check uses.
                    keptOverrides_[overrideId] = entry;
                  } else {
                    droppedStaleIds_.push(overrideId);
                  }
                });

                state.timelineOverrides = keptOverrides_;
                if (droppedStaleIds_.length) {
                  showBanner(
                    'Discarded ' + droppedStaleIds_.length + ' stale saved edit' +
                    (droppedStaleIds_.length === 1 ? '' : 's') +
                    ' that no longer matched the live schedule (from an earlier, ' +
                    'abandoned draft). Only edits that already agreed with the ' +
                    'live runsheet were kept. Re-make any change you still need ' +
                    'before publishing.',
                    'warning'
                  );
                  try {
                    console.warn('[runsheet] dropped stale timelineOverrides entries on restore', {
                      bride: brideName, droppedIds: droppedStaleIds_, savedAt: savedDraft.savedAt
                    });
                  } catch (e) {}
                }
              }

              var liveTeam = Array.isArray(state.activeTeam) ? state.activeTeam.slice() : [];
              var liveLead = state.leadArtist || '';
              var draftTeam = Array.isArray(savedDraft.activeTeam) ? savedDraft.activeTeam.slice() : null;
              var draftLead = savedDraft.leadArtist || '';

              // Order-insensitive compare: the team is a set, and a re-derive can
              // legitimately reorder it without meaning anything changed.
              var sameTeam = !draftTeam || (
                draftTeam.length === liveTeam.length &&
                draftTeam.map(String).sort().join('') === liveTeam.map(String).sort().join('')
              );
              var sameLead = !draftLead || String(draftLead) === String(liveLead);

              if (sameTeam && sameLead) {
                // Draft agrees with the server. Restoring is a no-op, so DON'T
                // mark the workspace dirty. That false dirty flag is what
                // produced the spurious "Leave site?" prompt on a page where
                // nothing had been typed.
                state.timelineDirty = !!savedDraft.timelineOverrides;
              } else {
                // A real divergence. Keep the previous behaviour (draft wins, so
                // genuine unsaved work is never lost) but make it impossible to
                // publish over live staffing without having been told.
                if (draftTeam) state.activeTeam = draftTeam;
                if (draftLead) state.leadArtist = draftLead;
                state.timelineDirty = true;

                var bits = [];
                if (!sameTeam) {
                  bits.push('team ' + (draftTeam || []).join(', ') +
                    ' (saved) vs ' + (liveTeam.length ? liveTeam.join(', ') : 'none') + ' (live)');
                }
                if (!sameLead) {
                  bits.push('lead artist ' + draftLead + ' (saved) vs ' +
                    (liveLead || 'none') + ' (live)');
                }
                showBanner(
                  'UNSAVED STAFFING RESTORED, and it does NOT match the live runsheet: ' +
                  bits.join('; ') + '. You are seeing your SAVED version. ' +
                  'Check this before publishing, or click Refresh Timeline to discard it and load live.',
                  'warning'
                );
                try {
                  console.warn('[runsheet] staffing draft diverges from live workspace', {
                    bride: brideName, draftTeam: draftTeam, liveTeam: liveTeam,
                    draftLead: draftLead, liveLead: liveLead, savedAt: savedDraft.savedAt
                  });
                } catch (e) {}
              }
            }
            rederiveDraftState_();
            renderStage();
            loadBrideAndPartyProfiles_((res.data || {}).email || '');
            // Phase 3b (BRIEFS/runsheet-json-first-save-publish-product-brief-2026-07-22.md):
            // if this bride's runsheet is ALREADY published, surface it on open so the
            // admin edits the live version in place (no second tab to compare). The
            // workspace already loads the published timeline (appPublishRunsheet writes
            // it back to the same tab appGetWorkspace reads), so this is purely the
            // missing signal: show the existing "View published runsheet" card + a
            // re-publish reminder. Skipped when a local draft was restored (those are
            // unsaved edits, not the published state).
            if (!savedDraft) showPublishedStateOnLoad_(res.data || {});
          } else {
            handleWorkspaceError(res.error || 'Failed to load workspace');
          }
        })
        .catch(function(err) {
          clearTimeout(workspaceTimeoutHandle_); // P0(1) fix: request settled (rejected), cancel the bound
          state.workspaceLoading = false;
          if (token !== state.requestToken) return;
          handleWorkspaceError(err);
        });
    }

    function resetBrideScopedState_(nextBrideName) {
      const nextBride = String(nextBrideName || "").trim();
      const currentBride = String(state.currentBride || "").trim();
      if (!nextBride || nextBride === currentBride) return;

      state.workspace = null;
      state.leadArtist = "";
      state.activeTeam = [];
      state.sourceServicesDraft = [];
      state.dragServiceId = "";
      state.dragSource = "";
      state.lastHydrateDebug = null;
      state.timelineDirty = false;
      state.editingServiceId = null;
      state.dragStarted = false;
      state.bridalPartyDraft = [];
      state.customBlocksDraft = [];
      state.collapsedPartyCards = {};
      state.partyProfiles = [];
      state.brideProfile = null;
      state.contextMenu = { open: false, serviceId: "", x: 0, y: 0 };
      state.skillSelections = { makeup: "", hair: "", additional: [] };
      state.timelineOverrides = {};
      state.hydrateTimedRowsFromBackend = false;
      state._hasUserEditedStaffing = false;
    }

    function rsbOverlayLocationFromD1_(brideName, token) {
      var url = rsbLocationCfUrl_('read');
      if (!url) return;
      var nf = (typeof window._jvNativeFetch === 'function' ? window._jvNativeFetch : window.fetch).bind(window);
      nf(url, { method: 'POST', headers: { 'Content-Type': 'text/plain' }, credentials: 'same-origin', body: JSON.stringify({ brideName: brideName }) })
        .then(function(r) { return r.json(); })
        .then(function(j) {
          if (!j || j.ok !== true || typeof j.location !== 'string' || token !== state.requestToken || !state.workspace) return;
          rsbSetConfirmedLocation_(brideName, j.location);
          if (state.workspace.location === j.location) return;
          state.workspace.location = j.location;
          var inp = document.getElementById('bookingLocation');
          if (inp && document.activeElement !== inp) inp.value = j.location;
        })
        .catch(function() { /* keep the GAS-sourced value; the save path reports its own failures */ });
    }

    function hydrateWorkspace(workspace, opts) {
      var hydOpts = opts || {};
      state.workspace = workspace || {};
      state.timelineDirty = false;
      state.lastHydrateDebug = buildHydrateDebug_(workspace);

      if (!state._hasUserEditedStaffing) {
        seedStaffingFromWorkspace_(state.workspace);
      } else {
        rebuildActiveTeamFromSkillSelections_();
      }

      // Only wipe timeline overrides on explicit forceReload; preserve drag/duration/arrival edits otherwise.
      if (hydOpts.forceReload) {
        state.timelineOverrides = {};
      }

      state.sourceServicesDraft = normaliseEditableServices(
  (workspace.sourceServices && workspace.sourceServices.length
    ? workspace.sourceServices
    : workspace.services) || []
);
state.customBlocksDraft = state.sourceServicesDraft.filter(function(service) {
  return !!service.isCustomBlock;
});

// Auto-detect runsheet tab source: if any service came from the tab, treat as hydrated truth.
var hasRunsheetTabServices = state.sourceServicesDraft.some(function(s) {
  return s.source === "runsheet_tab";
});
if (hasRunsheetTabServices) {
  state.hydrateTimedRowsFromBackend = true;
  // Normalize assignedArtist ↔ artist so timeline lane matching always works.
  state.sourceServicesDraft.forEach(function(s) {
    if (s.source === "runsheet_tab") {
      s.assignedArtist = s.assignedArtist || s.artist || "";
      s.artist = s.assignedArtist;
    }
  });
}

if (state.hydrateTimedRowsFromBackend) {
  hydrateSourceDraftTimedFieldsFromWorkspace_();
}
sanitizeSourceServicesDraftAssignments_();
ensureBrideServiceRow_();
// NOTE: rederiveDraftState_() is called by the caller AFTER draft restoration,
// so we do NOT call it here (it would derive from stale/empty overrides).

// Snapshot baseline for diff preview before write-back. Factored into a
// function (2026-09-10, Defect B fix) so a successful save can re-snapshot
// both baselines, not just re-run this block's logic at load time.
snapshotDraftBaselines_();

const collapsed = {};
(state.bridalPartyDraft || []).forEach(function(person, index) {
  const key = String(person.personId || person.id || index);
  collapsed[key] = index > 0;
});
state.collapsedPartyCards = collapsed;

document.getElementById("repairBtn").disabled = false;
      var hasSvc = (state.workspace.services && state.workspace.services.length) ||
                   (state.workspace.sourceServices && state.workspace.sourceServices.length) ||
                   (state.workspace.unscheduledServices && state.workspace.unscheduledServices.length);
      document.getElementById("publishBtn").disabled = !(hasSvc && state.workspace.readyBy);
      document.getElementById("bTitle").innerText = workspace.brideName || state.currentBride || "Runsheet Builder";
      document.getElementById("bMeta").innerText = buildMetaLine(state.workspace);

      renderHeroStrip();
      renderDebugPanel();
      renderStatus();
      renderStage();
    }

    function handleWorkspaceError(err) {
      // NEVER-BLANK GUARD (Jess 2026-07-23, after the Meaghan publish-504 scare):
      // if a timeline is ALREADY loaded on screen, a failed/timed-out RELOAD (very
      // common when GAS is saturated and the Cloudflare relay returns a 504) must
      // NOT wipe it to an error screen. The work is safe on the server (and in the
      // tab); the reload just couldn't fetch a fresh copy. Keep showing what's
      // there and warn non-destructively. Only show the hard empty-state on a true
      // COLD-LOAD failure where nothing was ever rendered for this bride.
      var haveLoadedTimeline = !!(state.workspace &&
        ((state.sourceServicesDraft && state.sourceServicesDraft.length) ||
         (state.workspace.services && state.workspace.services.length)));
      if (haveLoadedTimeline) {
        document.getElementById("repairBtn").disabled = false;
        document.getElementById("publishBtn").disabled = false;
        showBanner("Couldn't refresh from the server (it may be busy) - your work is still here and is saved. Try again in a moment; nothing was lost.", "warning");
        // Re-render from the workspace we still hold so the timeline stays visible.
        try { rederiveDraftState_(); renderStage(); } catch (e) { /* keep last paint */ }
        return;
      }
      state.workspace = null;
      state.debugExpanded = true;
      state.lastHydrateDebug = {
        state: "error",
        brideName: state.currentBride,
        message: getErrorMessage(err)
      };
      // P0 fix (Sub-D, 2026-10-26, HANDOFF-runsheet-builder-cassandra-findings-2026-10-26.md
      // findings #1/#2): Publish/Repair are explicitly disabled here (unchanged -- this branch
      // already matched the "unavailable" state correctly). What was missing is any retry
      // affordance: this empty-state previously had no button at all, so a cold-load failure
      // (including the new bounded timeout above) left the admin with nothing to click except
      // a full page reload. rsbRetryWorkspaceLoad_ below reuses loadWorkspace's existing
      // forceReload path verbatim -- not a new reload mechanism.
      document.getElementById("repairBtn").disabled = true;
      document.getElementById("publishBtn").disabled = true;
      showBanner(getErrorMessage(err), "error");
      document.getElementById("heroStrip").innerHTML = "";
      renderDebugPanel();
      document.getElementById("stageContent").innerHTML =
        '<div class="empty-state"><i class="fa-solid fa-triangle-exclamation"></i>' +
        '<div>' + escapeHtml(getErrorMessage(err)) + '</div>' +
        (state.currentBride ?
          '<button type="button" class="btn btn-primary" style="margin-top:14px;" ' +
          'onclick="rsbRetryWorkspaceLoad_(\'' + escapeHtml(state.currentBride).replace(/'/g, "\\'") + '\')">' +
          '<i class="fa-solid fa-rotate-right"></i> Retry</button>' : '') +
        '</div>';
    }

    /**
     * Retry affordance for the cold-load error empty-state (P0 fix, 2026-10-26, Cassandra
     * Healey incident). Reuses loadWorkspace's existing forceReload path -- identical to
     * rsbDiscardDraftAndReload_ / every other "Refresh Timeline" call site in this file.
     * @param {string} brideName
     */
    function rsbRetryWorkspaceLoad_(brideName) {
      loadWorkspace(brideName, {}, { forceReload: true });
    }

    var _loadingStepTimer = null;
    function showLoading() {
      document.getElementById("heroStrip").innerHTML = "";
      state.lastHydrateDebug = {
        state: "loading",
        brideName: state.currentBride,
        message: "Waiting for browser payload"
      };
      renderDebugPanel();
      document.getElementById("stageContent").innerHTML =
        '<div class="loader"><div class="spinner"></div><div id="loadingStepMsg">Loading CRM data...</div></div>';
      // Step-by-step progress messages
      var steps = [
        { text: "Checking invoices...", delay: 1500 },
        { text: "Resolving bridal party...", delay: 3000 },
        { text: "Building schedule...", delay: 5000 },
        { text: "Finalising workspace...", delay: 7500 }
      ];
      if (_loadingStepTimer) { _loadingStepTimer.forEach(clearTimeout); }
      _loadingStepTimer = steps.map(function(step) {
        return setTimeout(function() {
          var el = document.getElementById("loadingStepMsg");
          if (el) el.textContent = step.text;
        }, step.delay);
      });
    }

    function renderHeroStrip() {
      const w = state.workspace;
      if (!w) {
        document.getElementById("heroStrip").innerHTML = "";
        return;
      }

      const warningCount = Array.isArray(w.warnings) ? w.warnings.length : 0;
      const serviceCount = state.sourceServicesDraft.length;

      document.getElementById("heroStrip").innerHTML =
        '<div class="compact-banner-main">' +
          '<h3>' + escapeHtml(w.brideName || state.currentBride || "") + '</h3>' +
          '<p>' + escapeHtml(formatDateDisplay_(w.weddingDate)) + ' • ' + escapeHtml(w.location || "Location TBC") +
          ' • ' + escapeHtml(renderReadinessHeadline(w)) + '</p>' +
        '</div>' +
        '<div class="compact-banner-stats">' +
          renderMiniStat("Ready By", w.readyBy || "Confirm") +
          renderMiniStat("Services", String(serviceCount)) +
          renderMiniStat("Artists", String(w.recommendedArtistCount || 1)) +
          (warningCount
            ? '<div class="mini-stat" style="cursor:pointer;border-color:var(--warn-text);" onclick="openWarningsModal_()">' +
                '<span>Warnings</span><strong style="color:var(--warn-text);">' + escapeHtml(String(warningCount)) + '</strong>' +
              '</div>'
            : renderMiniStat("Warnings", "0")) +
        '</div>';
    }

    function renderHeroStat(label, value) {
      return (
        '<div class="hero-stat">' +
          "<span>" + escapeHtml(label) + "</span>" +
          "<strong>" + escapeHtml(value) + "</strong>" +
        "</div>"
      );
    }

    function renderStatus() {
      const w = state.workspace;
      if (!w) {
        hideBanner();
        return;
      }

      // Roster-changed-since-publish banner (BBS to builder additive merge). When
      // a bride's party grew after her runsheet was published, the backend appends
      // the new members as UNSCHEDULED blocks and sets rosterChangedSincePublish.
      // Surface it prominently: the timed rows are untouched, the planner just
      // needs to place the new people onto the timeline. Takes priority over the
      // generic warnings banner because it is directly actionable.
      // Reset the sticky flag each render; the roster branch below re-arms it.
      // Any non-roster path leaves it false so normal success banners work again.
      _rosterWarningActive = false;
      const rosterDelta = w.rosterChangedSincePublish;
      if (rosterDelta && rosterDelta.changed && Array.isArray(rosterDelta.newMembers) && rosterDelta.newMembers.length) {
        const names = rosterDelta.newMembers.map(function(nm) { return escapeHtml(String(nm || "")); }).join(", ");
        const n = rosterDelta.newMembers.length;
        // Arm the sticky flag BEFORE showing, so any later success banner (e.g. the
        // profile-loader "details loaded" note) cannot clobber this warning.
        _rosterWarningActive = true;
        showBanner(
          "Party roster changed since this runsheet was published. " + n + " new member" + (n === 1 ? "" : "s") +
          " added as unscheduled: " + names + ". Drag them onto the timeline, then re-publish.",
          "warning", true
        );
        return;
      }

      const warnings = Array.isArray(w.warnings) ? w.warnings : [];
      if (warnings.length) {
        const severity = warnings.some(function(item) {
          return String((item && item.severity) || "").toLowerCase() === "error";
        }) ? "error" : "warning";
        const extra = warnings.length > 1 ? " +" + (warnings.length - 1) + " more" : "";
        showBanner(escapeHtml(warnings[0].message + extra) + '  <span style="text-decoration:underline;cursor:pointer;margin-left:8px;" onclick="openWarningsModal_()">View all</span>', severity, true);
        return;
      }

      if (w.services && w.services.length) {
        showBanner("Preview ready. Drag services around, then publish when the schedule looks right.", "success");
        return;
      }

      if ((state.sourceServicesDraft || []).some(function(service) {
        return !!String(service.assignedArtist || service.artist || "").trim();
      })) {
        showBanner("Planner mode is ready. Services are staged in Working Areas until you place them on the timeline.", "success");
        return;
      }

      showBanner("The builder has prefilled the booking and service list. Confirm the ready-by time to generate the timed timeline.", "warning");
    }

    // Returns true when the debug panel should be visible. Gated behind the
    // ?debug=1 query param so it never appears in normal production sessions.
    function isDebugMode_() {
      try {
        return new URLSearchParams(window.location.search).get('debug') === '1';
      } catch (_) { return false; }
    }

    function renderDebugPanel() {
      const panel = document.getElementById("debugPanel");
      // Gate: hide entirely in normal (non-debug) sessions.
      if (!isDebugMode_()) {
        if (panel) panel.style.display = "none";
        return;
      }
      // Rebuild debug snapshot on every render so bucket counts stay current.
      if (state.workspace) {
        state.lastHydrateDebug = buildHydrateDebug_(state.workspace);
      }
      const debug = state.lastHydrateDebug;
      if (!panel || !debug) {
        if (panel) panel.style.display = "none";
        return;
      }

      // Build per-service assignment reason summary from workspace services.
      const svcReasons = ((state.workspace && state.workspace.services) || [])
        .map(function(s) {
          return escapeHtml(
            (s.personLabel || s.person || "?") + " " +
            (s.serviceLabel || s.service || "?") + " → " +
            (s.assignedArtist || s.artist || "unassigned") +
            " [" + (s.assignmentReason || "—") + "]"
          );
        }).join("<br>");

      panel.style.display = "block";
      panel.className = "debug-panel" + (state.debugExpanded ? "" : " collapsed");
      panel.innerHTML =
        '<div class="debug-head">' +
          "<h4>Browser Debug</h4>" +
          '<button class="debug-toggle" onclick="toggleDebugPanel()">' + (state.debugExpanded ? "Hide" : "Show") + "</button>" +
        "</div>" +
        '<div class="debug-body">' +
          '<div class="debug-grid">' +
            renderDebugItem_("State", debug.state || "") +
            renderDebugItem_("Hydrated From", debug.hydratedFrom || "—") +
            renderDebugItem_("Bride", debug.brideName || "") +
            renderDebugItem_("Date", debug.weddingDate || "") +
            renderDebugItem_("Location", debug.location || "") +
            renderDebugItem_("Ready By", debug.readyBy || "") +
            renderDebugItem_("Source Services", String(debug.sourceServices || 0)) +
            renderDebugItem_("On Timeline", String(debug.timedServices || 0)) +
            renderDebugItem_("In Working Area", String(debug.workingServices || 0)) +
            renderDebugItem_("Unscheduled", String(debug.unscheduledServices || 0)) +
            renderDebugItem_("From Sheet", String(debug.runsheetTabServices || 0)) +
            renderDebugItem_("Timing Bounds", debug.timingBounds || "—") +
            renderDebugItem_("Makeup Artist", escapeHtml(state.skillSelections.makeup || "—")) +
            renderDebugItem_("Hair Artist", escapeHtml(state.skillSelections.hair || "—")) +
            renderDebugItem_("Active Team", escapeHtml((state.activeTeam || []).join(", ") || "—")) +
            renderDebugItem_("User Edited Staffing", state._hasUserEditedStaffing ? "yes" : "no") +
            renderDebugItem_("Lead Artist", debug.leadArtist || "") +
            renderDebugItem_("Phone", debug.phone || "") +
            renderDebugItem_("Message", debug.message || "OK") +
          "</div>" +
          (state.debugExpanded && svcReasons
            ? '<div style="margin-top:8px;font-size:11px;line-height:1.6;color:var(--muted);"><strong>Service ownership:</strong><br>' + svcReasons + "</div>"
            : "") +
          (state.debugExpanded ? buildServiceIdentityDebugRows_() : "") +
        "</div>";
    }

    function buildServiceIdentityDebugRows_() {
      var services = (state.sourceServicesDraft || []).slice(0, 5);
      if (!services.length) return "";
      var rows = services.map(function(s) {
        return '<tr>' +
          '<td style="padding:2px 6px;">' + escapeHtml(String(s.id || s.itemId || "?").substring(0, 30)) + '</td>' +
          '<td style="padding:2px 6px;">' + escapeHtml(String(s.personId || "").substring(0, 20)) + '</td>' +
          '<td style="padding:2px 6px;">' + escapeHtml(s.assignedArtist || s.artist || "-") + '</td>' +
          '<td style="padding:2px 6px;">' + escapeHtml(s.startTimeOverride || s.startTime || s.start || "-") + '</td>' +
          '<td style="padding:2px 6px;">' + escapeHtml(s.source || "-") + '</td>' +
        '</tr>';
      });
      return '<div style="margin-top:8px;font-size:10px;line-height:1.4;color:var(--muted);overflow-x:auto;">' +
        '<strong>Service identity (first 5):</strong>' +
        '<table style="margin-top:4px;border-collapse:collapse;width:100%;">' +
          '<tr style="font-weight:700;border-bottom:1px solid var(--line);">' +
            '<td style="padding:2px 6px;">id</td><td style="padding:2px 6px;">personId</td>' +
            '<td style="padding:2px 6px;">artist</td><td style="padding:2px 6px;">startTime</td>' +
            '<td style="padding:2px 6px;">source</td>' +
          '</tr>' +
          rows.join("") +
        '</table>' +
      '</div>';
    }

    function toggleDebugPanel() {
      state.debugExpanded = !state.debugExpanded;
      renderDebugPanel();
    }

    function renderStage() {
      if (!state.workspace) {
        document.getElementById("stageContent").innerHTML =
          '<div class="empty-state"><i class="fa-solid fa-arrow-left"></i><div>Select a bride to start the staged runsheet builder.</div></div>';
        return;
      }

      // Preserve scroll position across re-renders (FRIC-R01)
      var stageContent = document.getElementById("stageContent");
      var workspace = stageContent ? stageContent.closest('.workspace') : null;
      var scrollTop = workspace ? workspace.scrollTop : 0;
      var sheet = stageContent ? stageContent.querySelector('.timeline-sheet') : null;
      var sheetScrollLeft = sheet ? sheet.scrollLeft : 0;

      if (state.currentStage === "booking") {
        renderBookingStage();
      } else if (state.currentStage === "team") {
        renderTeamStage();
      } else {
        renderTimelineStage();
      }

      // Restore scroll position after render
      if (workspace && scrollTop) workspace.scrollTop = scrollTop;
      var newSheet = stageContent ? stageContent.querySelector('.timeline-sheet') : null;
      if (newSheet && sheetScrollLeft) newSheet.scrollLeft = sheetScrollLeft;
      // Re-apply the height-scaler CSS var to the freshly-rendered timeline sheet
      // so the chosen scale persists across re-renders (Jess 2026-07-22).
      if (typeof rsbApplySlotScaleVar_ === 'function') rsbApplySlotScaleVar_();
      // Re-mark the active granularity button after each render (Jess 2026-07-23).
      if (typeof rsbApplyGranularityLabel_ === 'function') rsbApplyGranularityLabel_();
    }

    function renderBookingStage() {
      const w = state.workspace || {};
      const content = document.getElementById("stageContent");
      const readyByValue = displayTimeToInput(w.readyBy);
      // isPublished kept for future use but nothing conditional renders from it here yet.
      // Working Team and Lead Artist are NOT shown on Page 1 — they belong on Page 2.
      // Remove completely until publish state is proven reliable.

      content.innerHTML =
        '<div class="stage-grid">' +
          '<section class="stage-card">' +
            '<div class="card-head">' +
              '<div><h3>Booking Details</h3><p>Editable details for the final runsheet header.</p></div>' +
              // Two distinct actions, deliberately separate buttons (2026-07-21,
              // BUG-runsheet-page1-save-refresh-label-confusion). "Save + Refresh"
              // was the ONLY button here and did not persist anything - it only
              // reran the schedule preview from your edits (loadWorkspace, a live
              // recalc), which looked like a save but wasn't. Reported live: a
              // Ready By edit appeared to "revert" because the user (reasonably)
              // believed this button had saved it, then navigated away. Renamed to
              // "Recalculate Preview" (matching the wording already used sitewide
              // for this exact non-destructive action, e.g. showBanner call sites)
              // and added a real "Save Draft" button that calls the SAME
              // saveDraftChanges() the Timeline stage (Page 3) already uses to
              // persist to the CRM/Runsheet tab via bridge_runsheet_saveDraft.
              '<div style="display:flex;gap:8px;flex-wrap:wrap;">' +
                '<button class="btn btn-soft" onclick="saveDraftChanges()"><i class="fa-solid fa-floppy-disk"></i> Save Draft</button>' +
                '<button class="btn btn-ghost" onclick="applyBookingDetails()"><i class="fa-solid fa-rotate-right"></i> Recalculate Preview</button>' +
              '</div>' +
            '</div>' +

            '<div class="form-grid">' +
  renderEditableField("Bride", "bookingBrideName", w.brideName || state.currentBride || "", "text", "") +
  renderDateField_("Date", "bookingDate", w.weddingDate) +
  renderEditableField("Bride Phone", "bookingPhone", w.phone || "", "tel", "") +
  '<div class="field wide"><label>Address</label><input id="bookingLocation" type="text" placeholder="Enter address manually" value="' + escapeHtml(w.location || "") + '" onblur="handleLocationBlur(this)"><div id="bookingLocationStatus" class="muted-text" role="status" aria-live="polite"></div></div>' +
  // Page 1 Ready By must write into state.workspace.readyBy on edit. Without
  // this, typing here never updates state, and buildCurrentOverrides() (which
  // reads state.workspace.readyBy FIRST, per the 2026-07-21 payload fix) sends
  // the OLD value back to the server on Save + Refresh / Save Draft, silently
  // reverting the edit. Deliberately NOT reusing setSnapshotFinishTime_ (the
  // Page 3 equivalent) - that calls renderStage(), which would destructively
  // re-render the whole Page 1 form and wipe any other unsaved field the user
  // hasn't saved yet (Bride Phone, Address, Planner Notes, etc. also only sync
  // to state on Save, same as this field previously didn't at all). This
  // handler only updates state, no re-render. Bug: BUG-runsheet-page1-readyby-not-wired-2026-07-21.
  '<div class="field"><label>Ready By</label><input id="bookingReadyBy" type="time" value="' + escapeHtml(readyByValue || "") + '" onchange="syncBookingReadyByToState_(this.value)"></div>' +
  '<div class="field"><label>Service Package</label>' + renderBookingServicePackageSelect_() + '</div>' +
  renderEditableTextarea("Planner Notes", "bookingPlannerNotes", w.plannerNotes || "", "Short notes only.") +
'</div>' +

            '<div class="stage-actions">' +
              '<button class="btn btn-ghost" onclick="setStage(\'team\')"><i class="fa-solid fa-arrow-right"></i> Next: Team Builder</button>' +
            '</div>' +
          '</section>' +
          '<aside class="stack">' +
            renderPartyUnitsPanel() +
            '<div id="brideProfilePanel">' + renderBrideProfilePanel_() + '</div>' +
            '<div id="partyProfilesPanel">' + renderPartyProfilesPanel_() + '</div>' +
            '<div id="brideCardsPanel"></div>' +
            renderReferenceLinksPanel_() +
          '</aside>' +
        '</div>';
    }

    // Booking-stage lead artist selector intentionally removed.
// Lead artist belongs on Page 2 only.

    function inferServicePackageFromServices_() {
      var hasHair = false;
      var hasMakeup = false;
      (state.sourceServicesDraft || []).forEach(function(s) {
        if (s.isCustomBlock || s.isSystemBlock) return;
        var label = String(s.serviceLabel || s.service || "").toLowerCase();
        var person = String(s.personLabel || s.person || "").toLowerCase();
        if (label.includes("set up") || label.includes("pack") || person.includes("arrives") || person.includes("departs")) return;
        if (label.includes("hair") || label.includes("wave") || label.includes("blow") || label.includes("updo")) hasHair = true;
        if (label.includes("make")) hasMakeup = true;
      });
      if (hasHair && hasMakeup) return "Hair & Makeup";
      if (hasHair) return "Hair Only";
      if (hasMakeup) return "Makeup Only";
      return "";
    }

    // Normalise a service-package label so stored values like "Hair & Makeup"
    // match the dropdown option "Hair & Makeup Package" (bug 2026-07-22: the value
    // mismatch meant the saved package never showed as selected, then a re-render
    // reset the dropdown to blank and the blank got saved). Compares on the core
    // words, ignoring the trailing "Package"/"Only" and punctuation/spacing.
    function normalisePackageKey_(v) {
      var s = String(v || "").toLowerCase().replace(/\s+/g, " ").trim();
      s = s.replace(/\bpackage\b/g, "").replace(/\bonly\b/g, "").replace(/[^a-z& ]/g, "").replace(/\s+/g, " ").trim();
      var hasHair = /hair|wave|blow|updo|style/.test(s);
      var hasMakeup = /make/.test(s);
      if (hasHair && hasMakeup) return "hair_makeup";
      if (hasHair) return "hair";
      if (hasMakeup) return "makeup";
      return "";
    }

    function renderBookingServicePackageSelect_() {
      const current = String((state.workspace && state.workspace.servicesRequested) || "").trim() || inferServicePackageFromServices_();
      const currentKey = normalisePackageKey_(current);
      const options = ["", "Hair & Makeup", "Hair Only", "Makeup Only"];
      return (
        // onchange persists the pick into state.workspace so buildCurrentOverrides
        // (which reads the DOM but re-renders can reset it) always sees the value.
        '<select id="bookingServicesRequested" onchange="syncBookingPackageToState_(this.value)">' +
          options.map(function(opt) {
            const label = opt || "(select)";
            const isSel = opt ? (normalisePackageKey_(opt) === currentKey && currentKey !== "") : (currentKey === "");
            return '<option value="' + escapeHtml(opt) + '"' + (isSel ? ' selected' : '') + '>' + escapeHtml(label) + '</option>';
          }).join('') +
        '</select>'
      );
    }

    // Persist the Service Package pick into state.workspace so it survives a
    // re-render and is always in the save payload (mirrors syncBookingReadyByToState_).
    function syncBookingPackageToState_(value) {
      if (!state.workspace) state.workspace = {};
      state.workspace.servicesRequested = String(value || "").trim();
      state.timelineDirty = true;
    }

    function renderReferenceLinksPanel_() {
      var w = state.workspace || {};
      var agreementLink = String(w.agreementLink || '').trim();
      var invoiceUrls = w.invoiceUrls || [];
      var inspirationUrl = String(w.inspirationFolderUrl || '').trim();
      var planningUrl = String(w.runsheetLink || w.runsheetUrl || '').trim();

      function refLink_(label, url, icon) {
        if (!url) {
          return '<div class="ref-link ref-link--disabled"><i class="fa-solid ' + icon + '"></i> ' + escapeHtml(label) + ' <span class="ref-link-suffix">\u2014 not set</span></div>';
        }
        return '<a href="' + escapeHtml(url) + '" target="_blank" rel="noopener" class="ref-link"><i class="fa-solid ' + icon + '"></i> ' + escapeHtml(label) + '</a>';
      }

      var invoiceHtml;
      if (invoiceUrls.length === 0) {
        invoiceHtml = refLink_('Invoices', '', 'fa-file-invoice');
      } else if (invoiceUrls.length === 1) {
        invoiceHtml = refLink_('Invoice', invoiceUrls[0], 'fa-file-invoice');
      } else {
        invoiceHtml =
          '<div class="ref-link-group">' +
            '<a href="' + escapeHtml(invoiceUrls[0]) + '" target="_blank" rel="noopener" class="ref-link"><i class="fa-solid fa-file-invoice"></i> Invoice</a>' +
            '<span class="ref-link-badge" onclick="this.nextElementSibling.classList.toggle(\'show\')" title="Show all invoices">+' + (invoiceUrls.length - 1) + ' more</span>' +
            '<div class="ref-link-expand">' +
              invoiceUrls.slice(1).map(function(u, i) {
                return '<a href="' + escapeHtml(u) + '" target="_blank" rel="noopener" class="ref-link ref-link--sub"><i class="fa-solid fa-file-invoice"></i> Invoice ' + (i + 2) + '</a>';
              }).join('') +
            '</div>' +
          '</div>';
      }

      return (
        '<div class="panel">' +
          '<h4>Reference Links</h4>' +
          '<div class="panel-sub">Useful links the team may need during the morning.</div>' +
          '<div class="quiet-list">' +
            refLink_('Bride agreement', agreementLink, 'fa-file-signature') +
            invoiceHtml +
            refLink_('Inspiration folder', inspirationUrl, 'fa-images') +
            refLink_('Planning notes', planningUrl, 'fa-clipboard-list') +
          '</div>' +
        '</div>'
      );
    }

    function normaliseDateForInput_(value) {
      const raw = String(value || "").trim();
      if (!raw) return "";
      // Already ISO
      if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
      // DD/MM/YYYY → ISO
      var ddmm = raw.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
      if (ddmm) {
        return ddmm[3] + "-" + ddmm[2].padStart(2, "0") + "-" + ddmm[1].padStart(2, "0");
      }
      // Try native Date parse (handles "23 Nov 2026", "November 23, 2026" etc.)
      const d = new Date(raw);
      if (!isNaN(d.getTime())) {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return y + "-" + m + "-" + day;
      }
      return "";
    }

    function normaliseDateForAuDisplay_(value) {
      var iso = normaliseDateForInput_(value);
      if (!iso) return "";
      var parts = iso.split("-");
      return parts[2] + "/" + parts[1] + "/" + parts[0];
    }

    function renderDateField_(label, id, value) {
      var display = normaliseDateForAuDisplay_(value);
      return (
        '<div class="field">' +
          '<label>' + escapeHtml(label) + '</label>' +
          '<input id="' + escapeHtml(id) + '" type="text" placeholder="DD/MM/YYYY" pattern="\\d{2}/\\d{2}/\\d{4}" value="' + escapeHtml(display) + '">' +
          '<small></small>' +
        '</div>'
      );
    }

    function formatDateDisplay_(input) {
      if (!input) return '—';
      var s = String(input).trim();
      var d;
      // DD/MM/YYYY — JS Date() treats as MM/DD/YYYY, so parse manually
      var ddmm = s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
      if (ddmm) {
        d = new Date(parseInt(ddmm[3], 10), parseInt(ddmm[2], 10) - 1, parseInt(ddmm[1], 10));
      } else {
        d = (input instanceof Date) ? input : new Date(s);
      }
      if (isNaN(d.getTime())) return String(input);
      var days = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
      var months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      var day = d.getDate();
      var suffix = (day === 1 || day === 21 || day === 31) ? 'st' :
                   (day === 2 || day === 22) ? 'nd' :
                   (day === 3 || day === 23) ? 'rd' : 'th';
      return days[d.getDay()] + ' ' + day + suffix + ' ' + months[d.getMonth()] + ' ' + d.getFullYear();
    }

    function renderTeamStage() {
  const w = state.workspace;
  const content = document.getElementById("stageContent");
  const teamOptions = Array.isArray(w.team) ? w.team : [];
  const makeupArtists = teamOptions.filter(function(a) { return !!a.canMakeup; });
  const hairArtists   = teamOptions.filter(function(a) { return !!a.canHair; });
  const selectedTeam  = uniqueNames(state.activeTeam || []);

  content.innerHTML =
    '<div class="service-builder-grid">' +

      '<section class="stage-card">' +
        '<div class="card-head">' +
          '<div><h3>Bridal Party</h3><p>One card per person. Set their services, style, and preferred artist here.</p></div>' +
          '<div class="chip-row">' +
            '<span class="chip">' + escapeHtml(String(state.bridalPartyDraft.length)) + ' people</span>' +
            '<button class="btn btn-soft" style="font-size:12px;padding:5px 14px;" onclick="openAddPersonModal_()"><i class="fa-solid fa-user-plus"></i> Add person</button>' +
          '</div>' +
        '</div>' +
        renderInspirationStatusPanel_() +
        renderPartyRosterAndDetail_(teamOptions) +
      '</section>' +

      '<aside class="stack">' +
        '<div class="stage-card">' +
                        '<div class="card-head">' +
                '<div><h3>Artist Assignments</h3><p>Choose the working artists, then assign the lead.</p></div>' +
                renderAvailSummaryChips_(teamOptions) +
              '</div>' +

          renderTeamAvailabilityPanel_() +

          '<div class="skill-picker-grid">' +
            renderSkillPickerCard_('Makeup Artist', 'skillMakeupSelect', makeupArtists, state.skillSelections.makeup) +
            renderSkillPickerCard_('Hairstylist', 'skillHairSelect', hairArtists, state.skillSelections.hair) +
          '</div>' +

          '<div style="margin-bottom:14px;">' +
            '<div style="font-size:11px;text-transform:uppercase;letter-spacing:0.1em;font-weight:800;color:var(--muted);margin-bottom:8px;">Additional Artists</div>' +
            '<div id="additionalArtistCards" class="team-grid">' + renderAdditionalArtistCards_(teamOptions) + '</div>' +
          '</div>' +

          '<div class="lead-select-row">' +
            '<label>Lead Artist</label>' +
            '<select id="leadArtistSelect" onchange="handleLeadArtistChange(this.value)">' +
              (selectedTeam.length
                ? selectedTeam.map(function(name) {
                    return '<option value="' + escapeHtml(name) + '"' + (name === state.leadArtist ? ' selected' : '') + '>' + escapeHtml(name) + '</option>';
                  }).join('')
                : '<option value="">Select artists above first</option>'
              ) +
            '</select>' +
          '</div>' +

                        '<div class="stage-actions">' +
                '<button class="btn btn-soft" onclick="setStage(\'booking\')"><i class="fa-solid fa-arrow-left"></i> Back</button>' +
                '<button class="btn btn-ghost" onclick="setStage(\'timeline\')"><i class="fa-solid fa-arrow-right"></i> Next: Timeline</button>' +
                '<button class="btn btn-soft" onclick="applyTeamAndServices()"><i class="fa-solid fa-wand-magic-sparkles"></i> Refresh Timeline</button>' +
              '</div>' +
        '</div>' +
        renderTeamSummaryPanel() +
      '</aside>' +

    '</div>';

  bindStageInputs();
  schedulePartySafetyAlertsLoad_();
}

    function renderTimelineStage() {
      syncBridalPartyToSourceServices_();
      syncWorkspaceServicesFromSourceDraft_();
      const w = state.workspace;
      const content = document.getElementById("stageContent");
      const services = Array.isArray(w.services) ? w.services : [];
      const hasWorkingAssignments = (state.sourceServicesDraft || []).some(function(service) {
        return !!String(service.assignedArtist || service.artist || "").trim();
      });
      // Anything draggable (e.g. after Start Again, every real service sits
      // unscheduled here) or a team already selected (so lanes exist to drag
      // onto) means the drag-and-drop grid itself should stay on screen —
      // only fall back to the bare "nothing yet" placeholder when there is
      // truly nothing to work with at all.
      const hasDraftServices = (state.sourceServicesDraft || []).some(function(service) {
        return service && !service.isSystemBlock;
      });
      const hasLanes = getLaneNames().length > 0;

      if (!services.length && !hasWorkingAssignments && !hasDraftServices && !hasLanes) {
        // Choose copy based on what's actually missing. In hub mode the
        // services start empty (no scheduled rows yet) but readyBy is
        // usually populated — the old "Ready-by time still needed" copy
        // was misleading. Surface the real blocker.
        var readyByMissing = !(state.workspace && String(state.workspace.readyBy || '').trim());
        var emptyHeadline = readyByMissing ? 'Ready by time still needed.' : 'No scheduled services yet.';
        var emptyBody = readyByMissing
          ? 'Set the Ready By time in Booking Details, then click Recalculate Timeline to build the schedule.'
          : 'Click Recalculate Timeline to schedule the unscheduled services below into the morning, or drag them onto the timeline manually.';
        content.innerHTML =
          '<div class="stage-grid">' +
            '<section class="stage-card">' +
              '<div class="card-head"><div><h3>Timeline Preview</h3><p>The timeline will appear here once services are scheduled.</p></div></div>' +
              '<div class="empty-state">' +
                '<i class="fa-solid fa-clock"></i>' +
                '<div><strong>' + escapeHtml(emptyHeadline) + '</strong><br>' + escapeHtml(emptyBody) + '</div>' +
              "</div>" +
              '<div class="stage-actions">' +
                '<button class="btn btn-soft" onclick="setStage(\'booking\')"><i class="fa-solid fa-arrow-left"></i> Back to Booking</button>' +
              "</div>" +
              renderUnscheduledPanel_() +
            "</section>" +
            '<aside class="stack">' + renderTimelineSummaryPanel() + renderTeamSummaryPanel() + "</aside>" +
          "</div>";
        return;
      }

      content.innerHTML =
        '<div class="stage-grid">' +
          '<section class="stage-card">' +
            '<div class="card-head" style="flex-wrap:wrap;">' +
              '<div style="min-width:200px;"><h3>Drag + Drop Timeline</h3><p>Use the artist columns like the real runsheet sheet. Move services between columns, then rebuild when you want the timing recalculated.</p></div>' +
              '<div style="display:flex;flex-wrap:wrap;gap:8px;">' +
                '<button class="btn btn-soft" onclick="saveDraftChanges()"><i class="fa-solid fa-floppy-disk"></i> Save Draft</button>' +
                '<span id="autosaveIndicator" style="font-size:11px;color:var(--muted);align-self:center;opacity:0;transition:opacity 0.3s;"></span>' +
                '<button class="btn btn-soft" onclick="applyTeamAndServices()"><i class="fa-solid fa-rotate-right"></i> Recalculate</button>' +
                '<button class="btn btn-soft" onclick="openAddServiceModal()"><i class="fa-solid fa-plus"></i> Add Service</button>' +
                '<button class="btn btn-soft" onclick="openAddCustomBlockModal()"><i class="fa-solid fa-cube"></i> Custom Block</button>' +
                '<button class="btn btn-soft" onclick="applyArtistSetupPackdown_()"><i class="fa-solid fa-clock"></i> Start / Finish Times</button>' +
                // Match Placeholders moved to Page 1 (Party Profiles panel) 2026-07-22.
                '<button class="btn btn-ghost" onclick="startAgainClearSchedule()"><i class="fa-solid fa-arrow-rotate-left"></i> Start Again</button>' +
                // Timeline height scaler (Jess 2026-07-22): shrink the rows/cards to
                // fit a long morning on screen. Fit auto-sizes; -/+ nudge by 10%.
                '<span class="rsb-scale" style="display:inline-flex;align-items:center;gap:4px;margin-left:4px;padding:2px 6px;background:var(--brand-bg-soft);border-radius:3rem;">' +
                  '<button class="btn btn-soft" style="padding:6px 12px;" onclick="fitSlotScaleToWindow()" title="Shrink the timeline to fit the whole morning on screen"><i class="fa-solid fa-compress"></i> Fit</button>' +
                  '<button class="btn btn-soft" style="padding:6px 10px;" onclick="nudgeSlotScale(-0.1)" title="Shrink rows" aria-label="Shrink rows">&#8722;</button>' +
                  '<span id="rsbScaleLabel" style="font-size:11px;color:var(--muted);min-width:34px;text-align:center;">100%</span>' +
                  '<button class="btn btn-soft" style="padding:6px 10px;" onclick="nudgeSlotScale(0.1)" title="Grow rows" aria-label="Grow rows">+</button>' +
                '</span>' +
                // Slot granularity toggle (Jess 2026-07-23): 15-min / 30-min / hourly
                // grid rows. Cards keep their real duration at every setting; hourly
                // just shows one row per hour. Active button is aria-pressed.
                '<span class="rsb-gran" style="display:inline-flex;align-items:center;gap:4px;margin-left:4px;padding:2px 6px;background:var(--brand-bg-soft);border-radius:3rem;" title="How finely the timeline is divided">' +
                  '<button id="rsbGran15" class="btn btn-soft" style="padding:6px 10px;" onclick="setSlotGranularity_(15)" aria-pressed="true">15m</button>' +
                  '<button id="rsbGran30" class="btn btn-soft" style="padding:6px 10px;" onclick="setSlotGranularity_(30)" aria-pressed="false">30m</button>' +
                  '<button id="rsbGran60" class="btn btn-soft" style="padding:6px 10px;" onclick="setSlotGranularity_(60)" aria-pressed="false">1hr</button>' +
                '</span>' +
              '</div>' +
            '</div>' +
            renderInspirationStatusPanel_() +
            renderTimelineAlerts_() +
            renderFinishTimeMismatchAlert_() +
            '<div class="timeline-grid">' + renderTimelineLanes() + "</div>" +
            renderUnscheduledPanel_() +
            '<div class="stage-actions">' +
              '<button class="btn btn-soft" onclick="setStage(\'team\')"><i class="fa-solid fa-arrow-left"></i> Back to Team</button>' +
              ((state.workspace && (state.workspace.runsheetViewLink || state.workspace.runsheetLink))
                ? '<button type="button" class="btn btn-ghost" onclick="openRunsheetPreview()"><i class="fa-solid fa-eye"></i> Preview Runsheet</button>'
                : '') +
              '<button class="btn btn-primary" onclick="publishRunsheet()"><i class="fa-solid fa-paper-plane"></i> Publish Runsheet</button>' +
            "</div>" +
          "</section>" +
          '<aside class="stack">' +
            renderTimelineSummaryPanel() +
          "</aside>" +
        "</div>";
    }

    function isTouchDevice_() {
  try {
    return !!(
      (typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(pointer: coarse)").matches) ||
      ("ontouchstart" in window) ||
      (navigator.maxTouchPoints > 0)
    );
  } catch (e) {
    return false;
  }
}

const IS_TOUCH_DEVICE = typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(pointer: coarse)").matches;

    function renderPartyUnitsPanel() {
      const summary = buildPartyUnitSummary();
      return (
        '<div class="panel">' +
          "<h4>Bridal Party Units</h4>" +
          '<div class="party-unit-grid">' +
            renderPartyUnit("Bride", String(summary.bride)) +
            renderPartyUnit("Bridesmaid", String(summary.bridesmaid)) +
            renderPartyUnit("Mother", String(summary.mother)) +
            renderPartyUnit("Guest", String(summary.guest)) +
          "</div>" +
        "</div>"
      );
    }

    // Inspo is considered complete if EITHER:
    //   1. Legacy Google form submitted (person.inspirationSubmitted), OR
    //   2. Hub onboarding done: hairstyle picked AND ≥1 inspo upload exists.
    //
    // The new criteria reflects that the bridal hub portal replaced the
    // Google form. Members who completed their hub onboarding shouldn't
    // appear as "missing" just because they didn't submit the legacy form.
    function isInspoCompleteForPerson_(person) {
      if (person.inspirationSubmitted) return true;
      const isBride = String(person.role || '').trim().toLowerCase() === 'bride';
      const nameLow = String(person.name || person.profileName || person.personLabel || '').trim().toLowerCase();

      if (isBride) {
        const brief = state.brideCardsData && state.brideCardsData.brideBrief;
        const inspo = state.brideCardsData && state.brideCardsData.brideInspo;
        const hasHairstyle = !!(brief && String(brief.hairstylePref || '').trim());
        const hasInspoPhoto = !!((inspo && Array.isArray(inspo.hair) && inspo.hair.length) ||
                                  (inspo && Array.isArray(inspo.makeup) && inspo.makeup.length));
        return hasHairstyle && hasInspoPhoto;
      }

      // Party member: check both blob and partyProfiles (different sync paths)
      const cards = (state.brideCardsData && state.brideCardsData.partyMembers) || [];
      const blobEntry = cards.find(function(c) {
        return String(c.name || '').trim().toLowerCase() === nameLow;
      });
      const profile = (state.partyProfiles || []).find(function(p) {
        return String(p.name || '').trim().toLowerCase() === nameLow;
      });
      const hairStyle = (blobEntry && blobEntry.hair_style) || (profile && profile.hairStyle) || '';
      const hairUrls = String((blobEntry && blobEntry.hair_inspo_urls) || '').trim();
      const makeupUrls = String((blobEntry && blobEntry.makeup_inspo_urls) || '').trim();
      const hasInspoPhoto = !!(hairUrls || makeupUrls);
      return !!(String(hairStyle).trim() && hasInspoPhoto);
    }

    function buildInspirationMissingItems_() {
      const seen = {};
      return (state.bridalPartyDraft || []).filter(function(person, index) {
        const key = String(person.personId || person.id || index).trim() || String(index);
        if (seen[key]) return false;
        seen[key] = true;
        return !isInspoCompleteForPerson_(person);
      });
    }

    function renderInspirationStatusPanel_() {
      const missing = buildInspirationMissingItems_();
      const brideCard = (state.bridalPartyDraft || []).find(function(person) {
        return String(person.role || "").toLowerCase() === "bride";
      }) || null;
      const needsTrialAssist = !!(brideCard && brideCard.trialAssistSubmitted === false);
      if (!missing.length && !needsTrialAssist) return "";

      const names = missing.slice(0, 4).map(function(person) {
        return escapeHtml(person.profileName || person.personLabel || person.role || "Unnamed");
      }).join(", ");
      const extra = missing.length > 4 ? " +" + (missing.length - 4) + " more" : "";
      const trialAssistLine = needsTrialAssist
        ? '<p>Bride trial assist form is still missing.</p>'
        : '';

      return (
        '<div class="inspiration-status-panel">' +
          '<h4><i class="fa-solid fa-image" style="margin-right:8px;"></i>Inspiration Forms Missing</h4>' +
          (missing.length
            ? '<p>' + names + extra + ' still need inspiration form submissions or confirmation.</p>'
            : '<p>Drive truth is loaded for this bride.</p>') +
          trialAssistLine +
        '</div>'
      );
    }

    function renderTeamSummaryPanel() {
      const w = state.workspace;
      var roleCounts = { Bride: 0, Bridesmaid: 0, Mother: 0, Guest: 0, Other: 0 };
      (state.bridalPartyDraft || []).forEach(function(p) {
        var r = String(p.role || "").trim();
        if (r === "Bride") roleCounts.Bride++;
        else if (r === "Bridesmaid") roleCounts.Bridesmaid++;
        else if (r === "Mother") roleCounts.Mother++;
        else if (r === "Guest") roleCounts.Guest++;
        else roleCounts.Other++;
      });
      var tallyParts = [];
      if (roleCounts.Bride)      tallyParts.push(renderMiniStat("Bride", String(roleCounts.Bride)));
      if (roleCounts.Bridesmaid)  tallyParts.push(renderMiniStat("Bridesmaid", String(roleCounts.Bridesmaid)));
      if (roleCounts.Mother)      tallyParts.push(renderMiniStat("Mother", String(roleCounts.Mother)));
      if (roleCounts.Guest)       tallyParts.push(renderMiniStat("Guest", String(roleCounts.Guest)));
      if (roleCounts.Other)       tallyParts.push(renderMiniStat("Other", String(roleCounts.Other)));
      return (
        '<div class="panel">' +
          "<h4>Team Snapshot</h4>" +
          '<div class="panel-sub">Who is leading, how many are selected, and the bridal party breakdown.</div>' +
          '<div class="readonly-pair">' +
            renderMiniStat("Lead", state.leadArtist || "TBC") +
            renderMiniStat("Selected", String(state.activeTeam.length || 0)) +
            renderMiniStat("Recommended", String(w.recommendedArtistCount || 1)) +
            renderMiniStat("Service Rows", String(state.sourceServicesDraft.length)) +
          "</div>" +
          (tallyParts.length
            ? '<div style="margin-top:14px;"><div style="font-size:11px;text-transform:uppercase;letter-spacing:0.1em;font-weight:800;color:var(--muted);margin-bottom:6px;">Bridal Party</div>' +
              '<div class="readonly-pair">' + tallyParts.join("") + '</div></div>'
            : '') +
          '<div class="info-list" style="margin-top:14px;">' +
            renderInfoRow("Selected", renderArtistList_((state.activeTeam || []), "artist-avatar--sm")) +
          "</div>" +
        "</div>"
      );
    }

    function getSanitisedStartTime_(workspace) {
      var draft = workspace.draftStartTime || "";
      var earliest = findEarliestStart(workspace.services || []);
      if (!draft) return earliest || "TBC";
      if (!earliest) {
        // No timed services — validate draft against readyBy before using it.
        // A stale draft (e.g. "12:00 PM" default) should not display when
        // readyBy is earlier (e.g. "9:00 AM") and no services are scheduled.
        var readyByCheck = parseDisplayTimeToMinutes(workspace.readyBy || "");
        var draftCheck = parseDisplayTimeToMinutes(draft);
        if (readyByCheck !== null && draftCheck !== null && draftCheck >= readyByCheck) {
          return "TBC";
        }
        return draft;
      }
      var draftMin = parseDisplayTimeToMinutes(draft);
      var earliestMin = parseDisplayTimeToMinutes(earliest);
      if (draftMin === null) return earliest;
      if (earliestMin === null) return draft;
      // If draft start is after the earliest real service, it's stale — use real value
      if (draftMin > earliestMin) return earliest;
      // Also check against readyBy — draft start must not be after ready-by
      var readyByMin = parseDisplayTimeToMinutes(workspace.readyBy || "");
      if (readyByMin !== null && draftMin > readyByMin) return earliest;
      return draft;
    }

    // Editable mini-stat: same shell as renderMiniStat but the value is a
    // time <input> so start/finish can be edited directly from the Timeline
    // Snapshot. onChangeExpr is an inline handler string (e.g.
    // "setSnapshotStartTime_(this.value)"). When the time is unknown the input
    // is left blank (placeholder shows "TBC"); typing a value drives the edit.
    function renderMiniStatTimeInput_(label, displayValue, onChangeExpr) {
      var inputVal = displayTimeToInput(displayValue);
      return (
        '<div class="mini-stat mini-stat--editable">' +
          "<span>" + escapeHtml(label) + "</span>" +
          '<input type="time" class="mini-stat-input" value="' + escapeHtml(inputVal) + '"' +
            ' placeholder="TBC" aria-label="' + escapeHtml(label) + ' time"' +
            ' onchange="' + onChangeExpr + '">' +
        "</div>"
      );
    }

    function renderTimelineSummaryPanel() {
      const w = state.workspace;
      const latestFinish = findLatestFinish(w.services || []);
      return (
        '<div class="panel">' +
          "<h4>Timeline Snapshot</h4>" +
          '<div class="panel-sub">Edit the start or finish time here, then Recalculate or Publish to apply.</div>' +
          '<div class="readonly-pair">' +
            renderMiniStatTimeInput_("Starts", getSanitisedStartTime_(w), "setSnapshotStartTime_(this.value)") +
            renderMiniStatTimeInput_("Finishes", latestFinish || w.readyBy || "", "setSnapshotFinishTime_(this.value)") +
          "</div>" +
          '<div class="readonly-pair" style="grid-template-columns:1fr;margin-top:10px;">' +
            renderMiniStat("Timed Rows", String((w.services || []).filter(function(s) { return !s.isSystemBlock; }).length)) +
          "</div>" +
          '<div class="info-list" style="margin-top:14px;">' +
            renderInfoRow("Lead", w.leadArtist || "TBC") +
            renderInfoRow("Artists", (state.activeTeam || []).join(", ") || "No team selected") +
            renderInfoRow("Ready By", w.readyBy || "TBC") +
          "</div>" +
        "</div>"
      );
    }

    // ── Timeline Snapshot inline time editors ──────────────────────────────
    // Both write into draft state that already round-trips to the published
    // bride-facing runsheet (Save Draft / Recalculate / Publish all build the
    // payload from sourceServicesDraft + workspace.readyBy via
    // buildCurrentOverrides). No frozen-engine change: the FINISH edit anchors
    // the scheduler via booking.finishTime, mapped to input.readyBy, and the
    // START edit shifts every timed service by the delta and writes
    // startTimeOverride per row (which the frozen scheduler honours on
    // re-derive), mirroring the existing shiftScheduleToReadyBy_ pattern.

    // Page 1 "Ready By" onchange handler. Deliberately lightweight (no
    // renderStage()) - see the call-site comment in renderBookingStage() for
    // why: Page 1 fields (Bride Phone, Address, Planner Notes, etc.) only sync
    // to state.workspace on Save, so a destructive re-render here would wipe
    // any of those still-unsaved edits the moment the user tabs off this field.
    function syncBookingReadyByToState_(rawInputValue) {
      if (!state.workspace) return;
      var display = inputTimeToDisplay(rawInputValue);
      if (!display) { showBanner("Enter a valid finish time.", "warning"); return; }
      if (display === String(state.workspace.readyBy || "").trim()) return;
      state.workspace.readyBy = display;
      state.timelineDirty = true;
      try { var bMeta = document.getElementById("bMeta"); if (bMeta) bMeta.innerText = buildMetaLine(state.workspace); } catch (e) {}
      try { renderHeroStrip(); } catch (e) {}
    }

    // FINISH: sets workspace.readyBy (the scheduler's hard-stop anchor).
    // Persisted via booking.finishTime in buildCurrentOverrides().
    function setSnapshotFinishTime_(rawInputValue) {
      if (!state.workspace) return;
      var display = inputTimeToDisplay(rawInputValue);
      if (!display) { showBanner("Enter a valid finish time.", "warning"); return; }
      if (display === String(state.workspace.readyBy || "").trim()) return;
      state.workspace.readyBy = display;
      state.timelineDirty = true;
      try { var bMeta = document.getElementById("bMeta"); if (bMeta) bMeta.innerText = buildMetaLine(state.workspace); } catch (e) {}
      try { renderHeroStrip(); } catch (e) {}
      renderStage();
      showBanner("Finish time set to " + display + ". Click Recalculate to re-time the schedule, or Save Draft to keep it.", "success");
    }

    // START: shifts the whole timed schedule so the earliest service starts at
    // the chosen time. Sets startTimeOverride on each row (survives publish).
    function setSnapshotStartTime_(rawInputValue) {
      if (!state.workspace) return;
      var targetMin = parseDisplayTimeToMinutes(inputTimeToDisplay(rawInputValue));
      if (targetMin === null) { showBanner("Enter a valid start time.", "warning"); return; }

      var timed = (state.sourceServicesDraft || []).filter(function(s) {
        return !s.isSystemBlock && !s.isCustomBlock && isServiceTimed_(s);
      });
      if (!timed.length) {
        // No scheduled rows yet: record the desired start as the draft seed so
        // getSanitisedStartTime_ reflects it; the next Recalculate builds from it.
        state.workspace.draftStartTime = inputTimeToDisplay(rawInputValue);
        state.timelineDirty = true;
        renderStage();
        showBanner("Start time noted. Click Recalculate Timeline to build the schedule from it.", "success");
        return;
      }

      var startMins = timed.map(function(s) {
        return s.startMin !== null && s.startMin !== undefined ? Number(s.startMin) : parseDisplayTimeToMinutes(s.start || s.startTime || "");
      }).filter(function(v) { return v !== null && Number.isFinite(v); });
      if (!startMins.length) { showBanner("Could not read current start times.", "warning"); return; }

      var currentStart = Math.min.apply(null, startMins);
      var shiftBy = targetMin - currentStart;
      if (shiftBy === 0) return;

      (state.sourceServicesDraft || []).forEach(function(s) {
        if (!isServiceTimed_(s)) return;
        var sMin = s.startMin !== null && s.startMin !== undefined ? Number(s.startMin) : parseDisplayTimeToMinutes(s.start || s.startTime || "");
        var eMin = s.endMin !== null && s.endMin !== undefined ? Number(s.endMin) : parseDisplayTimeToMinutes(s.end || s.endTime || "");
        if (sMin === null || eMin === null) return;
        var newStart = Math.max(0, sMin + shiftBy);
        var newEnd = Math.max(newStart + 15, eMin + shiftBy);
        applyTimedPlacementToService_(s, s.assignedArtist || s.artist || "", newStart, newEnd);
      });

      rederiveDraftState_();
      state.timelineDirty = true;
      try { var bMeta2 = document.getElementById("bMeta"); if (bMeta2) bMeta2.innerText = buildMetaLine(state.workspace); } catch (e) {}
      try { renderHeroStrip(); } catch (e) {}
      renderStage();
      showBanner("Schedule shifted by " + (shiftBy > 0 ? "+" : "") + shiftBy + " minutes to start at " + formatMinutesAsDisplayTime_(targetMin) + ". Click Save Draft to keep it.", "success");
    }

    function renderSourceDropSlot(index) {
      return (
        '<div class="drop-slot" data-drop-type="source" data-drop-index="' + index + '"' +
          ' ondragover="handleDropOver(event)" ondragleave="handleDropLeave(event)" ondrop="handleSourceDrop(event)"></div>'
      );
    }

    function renderTimelineLanes() {
      const structure = buildTimelineStructure();
      if (!structure.laneNames.length) {
        return '<div class="empty-state"><i class="fa-solid fa-clock"></i><div>No timed rows are available yet.</div></div>';
      }

      const cols = ['92px'].concat(structure.laneNames.map(function() {
  return 'minmax(140px, 1fr)';
})).join(' ');
const sheetMinWidth = 92 + (structure.laneNames.length * 140);

      let html =
        '<div class="timeline-sheet" style="min-width:' + sheetMinWidth + 'px;">' +
          '<div class="timeline-sheet-grid" style="grid-template-columns:' + cols + ';">' +
            '<div class="timeline-corner">Time</div>';

      structure.laneNames.forEach(function(name) {
        html += renderTimelineHeadCell(name, structure.grouped[name] || [], getWorkingAreaServicesForArtist_(name));
      });

      structure.rows.forEach(function(row) {
        html += '<div class="timeline-time-cell">' + escapeHtml(inputTimeToDisplay(row.label)) + '</div>';
        structure.laneNames.forEach(function(name) {
          html += renderTimelineGridCell(name, row, structure.cellMap[name] || {});
        });
      });

      html += '</div></div>';
      return html;
    }

function renderTimelineHeadCell(artistName, items, workingItems) {
  const meta = buildArtistWindowMeta_(artistName, items);
  const realItems = items.filter(function(s) { return !s.isSystemBlock; });
  const working = Array.isArray(workingItems) ? workingItems : [];

  return (
    '<div class="timeline-head-cell">' +
      '<div class="timeline-head-title">' +
        renderArtistAvatar_(artistName, "artist-avatar--sm") +
        '<h4>' + escapeHtml(artistName) + '</h4>' +
      '</div>' +
      '<div class="timeline-head-meta">' +
        '<div>' + escapeHtml(String(realItems.length)) + ' timed</div>' +
        '<div>' + escapeHtml(String(working.length)) + ' in working area</div>' +
        '<div>Arrive ' + escapeHtml(meta.arrive || "TBC") + '</div>' +
        '<div>Pack up ' + escapeHtml(meta.pack || "TBC") + '</div>' +
      '</div>' +
      '<div class="timeline-working-area"' +
        ' data-drop-type="working"' +
        ' data-drop-artist="' + escapeHtml(artistName) + '"' +
        ' ondragover="handleDropOver(event)"' +
        ' ondragleave="handleDropLeave(event)"' +
        ' ondrop="handleTimelineDrop(event)">' +
        (working.length
          ? working.map(function(service) {
              return renderWorkingAreaServiceCard_(service, artistName);
            }).join('')
          : '<div class="timeline-working-empty">Working Area: drop assigned services here before timing them.</div>') +
      '</div>' +
    '</div>'
  );
}

    // Height (px) a service block should occupy given its duration.
    // Each grid row = 66px (min-height of .timeline-grid-cell).
    // top:6px + bottom:6px gap = subtract 12px from the total slot stack.
    function serviceBlockHeight_(startTimeStr, endTimeStr, fallbackDurationMin) {
  // Height scaler (Jess 2026-07-22): scale the timed-card height by the same
  // state.slotScale the CSS grid rows use (--rsb-slot-scale), so cards shrink with
  // the grid and a long morning fits on screen.
  const scale = (state && typeof state.slotScale === 'number' && state.slotScale > 0) ? state.slotScale : 1;
  const SLOT_H = 52 * scale;
  const SLOT_MIN = 15;
  const GAP = 8;
  const s = parseDisplayTimeToMinutes(startTimeStr);
  const e = parseDisplayTimeToMinutes(endTimeStr);
  const span = (s !== null && e !== null && e > s)
    ? Math.max(1, Math.ceil((e - s) / SLOT_MIN))
    : Math.max(1, Math.ceil(Number(fallbackDurationMin || SLOT_MIN) / SLOT_MIN));
  return Math.max(Math.round(28 * scale), Math.round(span * SLOT_H - GAP));
}

// ─── TIMELINE HEIGHT SCALER (Jess 2026-07-22) ────────────────────────────────
// Shrink the timeline rows + cards so a long wedding morning fits on screen,
// letting the planner get every card onto the timeline and then fine-tune.
// state.slotScale drives both the CSS grid (--rsb-slot-scale) and the JS card
// heights (serviceBlockHeight_).
var RSB_SLOT_SCALE_MIN = 0.4;
var RSB_SLOT_SCALE_MAX = 1;

function rsbApplySlotScaleVar_() {
  var sheet = document.querySelector('.jv-runsheet-builder .timeline-sheet');
  if (sheet) sheet.style.setProperty('--rsb-slot-scale', String(state.slotScale || 1));
  var label = document.getElementById('rsbScaleLabel');
  if (label) label.textContent = Math.round((state.slotScale || 1) * 100) + '%';
}

function setSlotScale_(scale) {
  var s = Math.max(RSB_SLOT_SCALE_MIN, Math.min(RSB_SLOT_SCALE_MAX, Number(scale) || 1));
  state.slotScale = s;
  renderStage();          // re-render so card heights (JS) update with the CSS grid
  rsbApplySlotScaleVar_(); // re-apply after the fresh render replaced the sheet
}

function nudgeSlotScale(delta) { setSlotScale_((state.slotScale || 1) + delta); }

// ─── TIMELINE SLOT GRANULARITY (Jess 2026-07-23) ─────────────────────────────
// Toggle the grid between 15-min, 30-min and 60-min rows so a long morning can
// be viewed more or less zoomed-out. Service CARD heights are computed from the
// real start/end times (serviceBlockHeight_), NOT the slot count, so a service
// keeps its true duration at every granularity - an hourly view just shows one
// row per hour with the card stretching across it. Default 15 (unchanged).
function rsbSlotMinutes_() {
  var g = Number(state && state.slotGranularity);
  return (g === 30 || g === 60) ? g : 15;
}

function setSlotGranularity_(mins) {
  var m = (Number(mins) === 30 || Number(mins) === 60) ? Number(mins) : 15;
  state.slotGranularity = m;
  renderStage();
  rsbApplySlotScaleVar_();
  rsbApplyGranularityLabel_();
}

function rsbApplyGranularityLabel_() {
  var m = rsbSlotMinutes_();
  ['15', '30', '60'].forEach(function (v) {
    var btn = document.getElementById('rsbGran' + v);
    if (btn) btn.setAttribute('aria-pressed', String(Number(v) === m));
  });
}

// ─── MATCH PLACEHOLDERS -> REAL PARTY MEMBERS (Jess 2026-07-22) ───────────────
// Re-link the runsheet's placeholder slots (e.g. "Bridesmaid 1", "Mum", "Guest")
// to the real onboarded bridal party members, using the SAME auto-match-by-role
// engine as the Admin CRM (adminCrm_autoMatchRunsheet). One click; only applies
// high-confidence role matches and never clobbers an existing assignment. After
// matching we reload the workspace so the real names appear on the cards.
function matchPlaceholdersToParty() {
  if (!hasBridge || !state.currentBride) return;
  var email = String((state.workspace && state.workspace.email) || '').trim();
  if (!email) {
    showBanner("Can't match yet, the bride's email isn't loaded. Reopen this bride and try again.", "warning");
    return;
  }
  // Confirmation added 2026-09-10 (BRIEFS/RESULTS-shanice-bridesmaid-swap-retry-
  // 2026-09-10.md): this fires adminCrm_autoMatchRunsheet immediately on click,
  // with NO prior confirmation -- and per adminCrm_applyRunsheetMatches' own
  // docblock, a match is applied to the CURRENT snapshot directly, i.e. an
  // already-PUBLISHED runsheet the bride may be actively viewing gets edited
  // live, not just a draft. A real Cowork test hit this blind (source services
  // jumped 16 -> 22 unexpectedly) and had to manually reload + re-verify nothing
  // persisted, rather than being warned up front. This does not change what the
  // action does or add a new write path -- same adminCrm_autoMatchRunsheet call,
  // same downstream effect -- it only requires an explicit confirm first, same
  // pattern this project already uses for the party-member merge action
  // (pmSelectMergeSource, assets/jv-admin-crm.js).
  var isPublished = !!(state.workspace && state.workspace.publishedAt);
  var confirmMsg = "Auto-match placeholder runsheet slots to your real bridal party members?" +
    (isPublished
      ? "\n\nThis runsheet is already published -- this will edit the LIVE version the bride sees immediately. You'll be prompted to re-publish the PDF afterward."
      : "\n\nThis only affects this draft.");
  if (!confirm(confirmMsg)) return;
  showBanner("Matching placeholder slots to the real bridal party members...", "success");
  // Step 1: run the confident auto-match by role (the one-click win).
  jvBridge.callAdmin('bridge_admin_generic', {
    function: 'adminCrm_autoMatchRunsheet',
    args: { brideEmail: email }
  }).then(function(res) {
    var d = (res && res.data) ? res.data : res;
    var applied = (d && d.ok) ? Number(d.applied || 0) : 0;
    // Step 2: fetch the remaining slots + the real-people pool, and if any slots
    // are still unmatched (e.g. an outlier like a past-bride guest that role-match
    // can't confidently place), open the manual per-slot picker right here.
    return jvBridge.callAdmin('bridge_admin_generic', {
      function: 'adminCrm_getRunsheetMatchSuggestions',
      args: { brideEmail: email }
    }).then(function(res2) {
      var s = (res2 && res2.data) ? res2.data : res2;
      var suggestions = (s && s.suggestions) || [];
      var allCards = (s && s.allCards) || [];
      openMatchPicker_(email, suggestions, allCards, applied);
    });
  }).catch(function(err) {
    showBanner("Match error: " + getErrorMessage(err), "error");
  });
}

// Manual per-slot picker: one dropdown per runsheet slot, listing the real
// (onboarded, non-placeholder) party members. Pre-filled with the confident
// suggestions. Reuses the same adminCrm_applyRunsheetMatches engine as the Admin
// CRM. A person can only hold one slot at a time (dedupe on save). Jess 2026-07-22.
function openMatchPicker_(brideEmail, suggestions, allCards, autoApplied) {
  // Pool = real people only (placeholders ARE the slots). Also collapse
  // duplicate rows (e.g. "Helen" + "Mum / Helen", or two identical rows from
  // repeated onboarding) so a person appears once in the dropdown. Dedupe on a
  // normalised name token; keep the entry that carries an email (Jess 2026-07-23).
  var _mpNorm = function(n) {
    return String(n || '').toLowerCase()
      .replace(/\bmum\b|\bmother\b|\bflower girl\b|\/|-/g, ' ')
      .replace(/[^a-z ]/g, '').replace(/\s+/g, ' ').trim();
  };
  var _mpRaw = (allCards || []).filter(function(c) {
    return c && !c.isPlaceholder && c.pmId && String(c.name || '').trim();
  });
  var _mpByNorm = {};
  _mpRaw.forEach(function(c) {
    var k = _mpNorm(c.name) || c.pmId;
    var prev = _mpByNorm[k];
    if (!prev || (!prev.email && c.email)) _mpByNorm[k] = c;
  });
  var people = Object.keys(_mpByNorm).map(function(k) { return _mpByNorm[k]; });
  if (!suggestions.length) {
    // Nothing left to match manually.
    if (autoApplied > 0) {
      showBanner(autoApplied + ' placeholder' + (autoApplied === 1 ? '' : 's') + ' matched. Refreshing the timeline...', "success");
      loadWorkspace(state.currentBride, {}, { forceReload: true });
    } else {
      showBanner("No unmatched runsheet slots to link.", "success");
    }
    return;
  }
  if (!people.length) {
    showBanner("No onboarded party members are available to match to these slots yet.", "warning");
    return;
  }

  var esc = function(v) { return escapeHtml(String(v == null ? '' : v)); };
  var optionsFor = function(selectedPmId) {
    var opts = '<option value="">(leave as placeholder)</option>';
    people.forEach(function(p) {
      var label = (p.name || '(unnamed)') + (p.role ? ' - ' + p.role : '');
      opts += '<option value="' + esc(p.pmId) + '"' + (p.pmId === selectedPmId ? ' selected' : '') + '>' + esc(label) + '</option>';
    });
    return opts;
  };

  var rows = suggestions.map(function(s, i) {
    var svc = (s.services || []).map(function(sv) { return (sv.start_time || 'TBC') + ' ' + (sv.service || ''); }).join(', ');
    var preId = (s.suggestedPmId && people.some(function(p){ return p.pmId === s.suggestedPmId; })) ? s.suggestedPmId : '';
    return '<div style="display:flex;gap:10px;align-items:center;padding:8px 0;border-bottom:1px solid var(--line,#efe7e2);">' +
      '<div style="flex:1;min-width:0;">' +
        '<div style="font-weight:600;color:var(--brand-text,#2f2524);">' + esc(s.slotLabel) + '</div>' +
        '<div style="font-size:11px;color:var(--muted,#9a918a);">' + esc(svc) + '</div>' +
      '</div>' +
      '<select data-slot="' + esc(s.slotLabel) + '" style="flex:1;min-width:0;max-width:220px;padding:8px 10px;border-radius:1rem;border:1px solid var(--line,#e6dad4);font-family:inherit;">' +
        optionsFor(preId) +
      '</select>' +
    '</div>';
  }).join('');

  var overlay = document.createElement('div');
  overlay.className = 'svc-modal-overlay';
  overlay.style.cssText = 'position:fixed;inset:0;background:rgba(30,23,22,0.45);display:flex;align-items:center;justify-content:center;z-index:9999;padding:20px;';
  overlay.innerHTML =
    '<div style="background:var(--brand-bg-white,#fff);border-radius:1.5rem;max-width:560px;width:100%;max-height:85vh;overflow:auto;padding:24px;box-shadow:0 12px 40px rgba(0,0,0,0.2);">' +
      '<h3 style="font-family:Raleway,sans-serif;font-weight:300;font-size:20px;margin:0 0 4px;color:var(--brand-dark,#1e1716);">Match Placeholders</h3>' +
      '<p style="font-size:13px;color:var(--muted,#6b6158);margin:0 0 16px;">' +
        (autoApplied > 0 ? (autoApplied + ' matched automatically. ') : '') +
        'Assign each remaining slot to the real party member. Leave any you are unsure about as a placeholder.</p>' +
      '<div id="matchPickerRows">' + rows + '</div>' +
      '<div style="display:flex;gap:10px;justify-content:flex-end;margin-top:20px;">' +
        '<button type="button" class="btn btn-ghost" id="matchPickerCancel">Cancel</button>' +
        '<button type="button" class="btn btn-primary" id="matchPickerSave">Save matches</button>' +
      '</div>' +
    '</div>';
  document.body.appendChild(overlay);

  var close = function() { if (overlay.parentNode) overlay.parentNode.removeChild(overlay); };
  overlay.addEventListener('click', function(e) { if (e.target === overlay) close(); });
  overlay.querySelector('#matchPickerCancel').onclick = function() {
    close();
    if (autoApplied > 0) { showBanner(autoApplied + ' matched. Refreshing...', "success"); loadWorkspace(state.currentBride, {}, { forceReload: true }); }
  };
  overlay.querySelector('#matchPickerSave').onclick = function() {
    // Build matches; a person can only hold one slot (last selection wins).
    var selects = overlay.querySelectorAll('select[data-slot]');
    var usedPm = {}, matches = [];
    // Iterate in order; if a person is picked twice, keep the first, blank later dupes.
    Array.prototype.forEach.call(selects, function(sel) {
      var slot = sel.getAttribute('data-slot');
      var pmId = sel.value || '';
      if (pmId && usedPm[pmId]) pmId = ''; // dedupe
      if (pmId) usedPm[pmId] = true;
      matches.push({ slotLabel: slot, pmId: pmId });
    });
    var saveBtn = overlay.querySelector('#matchPickerSave');
    saveBtn.disabled = true; saveBtn.textContent = 'Saving...';
    jvBridge.callAdmin('bridge_admin_generic', {
      function: 'adminCrm_applyRunsheetMatches',
      args: { brideEmail: brideEmail, matches: matches }
    }).then(function(res) {
      var d = (res && res.data) ? res.data : res;
      close();
      if (d && d.ok) {
        showBanner("Party matched to the runsheet. Refreshing the timeline...", "success");
        loadWorkspace(state.currentBride, {}, { forceReload: true });
      } else {
        showBanner("Save failed: " + ((d && d.error) || 'unknown error'), "error");
      }
    }).catch(function(err) {
      close();
      showBanner("Save error: " + getErrorMessage(err), "error");
    });
  };
}

// Auto-fit: shrink so the full scheduled span (earliest start to finish/ready-by)
// fits in roughly the viewport height. Counts the day back from the finish time.
function fitSlotScaleToWindow() {
  try {
    var services = (state.sourceServicesDraft || []).filter(function(s) {
      return s && !s.isSystemBlock;
    });
    var mins = [];
    services.forEach(function(s) {
      var st = parseDisplayTimeToMinutes(s.startTime || s.start || s.startTimeOverride || '');
      if (st !== null) mins.push(st);
    });
    var readyBy = parseDisplayTimeToMinutes((state.workspace && state.workspace.readyBy) || '');
    if (readyBy !== null) mins.push(readyBy);
    if (mins.length < 2) { setSlotScale_(0.6); return; } // sensible default when times are sparse
    var span = Math.max.apply(null, mins) - Math.min.apply(null, mins); // minutes
    var rows = Math.max(1, Math.ceil(span / 15));                        // 15-min slots
    var avail = Math.max(300, (window.innerHeight || 800) - 320);        // rough usable height
    var neededAtFull = rows * 60;                                        // 60px per row at full scale
    var scale = avail / neededAtFull;
    setSlotScale_(scale);
  } catch (e) {
    setSlotScale_(0.6);
  }
}

    function renderTimelineGridCell(artistName, row, laneMap) {
  const slot = laneMap[row.minutes];
  const classes = ["timeline-grid-cell"];
  if (slot && slot.type === "service") classes.push("occupied");
  if (slot && slot.type === "continuation") classes.push("continuation");

  let inner = "";

  if (slot && slot.type === "service") {
    const service = slot.service || {};
    const svcId = escapeHtml(service.id || service.itemId || "");
    const blockH = serviceBlockHeight_(
      service.startTime || service.start,
      service.endTime   || service.end,
      service.durationMin || service.duration
    );
    // Keep native drag handlers enabled until a real touch-specific drag system exists.
    // The previous coarse-pointer gate disabled drag/drop entirely on iPad/touch-classified
    // environments without providing any replacement interaction.
    var dragAttrs = ' draggable="true" ondragstart="handleTimelineDragStart(event)" ondragend="clearDragState()"';
    inner =
      '<div class="timeline-service-block editable' + (service.isBride ? " bride" : "") + (service.notes ? " has-note" : "") + '"' +
        ' style="height:' + blockH + 'px"' +
        dragAttrs +
        ' data-service-id="' + svcId + '"' +
        ' data-service-source="timeline"' +
        ' data-lane-artist="' + escapeHtml(artistName) + '" data-lane-index="' + Number(slot.index || 0) + '"' +
        ' oncontextmenu="openServiceContextMenu(event,\'' + svcId + '\')">' +
        '<div class="timeline-service-topline">' +
          '<div class="timeline-service-time">' + escapeHtml((service.startTime || service.start || "") + ' – ' + (service.endTime || service.end || "")) + '</div>' +
          renderServiceActionButtons_(svcId) +
        '</div>' +
        '<div class="timeline-service-title" style="display:flex;align-items:center;">' +
          renderTimelineCardAvatar_(service) +
          '<span>' + escapeHtml(resolveServiceDisplayName_(service)) + '</span>' +
        '</div>' +
        '<div class="timeline-service-meta">' + escapeHtml(toTitleCaseService_(service.serviceLabel || service.service || "")) +
          ' <span class="duration-badge" title="Click to change duration" onclick="event.stopPropagation();inlineDurationOverride_(\'' + svcId + '\')">' +
          (service.durationMin || service.duration || '?') + 'min</span>' +
        '</div>' +
        renderHubMetaForTimelineCard_(service) +
        renderServiceInspirationMeta_(service) +
        (service.notes ? '<div class="service-submeta">' + escapeHtml(service.notes) + '</div>' : '') +
      '</div>';
  } else if (slot && (slot.type === "setup" || slot.type === "pack")) {
    const blockH = serviceBlockHeight_(slot.start, slot.end, slot.service && (slot.service.durationMin || 15));
    inner =
      '<div class="timeline-service-block ' + escapeHtml(slot.type) + '" style="height:' + blockH + 'px">' +
        '<div class="timeline-service-time">' + escapeHtml((slot.start || "") + ' – ' + (slot.end || "")) + '</div>' +
        '<div class="timeline-service-title">' + escapeHtml(slot.label || "") + '</div>' +
      '</div>';
  } else if (slot && slot.type === "continuation") {
    inner = '<div class="timeline-continued"></div>';
  } else {
    inner = '<div class="drop-slot" data-time-min="' + Number(row.minutes || 0) + '"></div>';
  }

  return (
    '<div class="' + classes.join(" ") + '" data-drop-type="timeline" data-drop-artist="' + escapeHtml(artistName) + '"' +
      ' data-drop-index="' + Number(row.dropIndexByArtist[artistName] || 0) + '"' +
      ' data-drop-minutes="' + Number(row.minutes || 0) + '"' +
      ' ondragover="handleDropOver(event)" ondragleave="handleDropLeave(event)" ondrop="handleTimelineDrop(event)">' +
      inner +
    '</div>'
  );
}

function bldNormalizeRole_(raw) {
  var r = String(raw || '').toLowerCase().trim();
  if (r === 'bridesmaid' || r === 'maid of honour' || r === 'maid of honor' || r === 'moh' || r === 'matron of honour' || r === 'matron of honor') return 'Bridesmaid';
  if (r === 'mother of the bride' || r === 'mother of the groom' || r === 'mother' || r === 'mum') return 'Mother';
  if (r === 'flower girl') return 'Flower Girl';
  if (r === 'junior bridesmaid') return 'Junior Bridesmaid';
  if (r === 'guest') return 'Guest';
  return '';
}

function bldBuildNameMap_() {
  var map = {};
  var counts = {};
  var members = (state.brideCardsData && state.brideCardsData.partyMembers) || [];
  members.forEach(function(m) {
    var prefix = bldNormalizeRole_(m.role || m.memberRole || '');
    if (!prefix) return;
    counts[prefix] = (counts[prefix] || 0) + 1;
    var name = String(m.name || '').trim();
    if (name) map[prefix + ' ' + counts[prefix]] = name;
  });
  return map;
}

function resolveServiceDisplayName_(service) {
  var personId = String(service.personId || "").trim();
  var personLabel = String(service.personLabel || service.person || "").trim();
  var personLabelLower = personLabel.toLowerCase();
  var match = null;
  (state.bridalPartyDraft || []).forEach(function(p) {
    if (match) return;
    var pId = String(p.personId || p.id || "").trim();
    var pLabel = String(p.personLabel || p.name || "").trim();
    // Match by personId, personLabel, or role for generic labels like "Bride"/"Mum"
    if ((personId && pId === personId) || pLabel === personLabel) {
      match = p;
    } else if (personLabelLower === "bride" && String(p.role || "").toLowerCase() === "bride") {
      match = p;
    } else if ((personLabelLower === "mum" || personLabelLower === "mother") && String(p.role || "").toLowerCase() === "mother") {
      match = p;
    }
  });
  if (match && match.profileName) return match.profileName;
  // Fall back to onboarded party member names from bride cards data
  return bldBuildNameMap_()[personLabel] || personLabel;
}

function getEmptyLaneDropMinutes_(items) {
  const safeItems = Array.isArray(items) ? items : [];
  const starts = safeItems
    .map(function(item) { return parseDisplayTimeToMinutes(item.start || item.startTime || ""); })
    .filter(function(v) { return v !== null; });

  if (starts.length) return Math.min.apply(null, starts);

  const draftStart = parseDisplayTimeToMinutes((state.workspace && state.workspace.draftStartTime) || "");
  if (draftStart !== null) return draftStart;

  const readyBy = parseDisplayTimeToMinutes((state.workspace && state.workspace.readyBy) || "");
  if (readyBy !== null) return Math.max(0, readyBy - 60);

  return 480; // 8:00 AM fallback
}

    function buildTimelineStructure() {
      const laneNames = getLaneNames();
      const grouped = {};
      const slotMinutes = rsbSlotMinutes_();  // 15 | 30 | 60 (Jess 2026-07-23 granularity toggle)
      const timedServices = Array.isArray(state.workspace && state.workspace.services)
        ? state.workspace.services.slice()
        : [];

      laneNames.forEach(function(name) {
        grouped[name] = [];
      });

      timedServices.forEach(function(service) {
        const artist = String(service.artist || service.assignedArtist || "").trim();
        // System blocks always stay with their assigned artist lane (even during team changes).
        if (service.isSystemBlock) {
          if (artist && grouped[artist] !== undefined) grouped[artist].push(service);
          return;
        }
        // Route to matching artist lane only; orphans are shown in the unscheduled panel below the grid.
        if (artist && grouped[artist] !== undefined) {
          grouped[artist].push(service);
        }
      });

      let minStart = null;
      let maxEnd = null;
      laneNames.forEach(function(name) {
        grouped[name].sort(function(a, b) {
          return Number(parseDisplayTimeToMinutes(a.start || a.startTime || "") || 0) -
            Number(parseDisplayTimeToMinutes(b.start || b.startTime || "") || 0);
        });
        grouped[name].forEach(function(service) {
          const start = parseDisplayTimeToMinutes(service.start || service.startTime || "");
          const end = parseDisplayTimeToMinutes(service.end || service.endTime || "");
          if (start !== null && (minStart === null || start < minStart)) minStart = start;
          if (end !== null && (maxEnd === null || end > maxEnd)) maxEnd = end;
        });

        const meta = ((state.workspace && state.workspace.artistMeta) || {})[name] || {};
        const metaStart = parseDisplayTimeToMinutes(meta.startTime || "");
        const metaEnd = parseDisplayTimeToMinutes(meta.endTime || "");
        if (metaStart !== null && (minStart === null || metaStart < minStart)) minStart = metaStart;
        if (metaEnd !== null && (maxEnd === null || metaEnd > maxEnd)) maxEnd = metaEnd;
      });

      const readyByMin = parseDisplayTimeToMinutes((state.workspace && state.workspace.readyBy) || "");
      const fallbackStart = getDefaultTimelineStartMinutes_();
      const defaultStart = readyByMin !== null
        ? Math.max(0, readyByMin - (9 * 60))
        : (minStart !== null ? minStart : fallbackStart);
      const defaultEnd = readyByMin !== null
        ? readyByMin + 60
        : (maxEnd !== null ? maxEnd : (defaultStart + 180));
      const mergedStart = Math.max(
        0,
        Math.min(defaultStart, minStart === null ? defaultStart : minStart)
      );
      const mergedEnd = Math.max(
        defaultEnd,
        maxEnd === null ? defaultEnd : maxEnd
      );
      var startBound = Math.max(0, Math.floor(mergedStart / slotMinutes) * slotMinutes);
      var endBound = Math.ceil(mergedEnd / slotMinutes) * slotMinutes;
      // Guard against NaN/Infinity/negative bounds that would crash the loop
      if (!Number.isFinite(startBound) || !Number.isFinite(endBound) || endBound <= startBound) {
        startBound = 480; // 8:00 AM
        endBound = 720;   // 12:00 PM
      }
      // Cap at reasonable maximum (24 hours = 1440 minutes = 96 slots)
      if (endBound - startBound > 1440) {
        endBound = startBound + 720;
      }
      const rows = [];
      for (let minutes = startBound; minutes < endBound; minutes += slotMinutes) {
        const row = {
          minutes: minutes,
          label: String(Math.floor(minutes / 60)).padStart(2, "0") + ":" + String(minutes % 60).padStart(2, "0"),
          dropIndexByArtist: {}
        };
        laneNames.forEach(function(name) {
          row.dropIndexByArtist[name] = grouped[name].filter(function(service) {
            const start = parseDisplayTimeToMinutes(service.start || service.startTime || "");
            return start !== null && start < minutes;
          }).length;
        });
        rows.push(row);
      }

      const cellMap = {};
      laneNames.forEach(function(name) {
        cellMap[name] = {};
        const items = grouped[name] || [];
        const meta = ((state.workspace && state.workspace.artistMeta) || {})[name] || {};

        items.forEach(function(service, index) {
          const start = parseDisplayTimeToMinutes(service.start || service.startTime || "");
          const end = parseDisplayTimeToMinutes(service.end || service.endTime || "");
          if (start === null || end === null) return;

          const startSlot = Math.floor(start / slotMinutes) * slotMinutes;
          const endSlot = Math.ceil(end / slotMinutes) * slotMinutes;

          // Guard: skip if slots are invalid or would create too many continuation cells
          if (!Number.isFinite(startSlot) || !Number.isFinite(endSlot) || endSlot <= startSlot || (endSlot - startSlot) > 1440) return;

          // System blocks (Arrival & Set Up / Touch-ups & Pack Down) are physically
          // injected by the backend. Render them with the correct visual type so they
          // show as coloured swimlane headers/footers rather than client service cards.
          if (service.isSystemBlock) {
            const blockType = service.systemType === "arrival" ? "setup" : "pack";
            cellMap[name][startSlot] = {
              type: blockType,
              person: service.personLabel,
              label: service.serviceLabel,
              start: service.startTime || formatMinutesAsDisplayTime_(startSlot),
              end: service.endTime || formatMinutesAsDisplayTime_(endSlot),
              service: service,
              index: index
            };
            for (let cursor = startSlot + slotMinutes; cursor < endSlot; cursor += slotMinutes) {
              cellMap[name][cursor] = { type: "continuation", service: service, index: index };
            }
            return;
          }

          cellMap[name][startSlot] = {
            type: "service",
            service: service,
            index: index
          };

          for (let cursor = startSlot + slotMinutes; cursor < endSlot; cursor += slotMinutes) {
            cellMap[name][cursor] = {
              type: "continuation",
              service: service,
              index: index
            };
          }
        });

        const realItems = items.filter(function(service) {
          return !service.isSystemBlock;
        });
        var realStarts = realItems.map(function(service) {
          return parseDisplayTimeToMinutes(service.start || service.startTime || "");
        }).filter(function(value) { return value !== null && Number.isFinite(value); });
        var realEnds = realItems.map(function(service) {
          return parseDisplayTimeToMinutes(service.end || service.endTime || "");
        }).filter(function(value) { return value !== null && Number.isFinite(value); });
        const firstRealStart = realStarts.length ? Math.min.apply(null, realStarts) : null;
        const lastRealEnd = realEnds.length ? Math.max.apply(null, realEnds) : null;
        const metaStart = parseDisplayTimeToMinutes(meta.startTime || "");
        const metaEnd = parseDisplayTimeToMinutes(meta.endTime || "");

        // Setup + Packdown rows render ONLY when state.workspace.artistMeta
        // has explicit startTime/endTime values for this artist. That
        // happens when the planner clicks the "Start / Finish Times"
        // button (applyArtistSetupPackdown_ — line ~7140), which is the
        // explicit "we're done positioning, lock in the window" action.
        //
        // Pre-button-click these rows are absent so the planner can drop
        // services into any time slot. After the click, the rows fill in
        // and visually delimit the artist's working window.
        //
        // (ffbb09a tried auto-rendering from firstRealStart/lastRealEnd
        // whenever services existed — that blocked drops into setup slots
        // mid-edit. Reverted 2026-05-18 to button-gated rendering. The
        // applyArtistSetupPackdown_ function ALREADY computes setup +
        // pack hugging the first/last service, so the live-reflow goal
        // is achieved by re-clicking the button after drag-drop.)
        if (metaStart !== null && Number.isFinite(metaStart)) {
          var setupEnd = metaStart + slotMinutes;
          const setupSlot = Math.floor(metaStart / slotMinutes) * slotMinutes;
          if (!cellMap[name][setupSlot]) {
            cellMap[name][setupSlot] = {
              type: "setup",
              label: "Set up",
              start: formatMinutesAsDisplayTime_(metaStart),
              end: formatMinutesAsDisplayTime_(setupEnd),
              index: -1
            };
            for (let cursor = setupSlot + slotMinutes; cursor < setupEnd; cursor += slotMinutes) {
              if (!cellMap[name][cursor]) {
                cellMap[name][cursor] = { type: "continuation", index: -1 };
              }
            }
          }
        }

        if (metaEnd !== null && Number.isFinite(metaEnd)) {
          var packStart = Math.max(metaEnd - slotMinutes, metaStart !== null ? metaStart : 0);
          const packSlot = Math.floor(packStart / slotMinutes) * slotMinutes;
          if (metaEnd > packStart && !cellMap[name][packSlot]) {
            cellMap[name][packSlot] = {
              type: "pack",
              label: "Pack down",
              start: formatMinutesAsDisplayTime_(packStart),
              end: formatMinutesAsDisplayTime_(metaEnd),
              index: -1
            };
            for (let cursor = packSlot + slotMinutes; cursor < metaEnd; cursor += slotMinutes) {
              if (!cellMap[name][cursor]) {
                cellMap[name][cursor] = { type: "continuation", index: -1 };
              }
            }
          }
        }
      });

      return {
        laneNames: laneNames,
        grouped: grouped,
        rows: rows,
        cellMap: cellMap
      };
    }

    function getDefaultTimelineStartMinutes_() {
      const draftStart = parseDisplayTimeToMinutes((state.workspace && state.workspace.draftStartTime) || "");
      if (draftStart !== null) return draftStart;

      const earliestOverride = (state.sourceServicesDraft || [])
        .map(function(service) {
          return parseDisplayTimeToMinutes(service.startTimeOverride || service.startTime || service.start || "");
        })
        .filter(function(value) { return value !== null; });
      if (earliestOverride.length) return Math.min.apply(null, earliestOverride);

      const readyBy = parseDisplayTimeToMinutes((state.workspace && state.workspace.readyBy) || "");
      if (readyBy !== null) return Math.max(0, readyBy - 180);

      return 480;
    }

    function buildArtistWindowMeta_(artistName, items) {
      const slotMinutes = 15;
      const artistMeta = ((state.workspace && state.workspace.artistMeta) || {})[String(artistName || "").trim()] || {};
      const metaStart = parseDisplayTimeToMinutes(artistMeta.startTime || "");
      const metaEnd = parseDisplayTimeToMinutes(artistMeta.endTime || "");
      if (metaStart !== null || metaEnd !== null) {
        return {
          arrive: metaStart !== null ? formatMinutesAsDisplayTime_(metaStart) : "",
          pack: metaEnd !== null ? formatMinutesAsDisplayTime_(metaEnd) : ""
        };
      }

      if (!items || !items.length) {
        return {
          arrive: "",
          pack: ""
        };
      }

      const starts = items
        .map(function(item) { return parseDisplayTimeToMinutes(item.start || item.startTime || ""); })
        .filter(function(item) { return item !== null; });
      const ends = items
        .map(function(item) { return parseDisplayTimeToMinutes(item.end || item.endTime || ""); })
        .filter(function(item) { return item !== null; });

      if (!starts.length || !ends.length) {
        return {
          arrive: "",
          pack: ""
        };
      }

      const firstStart = Math.min.apply(null, starts);
      const lastEnd = Math.max.apply(null, ends);

      return {
        arrive: formatMinutesAsDisplayTime_(Math.max(0, firstStart - slotMinutes)),
        pack: formatMinutesAsDisplayTime_(lastEnd + slotMinutes)
      };
    }

    function getWorkingAreaServicesForArtist_(artistName) {
      const safeArtist = String(artistName || "").trim();
      return (state.sourceServicesDraft || []).filter(function(service) {
        const assignedArtist = String(service.assignedArtist || service.artist || "").trim();
        return assignedArtist === safeArtist && !isServiceTimed_(service);
      });
    }

    function renderWorkingAreaServiceCard_(service, artistName) {
  const svcId = escapeHtml(getServiceId_(service));
  const label = toTitleCaseService_(service.serviceLabel || service.service || "");
  const duration = Number(service.durationMin || service.duration || 0);
  // Keep native drag handlers enabled until a real touch-specific drag system exists.
  // The previous coarse-pointer gate disabled drag/drop entirely on iPad/touch-classified
  // environments without providing any replacement interaction.
  const dragAttrs = ' draggable="true" ondragstart="handleTimelineDragStart(event)" ondragend="clearDragState()"';

  return (
    '<div class="working-service-card' + (service.isBride ? ' bride' : '') + '"' +
      dragAttrs +
      ' data-service-id="' + svcId + '"' +
      ' data-service-source="working"' +
      ' data-lane-artist="' + escapeHtml(artistName || service.assignedArtist || service.artist || "") + '"' +
      ' oncontextmenu="openServiceContextMenu(event,\'' + svcId + '\')">' +
      '<div class="working-service-topline">' +
        '<div class="working-service-meta">' + escapeHtml(duration ? duration + ' min' : 'Untimed') + '</div>' +
        renderServiceActionButtons_(svcId) +
      '</div>' +
      '<div class="working-service-title">' + escapeHtml(resolveServiceDisplayName_(service)) + '</div>' +
      '<div class="working-service-meta">' + escapeHtml(label) + '</div>' +
      renderServiceInspirationMeta_(service) +
      (service.notes ? '<div class="working-service-meta">' + escapeHtml(service.notes) + '</div>' : '') +
    '</div>'
  );
}

    function renderServiceActionButtons_(serviceId) {
      return (
        '<div class="service-action-group">' +
          renderServiceMoveButton_(serviceId) +
          renderServiceEditButton_(serviceId) +
          '<button type="button" class="svc-card-actions" aria-label="Service actions"' +
            ' onmousedown="handleServiceEditButtonMouseDown(event)"' +
            ' onclick="openServiceContextMenu(event,\'' + escapeHtml(serviceId) + '\')">' +
            '<i class="fa-solid fa-ellipsis-vertical"></i>' +
          '</button>' +
        '</div>'
      );
    }

    function renderServiceEditButton_(serviceId) {
      return (
        '<button type="button" class="service-edit-btn" title="Edit service"' +
          ' onmousedown="handleServiceEditButtonMouseDown(event)"' +
          ' onclick="handleServiceEditButtonClick(event,\'' + escapeHtml(serviceId) + '\')">' +
          '<i class="fa-solid fa-pen"></i>' +
        '</button>'
      );
    }

    function renderServiceMoveButton_(serviceId) {
      return (
        '<button type="button" class="service-edit-btn" title="Move service"' +
          ' onmousedown="handleServiceEditButtonMouseDown(event)"' +
          ' onclick="handleServiceMoveButtonClick(event,\'' + escapeHtml(serviceId) + '\')">' +
          '<i class="fa-solid fa-arrows-up-down-left-right"></i>' +
        '</button>'
      );
    }

    function renderServiceInspirationMeta_(service) {
      if (!service || service.isCustomBlock || service.isSystemBlock) return "";
      const bits = [];
      if (service && service.inspirationFolder) {
        bits.push('<div class="service-submeta">Inspo folder linked</div>');
      }
      if (service && service.inspirationSubmitted) {
        bits.push('<div class="service-submeta" style="color:var(--success-text);">Inspo submitted</div>');
      } else {
        bits.push('<div class="service-submeta" style="color:var(--error-text);">Inspo form missing</div>');
      }
      return bits.join("");
    }

    // Resolve a profile pic URL for the person on a service. Mirrors the
    // Page 2 card avatar lookup so timeline cards / modal can show the
    // same face.
    function getProfilePicForService_(service) {
      if (!service) return '';
      const isBride = !!service.isBride || String(service.personLabel || service.person || "").trim().toLowerCase() === "bride";
      const nameLow = String(service.personLabel || service.person || "").trim().toLowerCase();
      if (isBride) {
        const bp = state.brideCardsData && state.brideCardsData.brideProfile;
        if (bp && bp.profilePicUrl) return bp.profilePicUrl;
        if (state.workspace && Array.isArray(state.workspace.people)) {
          const bridePerson = state.workspace.people.find(function(p) {
            return p && String(p.role || '').trim().toLowerCase() === 'bride';
          });
          if (bridePerson && bridePerson.profilePicUrl) return bridePerson.profilePicUrl;
        }
        return '';
      }
      const cards = (state.brideCardsData && state.brideCardsData.partyMembers) || [];
      const blob = cards.find(function(c) { return String(c.name || '').trim().toLowerCase() === nameLow; });
      if (blob && (blob.profile_pic_url || blob.profilePicUrl)) return blob.profile_pic_url || blob.profilePicUrl;
      if (Array.isArray(state.partyProfiles)) {
        const pp = state.partyProfiles.find(function(x) { return String(x.name || '').trim().toLowerCase() === nameLow; });
        if (pp && pp.profilePicUrl) return pp.profilePicUrl;
      }
      return '';
    }

    // Small 24px avatar for the timeline service block. Initials fall
    // back when no pic is available.
    function renderTimelineCardAvatar_(service) {
      const name = String(service.personLabel || service.person || '?').trim();
      const initials = name.split(' ').map(function(p) { return p.charAt(0); }).join('').slice(0, 2).toUpperCase() || '?';
      const picUrl = getProfilePicForService_(service);
      const base = 'display:inline-flex;align-items:center;justify-content:center;width:22px;height:22px;border-radius:50%;flex:0 0 auto;font-size:10px;font-weight:600;color:var(--brand-primary-deep);background:var(--brand-bg-soft);vertical-align:middle;margin-right:6px;';
      if (picUrl) {
        return '<span style="' + base + 'background-image:url(' + escapeHtml(picUrl) + ');background-size:cover;background-position:center;color:transparent;"></span>';
      }
      return '<span style="' + base + '">' + escapeHtml(initials) + '</span>';
    }

    // Surface hub data on the Page 3 timeline card so the artist can see
    // at-a-glance what the bride / member needs without opening the card.
    // Adds only the highest-value fields:
    //   - Hair service cards: hairstyle preference (so the artist knows
    //     which look to execute without flipping to Page 2).
    //   - All cards: allergies as a red chip (safety critical on the day).
    // Sources: hub blob first, fall through to state.partyProfiles for
    // members whose blob is stale; bride sources from brideBrief/Profile.
    function renderHubMetaForTimelineCard_(service) {
      if (!service || service.isCustomBlock || service.isSystemBlock) return "";
      const isBride = !!service.isBride || String(service.personLabel || service.person || "").trim().toLowerCase() === "bride";
      const nameLow = String(service.personLabel || service.person || "").trim().toLowerCase();

      let hairStyle = "";
      let allergies = "";
      if (isBride) {
        const brief = state.brideCardsData && state.brideCardsData.brideBrief;
        if (brief && brief.hairstylePref) hairStyle = brief.hairstylePref;
        // Bride profile doesn't carry an allergies field; brideBrief
        // makeupSituationJson may include it. Parse defensively.
        if (brief && brief.makeupSituationJson) {
          try {
            const m = JSON.parse(brief.makeupSituationJson);
            if (m && m.allergies) allergies = String(m.allergies).trim();
          } catch (_) {}
        }
      } else {
        const cards = (state.brideCardsData && state.brideCardsData.partyMembers) || [];
        const blob = cards.find(function(c) { return String(c.name || '').trim().toLowerCase() === nameLow; });
        if (blob && blob.hair_style) hairStyle = blob.hair_style;
        if (blob && blob.allergies)  allergies  = blob.allergies;
        if (!hairStyle && Array.isArray(state.partyProfiles)) {
          const pp = state.partyProfiles.find(function(x) { return String(x.name || '').trim().toLowerCase() === nameLow; });
          if (pp && pp.hairStyle) hairStyle = pp.hairStyle;
          if (!allergies && pp && pp.allergies) allergies = pp.allergies;
        }
      }

      const isHairService = /hair/i.test(String(service.serviceLabel || service.service || ""));
      const bits = [];
      if (isHairService && hairStyle) {
        bits.push('<div class="service-submeta" style="font-weight:600;">' + escapeHtml(hairStyle) + '</div>');
      }
      if (allergies) {
        bits.push('<div class="service-submeta" style="color:var(--error-text);font-weight:600;"><i class="fa-solid fa-triangle-exclamation" style="margin-right:4px;"></i>' + escapeHtml(allergies) + '</div>');
      }
      return bits.join("");
    }

    function renderPdfField(label, fieldHtml) {
      return (
        '<div class="pdf-label">' + escapeHtml(label) + "</div>" +
        '<div class="pdf-value">' + fieldHtml + "</div>"
      );
    }

    function renderPdfWideField(label, fieldHtml) {
      return (
        '<div class="pdf-label">' + escapeHtml(label) + "</div>" +
        '<div class="pdf-value" style="grid-column: span 3;">' + fieldHtml + "</div>"
      );
    }

    function toTitleCaseService_(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, function(ch) { return ch.toUpperCase(); });
}

// Snapshots both diff baselines (_baselineServices, _baselineBooking) from the
// CURRENT state.sourceServicesDraft / state.workspace. Called once at workspace
// load (see the module-scope call right after ensureBrideServiceRow_ above),
// and again after a save genuinely confirms (Defect B fix, 2026-09-10, per
// BRIEFS/RESULTS-runsheet-builder-view-e2e-grade3-xray-2026-09-10.md) -- without
// the re-snapshot, a second edit in the same session diffed against the
// pre-save state and buildDraftDiffSummary_() showed the already-saved change
// as still pending, blocking Confirm Save on a genuinely new edit.
function snapshotDraftBaselines_() {
  state._baselineServices = JSON.parse(JSON.stringify(state.sourceServicesDraft || []));
  // Booking-header baseline (finish time, bride name, phone, location, services
  // requested, planner notes), used so buildDraftDiffSummary_() can diff against
  // it. Bug found 2026-07-20: the diff gate previously only checked services and
  // artists, so an edit to Finish Time (or any Page 1 field) showed "No changes
  // detected" and silently blocked Confirm Save, even though buildCurrentOverrides()
  // had already picked up the real edit correctly.
  state._baselineBooking = {
    brideName:         String((state.workspace && state.workspace.brideName) || ""),
    phone:             String((state.workspace && state.workspace.phone) || ""),
    finishTime:        String((state.workspace && state.workspace.readyBy) || ""),
    location:          String((state.workspace && state.workspace.location) || ""),
    servicesRequested: String((state.workspace && state.workspace.servicesRequested) || ""),
    plannerNotes:      String((state.workspace && state.workspace.plannerNotes) || "")
  };
}

// ─── SINGLE SOURCE OF TRUTH ──────────────────────────────────────────────────
// ONLY mutate state.sourceServicesDraft.
// Everything else (workspace.services, bridalPartyDraft) is DERIVED.
// Call rederiveDraftState_() after any sourceServicesDraft mutation.

function rederiveDraftState_() {
  // Bug B fix (2026-07-03 audit): promote any artist-assigned-but-untimed
  // service into its lane on load, instead of leaving it stranded in the
  // Working Area until the user clicks "Refresh Timeline". No-ops on already
  // timed services (published/refresh loads) and on services with no artist.
  autoTimeUntimedAssignedServices_(state.sourceServicesDraft);
  syncWorkspaceServicesFromSourceDraft_();
  state.bridalPartyDraft = dedupeBridalPartyDraft_(
    groupServicesIntoBridalPartyDraft_(state.sourceServicesDraft)
  );
  // Enrich bridal party draft with gallery/profile data from workspace.people
  // (galleries live on people[], not on sourceServicesDraft[])
  enrichBridalPartyFromWorkspacePeople_();
  // Pre-select services based on the booked package for any person with no services yet
  applyPackageDefaultsIfEmpty_();
  // Collapse a bare-first-name placeholder into a richer same-role person
  // once we know their full name (Cassandra Healey P1(5), 2026-10-27). Runs
  // LAST so it catches a placeholder regardless of which earlier step
  // (grouping, enrichment, or package defaults) put it in the draft.
  state.bridalPartyDraft = collapsePlaceholderDuplicates_(state.bridalPartyDraft);
}

function applyPackageDefaultsIfEmpty_() {
  var pkg = String((state.workspace && state.workspace.servicesRequested) || "").toLowerCase();
  var defaults = [];
  if (pkg.includes("hair") && pkg.includes("makeup")) defaults = ["Hair", "Makeup"];
  else if (pkg.includes("hairstyling") || (pkg.includes("hair") && !pkg.includes("makeup"))) defaults = ["Hair"];
  else if (pkg.includes("makeup")) defaults = ["Makeup"];
  if (!defaults.length) return;

  var anyApplied = false;
  state.bridalPartyDraft.forEach(function(person) {
    if (!person.servicesBooked || !person.servicesBooked.length) {
      person.servicesBooked = defaults.slice();
      anyApplied = true;
    }
  });
  if (anyApplied) syncBridalPartyToSourceDraft_();
}

function enrichBridalPartyFromWorkspacePeople_() {
  var people = (state.workspace && state.workspace.people) || [];
  if (!people.length || !state.bridalPartyDraft.length) return;

  // Index people by multiple keys for matching
  var peopleByKey = {};
  var peopleByRole = {};
  people.forEach(function(p) {
    var name = String(p.name || p.profileName || "").trim().toLowerCase();
    var role = String(p.role || "").trim().toLowerCase();
    if (name) peopleByKey[role + "|" + name] = p;
    if (name) peopleByKey[name] = p;
    var pid = String(p.personId || "").trim();
    if (pid) peopleByKey[pid] = p;
    // Build role buckets for fallback matching
    var normRole = (role.includes("mother") || role.includes("mum")) ? "mother"
      : role.includes("bride") && !role.includes("bridesmaid") ? "bride"
      : role.includes("bridesmaid") ? "bridesmaid"
      : role.includes("guest") ? "guest" : role;
    if (!peopleByRole[normRole]) peopleByRole[normRole] = [];
    peopleByRole[normRole].push(p);
  });

  state.bridalPartyDraft.forEach(function(person) {
    var pid = String(person.personId || person.id || "").trim();
    var name = String(person.personLabel || person.name || "").trim().toLowerCase();
    var role = String(person.role || "").trim().toLowerCase();
    var normRole = (role.includes("mother") || name.includes("mum") || name.includes("mother")) ? "mother"
      : (role === "bride" || name === "bride") ? "bride"
      : role.includes("bridesmaid") ? "bridesmaid"
      : role.includes("guest") ? "guest" : role;

    // Tier 1: exact key match
    var match = peopleByKey[pid] || peopleByKey[role + "|" + name] || peopleByKey[name] || null;

    // Tier 2: fuzzy name match within role bucket
    if (!match && peopleByRole[normRole]) {
      for (var i = 0; i < peopleByRole[normRole].length; i++) {
        var candidate = peopleByRole[normRole][i];
        var candName = String(candidate.profileName || candidate.name || "").trim().toLowerCase();
        if (candName && name && (candName.indexOf(name) > -1 || name.indexOf(candName) > -1)) {
          match = candidate;
          break;
        }
      }
    }

    // Tier 3: single-member role bucket (e.g., only one "mother" in Drive)
    if (!match && peopleByRole[normRole] && peopleByRole[normRole].length === 1) {
      match = peopleByRole[normRole][0];
    }

    if (!match) return;

    if (!person.inspirationGallery || !person.inspirationGallery.length) {
      person.inspirationGallery = match.inspirationGallery || [];
    }
    if (!person.trialGallery || !person.trialGallery.length) {
      person.trialGallery = match.trialGallery || [];
    }
    if (!person.trialFolderUrl && match.trialFolderUrl) {
      person.trialFolderUrl = match.trialFolderUrl;
    }
    if (!person.inspirationFolder && match.inspirationFolder) {
      person.inspirationFolder = match.inspirationFolder;
    }
    if (!person.inspirationSubmitted && match.inspirationSubmitted) {
      person.inspirationSubmitted = true;
    }
    if (!person.profileName && match.profileName) {
      person.profileName = match.profileName;
    }
    if (person.trialAssistSubmitted === undefined && match.trialAssistSubmitted !== undefined) {
      person.trialAssistSubmitted = match.trialAssistSubmitted;
    }
    if (person.trialFeedbackSubmitted === undefined && match.trialFeedbackSubmitted !== undefined) {
      person.trialFeedbackSubmitted = match.trialFeedbackSubmitted;
    }
    if (!person.profilePicUrl && match.profilePicUrl) {
      person.profilePicUrl = match.profilePicUrl;
    }
  });
}

function syncWorkspaceServicesFromSourceDraft_() {
  if (!state.workspace) return;

  const existingById = {};
  const systemBlocks = [];
  (state.workspace.services || []).forEach(function(service) {
    const id = getServiceId_(service);
    if (service && service.isSystemBlock) {
      systemBlocks.push(service);
      return;
    }
    if (id) existingById[id] = service;
  });

  const nextServices = (state.sourceServicesDraft || [])
    .filter(function(service) {
      const assignedArtist = String(service.assignedArtist || service.artist || "").trim();
      return !!assignedArtist && isServiceTimed_(service);
    })
    .map(function(service) {
      return buildWorkspaceServiceFromSource_(service, existingById[getServiceId_(service)] || null);
    })
    .concat(state.hydrateTimedRowsFromBackend ? systemBlocks : [])
    .sort(function(a, b) {
      const aMin = Number(a.startMin != null ? a.startMin : (parseDisplayTimeToMinutes(a.startTime || a.start || "") || 0));
      const bMin = Number(b.startMin != null ? b.startMin : (parseDisplayTimeToMinutes(b.startTime || b.start || "") || 0));
      return aMin - bMin;
    });

  state.workspace.services = nextServices;
  state.workspace.unscheduledServices = (state.sourceServicesDraft || [])
    .filter(function(service) {
      return !String(service.assignedArtist || service.artist || "").trim();
    })
    .map(function(service) {
      return buildWorkspaceServiceFromSource_(service, null);
    });
}

function buildWorkspaceServiceFromSource_(source, existing) {
  const src = source || {};
  const clone = Object.assign({}, existing || {});
  const person = String(src.person || src.personLabel || clone.person || clone.personLabel || "").trim();
  const serviceLabel = String(src.service || src.serviceLabel || clone.service || clone.serviceLabel || "").trim();
  const assignedArtist = String(src.assignedArtist || src.artist || clone.assignedArtist || clone.artist || "").trim();

  clone.id = String(src.id || src.itemId || clone.id || clone.itemId || "").trim();
  clone.itemId = clone.id;
  clone.pmId = String(src.pmId || src.pm_id || clone.pmId || clone.pm_id || "").trim();
  clone.serviceInstanceId = String(src.serviceInstanceId || clone.serviceInstanceId || "").trim();
  clone.scheduleEntryId = String(src.scheduleEntryId || clone.scheduleEntryId || "").trim();
  clone.order = Number(src.order || clone.order || 0);
  clone.person = person;
  clone.personLabel = person;
  clone.personId = String(src.personId || clone.personId || "").trim();
  clone.role = String(src.role || clone.role || "").trim();
  clone.service = serviceLabel;
  clone.serviceLabel = serviceLabel;
  clone.category = String(src.category || clone.category || "").trim();
  clone.durationMin = Number(src.durationMin || src.duration || clone.durationMin || clone.duration || 0);
  clone.duration = clone.durationMin;
  clone.artist = assignedArtist;
  clone.assignedArtist = assignedArtist;
  clone.startTimeOverride = String(src.startTimeOverride || clone.startTimeOverride || "").trim();
  clone.start = String(src.start || src.startTime || clone.start || clone.startTime || "").trim();
  clone.startTime = clone.start;
  clone.end = String(src.end || src.endTime || clone.end || clone.endTime || "").trim();
  clone.endTime = clone.end;
  clone.startMin = src.startMin !== undefined && src.startMin !== null && src.startMin !== ""
    ? Number(src.startMin)
    : parseDisplayTimeToMinutes(clone.startTime || clone.start || "");
  clone.endMin = src.endMin !== undefined && src.endMin !== null && src.endMin !== ""
    ? Number(src.endMin)
    : parseDisplayTimeToMinutes(clone.endTime || clone.end || "");
  clone.notes = String(src.notes || "").trim();
  clone.hairArtist = String(src.hairArtist || "").trim();
  clone.makeupArtist = String(src.makeupArtist || "").trim();
  clone.hairstyleChosen = String(src.hairstyleChosen || "").trim();
  clone.inspirationFolder = String(src.inspirationFolder || clone.folderUrl || "").trim();
  clone.inspirationSubmitted = !!src.inspirationSubmitted;
  clone.isCustomBlock = !!src.isCustomBlock;
  clone.isBride = !!src.isBride;

  return clone;
}

function isServiceTimed_(service) {
  if (!service) return false;
  // Runsheet-tab services are always considered timed — they came from the sheet with timing.
  if (service.source === "runsheet_tab") return true;
  if (service.startMin !== undefined && service.startMin !== null && service.startMin !== "") {
    return !isNaN(Number(service.startMin));
  }
  return parseDisplayTimeToMinutes(service.startTime || service.start || "") !== null;
}

function hydrateSourceDraftTimedFieldsFromWorkspace_() {
  const timedById = {};
  ((state.workspace && state.workspace.services) || []).forEach(function(service) {
    const id = getServiceId_(service);
    if (!id || service.isSystemBlock) return;
    timedById[id] = service;
  });

  (state.sourceServicesDraft || []).forEach(function(service) {
    const timed = timedById[getServiceId_(service)];
    if (!timed) return;
    service.start = String(timed.start || timed.startTime || "").trim();
    service.startTime = service.start;
    service.end = String(timed.end || timed.endTime || "").trim();
    service.endTime = service.end;
    service.startMin = timed.startMin !== undefined && timed.startMin !== null
      ? Number(timed.startMin)
      : parseDisplayTimeToMinutes(service.start);
    service.endMin = timed.endMin !== undefined && timed.endMin !== null
      ? Number(timed.endMin)
      : parseDisplayTimeToMinutes(service.end);
    if (!service.startTimeOverride && service.start) {
      service.startTimeOverride = service.start;
    }
  });
}

function renderArtistAvatar_(name, sizeClass) {
  const safe = String(name || "").trim();
  const artist = getArtistByName_(safe);
  const photoUrl = String((artist && artist.photoUrl) || "").trim();
  const initials = safe
    .split(/\s+/)
    .map(function(part) { return part.charAt(0).toUpperCase(); })
    .join("")
    .slice(0, 2) || "?";
  const classes = "artist-avatar " + (sizeClass || "artist-avatar--md");

  if (photoUrl) {
    return (
      '<span class="' + classes + '">' +
        '<img src="' + escapeHtml(photoUrl) + '" alt="' + escapeHtml(safe || "Artist") + '">' +
      '</span>'
    );
  }

  return (
    '<span class="' + classes + '">' +
      '<span class="artist-avatar-fallback">' + escapeHtml(initials) + '</span>' +
    '</span>'
  );
}

function renderArtistList_(names, sizeClass) {
  const safeNames = uniqueNames(names || []);
  if (!safeNames.length) return "No team selected";

  return (
    '<div class="artist-list">' +
      safeNames.map(function(name) {
        return (
          '<span class="artist-pill">' +
            renderArtistAvatar_(name, sizeClass || "artist-avatar--sm") +
            '<span>' + escapeHtml(name) + '</span>' +
          '</span>'
        );
      }).join("") +
    '</div>'
  );
}

    function renderPartyUnit(label, value) {
      return (
        '<div class="party-unit">' +
          "<span>" + escapeHtml(label) + "</span>" +
          "<strong>" + escapeHtml(value) + "</strong>" +
        "</div>"
      );
    }

   function buildPartyUnitSummary() {
  // BUG-2 fix (ref RESULTS-COWORK-runsheet-builder-BUG1-BUG2-datagather-2026-07-17.md):
  // count from state.partyProfiles (the hub roster, sourced from the
  // jv_party_members_json blob / jv_bridal_party_member metaobject), NOT from
  // state.bridalPartyDraft. bridalPartyDraft carries blank role values at count
  // time (a role-mapping gap during draft restore), so every tile read 0 even
  // for a bride with a full party (confirmed on Shanice Pitty). partyProfiles
  // renders the Party Profiles panel correctly and DOES carry role, so it is the
  // reliable count source. partyProfiles is party-members-only, so the bride tile
  // is set separately below.
  const summary = { bride: 0, bridesmaid: 0, mother: 0, guest: 0 };
  const seen = {};
  const trace = [];

  // Bride is not in partyProfiles (members-only), so set her tile directly when
  // a bride workspace is loaded.
  if (state.workspace && (state.workspace.email || state.brideProfile)) {
    summary.bride = 1;
  }

  (state.partyProfiles || []).forEach(function(person, index) {
    person = person || {};
    const personLabel = String(person.name || person.profileName || "").trim();
    const key = String(person.pm_id || person.id || "").trim() || personLabel.toLowerCase() || String(index);
    if (!key || seen[key]) return;
    seen[key] = true;

    // Trust person.role first (carried by the hub roster); fall back to
    // inferRoleFromPerson_ if role is empty.
    var role;
    var rawRole = String(person.role || "").trim();
    if (rawRole) {
      var rl = rawRole.toLowerCase();
      if (rl === "bride") role = "bride";
      else if (rl.indexOf("bridesmaid") > -1 || rl === "maid of honour" || rl === "maid of honor" || rl === "moh" || rl === "matron of honour" || rl === "matron of honor") role = "bridesmaid";
      else if (rl.indexOf("mother") > -1 || rl === "mum" || rl === "mob" || rl === "mog") role = "mother";
      else if (rl.indexOf("flower") > -1) role = "flower girl";
      else if (rl.indexOf("guest") > -1) role = "guest";
      else role = rl;
    } else {
      role = String(inferRoleFromPerson_(personLabel, { isBride: false, role: person.role }) || "").toLowerCase();
    }

    trace.push({ person: personLabel, rawRole: rawRole, resolved: role });

    if (role === "bride") summary.bride += 1;
    else if (role === "bridesmaid") summary.bridesmaid += 1;
    else if (role === "mother") summary.mother += 1;
    else if (role === "guest") summary.guest += 1;
  });

  try {
    console.info('[partyUnitSummary] summary:', summary, 'trace:', trace, 'partyProfiles.length:', (state.partyProfiles || []).length);
  } catch (_) {}

  return summary;
}

    function buildPersonBundleSummary() {
      const grouped = {};
      (state.sourceServicesDraft || []).forEach(function(service) {
        const person = String(service.personLabel || "Unnamed").trim();
        if (!grouped[person]) grouped[person] = [];
        grouped[person].push(String(service.serviceLabel || "Service").trim());
      });

      return Object.keys(grouped).map(function(person) {
        return {
          person: person,
          bundle: grouped[person].join(" + ")
        };
      });
    }

    function renderEditableField(label, id, value, type, helpText) {
      return (
        '<div class="field">' +
          "<label>" + escapeHtml(label) + "</label>" +
          '<input id="' + escapeHtml(id) + '" type="' + escapeHtml(type || "text") + '" value="' + escapeHtml(value || "") + '">' +
          '<small>' + escapeHtml(helpText || "") + "</small>" +
        "</div>"
      );
    }

    function renderEditableTextarea(label, id, value, helpText) {
      return (
        '<div class="field wide">' +
          "<label>" + escapeHtml(label) + "</label>" +
          '<textarea id="' + escapeHtml(id) + '" rows="3">' + escapeHtml(value || "") + "</textarea>" +
          '<small>' + escapeHtml(helpText || "") + "</small>" +
        "</div>"
      );
    }

    function renderInlineInput(label, onchange, value) {
      return (
        '<div class="field">' +
          "<label>" + escapeHtml(label) + "</label>" +
          '<input type="text" value="' + escapeHtml(value || "") + '" onchange="' + onchange + '">' +
        "</div>"
      );
    }

    function renderInlineSelect(label, optionsHtml, onchange) {
      return (
        '<div class="field">' +
          "<label>" + escapeHtml(label) + "</label>" +
          '<select onchange="' + onchange + '">' + optionsHtml + "</select>" +
        "</div>"
      );
    }

    function renderMiniStat(label, value) {
      return (
        '<div class="mini-stat">' +
          "<span>" + escapeHtml(label) + "</span>" +
          "<strong>" + escapeHtml(value) + "</strong>" +
        "</div>"
      );
    }

    function renderInfoRow(label, detail) {
      return (
        '<div class="info-row">' +
          "<strong>" + escapeHtml(label) + "</strong>" +
          "<div>" + detail + "</div>" +
        "</div>"
      );
    }

    function renderWarningRow(label, detail) {
      return (
        '<div class="warning-row">' +
          "<strong>" + escapeHtml(label) + "</strong>" +
          "<div>" + escapeHtml(detail || "Needs review") + "</div>" +
        "</div>"
      );
    }

    function renderServicesSourceChips() {
      const sources = {};
      state.sourceServicesDraft.forEach(function(service) {
        const key = String(service.source || "missing").trim();
        sources[key] = (sources[key] || 0) + 1;
      });

      const keys = Object.keys(sources);
      if (!keys.length) return '<span class="chip missing">No service sources</span>';

      return keys.map(function(key) {
        return '<span class="chip ' + escapeHtml(key) + '">' + escapeHtml(key) + " • " + escapeHtml(String(sources[key])) + "</span>";
      }).join("");
    }

    function renderReadinessHeadline(workspace) {
      if (workspace.services && workspace.services.length) return "Timeline ready";
      if ((state.sourceServicesDraft || []).some(function(service) {
        return !!String(service.assignedArtist || service.artist || "").trim();
      })) return "Planner mode ready";
      if (workspace.readyBy) return "Waiting on scheduling pass";
      return "Needs ready-by time";
    }

    function renderDebugItem_(label, value) {
      return (
        '<div class="debug-item">' +
          "<span>" + escapeHtml(label) + "</span>" +
          "<strong>" + escapeHtml(value || "—") + "</strong>" +
        "</div>"
      );
    }

    function bindStageInputs() {
      const leadSelect = document.getElementById("leadArtistSelect");
      if (leadSelect) {
        leadSelect.value = state.leadArtist || leadSelect.value;
      }
    }

function handleLeadArtistChange(value) {
  state.leadArtist = String(value || "").trim();
  sanitizeSourceServicesDraftAssignments_();
syncArtistsIntoBridalPartyDraft_();
syncWorkspaceServicesFromSourceDraft_();
renderStage();
}


    function updateServiceField(index, field, value) {
      const service = state.sourceServicesDraft[index];
      if (!service) return;

      if (field === "personLabel") {
        service.personLabel = String(value || "").trim();
        service.person = service.personLabel;
      } else if (field === "serviceLabel") {
        service.serviceLabel = String(value || "").trim();
        service.service = service.serviceLabel;
      } else if (field === "assignedArtist") {
        const nextArtist = String(value || "").trim();
        if (nextArtist && !canArtistHandleService_(nextArtist, service)) {
          showBanner(nextArtist + " is not marked for " + describeServiceSkill_(service) + ".", "warning");
          return;
        }
        service.assignedArtist = nextArtist;
        service.artist = service.assignedArtist;
      } else {
        service[field] = value;
      }

      sanitizeSourceServicesDraftAssignments_();
      syncWorkspaceServicesFromSourceDraft_();
      renderStage();
    }

    function buildArtistOptions(index) {
      const service = state.sourceServicesDraft[index] || {};
      const selected = String(service.assignedArtist || service.artist || "").trim();
      const names = uniqueNames([].concat(state.activeTeam || [], [selected || ""]));
      const options = ['<option value="">Planner decides</option>'];
      names.forEach(function(name) {
        if (!name) return;
        if (name !== selected && !canArtistHandleService_(name, service)) return;
        options.push('<option value="' + escapeHtml(name) + '"' + (name === selected ? " selected" : "") + '>' + escapeHtml(name) + "</option>");
      });
      return options.join("");
    }

    function getArtistByName_(name) {
      const safeName = String(name || "").trim();
      return ((state.workspace && state.workspace.team) || []).find(function(artist) {
        return String(artist.name || "").trim() === safeName;
      }) || null;
    }

    function seedStaffingFromWorkspace_(workspace) {
      const safeWorkspace = workspace || {};
      var selectedArtists = uniqueNames(safeWorkspace.selectedArtists || []);

      // Filter out spreadsheet template placeholders (e.g. "ARTIST") that are not real team members.
      // Tabs created from the template have "ARTIST" in D15/F15; appParseRunsheetTab_ includes these
      // as lane artists, poisoning selectedArtists. Only keep names present in the actual team roster.
      const teamNames = uniqueNames(
        (safeWorkspace.team || []).map(function(t) { return String(t.name || '').trim(); }).filter(Boolean)
      );
      if (teamNames.length) {
        selectedArtists = selectedArtists.filter(function(name) { return teamNames.indexOf(name) > -1; });
      }

      const leadArtist = String(safeWorkspace.leadArtist || selectedArtists[0] || "").trim();
      if (leadArtist && selectedArtists.indexOf(leadArtist) === -1) {
        selectedArtists.unshift(leadArtist);
      }

      state.leadArtist = leadArtist;

      // When hydrated from the runsheet tab, use sheet artists directly.
      // Do NOT capability-score — sheet is truth.
      if (String(safeWorkspace.hydratedFrom || "").trim() === "runsheetTab" && selectedArtists.length) {
        state.skillSelections = {
          makeup: selectedArtists[0] || "",
          hair: selectedArtists.length > 1 ? selectedArtists[1] : selectedArtists[0] || "",
          additional: selectedArtists.slice(2)
        };
      } else {
        state.skillSelections = deriveSkillSelectionsFromWorkspace_(safeWorkspace, selectedArtists, leadArtist);
      }
      rebuildActiveTeamFromSkillSelections_();
    }

    function deriveSkillSelectionsFromWorkspace_(workspace, selectedArtists, leadArtist) {
      const selected = uniqueNames(selectedArtists || []);
      const services = (workspace && workspace.sourceServices && workspace.sourceServices.length)
        ? workspace.sourceServices
        : ((workspace && workspace.services) || []);
      const counts = {
        makeup: {},
        hair: {}
      };

      (services || []).forEach(function(service) {
        const category = inferCategoryFromLabel_(service.category || service.serviceLabel || service.service || "");
        if (!category) return;

        const explicitArtist = category === "makeup"
          ? String(service.makeupArtist || "").trim()
          : String(service.hairArtist || "").trim();
        const assignedArtist = String(service.assignedArtist || service.artist || "").trim();
        const candidate = explicitArtist || assignedArtist;
        if (!candidate || selected.indexOf(candidate) === -1) return;

        if (!counts[category][candidate]) counts[category][candidate] = 0;
        counts[category][candidate] += 1;
      });

      const makeup = chooseHydratedSkillArtist_("makeup", selected, counts.makeup, leadArtist);
      const hair = chooseHydratedSkillArtist_("hair", selected, counts.hair, leadArtist, makeup);
      const additional = selected.filter(function(name) {
        return name !== makeup && name !== hair;
      });

      return {
        makeup: makeup,
        hair: hair,
        additional: additional
      };
    }

    function chooseHydratedSkillArtist_(skillType, selectedArtists, scoredCandidates, leadArtist, preferredOther) {
      const selected = uniqueNames(selectedArtists || []);
      const scores = scoredCandidates || {};
      const compatible = selected.filter(function(name) {
        const artist = getArtistByName_(name);
        if (!artist) return false;
        return skillType === "makeup" ? !!artist.canMakeup : !!artist.canHair;
      });

      let best = "";
      let bestScore = -1;
      compatible.forEach(function(name) {
        const score = Number(scores[name] || 0);
        if (score > bestScore) {
          best = name;
          bestScore = score;
        }
      });
      if (bestScore > 0) return best;

      if (preferredOther && compatible.length > 1) {
        const alternative = compatible.find(function(name) { return name !== preferredOther; });
        if (alternative) return alternative;
      }

      if (leadArtist && compatible.indexOf(leadArtist) > -1) return leadArtist;

      if (compatible.length === 1) return compatible[0];

      return compatible[0] || "";
    }

    function describeServiceSkill_(service) {
      const label = String(service.serviceLabel || service.service || "").toLowerCase();
      if (label.indexOf("make") > -1) return "makeup";
      if (label.indexOf("hair") > -1 || label.indexOf("wave") > -1 || label.indexOf("blow") > -1) return "hair";
      return "this service";
    }

    function canArtistHandleService_(artistName, service) {
      const safeArtist = String(artistName || "").trim();
      if (!safeArtist) return true;
      const artist = getArtistByName_(safeArtist);
      if (!artist) return true;
      const need = describeServiceSkill_(service);
      if (need === "makeup") return !!artist.canMakeup;
      if (need === "hair") return !!artist.canHair;
      return true;
    }

    function firstCompatibleArtistForService_(service) {
      const names = uniqueNames((state.activeTeam || []).concat(state.leadArtist ? [state.leadArtist] : []));
      for (let i = 0; i < names.length; i += 1) {
        if (canArtistHandleService_(names[i], service)) return names[i];
      }
      return "";
    }

    function sanitizeSourceServicesDraftAssignments_() {
      (state.sourceServicesDraft || []).forEach(function(service) {
        // Never override assignments that came from the runsheet tab — sheet is truth.
        if (service.source === "runsheet_tab") return;
        const assigned = String(service.assignedArtist || service.artist || "").trim();
        if (!assigned) return;

        // Skill-mismatch is NO LONGER an automatic reset — the team
        // roster's canHair/canMakeup flags can be out of sync with an
        // artist's actual capability (e.g. Kaelin is tagged "ALL" in
        // the UI but has canHair=false in the roster data). Auto-
        // resetting silently reverted legitimate planner drag-drop
        // assignments. Trust the planner's explicit pick instead and
        // surface a non-blocking warning so they can fix the roster.
        //
        // We still reset when the artist isn't recognised at all
        // (e.g. removed from the team), which is the clear "stale"
        // case the sanitizer was originally built for.
        const artistObj = getArtistByName_(assigned);
        if (!artistObj) {
          const fallback = service.isBride && state.leadArtist && canArtistHandleService_(state.leadArtist, service)
            ? state.leadArtist
            : firstCompatibleArtistForService_(service);
          service.assignedArtist = fallback;
          service.artist = fallback;
        }
      });
    }

    function applyBookingDetails() {
      if (!state.currentBride) return;
      loadWorkspace(state.currentBride, buildCurrentOverrides());
    }

    // Captures live DOM values for the Page 1 booking fields into
    // state.workspace before a destructive renderStage() wipes them. These
    // fields (Bride, Bride Phone, Planner Notes - Address and Ready By already
    // have their own sync paths, see handleLocationBlur/syncBookingReadyByToState_)
    // never wired an onchange/oninput, so a typed edit only ever lived in the
    // DOM. applyLocalChanges()'s renderStage() call rebuilds the Page 1 form
    // from state.workspace, silently reverting to the last-saved value the
    // moment the user clicked Save Draft - the actual root cause of
    // BUG-runsheet-page1-readyby-not-wired-2026-07-21's sibling bug, confirmed
    // live via BRIEFS/RESULTS-runsheet-builder-view-e2e-grade3-xray-2026-09-10.md
    // (Defect A). Fixed 2026-09-10 by capturing the DOM into state right before
    // the wipe, the same place Ready By already protects itself.
    function captureBookingFieldsIntoState_() {
      if (!state.workspace) return;
      var brideInput = document.getElementById("bookingBrideName");
      var phoneInput = document.getElementById("bookingPhone");
      var notesInput = document.getElementById("bookingPlannerNotes");
      var dateInput  = document.getElementById("bookingDate");
      if (brideInput) state.workspace.brideName = brideInput.value;
      if (phoneInput) state.workspace.phone = phoneInput.value;
      if (notesInput) state.workspace.plannerNotes = notesInput.value;
      if (dateInput && dateInput.value) state.workspace.weddingDate = normaliseDateForInput_(dateInput.value);
    }

    function applyLocalChanges(silent) {
  captureBookingFieldsIntoState_();
  const sourceById = {};
  (state.sourceServicesDraft || []).forEach(function(src) {
    const id = getServiceId_(src);
    if (id) sourceById[id] = src;
  });

  ((state.workspace && state.workspace.services) || []).forEach(function(ws) {
    const id = getServiceId_(ws);
    if (!id || !sourceById[id]) return;

    const src = sourceById[id];

    if (ws.durationMin || ws.duration) {
  src.durationMin = src.duration = Number(ws.durationMin || ws.duration);
}
if (ws.notes !== undefined) src.notes = ws.notes;
if (ws.assignedArtist || ws.artist) {
  src.assignedArtist = src.artist = String(ws.assignedArtist || ws.artist || "").trim();
}
if (ws.personLabel || ws.person) {
  src.personLabel = src.person = String(ws.personLabel || ws.person || "").trim();
}
if (ws.serviceLabel || ws.service) {
  src.serviceLabel = src.service = String(ws.serviceLabel || ws.service || "").trim();
}
if (ws.personId) src.personId = String(ws.personId).trim();
if (ws.hairArtist !== undefined) src.hairArtist = String(ws.hairArtist || "").trim();
if (ws.makeupArtist !== undefined) src.makeupArtist = String(ws.makeupArtist || "").trim();
if (ws.start !== undefined || ws.startTime !== undefined) {
  src.start = String(ws.start || ws.startTime || "").trim();
  src.startTime = src.start;
}
if (ws.end !== undefined || ws.endTime !== undefined) {
  src.end = String(ws.end || ws.endTime || "").trim();
  src.endTime = src.end;
}
if (ws.startTimeOverride !== undefined) src.startTimeOverride = ws.startTimeOverride;
if (ws.startMin !== undefined) src.startMin = ws.startMin;
if (ws.endMin !== undefined) src.endMin = ws.endMin;
  });

  rederiveDraftState_();
  state.timelineDirty = false;
  renderStage();
  if (!silent) {
    showBanner("Local changes applied to draft. These will be used in the next rebuild.", "success");
  }
}

    // ─── DIFF PREVIEW BEFORE WRITE-BACK ─────────────────────────────────────────

    function buildDraftDiffSummary_() {
      var baseline = state._baselineServices || [];
      var current = state.sourceServicesDraft || [];
      var baseById = {};
      baseline.forEach(function(s) {
        var id = String(s.id || s.itemId || "");
        if (id) baseById[id] = s;
      });

      var added = 0;
      var removed = 0;
      var timeChanged = 0;
      var artistChanged = 0;
      var labelChanged = 0;
      var durationChanged = 0;
      var details = [];

      current.forEach(function(s) {
        var id = String(s.id || s.itemId || "");
        var old = baseById[id];
        if (!old) {
          added++;
          details.push("+ Added: " + (s.personLabel || s.person || "?") + " — " + (s.serviceLabel || s.service || "?"));
          return;
        }
        delete baseById[id];
        var oldArtist = String(old.assignedArtist || old.artist || "").trim();
        var newArtist = String(s.assignedArtist || s.artist || "").trim();
        var oldTime = String(old.startTimeOverride || old.startTime || old.start || "").trim();
        var newTime = String(s.startTimeOverride || s.startTime || s.start || "").trim();
        var oldLabel = String(old.serviceLabel || old.service || "").trim();
        var newLabel = String(s.serviceLabel || s.service || "").trim();
        var oldDur = Number(old.durationMin || old.duration || 0);
        var newDur = Number(s.durationMin || s.duration || 0);
        var person = s.personLabel || s.person || "?";

        if (oldArtist !== newArtist) {
          artistChanged++;
          details.push("~ " + person + ": artist " + (oldArtist || "none") + " → " + (newArtist || "none"));
        }
        if (oldTime !== newTime) {
          timeChanged++;
          details.push("~ " + person + " " + newLabel + ": time " + (oldTime || "unset") + " → " + (newTime || "unset"));
        }
        if (oldLabel !== newLabel) {
          labelChanged++;
          details.push("~ " + person + ": service " + oldLabel + " → " + newLabel);
        }
        if (oldDur !== newDur && newDur > 0) {
          durationChanged++;
          details.push("~ " + person + " " + newLabel + ": duration " + oldDur + " → " + newDur + " min");
        }
      });

      Object.keys(baseById).forEach(function(id) {
        var old = baseById[id];
        removed++;
        details.push("- Removed: " + (old.personLabel || old.person || "?") + " — " + (old.serviceLabel || old.service || "?"));
      });

      // Booking-header fields (finish time, bride name, phone, location,
      // services requested, planner notes) are NOT covered by the services or
      // artist diff above. Without this, editing ONLY a Page 1 field shows
      // "No changes detected" and Confirm Save stays disabled, even though the
      // field really did change and buildCurrentOverrides() would send it.
      // Fixed 2026-07-20.
      var bookingChanged = 0;
      var baseBooking = state._baselineBooking || {};
      var currentBooking = (typeof buildCurrentOverrides === "function" ? buildCurrentOverrides().booking : null) || {};
      var bookingFieldLabels = {
        brideName:         "Bride name",
        weddingDate:        "Date",
        phone:              "Phone",
        finishTime:         "Finish time",
        location:           "Location",
        servicesRequested:  "Services requested",
        plannerNotes:       "Planner notes"
      };
      Object.keys(bookingFieldLabels).forEach(function(key) {
        var oldVal = String(baseBooking[key] || "").trim();
        var newVal = String(currentBooking[key] || "").trim();
        if (oldVal !== newVal) {
          bookingChanged++;
          details.push("~ " + bookingFieldLabels[key] + ": " + (oldVal || "unset") + " to " + (newVal || "unset"));
        }
      });

      var total = added + removed + timeChanged + artistChanged + labelChanged + durationChanged + bookingChanged;
      var summary = [];
      if (added) summary.push(added + " added");
      if (removed) summary.push(removed + " removed");
      if (timeChanged) summary.push(timeChanged + " time" + (timeChanged > 1 ? "s" : "") + " updated");
      if (artistChanged) summary.push(artistChanged + " reassigned");
      if (labelChanged) summary.push(labelChanged + " renamed");
      if (durationChanged) summary.push(durationChanged + " duration" + (durationChanged > 1 ? "s" : "") + " changed");
      if (bookingChanged) summary.push(bookingChanged + " booking field" + (bookingChanged > 1 ? "s" : "") + " updated");

      return {
        total: total,
        summary: summary.length ? summary.join(", ") : "No changes detected",
        details: details,
        hasChanges: total > 0
      };
    }

    function openDiffPreviewModal_() {
      var diff = buildDraftDiffSummary_();
      var detailHtml = diff.details.length
        ? diff.details.map(function(d) {
            var cls = d.charAt(0) === "+" ? "color:var(--success-text)"
                    : d.charAt(0) === "-" ? "color:var(--error-text)"
                    : "color:var(--warn-text)";
            return '<div style="' + cls + ';font-size:13px;line-height:1.6;">' + escapeHtml(d) + '</div>';
          }).join("")
        : '<div style="color:var(--muted);font-size:13px;">No changes to write back.</div>';

      var modal = document.getElementById("diffPreviewModal");
      document.getElementById("diffSummaryText").innerHTML =
        '<strong>' + escapeHtml(diff.summary) + '</strong>';
      document.getElementById("diffDetailList").innerHTML = detailHtml;
      document.getElementById("diffConfirmBtn").disabled = !diff.hasChanges;
      modal.classList.add("open");
    }

    function closeDiffPreviewModal_() {
      document.getElementById("diffPreviewModal").classList.remove("open");
    }

    function confirmSaveDraft_() {
      closeDiffPreviewModal_();
      executeSaveDraft_();
    }

    function executeSaveDraft_() {
      // V2 save (flag-gated, dark): ids-only intent to the Cloudflare drafts API. Legacy path below is
      // untouched and is the only path while window.jvRunsheetV2Enabled is not true.
      if (rsbV2On_()) { rsbV2Init_(); applyLocalChanges(true); window.jvRunsheetV2.saveDraft(); return; }
      showBanner("Saving draft to the bride tab...", "success");

      var payload = buildCurrentOverrides();
      payload.brideName = state.currentBride;
      var expectedServiceCount = (state.sourceServicesDraft || []).filter(function(s) {
        return !s.isSystemBlock;
      }).length;
      var expectedLanes = (state.activeTeam || []).length;

      // Phase 2 (BRIEFS/runsheet-json-first-save-publish-product-brief-2026-07-22.md):
      // route Save Draft through the fire-and-forget async path so a slow
      // appSaveDraft can never die at the ~55s Cloudflare relay wall (the bug that
      // wiped page-3 work). Kill-switch OFF on the server returns asyncFallback (the
      // old synchronous result), handled here exactly as before. On accepted+jobId
      // we poll for confirmation and DO NOT destructively re-hydrate from a server
      // rebuild: the in-memory draft is already correct, and re-hydrating was itself
      // what dropped moved services on a lossy read-back.
      jvBridge.callAdmin('bridge_runsheet_saveDraftAsync', payload)
        .then(function(res) {
          // Kill-switch OFF (or any sync fallback): server ran the OLD synchronous
          // save and returned the full result tagged asyncFallback. Handle as today.
          if (res && res.asyncFallback) {
            if (res.ok) {
              saveDraftOnSyncSuccess_(res.data || {}, expectedServiceCount, expectedLanes);
            } else {
              saveDraftFailSafe_(res && res.error);
            }
            return;
          }

          // Accepted: background save job running. Confirm optimistically and poll.
          if (res && res.ok && res.accepted && res.jobId) {
            showBanner('Saving your draft in the background. This usually takes a few seconds, you can keep working.', 'success');
            saveDraftStartPoll_(res.jobId);
            return;
          }

          // Instant reject (bad payload / unexpected shape): protect the work.
          saveDraftFailSafe_((res && res.error) || 'Save did not start.');
        })
        .catch(function(err) {
          saveDraftFailSafe_(getErrorMessage(err));
        });
    }

    // Manual, explicitly targeted canary harness. This is never called by the
    // normal Save Draft button; it exists only for one browser test after the
    // matching GAS route and Worker gate are enabled.
    window.JV_serviceIdentityCanaryFromCurrentDraft = function(serviceInstanceId, scheduleEntryId, ownerType, pmId) {
      var draft = buildCurrentOverrides();
      var brideUid = String((state.workspace && state.workspace.brideUid) || '').trim();
      draft.brideUid = brideUid;
      draft.brideName = String(state.currentBride || '').trim();
      if (!brideUid || !serviceInstanceId || !scheduleEntryId) {
        return Promise.reject(new Error('Canonical bride, service, and schedule IDs are required.'));
      }
      return jvBridge.callAdmin('bridge_runsheet_serviceIdentityCanary', {
        brideUid: brideUid,
        ownerType: ownerType || 'party_member',
        pmId: pmId || null,
        serviceInstanceId: String(serviceInstanceId).trim(),
        scheduleEntryId: String(scheduleEntryId).trim(),
        idempotencyKey: 'browser-canary|' + brideUid + '|' + String(serviceInstanceId).trim() + '|' + Date.now(),
        draft: draft
      });
    };

    // Synchronous-save success handler (kill-switch OFF path). Keeps the exact
    // pre-Phase-2 behaviour: re-hydrate from the server rebuild and show the
    // "X services written" summary. Only used when the server ran the old sync save.
    function saveDraftOnSyncSuccess_(safe, expectedServiceCount, expectedLanes) {
      safe = safe || {};
      hydrateWorkspace(safe);
      rederiveDraftState_();
      // Defect B fix (2026-09-10), same reasoning as the async poll's 'done'
      // branch above: re-snapshot both diff baselines now that the server has
      // actually confirmed this save, or a second edit in the same session
      // would diff against the pre-save state.
      snapshotDraftBaselines_();
      renderStage();

      var tabName = state.currentBride || "bride";
      var writtenServices = Number(safe.servicesWritten || expectedServiceCount);
      var writtenLanes = Number(safe.lanesWritten || expectedLanes);
      var summaryParts = [
        "Saved to " + tabName + " tab",
        writtenServices + " service row" + (writtenServices !== 1 ? "s" : "") + " written",
        writtenLanes + " artist lane" + (writtenLanes !== 1 ? "s" : "") + " updated"
      ];

      var rehydratedCount = ((safe.services || []).length);
      if (rehydratedCount > 0 && Math.abs(rehydratedCount - expectedServiceCount) > 2) {
        showBanner(
          summaryParts.join(" · ") +
          ". Warning: expected ~" + expectedServiceCount + " services but rehydrated " + rehydratedCount + ". Check the runsheet tab.",
          "warning"
        );
      } else {
        showBanner(summaryParts.join(" · "), "success");
      }
      state.timelineDirty = false;
    }

    // Poll the async save job until done / error / ceiling. On done we simply
    // confirm: the in-memory draft is authoritative and stays as-is (no destructive
    // re-hydrate). On error the work is preserved by saveDraftFailSafe_.
    function saveDraftStartPoll_(jobId) {
      if (_saveDraftPollTimer) { clearTimeout(_saveDraftPollTimer); _saveDraftPollTimer = null; }
      _saveDraftPollStart = Date.now();

      function tick() {
        jvBridge.callAdmin('bridge_runsheet_saveDraftStatus', { jobId: jobId })
          .then(function(res) {
            var d = (res && res.ok && res.data) ? res.data : (res && res.status ? res : null);
            var status = d ? d.status : null;
            if (status === 'done') {
              state.timelineDirty = false;
              // Defect B fix (2026-09-10, confirmed live via BRIEFS/RESULTS-
              // runsheet-builder-view-e2e-grade3-xray-2026-09-10.md): both diff
              // baselines were set once at workspace load and never refreshed on
              // a successful save, so a SECOND edit in the same session diffed
              // against the pre-save state and showed the already-saved change
              // as still pending -- blocking the user from making a genuinely
              // new edit within the same session. Re-snapshot from the now-saved
              // state, same helper the module-scope load call uses.
              snapshotDraftBaselines_();
              var n = Number(d.servicesWritten || 0);
              showBanner(
                'Draft saved' + (n ? (' (' + n + ' service row' + (n === 1 ? '' : 's') + ' written)') : '') + '.',
                'success'
              );
              return;
            }
            if (status === 'error') {
              saveDraftFailSafe_((d.reasons && d.reasons.join(', ')) || 'unknown error');
              return;
            }
            // pending / running: keep polling until the ceiling.
            if (!saveDraftScheduleNext_(tick)) return;
          })
          .catch(function() {
            // Transient poll failure: keep the same schedule so a network blip does
            // not abandon the inline confirmation.
            if (!saveDraftScheduleNext_(tick)) return;
          });
      }
      _saveDraftPollTimer = setTimeout(tick, JV_SAVEDRAFT_POLL_INTERVAL_MS);
    }

    // Next save-poll delay. Fast ticks up to the ceiling, then stop and reassure:
    // the save is still running in the background and the work is safe locally.
    function saveDraftScheduleNext_(tick) {
      var elapsed = Date.now() - _saveDraftPollStart;
      if (elapsed > JV_SAVEDRAFT_POLL_MAX_MS) {
        autosaveNow_();
        showBanner(
          'Still finishing in the background. This is normal for a large wedding, and your changes are already saved, ' +
          'they just take a little longer to confirm. You can keep working or reopen this bride shortly to see the saved version.',
          'success'
        );
        return false;
      }
      _saveDraftPollTimer = setTimeout(tick, JV_SAVEDRAFT_POLL_INTERVAL_MS);
      return true;
    }

    // Preserve the user's work whenever a Save Draft round-trip does not
    // confirm (timeout OR backend error). Never silently drop page-3 edits.
    function saveDraftFailSafe_(errText) {
      autosaveNow_();               // persist to localStorage right now
      state.timelineDirty = true;   // keep it dirty, nothing was written server-side
      var msg = String(errText || '');
      var isTimeout = /timed out|timeout|upstream|relay|55s|504|deadline/i.test(msg);
      if (isTimeout) {
        showBanner(
          'Save is taking longer than expected and did not confirm. Your changes are safe in this browser (autosaved), ' +
          'but they are NOT saved to the server yet. Try Save Draft again in a moment, and do not close this bride until it confirms.',
          'warning'
        );
      } else {
        showBanner(
          'Save did not confirm (' + (msg || 'unknown error') + '). Your changes are safe in this browser (autosaved) but NOT saved to the server. ' +
          'Try Save Draft again, and do not close this bride until it confirms.',
          'error'
        );
      }
    }

    function saveDraftChanges() {
      if (!state.currentBride) return;
      var locWait = rsbAwaitLocationSave_(8000);
      if (locWait) { locWait.then(function() { if (state.currentBride) { applyLocalChanges(true); openDiffPreviewModal_(); } }); return; }
      applyLocalChanges(true);
      openDiffPreviewModal_();
    }

    function applyTeamAndServices() {
      if (!state.currentBride) return;
      // In-flight guard (Bug A, 2026-07-03 audit): the workspace round-trip can
      // take ~8-16s. Hammering Refresh/Recalculate mid-wait used to stack racing
      // loads and read as "glitching". Refuse politely while one is running.
      if (state.workspaceLoading) {
        showBanner("Still building the timeline — one moment…", "warning");
        return;
      }
      // First apply local edits so they survive the rebuild
      applyLocalChanges(true);
      loadWorkspace(state.currentBride, buildCurrentOverrides(), { hydrateTimedRowsFromBackend: true });
    }

    function buildServiceAssignmentsDraft_(services) {
      const assignments = {};
      (services || []).forEach(function(service) {
        const id = getServiceId_(service);
        const artist = String(service.assignedArtist || service.artist || "").trim();
        if (id && artist) assignments[id] = artist;
      });
      return assignments;
    }

    function buildCurrentOverrides(extra) {
      const bookingReadyByInput          = document.getElementById("bookingReadyBy");
      const bookingLocationInput         = document.getElementById("bookingLocation");
      const bookingServicesRequestedInput= document.getElementById("bookingServicesRequested");
      const bookingBrideNameInput        = document.getElementById("bookingBrideName");
      const bookingDateInput             = document.getElementById("bookingDate");
      const bookingPhoneInput            = document.getElementById("bookingPhone");
      const bookingPlannerNotesInput     = document.getElementById("bookingPlannerNotes");

      const services = normalisePayloadServices(state.sourceServicesDraft || []);

      const overrides = {
  booking: {
    brideName:        bookingBrideNameInput         ? bookingBrideNameInput.value                   : (state.workspace && state.workspace.brideName) || "",
    weddingDate:      bookingDateInput              ? normaliseDateForInput_(bookingDateInput.value)  : "",
    phone:            bookingPhoneInput             ? bookingPhoneInput.value                       : (state.workspace && state.workspace.phone) || "",
    // state.workspace.readyBy is authoritative: it's the value both the Page 1
    // "Ready By" field AND the Page 3 "Finishes" mini-stat write to
    // (setSnapshotFinishTime_), but only the former syncs its own DOM input.
    // Reading the DOM first meant an edit made via the Page 3 mini-stat (the
    // common path, since the user is usually on the timeline, not booking,
    // when they notice finish time needs fixing) was silently discarded by
    // this payload whenever #bookingReadyBy happened to hold a stale value.
    // Bug fix 2026-07-21 (BUILD-runsheet-finishtime-payload-bug-and-island-perf).
    finishTime:       (state.workspace && state.workspace.readyBy) || (bookingReadyByInput ? inputTimeToDisplay(bookingReadyByInput.value) : "") || "",
    location:         rsbEffectiveLocation_(bookingLocationInput),
    servicesRequested:bookingServicesRequestedInput ? bookingServicesRequestedInput.value           : (state.workspace && state.workspace.servicesRequested) || "",
    plannerNotes:     bookingPlannerNotesInput      ? bookingPlannerNotesInput.value                : (state.workspace && state.workspace.plannerNotes) || ""
  },
  staffingDraft: {
    makeupArtist: state.skillSelections.makeup || "",
    hairArtist:   state.skillSelections.hair   || "",
    activeTeam:   state.activeTeam.slice(),
    serviceAssignments: buildServiceAssignmentsDraft_(services)
  },
  services: services,
  timelineDraft: {
    manualOverrides: Object.assign({}, state.timelineOverrides || {})
  },
  leadArtist: state.leadArtist || "",
  overrideArtists: uniqueNames((state.activeTeam || []).concat(state.leadArtist ? [state.leadArtist] : [])),
  // Schedule provenance carry-through (2026-09-03, external GPT audit finding 3,
  // approved BRIEFS/External GPT/Runsheet Builder/Deliverables/Actioned/
  // 05-runsheet-maximise-existing-features-DECISIONS-2026-09-03.md). These two
  // fields are already computed by RunsheetGenerator.gs at plan time and already
  // survive into state.workspace from the last load/recalc -- but the PUBLISH-path
  // workspace builder (appBuildSidebarWorkspaceFromDraftPayload_, RunsheetEngineV4.gs)
  // never received them as input, so it hardcoded empty objects and the values were
  // lost at publish. Carrying the CLIENT'S OWN last-known values through the
  // payload is a deliberate choice over recomputing inside the frozen file: this
  // data is a passive record of "why did the schedule turn out this way", not a
  // decision the publish step makes, so reusing what was already correctly
  // computed avoids re-deriving scoring logic inside code this project treats as
  // frozen. Admin/staff-facing only via the readiness view -- never rendered to
  // brides.
  artistCountReasoning: (state.workspace && state.workspace.artistCountReasoning) || {},
  scheduleMeta: (state.workspace && state.workspace.scheduleMeta) || {}
};

      if (overrides.booking.finishTime) {
        overrides.finishTime = overrides.booking.finishTime;
      }

      return Object.assign(overrides, extra || {});
    }

    // Pre-publish: detect and close timing gaps between services per artist.
    // Ensures no stale timing from tab reloads produces unexplained gaps in the PDF.
    function prePublishSanitiseTiming_(services) {
      if (!services || !services.length) return services;

      // Group services by artist
      var byArtist = {};
      services.forEach(function(s) {
        var a = String(s.assignedArtist || s.artist || "").trim();
        if (!a) return;
        if (!byArtist[a]) byArtist[a] = [];
        byArtist[a].push(s);
      });

      Object.keys(byArtist).forEach(function(artistName) {
        var lane = byArtist[artistName]
          .filter(function(s) {
            return s.startMin !== undefined && s.startMin !== null && !isNaN(Number(s.startMin));
          })
          .sort(function(a, b) { return Number(a.startMin) - Number(b.startMin); });

        if (lane.length < 2) return;

        // Walk through the lane and close any gap > 15 min between consecutive services
        for (var i = 1; i < lane.length; i++) {
          var prev = lane[i - 1];
          var curr = lane[i];
          var prevEnd = Number(prev.startMin) + Number(prev.durationMin || prev.duration || 0);
          var gap = Number(curr.startMin) - prevEnd;
          if (gap > 15) {
            // Shift this service (and all subsequent ones) backward to close the gap
            var shift = gap - 0; // close gap completely — pack tightly
            for (var j = i; j < lane.length; j++) {
              lane[j].startMin = Number(lane[j].startMin) - shift;
              lane[j].endMin = lane[j].startMin + Number(lane[j].durationMin || lane[j].duration || 0);
              lane[j].startTime = lane[j].start = formatMinutesAsDisplayTime_(lane[j].startMin);
              lane[j].endTime = lane[j].end = formatMinutesAsDisplayTime_(lane[j].endMin);
            }
          }
        }
      });

      return services;
    }

    function openRunsheetPreview() {
      if (!state.currentBride) return;
      var bride = state.currentBride;

      // PREVIEW IS READ-ONLY — opens the public viewer at the last
      // PUBLISHED snapshot. Does NOT trigger publish / save / notify.
      //
      // History:
      //   * Original pre-published via bridge_runsheet_publish so the
      //     viewer reflected current planner edits. That route fired
      //     team notifications (Chat DMs) as a side effect — every
      //     Preview click bombed artists' phones.
      //   * 2026-05-19 round 1: reverted to read-only.
      //   * 2026-05-19 round 2: re-attempted as silent-publish with
      //     notify:{silent:true} now that the gates work. THAT BUILD
      //     LOST PLANNER STATE — buildCurrentOverrides() doesn't
      //     round-trip the full Page 3 timeline (artist columns
      //     collapsed, 16 timed rows became 9 on jfg). The publish
      //     bridge pre-clears the runsheet tab then rewrites from
      //     payload, so an incomplete payload = data loss.
      //   * 2026-05-19 round 3: reverted again. Preview stays
      //     strictly read-only until either (a) a separate preview
      //     endpoint is built that doesn't touch the main runsheet
      //     tab, or (b) buildCurrentOverrides() round-trip is fixed.
      //
      // If nothing is published yet, the viewer says "Runsheet
      // unavailable" — that's the correct state. Use Publish Runsheet
      // when you actually want a shareable snapshot.
      var win = window.open('about:blank', '_blank');

      // Win B (2026-07-12): CDN fast-path. The viewer token is already published
      // to the `bridal_card_data` island (viewer_token, mirrored from CRM
      // RUNSHEET_VIEWER_TOKEN). When present we build the /pages/runsheet-view URL
      // client-side and skip the bridge_runsheet_getPreviewUrl GAS call (which
      // writes CRM + pushes a Shopify metafield on first call, and always makes a
      // metafieldsSet HTTP hop). The URL byte-matches runsheetViewerPublic_buildUrl_.
      // Falls through to the live GAS path when the bride has no token yet
      // (never-published / pre-backfill) so an unpublished bride still previews.
      var islandToken = rsbViewerTokenFromIsland_(state.workspace && state.workspace.email);
      if (islandToken) {
        var fastUrl = 'https://jessicavegas.com/pages/runsheet-view?token=' + encodeURIComponent(islandToken);
        if (win) win.location.href = fastUrl; else window.open(fastUrl, '_blank', 'noopener');
        return;
      }

      // CF SHADOW/PRIMARY (Runsheet Builder Slice 1 / lane B1, 2026-10-17):
      // same window.JV_RUNSHEET_STATUS_READS_MODE gate as publishNotifyFetchLast_
      // above -- 'off' (default/unset) leaves this exact GAS call unchanged.
      var rsrMode = window.JV_RUNSHEET_STATUS_READS_MODE || 'off';
      var gasPreviewPromise = jvBridge.callAdmin('bridge_runsheet_getPreviewUrl', { brideName: bride });

      if (rsrMode === 'shadow' && jvBridge.callAdminRelay) {
        jvBridge.callAdminRelay('bridge_runsheet_getPreviewUrl', { brideName: bride })
          .then(function(cfRes) {
            gasPreviewPromise.then(function(gasRes) {
              var gasUrl = gasRes && gasRes.ok && gasRes.data && gasRes.data.url;
              var cfUrl = cfRes && cfRes.ok && cfRes.data && cfRes.data.url;
              if ((gasUrl || '') !== (cfUrl || '')) {
                console.warn('[runsheet][shadow] getPreviewUrl mismatch for ' + bride + ': GAS="' + gasUrl + '" CF="' + cfUrl + '"');
              }
            });
          })
          .catch(function() {});
      }

      var previewPromise = (rsrMode === 'primary' && jvBridge.callAdminRelay)
        ? jvBridge.callAdminRelay('bridge_runsheet_getPreviewUrl', { brideName: bride })
            .then(function(cfRes) { return (cfRes && cfRes.ok && cfRes.data && cfRes.data.url) ? cfRes : gasPreviewPromise; })
            .catch(function() { return gasPreviewPromise; })
        : gasPreviewPromise;

      previewPromise
        .then(function (res) {
          var url = res && res.ok && res.data && res.data.url;
          if (!url) {
            if (win) win.close();
            showBanner((res && res.error) ? ('Could not open preview: ' + res.error) : 'No published runsheet to preview yet. Click Publish Runsheet first.', 'error');
            return;
          }
          if (win) win.location.href = url; else window.open(url, '_blank', 'noopener');
        })
        .catch(function (err) {
          if (win) win.close();
          showBanner('Could not open preview: ' + (err && err.message ? err.message : err), 'error');
        });
    }
    window.openRunsheetPreview = openRunsheetPreview;

    // Calendar attendee diff confirmation — Q2 lock of
    // BRIEFS/runsheet-publish-notification-audit-2026-05-19.md.
    // Before publish actually fires, fetch the diff between the current
    // calendar event attendees and the artists in state.activeTeam (the
    // planner's unsaved selection). If artists are being added or
    // removed, Google will send invitation / removal emails — make the
    // admin confirm explicitly. If there's no event yet or the diff is
    // empty, skip the modal.
    async function calendarDiffConfirmModal_(brideName, intendedNames) {
      // LAZY (finding #4 part 2, 2026-07-08): previewCalendarDiff is a ~23s cold read.
      // Awaiting it before showing anything froze the publish flow for ~23s. Now we
      // pop a light "Checking calendar attendees..." overlay INSTANTLY and resolve the
      // diff in the background. If there is no event / no attendee change / a fetch
      // error, we auto-continue (close the overlay, ok:true) exactly as before, so the
      // user never even sees it for the common no-change case. Only a genuine attendee
      // change swaps the overlay to the add/remove confirm with Proceed / Cancel.
      return new Promise(function(resolve) {
        var overlay = document.createElement('div');
        overlay.style.cssText = 'position:fixed;inset:0;background:rgba(30,23,22,0.6);display:flex;align-items:center;justify-content:center;z-index:10000;';
        overlay.innerHTML =
          '<div id="cdm-card" style="background:#fff;border-radius:1.5rem;padding:2rem;max-width:560px;width:90%;box-shadow:0 4px 24px rgba(47,37,36,0.15);max-height:90vh;overflow-y:auto;">'
          + '<h3 style="margin:0 0 10px;font-size:18px;color:var(--brand-dark,#1e1716);">Checking calendar attendees...</h3>'
          + '<p style="margin:0 0 14px;font-size:14px;color:var(--brand-text,#2f2524);line-height:1.5;"><i class="fa-solid fa-spinner fa-spin" style="margin-right:8px;color:var(--brand-primary,#c99591);"></i>Making sure we only email people whose invite actually changes. This takes a moment.</p>'
          + '<div style="display:flex;gap:10px;justify-content:flex-end;margin-top:8px;">'
          +   '<button id="cdm-cancel" style="padding:10px 20px;border:1px solid #e6dad4;background:#fff;border-radius:3rem;cursor:pointer;font-size:14px;color:var(--brand-text,#2f2524);">Cancel publish</button>'
          + '</div></div>';
        document.body.appendChild(overlay);

        var settled = false;
        function done(result) {
          if (settled) return;
          settled = true;
          if (overlay.parentNode) document.body.removeChild(overlay);
          resolve(result);
        }
        // Cancel during the check aborts the publish.
        overlay.querySelector('#cdm-cancel').addEventListener('click', function() { done({ ok: false, cancelledDuringCheck: true }); });
        overlay.addEventListener('click', function(e) { if (e.target === overlay && !settled) done({ ok: false, cancelledDuringCheck: true }); });

        jvBridge.callAdmin('bridge_runsheet_previewCalendarDiff', {
          brideName: brideName,
          intendedArtistNames: intendedNames || []
        }).then(function(res) {
          if (settled) return; // user cancelled while we were fetching
          var diff = (res && res.ok && res.data) ? res.data : null;
          if (!diff || !diff.ok) {
            if (window && window.console) console.warn('[calendarDiff] preview unavailable, proceeding without confirm:', res);
            return done({ ok: true, skipped: true });
          }
          if (diff.noEvent) return done({ ok: true, skipped: true, reason: 'no event yet' });
          if (!diff.changed) return done({ ok: true, skipped: true, reason: 'no attendee change' });

          // Genuine attendee change: swap the overlay to the confirm content.
          var addList = (diff.add || []).map(function(e) { return '<li style="margin-bottom:4px;">' + e + '</li>'; }).join('');
          var removeList = (diff.remove || []).map(function(e) { return '<li style="margin-bottom:4px;">' + e + '</li>'; }).join('');
          var addBlock = addList
            ? '<div style="margin-bottom:14px;"><div style="font-size:13px;font-weight:600;color:#1f6b46;margin-bottom:6px;">Will be ADDED to the event (' + diff.add.length + '):</div><ul style="margin:0;padding-left:20px;font-size:13px;color:var(--brand-text,#2f2524);line-height:1.5;">' + addList + '</ul></div>'
            : '';
          var removeBlock = removeList
            ? '<div style="margin-bottom:14px;"><div style="font-size:13px;font-weight:600;color:#9a2020;margin-bottom:6px;">Will be REMOVED from the event (' + diff.remove.length + '):</div><ul style="margin:0;padding-left:20px;font-size:13px;color:var(--brand-text,#2f2524);line-height:1.5;">' + removeList + '</ul><div style="font-size:12px;color:#7e706c;margin-top:6px;font-style:italic;">Google Calendar will email each removed person a removal notification.</div></div>'
            : '';
          var eventLine = diff.eventSummary ? '<div style="font-size:13px;color:#7e706c;margin-bottom:14px;">Event: <strong style="color:var(--brand-dark,#1e1716);">' + diff.eventSummary + '</strong></div>' : '';
          var card = overlay.querySelector('#cdm-card');
          card.innerHTML =
            '<h3 style="margin:0 0 10px;font-size:18px;color:var(--brand-dark,#1e1716);">Calendar attendees will change</h3>'
            + '<p style="margin:0 0 14px;font-size:14px;color:var(--brand-text,#2f2524);line-height:1.5;">Publishing will update the wedding-day Calendar event’s attendee list. Each person added or removed gets an email from Google Calendar.</p>'
            + eventLine + addBlock + removeBlock
            + '<div style="display:flex;gap:10px;justify-content:flex-end;margin-top:8px;">'
            +   '<button id="cdm-cancel2" style="padding:10px 20px;border:1px solid #e6dad4;background:#fff;border-radius:3rem;cursor:pointer;font-size:14px;color:var(--brand-text,#2f2524);">Cancel publish</button>'
            +   '<button id="cdm-ok" style="padding:10px 24px;background:var(--brand-primary,#c99591);color:#fff;border:none;border-radius:3rem;cursor:pointer;font-size:14px;font-weight:600;">Proceed</button>'
            + '</div>';
          card.querySelector('#cdm-ok').addEventListener('click', function() { done({ ok: true, diff: diff }); });
          card.querySelector('#cdm-cancel2').addEventListener('click', function() { done({ ok: false, diff: diff }); });
        }).catch(function(err) {
          if (settled) return;
          if (window && window.console) console.warn('[calendarDiff] error, proceeding without confirm:', err);
          done({ ok: true, skipped: true, error: getErrorMessage(err) });
        });
      });
    }

    // ─── FIRE-AND-FORGET PUBLISH (2026-07-08) ────────────────────────────────
    // BRIEFS/BUILD-runsheet-publish-fire-and-forget-2026-07-08.md
    // Publish returns in ~1s: the frozen appPublishRunsheet (40-55s snapshot build)
    // now runs in the GAS deferred queue, not on the request, so the worker no longer
    // 504s. Conflicts BLOCK INSTANTLY client-side, before any bridge call, so the user
    // never waits AND never misses a conflict. A lightweight status poll flips the
    // button back and shows the viewer link when the background job completes.

    // Instant, client-side conflict gate. Mirrors the frozen engine's publish
    // throw (RunsheetEngineV4.gs:234-238): reject when there are unscheduled
    // services OR same-artist / same-person timing overlaps. Reuses the builder's
    // own detectTimelineAlerts_() (the client analog of appDetectWorkspaceConflicts_).
    // Returns { blocked, reasons[] }.
    function publishConflictGate_() {
      var reasons = [];

      // 1. Unscheduled services (unassigned to any artist). The engine throws
      //    "No scheduled services are available to publish." / leaves them as
      //    conflicts. state.workspace.unscheduledServices is kept current by
      //    rederiveDraftState_ (line ~2514).
      var unscheduled = (state.workspace && state.workspace.unscheduledServices) || [];
      var realUnscheduled = unscheduled.filter(function(s) {
        return s && !s.isSystemBlock && s.category !== 'system';
      });
      if (realUnscheduled.length) {
        var names = realUnscheduled.map(function(s) {
          var who = String(s.personLabel || s.person || '').trim();
          var what = String(s.serviceLabel || s.service || 'service').trim();
          return who ? (who + ' - ' + what) : what;
        });
        reasons.push('Unscheduled services (assign an artist + time before publishing): ' + names.join(', '));
      }

      // 2. Same-artist / same-person timing overlaps. detectTimelineAlerts_
      //    returns error-severity alerts for exactly these two cases.
      try {
        var alerts = (typeof detectTimelineAlerts_ === 'function') ? detectTimelineAlerts_() : [];
        alerts.forEach(function(a) {
          if (a && a.severity === 'error' && a.message) reasons.push(a.message);
        });
      } catch (e) { /* fail open on the overlap check; the server throw is the backstop */ }

      // 3. Lead artist not a real roster member (BUG-debbie-lyons-runsheet-
      //    publish-b13-leadartist-2026-09-06). seedStaffingFromWorkspace_
      //    already filters selectedArtists down to real team.name entries
      //    before assembling activeTeam, but state.leadArtist itself is never
      //    checked against that same roster -- it's taken as-is from
      //    workspace.leadArtist or a restored localStorage draft (see the
      //    draft-restore comment above autosaveNow_). A stale/typo'd draft
      //    value (confirmed live: "Jessica Vegas", the business name, not an
      //    artist) survives silently until publish, where the frozen engine's
      //    Sheets dropdown validation rejects it with no useful context for
      //    the admin -- 3 failed publish attempts for one bride traced to
      //    this exact gap. Catching it here (before any network call, same
      //    pattern as checks 1+2 above) turns a cryptic server-side dropdown
      //    error into a clear, fixable message.
      var leadArtist = String(state.leadArtist || '').trim();
      if (leadArtist) {
        var rosterNames = uniqueNames(
          ((state.workspace && state.workspace.team) || []).map(function(t) { return String(t.name || '').trim(); }).filter(Boolean)
        );
        if (rosterNames.length && rosterNames.indexOf(leadArtist) === -1) {
          reasons.push('Lead artist "' + leadArtist + '" is not a real team member (found: ' +
            rosterNames.join(', ') + '). Re-pick the lead artist from the dropdown before publishing.');
        }
      }

      return { blocked: reasons.length > 0, reasons: reasons };
    }

    // Render the conflict list into the status banner (raw HTML) and stop.
    function showPublishConflictBanner_(reasons) {
      var items = (reasons || []).map(function(r) {
        return '<li style="margin:2px 0;">' + escapeHtml(String(r)) + '</li>';
      }).join('');
      var html =
        '<strong>Cannot publish yet.</strong> Resolve these first:' +
        '<ul style="margin:6px 0 0 18px;padding:0;list-style:disc;">' + items + '</ul>';
      showBanner(html, 'error', true);
    }

    var _publishPollTimer = null;
    var _publishPollStart = 0;
    // Async Save Draft poll (Phase 2). A save is lighter than a publish (no PDF /
    // notify / calendar tail), so it finishes faster; still allow generous headroom
    // for the shared minute-drain pickup + a cold 52s appSaveDraft.
    var _saveDraftPollTimer = null;
    var _saveDraftPollStart = 0;
    var JV_SAVEDRAFT_POLL_INTERVAL_MS = 3000;
    // 5 min ceiling. Live evidence (Rachel Anderson, 2026-07-22): a heavy bride
    // (12 services + full party) can take ~3 min for the background appSaveDraft to
    // finish, so the old 2.5 min ceiling gave up watching JUST before "done" landed
    // and showed the reopen-to-confirm fallback even though the save was seconds away.
    var JV_SAVEDRAFT_POLL_MAX_MS = 300000;
    var JV_PUBLISH_POLL_INTERVAL_MS = 4000;
    // Worst case the client must poll through (BUG-runsheet-publish-poll-timeout-2026-07-12):
    // up to ~60s for the shared minute-drain tick to pick the job up, plus the 40-55s frozen
    // build, plus GAS cold-start and queue contention. The old 120s ceiling gave up before a
    // busy-but-normal publish finished, so the admin saw "Still publishing... reopen in a
    // minute" and never got the inline success + viewer link.
    // 240s (raised from 210s, BRIEF-runsheet-publish-drainer-throughput-2026-07-12): a live
    // back-to-back publish measured ~226-240s for the SECOND (stacked) publish even after the
    // server self-continuation fix. That is the genuine floor for two heavy ~113s builds (GAS
    // cannot parallelise two builds in one execution). At 210s that stacked case tripped the
    // soft "still finishing" fallback banner every time. 240s covers the stacked floor so a
    // real back-to-back publish lands inline WITHOUT the banner, and still stays under the
    // ~5-min patience boundary. The slow-tick fallback + outer ceiling below remain the safety
    // net for anything heavier.
    var JV_PUBLISH_POLL_MAX_MS = 240000;
    // Past the fast ceiling, DON'T stop: fall back to a slow background tick so a publish
    // that ran unusually long still surfaces its success + viewer link inline instead of
    // forcing a manual reopen. Slow-polls every 15s for a further 2 min.
    var JV_PUBLISH_POLL_SLOW_INTERVAL_MS = 15000;
    var JV_PUBLISH_POLL_SLOW_MAX_MS = 120000;

    function publishSetButton_(disabled, html) {
      var btn = document.getElementById('publishBtn');
      if (!btn) return;
      btn.disabled = !!disabled;
      btn.innerHTML = html;
    }

    function publishResetButton_() {
      publishSetButton_(false, '<i class="fa-solid fa-paper-plane"></i> Publish Runsheet');
    }

    // Handle the terminal success UX (shared by the sync-fallback path + the poll).
    function publishOnSuccess_(data, notifyOpts) {
      data = data || {};
      state.timelineDirty = false;
      autosaveClear_(state.currentBride);
      var pubMsg = 'Runsheet published successfully.';
      if (data.pdfName) pubMsg += ' PDF saved: ' + data.pdfName;
      showBanner(pubMsg, 'success');
      if (data.pdfError) {
        showBanner('PDF EXPORT FAILED - the runsheet was published but no PDF was created. Bride will not be able to download it. Error: ' + (data.pdfWarning || 'Unknown'), 'error');
      } else if (data.pdfWarning) {
        showBanner('PDF export warning: ' + data.pdfWarning, 'warning');
      }
      // AUTO-OPEN-RAW-SHEET REMOVED (2026-08-21). data.url is
      // appBuildRunsheetUrl_(tab), a direct https://docs.google.com/spreadsheets/...
      // edit link to the internal working tab, an admin/engine artifact, never a
      // bride-facing or team-facing view. Every publish was silently forcing that
      // internal Sheet open in a new browser tab. Confirmed live, 2026-08-21, on
      // Georgia C's publish session (Jess: "upon clicking publish, after a while,
      // a new tab opens in browser that shows the old sheets connected runsheet").
      // The correct, bride-facing published view is data.viewerUrl (the token
      // runsheet-view URL), already handled correctly on the very next line via
      // showPublishedViewerLink_ - that stays, this line is the only one removed.
      if (data.viewerUrl) showPublishedViewerLink_(data.viewerUrl);

      // Update-flow dispatches (bride + team update notifications) run ONLY on the
      // synchronous fallback path. On the async path the drainer's
      // runsheetPublishAndNotify already handled first-publish notify; the manual
      // "send update" buttons remain available on the page for a re-publish.
      if (notifyOpts && notifyOpts.isUpdate && notifyOpts._dispatchUpdates) {
        publishDispatchUpdateNotifications_(notifyOpts);
      }
      loadWorkspace(state.currentBride);
    }

    function publishDispatchUpdateNotifications_(notifyOpts) {
      var updates = [];
      if (notifyOpts.sendEmail || notifyOpts.sendSms) {
        updates.push(
          jvBridge.callAdmin('bridge_runsheet_manualNotifyUpdate', {
            brideName: state.currentBride,
            reason: notifyOpts.reason,
            sendEmail: notifyOpts.sendEmail,
            sendSms: notifyOpts.sendSms,
            customSms: notifyOpts.customSms
          }).then(function(r) {
            if (r && r.ok && r.data && r.data.ok) {
              var bits = [];
              if (r.data.emailSent) bits.push('email');
              if (r.data.smsSent) bits.push('SMS');
              showBanner('Bride update notification sent (' + bits.join(' + ') + ').', 'success');
            } else {
              showBanner('Bride update notification failed: ' + ((r && r.data && r.data.errors) ? r.data.errors.join(', ') : (r && r.error) || 'unknown'), 'error');
            }
          }).catch(function(err) {
            showBanner('Bride update notification error: ' + getErrorMessage(err), 'error');
          })
        );
      }
      if (notifyOpts.notifyTeam) {
        updates.push(
          jvBridge.callAdmin('bridge_runsheet_notifyTeamOfUpdate', {
            brideName: state.currentBride,
            reason: notifyOpts.reason
          }).then(function(r) {
            if (r && r.ok && r.data && r.data.ok) {
              var count = (r.data.artistsNotified || []).length;
              var modeLabel = r.data.mode === 'per_artist' ? 'direct messages' : (r.data.mode === 'team_space' ? 'team Chat space' : 'mixed');
              showBanner('Team notified (' + count + ' artist' + (count === 1 ? '' : 's') + ' via ' + modeLabel + ').', 'success');
            } else {
              showBanner('Team notification failed: ' + ((r && r.data && r.data.errors) ? r.data.errors.join(', ') : (r && r.error) || 'unknown'), 'error');
            }
          }).catch(function(err) {
            showBanner('Team notification error: ' + getErrorMessage(err), 'error');
          })
        );
      }
      Promise.all(updates).catch(function() { /* individual banners already shown */ });
    }

    // Poll the async job until done / conflict / error / timeout.
    function publishStartPoll_(jobId, notifyOpts) {
      if (_publishPollTimer) { clearTimeout(_publishPollTimer); _publishPollTimer = null; }
      _publishPollStart = Date.now();

      function tick() {
        jvBridge.callAdmin('bridge_runsheet_publishStatus', { jobId: jobId })
          .then(function(res) {
            var d = (res && res.ok && res.data) ? res.data : (res && res.status ? res : null);
            var status = d ? d.status : null;
            if (status === 'done') {
              publishResetButton_();
              publishOnSuccess_(d, notifyOpts);
              return;
            }
            if (status === 'conflict') {
              publishResetButton_();
              // The fallback must NOT assert a cause the server did not give us
              // (2026-08-06). It used to read "The schedule still has unscheduled
              // conflicts", which on 2026-08-06 was shown for a publish rejected
              // over a placeholder ARTIST in the roster, while the schedule had
              // 7 services, 0 unscheduled and 0 conflicts. Hours were spent
              // re-checking a schedule that was already correct.
              // Say what is true instead: rejected, reason not supplied.
              showPublishConflictBanner_(d.reasons && d.reasons.length ? d.reasons : ['Publish was rejected, but the server did not say why. This is often the roster (a placeholder artist such as "ARTIST" is refused), not the schedule. Check the runsheet log before changing your times.']);
              return;
            }
            if (status === 'error') {
              publishResetButton_();
              showBanner('Publish failed: ' + ((d.reasons && d.reasons.join(', ')) || 'unknown error') + '. Please try again.', 'error');
              return;
            }
            // pending / running / partial: keep polling. Fast interval until the
            // fast ceiling, then a slow background tick so a long publish still
            // surfaces its success + viewer link inline (see publishScheduleNext_).
            if (!publishScheduleNext_(tick)) return;
          })
          .catch(function() {
            // Transient poll failure: same fast-then-slow schedule, so a network
            // blip mid-publish doesn't abandon the inline success.
            if (!publishScheduleNext_(tick)) return;
          });
      }
      _publishPollTimer = setTimeout(tick, JV_PUBLISH_POLL_INTERVAL_MS);
    }

    // Decide the next poll delay. Returns true if another tick was scheduled, false
    // if the final ceiling was hit (caller returns). Fast 4s ticks up to
    // JV_PUBLISH_POLL_MAX_MS, then slow 15s ticks for a further JV_PUBLISH_POLL_SLOW_MAX_MS
    // so a publish that ran unusually long still completes inline. Only the FINAL
    // ceiling flips the button back and shows the softened "still finishing" copy.
    function publishScheduleNext_(tick) {
      var elapsed = Date.now() - _publishPollStart;
      var finalCeiling = JV_PUBLISH_POLL_MAX_MS + JV_PUBLISH_POLL_SLOW_MAX_MS;
      if (elapsed > finalCeiling) {
        publishResetButton_();
        showBanner('Still finishing in the background. The viewer link will appear here the moment it is ready, or reopen this bride shortly to confirm.', 'warning');
        return false;
      }
      var delay = (elapsed > JV_PUBLISH_POLL_MAX_MS)
        ? JV_PUBLISH_POLL_SLOW_INTERVAL_MS
        : JV_PUBLISH_POLL_INTERVAL_MS;
      _publishPollTimer = setTimeout(tick, delay);
      return true;
    }

    // -------------------------------------------------------------------
    // Phase 4b (2026-10-13) — CF publish branch helpers.
    //
    // Live-controlled by `window.jvPublishOnCfEnabled`, emitted in
    // sections/jv-runsheet-builder.liquid from the shop metafield
    // `custom.publish_on_cf_enabled` (boolean). Default OFF.
    //
    // Shape: on REPUBLISH only (bride already has a published_runsheets row
    // in D1 per bridge_runsheet_hasPublishedRow), fire bridge_runsheet_publishCf
    // → poll jobUuid via window.jvJobPoll.pollJobStatus → render success.
    // Fresh publishes stay on the GAS path (bridge_runsheet_publishAsync),
    // unchanged.
    //
    // MUTED TAIL (Jess decision (c), 2026-10-13): the CF route writes D1 +
    // metafields + calendar event. PDF, bride push, team push, SMS, email,
    // team Chat side effects are NOT auto-fired by this branch; the admin
    // fires them via the existing admin UI buttons post-success. Zero new
    // GAS logic, zero new bridge target.
    //
    // ERROR POLICY: on CF-path error (hasPublishedRow fails, publishCf
    // rejects, poll returns 'failed'/'timeout'), fail LOUD rather than
    // silently falling through to GAS. During the soak, a silent GAS
    // fallback would mask real CF bugs; the admin can retry explicitly.
    // -------------------------------------------------------------------
    function publishCfFlagOn_(brideEmail) {
      // Issue #10 S3a (2026-10-16): CF is now the DEFAULT publish path for every
      // bride. Supersedes the Issue #10 S1 per-bride allowlist (which gated CF
      // behind `window.JV_PUBLISH_ON_CF_ALLOWLIST` while the shop-wide flag was
      // OFF) — now that the default is ON, the allowlist's job is subsumed by
      // this function, so it was removed here rather than left as dead code.
      // `brideEmail` is accepted (and still passed by the call site) for
      // signature compatibility but is unused — nothing is per-bride any more.
      // Emergency rollback: set `window.jvPublishCfForceGas = true` (via a shop
      // metafield toggle or inline script) and the GAS path takes over again.
      // The old opt-in `window.jvPublishOnCfEnabled` is still honoured for
      // explicit opt-out (`=== false`) during the migration soak.
      if (window.jvPublishCfForceGas === true || window.jvPublishCfForceGas === '1') return false;
      if (window.jvPublishOnCfEnabled === false) return false;
      return true;
    }

    async function publishIsRepublish_(brideName, brideEmail) {
      try {
        var res = await jvBridge.callAdmin('bridge_runsheet_hasPublishedRow', {
          brideName: brideName,
          brideEmail: brideEmail || ''
        });
        return !!(res && res.ok && res.exists);
      } catch (_) {
        // Transport error on the probe → treat as "not a republish" so the
        // GAS path handles it. The probe is a routing decision, not a
        // correctness gate; a false negative here just means we take the
        // safer existing path.
        return false;
      }
    }

    function publishCfOnSuccess_(cfResult, notifyOpts) {
      publishResetButton_();
      cfResult = cfResult || {};
      if (cfResult.partial) {
        showBanner(
          'Runsheet published. One or more metafields didn\'t finish writing — the bride hub may need up to a minute to refresh. ' +
          'Snapshot ID: ' + (cfResult.snapshotId || 'n/a'),
          'warning', true
        );
      } else {
        showBanner('Runsheet published.', 'success');
      }
      // Muted-tail per Jess (c): no auto-fire of PDF/push/SMS/email/Chat
      // on this branch. If notifyOpts say the admin wants to notify, remind
      // them to use the existing admin UI buttons on this page.
      if (notifyOpts && (notifyOpts.sendEmail || notifyOpts.sendSms || notifyOpts.notifyTeam)) {
        showBanner(
          'Reminder: you ticked notify. The CF publish doesn\'t auto-fire comms yet — ' +
          'use the Send SMS / Send Email / Notify Team buttons on this page to send them.',
          'info', true
        );
      }
      // Reload workspace to pick up the new published state (same shape the
      // GAS path's publishOnSuccess_ follow-up uses).
      if (state.currentBride) {
        loadWorkspace(state.currentBride, {}, { forceReload: true });
      }
    }

    async function tryPublishViaCf_(payload, notifyOpts) {
      var brideName = String(state.currentBride || payload.brideName || '').trim();
      var brideEmail = String((state.workspace && state.workspace.email) || '').trim();
      if (!brideName) return false; // can't route; let GAS handle

      var isRepub = await publishIsRepublish_(brideName, brideEmail);
      if (!isRepub) return false; // fresh publish → GAS

      // Build CF payload. Deliberately MINIMAL for Phase 4b first ship —
      // the CF handler auto-builds the safe metafield subset from inputs
      // (buildAutoMetafieldsSubset_ in runsheet-publish.mjs). jv_runsheet_json
      // auto-build is NOT yet implemented server-side (needs the full viewer-
      // blob composer, Phase 4b-2) — during soak the bride hub falls back
      // to live GAS for runsheet reads (no bride-facing regression).
      var cfPayload = {
        brideName: brideName,
        brideEmail: brideEmail,
        services: payload.services || [],
        staffingDraft: payload.staffingDraft || null,
        publishedBy: (window._jvAdminEmail || window._jvCustomerEmail || '') || null,
        tenantId: 'jessica-vegas'
        // calendarId/eventId/calendarDiff deliberately omitted — the current
        // builder state doesn't expose them client-side yet. CF handler
        // skips the calendar step cleanly when these are absent (that's
        // Phase 1's own documented fallback).
        // metafields deliberately omitted — CF handler auto-builds the
        // safe subset from brideContext when present. For this first ship,
        // no brideContext either; auto-build publishes just runsheet_published_at,
        // and the admin's post-success UI buttons re-fire the GAS deferred
        // tail which writes the fuller set.
      };

      var enqueueRes;
      try {
        enqueueRes = await jvBridge.callAdmin('bridge_runsheet_publishCf', cfPayload);
      } catch (e) {
        publishResetButton_();
        showBanner('CF publish could not start: ' + getErrorMessage(e) + '. Try again, or disable the CF flag to use the old path.', 'error', true);
        return true; // CF path owns this outcome; don't double-fire GAS.
      }

      if (!(enqueueRes && enqueueRes.ok && enqueueRes.jobUuid)) {
        publishResetButton_();
        var msg = (enqueueRes && enqueueRes.error) || 'CF publish rejected the request (no jobUuid returned).';
        showBanner(msg, 'error', true);
        return true;
      }

      publishSetButton_(true, '<i class="fa-solid fa-spinner fa-spin"></i> Publishing on CF...');

      if (!window.jvJobPoll || typeof window.jvJobPoll.pollJobStatus !== 'function') {
        publishResetButton_();
        showBanner('CF publish started but the client poller (jv-job-poll.js) is not loaded. Please reload the page and try again.', 'error', true);
        return true;
      }

      var finalStatus;
      try {
        finalStatus = await window.jvJobPoll.pollJobStatus(enqueueRes.jobUuid, {
          maxMs: 10 * 60 * 1000,
          pollIntervalMs: 3000,
          onProgress: function (s) {
            if (s && s.status === 'running') {
              publishSetButton_(true, '<i class="fa-solid fa-spinner fa-spin"></i> Publishing on CF (running)...');
            }
          }
        });
      } catch (e) {
        publishResetButton_();
        showBanner('CF publish poll error: ' + getErrorMessage(e), 'error', true);
        return true;
      }

      if (finalStatus && finalStatus.status === 'done') {
        publishCfOnSuccess_(finalStatus.result, notifyOpts);
        return true;
      }
      if (finalStatus && finalStatus.status === 'failed') {
        publishResetButton_();
        showBanner('CF publish failed: ' + (finalStatus.error || 'unknown error'), 'error', true);
        return true;
      }
      // 'timeout' or unexpected
      publishResetButton_();
      showBanner((finalStatus && finalStatus.error) || 'CF publish did not finish in the allowed time. The job may still be running — refresh in a minute to check.', 'warning', true);
      return true;
    }

    // ── Runsheet V2 (Sub-D lane E14, slice P7a, DARK) ─────────────────────────────────────────
    // Active ONLY when window.jvRunsheetV2Enabled === true (shop metafield custom.runsheet_v2_enabled,
    // default false) AND assets/jv-runsheet-v2.js loaded. With the flag false every hook below is a
    // no-op and the legacy save/publish paths run exactly as before. No GAS fallback inside V2.
    function rsbV2On_() {
      return window.jvRunsheetV2Enabled === true && !!window.jvRunsheetV2;
    }
    function rsbV2Ctx_() {
      return {
        getServices: function() { return state.sourceServicesDraft || []; },
        setServices: function(list) { state.sourceServicesDraft = list; state.timelineDirty = true; try { syncWorkspaceServicesFromSourceDraft_(); renderStage(); } catch (e) {} },
        getWorkspace: function() { return state.workspace; },
        getBrideName: function() { return state.currentBride; },
        escapeHtml: escapeHtml,
        showBanner: showBanner,
        formatTime: formatMinutesAsDisplayTime_,
        reload: function() { loadWorkspace(state.currentBride, null, { forceReload: true }); },
        markSaved: function() {},
        setPublishing: function(on) { if (on) publishSetButton_(true, '<i class="fa-solid fa-spinner fa-spin"></i> Publishing...'); else publishResetButton_(); },
        afterPublish: function() { state.timelineDirty = false; autosaveClear_(state.currentBride); }
      };
    }
    function rsbV2Init_() {
      if (!rsbV2On_()) return;
      // Re-bind on every V2 entry point. Shopify can re-evaluate the V2 asset
      // (for example after a section/preview refresh), which replaces
      // window.jvRunsheetV2 and its private state while this builder closure
      // survives. A permanent `.done` flag then leaves the replacement module
      // with ctx=null, so save/publish cannot reach the loaded workspace.
      // init() only assigns the current context and is intentionally idempotent.
      window.jvRunsheetV2.init(rsbV2Ctx_());
    }

    // Re-entry guard (Sub-D 2026-10-20): no guard existed before the Publishing... button state, so a
    // double-click during the CF address-save wait (up to 8s) or the notify/calendar modals could queue
    // two publishes. Flag set at entry, released in finally on every exit path. Applies flag-on and flag-off
    // (a second concurrent call can only duplicate modals/publish, so ignoring it is safe either way).
    async function publishRunsheet() {
      if (state._publishBusy) return;
      state._publishBusy = true;
      try { return await publishRunsheetInner_(); }
      finally { state._publishBusy = false; }
    }
    async function publishRunsheetInner_() {
      if (!state.currentBride) return;
      var locWait = rsbAwaitLocationSave_(8000);
      if (locWait) { await locWait; if (!state.currentBride) return; }

      // V2 publish (flag-gated, dark): same instant conflict gate, then ONE Cloudflare path for fresh and
      // republish. Legacy notify/calendar modals are bypassed: notifications are manual after publish.
      if (rsbV2On_()) {
        rsbV2Init_();
        applyLocalChanges(true);
        var gateV2 = publishConflictGate_();
        if (gateV2.blocked) { showPublishConflictBanner_(gateV2.reasons); return; }
        return window.jvRunsheetV2.publish();
      }

      // INSTANT CLIENT-SIDE CONFLICT GATE runs FIRST, before ANY modal or bridge call
      // (finding #4, 2026-07-08). A conflicted/unscheduled runsheet now blocks in well
      // under a second with the conflict list and ZERO network calls. Previously this
      // ran AFTER the notify + calendar-diff modals, so the user waited ~48s of slow
      // modal reads (getLastNotification ~24s + previewCalendarDiff ~23s) before being
      // told about a conflict. Only a conflict-free runsheet now proceeds to the modals
      // + background publish. The server-side appPublishRunsheet conflict throw remains
      // the backstop (surfaced via the poll + admin Chat).
      applyLocalChanges(true);
      var gate = publishConflictGate_();
      if (gate.blocked) {
        showPublishConflictBanner_(gate.reasons);
        return; // no modal, no bridge fire, no wait
      }

      var notifyOpts = await publishNotifyModal_();
      if (!notifyOpts.ok) return;

      // Q2 lock: confirm calendar attendee changes before publishing.
      var calConfirm = await calendarDiffConfirmModal_(state.currentBride, (state.activeTeam || []).slice());
      if (!calConfirm.ok) {
        showBanner('Publish cancelled.', 'info');
        return;
      }

      publishSetButton_(true, '<i class="fa-solid fa-spinner fa-spin"></i> Publishing...');

      const payload = buildCurrentOverrides();
      payload.brideName = state.currentBride;
      if (notifyOpts.isUpdate) {
        payload.sendEmail = false;
        payload.sendSms = false;
        payload.customSms = '';
        if (notifyOpts.reason) payload.changeNote = notifyOpts.reason;
        payload.isUpdate = true;
      } else {
        payload.sendEmail = notifyOpts.sendEmail;
        payload.sendSms   = notifyOpts.sendSms;
        payload.customSms = notifyOpts.customSms;
        // Explicit-tick force-send (decision 1, BRIEF 2026-07-10): a first publish
        // that re-notifies an already-notified bride must override the
        // already-notified guard. Only an explicit email/SMS tick sets force, so an
        // unticked re-publish still won't spam.
        if (notifyOpts.sendEmail || notifyOpts.sendSms) payload.force = true;
        // First-publish team Chat opt-in (decision 2): carried to the drainer so it
        // fires runsheetNotifyTeamOfUpdate after a successful async publish.
        payload.sendTeamChat = !!notifyOpts.notifyTeam;
        if (notifyOpts.reason) payload.changeNote = notifyOpts.reason;
      }

      if (Array.isArray(payload.services)) {
        // NAMED, NOT SILENT (2026-09-14, state/findings/URGENT-runsheet-publish-
        // silent-timing-drift-2026-09-14.md). prePublishSanitiseTiming_ can
        // pull a service's confirmed time backward to close a >15-min gap on
        // its artist lane -- AFTER the admin already confirmed a specific
        // value in the Save Draft diff dialog. A real publish (Isabelle
        // Sharp's Makeup service, entered/confirmed as 1:20 PM) came out at
        // 1:15 PM in the Timeline Preview, a value the admin never saw or
        // chose. Snapshot each service's pre-sanitise start time first so any
        // shift this function makes can be reported, not just silently sent.
        var _preSanitiseStarts_ = {};
        payload.services.forEach(function(s) {
          var sid = getServiceId_(s);
          if (sid) _preSanitiseStarts_[sid] = Number(s.startMin);
        });
        payload.services = prePublishSanitiseTiming_(payload.services);
        var _sanitiseShifts_ = [];
        payload.services.forEach(function(s) {
          var sid = getServiceId_(s);
          if (!sid || !(sid in _preSanitiseStarts_)) return;
          var before = _preSanitiseStarts_[sid];
          var after = Number(s.startMin);
          if (!isNaN(before) && !isNaN(after) && before !== after) {
            _sanitiseShifts_.push({
              id: sid,
              person: String(s.personLabel || s.person || 'Unknown').trim(),
              service: String(s.serviceLabel || s.service || '').trim(),
              beforeLabel: formatMinutesAsDisplayTime_(before),
              afterLabel: formatMinutesAsDisplayTime_(after)
            });
          }
        });
        if (_sanitiseShifts_.length) {
          var _shiftLines_ = _sanitiseShifts_.map(function(sh) {
            return escapeHtml(sh.person + ' ' + sh.service + ': ' + sh.beforeLabel + ' -> ' + sh.afterLabel);
          }).join('<br>');
          showBanner(
            'Closed a scheduling gap before publishing, this moved ' +
            (_sanitiseShifts_.length === 1 ? 'a time you set' : _sanitiseShifts_.length + ' times you set') +
            ':<br>' + _shiftLines_ +
            '<br>This is what was actually published. Check the Timeline Preview if this is not what you expected.',
            'warning',
            true
          );
        }
      }

      // BBS reconciliation pre-check (BRIEFS/SCOPING-runsheet-bbs-two-way-
      // reconciliation-2026-09-18.md, Jess's requirement 3): check BEFORE
      // publishing so a stale runsheet can't go out still disagreeing with
      // the BBS/_PartyMembers truth. Confirm-not-block, per Jess's own
      // wording -- "Publish anyway" stays available, never a hard stop. A
      // check failure (network/parse) never blocks publish -- degrades to
      // "no mismatch detected", the existing conflict gate above already
      // covers hard-stop cases.
      // DETECT + ADMIN-MANUAL (Jess's decision, LANE-bbs-runsheet-reconcile-
      // activation-2026-10-12, gap 4): "Fix first" no longer auto-corrects
      // anything (the old GAS bridge_runsheet_bbsReconcileApply really DID
      // rewrite SERVICE_DATA_JSON -- but from the SAME roster-row-count
      // entitlement source the whole reconcile rebuild exists to fix, so it
      // could silently write WRONG party service entries for a bride-only/
      // partial booking; this was a real correctness risk, not just an
      // inert no-op). The CF-routed apply only logs the drift as
      // acknowledged (bbs_reconcile_corrections, fire-and-forget, never
      // blocks). "Fix first" now cancels this publish attempt so the admin
      // can go edit the party/service entries herself, then click Publish
      // again -- same as before MINUS the (unsafe) auto-write.
      var bbsCheck = await bbsReconcileCheck_(state.currentBride);
      if (bbsCheck.checkFailed) {
        var publishUnchecked = await bbsReconcileUnavailableModal_();
        if (!publishUnchecked) {
          showBanner('Publish stopped: the Bridal Booking Summary check could not run. Please try again.', 'warning');
          publishResetButton_();
          return;
        }
      } else if (bbsCheck.ok && bbsCheck.skipped) {
        showBanner('Not compared with the Bridal Booking Summary (' + String(bbsCheck.skipped).replace(/_/g, ' ') + ').', 'warning');
      }
      if (bbsCheck.ok && !bbsCheck.inSync && bbsCheck.diff && bbsCheck.diff.length) {
        var proceedAnyway = await bbsReconcileDiffModal_(bbsCheck.diff, {
          title: 'This runsheet does not match the Bridal Booking Summary',
          applyLabel: 'Fix first, then I\'ll publish',
          skipLabel: 'Publish anyway'
        });
        if (proceedAnyway) {
          jvBridge.callAdmin('bridge_runsheet_bbsReconcileApply', { brideName: state.currentBride }).catch(function() {});
          showBanner('Publish cancelled. Please update the party/service entries above to match the Bridal Booking Summary, then click Publish again.', 'warning');
          publishResetButton_();
          return;
        }
        // "Publish anyway" chosen -- fall through and publish the payload as-is.
      }

      // Phase 4b (2026-10-13): CF publish branch, ON for a REPUBLISH ONLY and
      // ONLY when the shop metafield `custom.publish_on_cf_enabled` is true.
      // Fresh publishes stay on GAS unchanged. If the CF branch handles the
      // request (success OR a loud CF-side error with its own UI), we
      // short-circuit and DO NOT fire the GAS path. See tryPublishViaCf_
      // above for the full branch logic + error policy.
      if (publishCfFlagOn_((state.workspace && state.workspace.email) || '')) {
        try {
          var cfHandled = await tryPublishViaCf_(payload, notifyOpts);
          if (cfHandled) return;
        } catch (cfErr) {
          // tryPublishViaCf_ catches its own errors; this is defensive only.
          // Log and fall through to GAS so the admin isn't left spinning.
          try { console.warn('[publishRunsheet] CF branch unexpected throw, falling through to GAS', cfErr); } catch (_) {}
        }
      }

      jvBridge.callAdmin('bridge_runsheet_publishAsync', payload)
        .then(function(res) {
          // Kill-switch OFF: the backend ran the OLD synchronous path and returned
          // the full pubResult (tagged asyncFallback). Handle it exactly like today.
          if (res && res.asyncFallback) {
            publishResetButton_();
            if (res.ok) {
              publishOnSuccess_(res.data || {}, Object.assign({}, notifyOpts, { _dispatchUpdates: true }));
            } else if (res.validationFailed && res.reasons) {
              showPublishConflictBanner_(res.reasons);
            } else {
              showBanner(res.error || 'Publish failed', 'error');
            }
            return;
          }

          // Instant validation reject (bad payload), no job queued.
          if (res && res.ok === false) {
            publishResetButton_();
            if (res.validationFailed && res.reasons) {
              showPublishConflictBanner_(res.reasons);
            } else {
              showBanner(res.error || 'Publish failed', 'error');
            }
            return;
          }

          // Accepted: background job running. Poll for completion.
          if (res && res.ok && res.accepted && res.jobId) {
            publishSetButton_(true, '<i class="fa-solid fa-spinner fa-spin"></i> Publishing... we\'ll confirm when it\'s ready');
            showBanner('Publishing your runsheet in the background. This usually takes under a minute, you can keep working.', 'success');
            // On async success the poll reports 'done'; dispatch the update-flow
            // notifications (manualNotifyUpdate / notifyTeamOfUpdate) then, since the
            // drainer only handles first-publish auto-notify, not the re-publish update.
            publishStartPoll_(res.jobId, Object.assign({}, notifyOpts, { _dispatchUpdates: true }));
            return;
          }

          // Unexpected shape: reset + surface.
          publishResetButton_();
          showBanner((res && res.error) || 'Publish did not start. Please try again.', 'error');
        })
        .catch(function(err) {
          publishResetButton_();
          showBanner(getErrorMessage(err), 'error');
        });
    }

    function refreshSourceData() {
      if (!state.currentBride) return;
      const btn = document.getElementById("repairBtn");
      btn.disabled = true;
      btn.innerHTML = '<i class="fa-solid fa-spinner fa-spin"></i> Repairing...';
      showBanner("Checking invoices, Drive links, and cached runsheet data for this bride...", "success");

      jvBridge.callAdmin('bridge_runsheet_repairSources', { brideName: state.currentBride })
        .then(function(res) {
          btn.disabled = false;
          btn.innerHTML = '<i class="fa-solid fa-screwdriver-wrench"></i> Repair Source Data';
          if (res.ok) {
            var result = res.data || {};
            if (result.status === 'warn') {
              var failedSteps = (result.steps || [])
                .filter(function(s) { return s.result && s.result.status === 'error'; })
                .map(function(s) { return s.step; })
                .join(', ');
              showBanner(
                'Repair completed with warnings — some steps did not succeed' +
                (failedSteps ? ' (' + failedSteps + ')' : '') +
                '. Please check and try again.',
                'warning'
              );
            } else {
              showBanner('Source data repaired successfully.', 'success');
            }
            state.sourceServicesDraft = [];
            state.timelineOverrides = {};
            state.timelineDirty = false;
            autosaveClear_(state.currentBride);
            loadWorkspace(state.currentBride, { forceRebuild: true }, { forceReload: true });
          } else {
            showBanner(res.error || 'Repair failed', "error");
          }
        })
        .catch(function(err) {
          btn.disabled = false;
          btn.innerHTML = '<i class="fa-solid fa-screwdriver-wrench"></i> Repair Source Data';
          showBanner(getErrorMessage(err), "error");
        });
    }

    /**
     * Full source-data repair — calls bridge_runsheet_repairSourcesFull.
     * Runs legacy repair PLUS read-only diagnostic checks on every source:
     * runsheet tab, CRM row, form submissions cross-referenced with Drive
     * folders (inspiration / trial feedback / trial assist), calendar, and
     * booking details. This is what the "Repair Source Data" button calls.
     */
    function refreshSourceDataFull() {
      if (!state.currentBride) return;
      var btn = document.getElementById("repairBtn");
      var restoreBtn = function () {
        if (!btn) return;
        btn.disabled = false;
        btn.innerHTML = '<i class="fa-solid fa-screwdriver-wrench"></i> Repair Source Data';
      };
      if (btn) {
        btn.disabled = true;
        btn.innerHTML = '<i class="fa-solid fa-spinner fa-spin"></i> Repairing...';
      }
      showBanner("Running full source-data repair (invoices + tab + CRM + forms + calendar + booking)...", "success");

      jvBridge.callAdmin('bridge_runsheet_repairSourcesFull', { brideName: state.currentBride })
        .then(function(res) {
          restoreBtn();
          if (!res.ok) {
            showBanner(res.error || 'Full repair failed', "error");
            return;
          }
          var result = res.data || {};
          var steps = result.steps || [];
          var warnings = steps.filter(function(s) {
            return s.result && (s.result.status === 'warn' || s.result.status === 'error');
          });

          if (warnings.length > 0) {
            var summary = warnings.map(function(s) {
              return s.step + ': ' + (s.result.message || s.result.status);
            }).join(' | ');
            showBanner(
              'Repair done — ' + (steps.length - warnings.length) + '/' + steps.length + ' checks passed, ' +
              warnings.length + ' warning(s): ' + summary,
              'warning'
            );
          } else {
            showBanner('Source data repaired — all ' + steps.length + ' checks passed.', 'success');
          }

          // Log full step details to console for debugging (inc. asset lists per form section)
          console.log('[refreshSourceDataFull] Result:', JSON.stringify(result, null, 2));

          var brideForCheck = state.currentBride;
          var reloadWorkspace_ = function () {
            state.sourceServicesDraft = [];
            state.timelineOverrides = {};
            state.timelineDirty = false;
            autosaveClear_(brideForCheck);
            loadWorkspace(brideForCheck, { forceRebuild: true }, { forceReload: true });
          };

          // BBS reconciliation check (BRIEFS/SCOPING-runsheet-bbs-two-way-
          // reconciliation-2026-09-18.md): the legacy repair step above just
          // rewrote SERVICE_DATA_JSON from invoices -- check it against the
          // BBS/_PartyMembers truth NOW, since this is exactly the moment
          // invoice-derived drift would surface. Never silent: shows the diff
          // modal only if a real mismatch exists, admin can decline.
          bbsReconcileOfferCorrection_(brideForCheck).then(function() {
            reloadWorkspace_();
          });
        })
        .catch(function(err) {
          restoreBtn();
          showBanner(getErrorMessage(err), "error");
        });
    }

    function handleSourceDragStart(event) {
      state.dragServiceId = "";
      state.dragSource = "source";
      const index = Number(event.currentTarget.getAttribute("data-source-index"));
      const service = state.sourceServicesDraft[index];
      if (!service) return;
      state.dragServiceId = service.id;
      event.currentTarget.classList.add("dragging");
    }

    function handleTimelineDragStart(event) {
      const card = event.target.closest("[data-service-id]");
      if (!card) {
        try { console.log('[drag] dragstart fired but no [data-service-id] ancestor found', event.target); } catch (_) {}
        return;
      }
      state.dragSource = String(card.getAttribute("data-service-source") || "timeline").trim();
      state.dragServiceId = String(card.getAttribute("data-service-id") || "");
      state.dragStarted = true;   // flag so onclick knows to skip opening the modal
      card.classList.add("dragging");
      try {
        console.log('[drag] start', {
          dragServiceId: state.dragServiceId,
          dragSource: state.dragSource,
          laneArtist: card.getAttribute('data-lane-artist')
        });
      } catch (_) {}
    }

    function clearDragState() {
      state.dragServiceId = "";
      state.dragSource = "";
      state.dragStarted = false;
      Array.from(document.querySelectorAll(".dragging")).forEach(function(node) {
        node.classList.remove("dragging");
      });
      Array.from(document.querySelectorAll(".drop-slot.active, [data-drop-type].active")).forEach(function(node) {
        node.classList.remove("active");
      });
      Array.from(document.querySelectorAll(".timeline-grid-cell.drop-active, .timeline-empty-dropzone.drop-active, .timeline-working-area.drop-active, .unscheduled-panel.drop-active")).forEach(function(node) {
        node.classList.remove("drop-active");
      });
    }

  function getDropTarget_(event) {
  return event.target.closest("[data-drop-type]") || event.currentTarget;
}

  function handleDropOver(event) {
  event.preventDefault();
  event.stopPropagation();
  const dropTarget = getDropTarget_(event);
  if (!dropTarget) return;
  dropTarget.classList.add("active");

  if (
    dropTarget.classList.contains("timeline-grid-cell") ||
    dropTarget.classList.contains("timeline-empty-dropzone") ||
    dropTarget.classList.contains("timeline-working-area") ||
    dropTarget.classList.contains("unscheduled-panel")
  ) {
    dropTarget.classList.add("drop-active");
  }
}

 function handleDropLeave(event) {
  const dropTarget = getDropTarget_(event);
  if (!dropTarget) return;
  // Ignore leave events fired when cursor moves from the zone to one of its children.
  if (event.relatedTarget && dropTarget.contains(event.relatedTarget)) return;
  dropTarget.classList.remove("active");
  dropTarget.classList.remove("drop-active");
}

    function handleSourceDrop(event) {
      event.preventDefault();
      event.currentTarget.classList.remove("active");
      if (state.dragSource !== "source" || !state.dragServiceId) return;

      pushUndoSnapshot_();
      const targetIndex = Number(event.currentTarget.getAttribute("data-drop-index"));
      moveSourceService(state.dragServiceId, targetIndex);
      clearDragState();
      renderStage();
    }

    function handleUnscheduledDrop(event) {
  event.preventDefault();
  const dropTarget = getDropTarget_(event);
  if (dropTarget) {
    dropTarget.classList.remove("active");
    dropTarget.classList.remove("drop-active");
  }
  if (!state.dragSource || !state.dragServiceId) return;

  pushUndoSnapshot_();
  const serviceId = state.dragServiceId;
  moveServiceToArtistWorkingArea_(serviceId, "");
  rederiveDraftState_();
  state.timelineDirty = true;
  clearDragState();
  renderStage();
  showBanner("Service moved to Unscheduled. Click Recalculate Timeline to rebuild from the current draft.", "warning");
}

// Start Again: clear the schedule TIMES but KEEP each service's artist, so every
// card drops back into that artist's Working Area at the top of their lane (not a
// separate Unscheduled pool at the bottom, which was awkward to drag from). Jess
// 2026-07-22: "they should just go to the working area at the top". A service with
// no artist assigned (genuinely unassigned) still falls to the Unscheduled pool,
// as it must. Because sourceServicesDraft already holds one row per (person,
// service), a party member booked for hair + makeup re-appears as two cards.
// System blocks (Arrival/Set Up, Touch-Ups & Pack Down) and custom blocks are
// left untouched. Undoable via the undo snapshot.
async function startAgainClearSchedule() {
  var services = (state.sourceServicesDraft || []).filter(function(s) {
    return s && !s.isSystemBlock && !s.isCustomBlock;
  });
  if (!services.length) {
    showBanner("There are no scheduled services to clear.", "warning");
    return;
  }

  var ok = await confirmModal_(
    "Clear the schedule times and start again? Every service goes back to its artist's Working Area at the top as a drag-and-drop card (one per person per service, so hair and makeup stay separate), ready to drop onto the timeline. Each service keeps its assigned artist; your team, booking details and inspiration are kept. This does not affect any already-published runsheet until you re-publish.",
    "Yes, clear the times",
    "Keep it"
  );
  if (!ok) return;

  pushUndoSnapshot_();

  // ONE DROP AREA (Jess 2026-07-23): every service must stage in an artist's
  // Working Area at the top - nothing may fall to the bottom Unscheduled panel.
  // A service with no artist previously matched no working area and dropped to
  // the bottom (the unwanted 2nd drop zone). We now assign any artist-less
  // service to the first available lane so it stays at the top; the planner can
  // drag it to the right artist/time from there.
  var laneNamesForStart = (typeof getLaneNames === 'function') ? getLaneNames() : [];
  var fallbackArtist = (laneNamesForStart && laneNamesForStart.length) ? laneNamesForStart[0] : '';
  services.forEach(function(s) {
    var id = getServiceId_(s);
    // Keep the service's CURRENT artist so it stages in that artist's Working
    // Area, only the timing is cleared. If it has none, stage it in the first
    // lane's Working Area so it never lands in the bottom Unscheduled panel.
    var keepArtist = String(s.assignedArtist || s.artist || "").trim() || fallbackArtist;
    if (id) moveServiceToArtistWorkingArea_(id, keepArtist, { skipArtistMetaClear: true });
  });

  rederiveDraftState_();
  state.timelineDirty = true;
  clearDragState();
  renderStage();
  showBanner("Schedule times cleared. Each service is back in its artist's Working Area at the top. Drag them onto the timeline to rebuild.", "success");
}

function handleTimelineDrop(event) {
      event.preventDefault();
      const dropTarget = getDropTarget_(event);
      if (dropTarget) {
        dropTarget.classList.remove("active");
        dropTarget.classList.remove("drop-active");
      }
      try {
        console.log('[drag] drop', {
          dragServiceId: state.dragServiceId,
          dragSource: state.dragSource,
          dropTargetFound: !!dropTarget,
          dropType: dropTarget && dropTarget.getAttribute('data-drop-type'),
          dropArtist: dropTarget && dropTarget.getAttribute('data-drop-artist'),
          dropMinutes: dropTarget && dropTarget.getAttribute('data-drop-minutes')
        });
      } catch (_) {}
      if (!state.dragSource || !state.dragServiceId || !dropTarget) return;

      const dropType = String(dropTarget.getAttribute("data-drop-type") || "").trim();
      const targetArtist  = String(dropTarget.getAttribute("data-drop-artist") || "").trim();
      const rawMinutes    = dropTarget.getAttribute("data-drop-minutes");
      const targetMinutes = rawMinutes !== null ? Number(rawMinutes) : null;

      if (dropType === "working") {
        moveServiceToArtistWorkingArea_(state.dragServiceId, targetArtist);
        rederiveDraftState_();
        state.timelineDirty = true;
        showBanner("Service moved into the Working Area. Assign a time when you're ready.", "warning");
        renderStage();
      } else if (dropType === "timeline") {
        applyTimelineMove(state.dragServiceId, targetArtist, targetMinutes);
      }
      clearDragState();
    }

// ── Location blur — save the wedding address ─────────────────────────────
// Flag window.jvRsbLocationCf (shop metafield custom.runsheet_wedding_location_cf, default OFF).
// ON: Cloudflare only (App Proxy entity runsheet-wedding-location/save -> D1, mirrored to Sheet + Shopify
// by the server). NO GAS fallback. A failure is shown under the Address field, never swallowed.
// OFF: the original GAS bridge call, unchanged.
function rsbLocationStatus_(text, isError) {
  var el = document.getElementById('bookingLocationStatus');
  if (!el) return;
  el.textContent = text || '';
  el.style.color = isError ? 'var(--error-text)' : '';
  el.className = isError ? '' : 'muted-text';
}
function rsbLocationCfUrl_(action) {
  var base = String(window.jvRsbLocationCfBase || '').replace(/\/+$/, '');
  return base ? base + '/partner/runsheet-wedding-location/' + action : '';
}
// Draft/publish address source (Sub-D, 2026-10-20). With window.jvRsbLocationCf ON the wedding address is owned by
// Cloudflare (D1). Save Draft / publish / reload overrides all pass through buildCurrentOverrides(), so this is the one
// place that decides what address the GAS path receives: the last value D1 CONFIRMED for this bride (read overlay or a
// successful save), never the raw input box, so an unsaved/failed edit or stale GAS value cannot overwrite it.
// Before D1 has answered, falls back to the loaded workspace value (the GAS-sourced value, unchanged behaviour).
// Flag OFF: original behaviour (input value).
function rsbSetConfirmedLocation_(brideName, value) {
  state._cfLocation = { bride: String(brideName || ''), value: String(value == null ? '' : value) };
}
function rsbEffectiveLocation_(input) {
  if (window.jvRsbLocationCf !== true) {
    return input ? input.value : (state.workspace && state.workspace.location) || "";
  }
  var c = state._cfLocation;
  var confirmed = (c && c.bride === String(state.currentBride || '')) ? c.value : null;
  var effective = confirmed !== null ? confirmed : ((state.workspace && state.workspace.location) || "");
  if (input && String(input.value).trim() !== String(effective).trim()) {
    rsbLocationStatus_('Address not saved yet: the last saved address is being used. Click out of the field to save it.', true);
  }
  return effective;
}
// Await any in-flight CF address save (Sub-D, 2026-10-20). Returns null when nothing is pending (or flag OFF) so
// callers stay synchronous on the unchanged path. Otherwise a promise that ALWAYS resolves (save success, save
// failure, or timeout), so a failed/slow save never blocks Save Draft / publish; the confirmed value is then used
// and the red unsaved-status stays visible via rsbEffectiveLocation_.
function rsbAwaitLocationSave_(maxMs) {
  if (window.jvRsbLocationCf !== true || !state._locSavePending) return null;
  var pending = state._locSavePending;
  return new Promise(function(resolve) {
    var t = setTimeout(resolve, maxMs > 0 ? maxMs : 8000);
    pending.then(function() { clearTimeout(t); resolve(); }, function() { clearTimeout(t); resolve(); });
  });
}
function handleLocationBlur(input) {
  var brideName = state.currentBride;
  if (!brideName) return;
  var newLocation = (input && input.value) ? input.value.trim() : '';
  if (window.jvRsbLocationCf === true) {
    var url = rsbLocationCfUrl_('save');
    if (!url) { rsbLocationStatus_('Address not saved: connection unavailable. Reload and try again.', true); return; }
    var nf = (typeof window._jvNativeFetch === 'function' ? window._jvNativeFetch : window.fetch).bind(window);
    rsbLocationStatus_('Saving address...', false);
    var savePromise = nf(url, { method: 'POST', headers: { 'Content-Type': 'text/plain' }, credentials: 'same-origin',
      body: JSON.stringify({ brideName: brideName, brideUid: (state.workspace && state.workspace.brideUid) || '', location: newLocation }) })
      .then(function(r) { return r.json().then(function(j) { return { ok: r.ok && j && j.ok === true, j: j }; }, function() { return { ok: false, j: null }; }); })
      .then(function(res) {
        if (res.ok) {
          if (state.workspace) state.workspace.location = newLocation;
          rsbSetConfirmedLocation_(brideName, newLocation);
          rsbLocationStatus_(res.j && res.j.noop === true ? 'Address already up to date' : 'Address saved', false);
        } else {
          rsbLocationStatus_('Address not saved' + (res.j && res.j.error ? ' (' + String(res.j.error).replace(/_/g, ' ') + ')' : '') + '. Please try again.', true);
        }
      })
      .catch(function() { rsbLocationStatus_('Address not saved: no reply from the server. Check your connection and try again.', true); });
    state._locSavePending = savePromise;
    savePromise.then(function() { if (state._locSavePending === savePromise) state._locSavePending = null; });
    return;
  }
  jvBridge.call('bridge_runsheet_updateLocation', { brideName: brideName, location: newLocation })
    .then(function(res) {
      if (res && res.ok) {
        if (state.workspace) state.workspace.location = newLocation;
      }
    })
    .catch(function() {});
}

// ── Touch-drag shim — iPad/iOS native drag/drop via touch events ──────────
(function() {
  var touchDragEl   = null;
  var touchGhost    = null;
  var lastDropEl    = null;

  function findDraggable(el) {
    while (el) {
      if (el.getAttribute && el.getAttribute('draggable') === 'true') return el;
      el = el.parentElement;
    }
    return null;
  }

  function findDropEl(x, y) {
    if (touchGhost) touchGhost.style.pointerEvents = 'none';
    var under = document.elementFromPoint(x, y);
    if (touchGhost) touchGhost.style.pointerEvents = '';
    while (under) {
      if (under.getAttribute && under.getAttribute('data-drop-type')) return under;
      under = under.parentElement;
    }
    return null;
  }

  function setDropHighlight(el, on) {
    if (!el) return;
    if (on) {
      el.classList.add('active');
      if (el.classList.contains('timeline-grid-cell') ||
          el.classList.contains('timeline-empty-dropzone') ||
          el.classList.contains('timeline-working-area') ||
          el.classList.contains('unscheduled-panel')) {
        el.classList.add('drop-active');
      }
    } else {
      el.classList.remove('active', 'drop-active');
    }
  }

  document.addEventListener('touchstart', function(e) {
    var draggable = findDraggable(e.target);
    if (!draggable) return;
    var card = draggable.closest('[data-service-id]') || draggable;
    state.dragSource    = String(card.getAttribute('data-service-source') || 'timeline').trim();
    state.dragServiceId = String(card.getAttribute('data-service-id') || '');
    state.dragStarted   = true;
    card.classList.add('dragging');
    touchDragEl = draggable;
    // Ghost follows the finger
    touchGhost = draggable.cloneNode(true);
    touchGhost.style.cssText = 'position:fixed;pointer-events:none;opacity:0.65;z-index:9999;' +
      'width:' + draggable.offsetWidth + 'px;top:-200px;left:-200px;transition:none;';
    document.body.appendChild(touchGhost);
  }, { passive: true });

  document.addEventListener('touchmove', function(e) {
    if (!touchDragEl) return;
    var t = e.touches[0];
    touchGhost.style.top  = (t.clientY - 28) + 'px';
    touchGhost.style.left = (t.clientX - touchDragEl.offsetWidth / 2) + 'px';
    var dropEl = findDropEl(t.clientX, t.clientY);
    if (dropEl !== lastDropEl) {
      setDropHighlight(lastDropEl, false);
      lastDropEl = dropEl;
      setDropHighlight(dropEl, true);
    }
  }, { passive: true });

  document.addEventListener('touchend', function(e) {
    if (!touchDragEl) return;
    var t = e.changedTouches[0];
    var dropEl = findDropEl(t.clientX, t.clientY);
    setDropHighlight(lastDropEl, false);
    if (touchGhost) { document.body.removeChild(touchGhost); touchGhost = null; }
    touchDragEl = null;
    lastDropEl  = null;
    if (!dropEl || !state.dragServiceId) { clearDragState(); return; }
    var dropType = String(dropEl.getAttribute('data-drop-type') || '').trim();
    if (dropType === 'source') {
      pushUndoSnapshot_();
      moveSourceService(state.dragServiceId, Number(dropEl.getAttribute('data-drop-index')));
      clearDragState();
      renderStage();
    } else if (dropType === 'working' || dropType === 'timeline') {
      // Reuse the existing drop handler via a synthetic event-like object
      var fake = { preventDefault: function() {}, stopPropagation: function() {}, target: dropEl, currentTarget: dropEl };
      handleTimelineDrop(fake);
    } else {
      clearDragState();
    }
  }, { passive: true });
})();

    function moveSourceService(serviceId, targetIndex) {
      const current = state.sourceServicesDraft.slice();
      const fromIndex = current.findIndex(function(item) { return item.id === serviceId; });
      if (fromIndex === -1) return;

      const moved = current.splice(fromIndex, 1)[0];
      const safeTarget = Math.max(0, Math.min(targetIndex, current.length));
      current.splice(safeTarget, 0, moved);
      current.forEach(function(item, index) {
        item.order = index + 1;
      });
      state.sourceServicesDraft = current;
    }

    function getServiceId_(service) {
      return String((service && (service.id || service.itemId)) || "").trim();
    }

    function getServiceDurationMin_(service) {
      return Math.max(15, Number(service && (service.durationMin || service.duration || 60)));
    }

    function rangesOverlap_(aStart, aEnd, bStart, bEnd) {
      return aStart < bEnd && bStart < aEnd;
    }

    function findWorkspaceServiceById_(serviceId) {
      return ((state.workspace && state.workspace.services) || []).find(function(item) {
        return getServiceId_(item) === String(serviceId);
      }) || null;
    }

    function findSourceServiceById_(serviceId) {
      return (state.sourceServicesDraft || []).find(function(item) {
        return getServiceId_(item) === String(serviceId);
      }) || null;
    }

    function validateTimelinePlacement_(serviceId, targetArtist, startMin, endMin, serviceRef) {
      const services = ((state.workspace && state.workspace.services) || []).filter(function(item) {
        return !item.isSystemBlock && getServiceId_(item) !== String(serviceId);
      });

      const personName = String(serviceRef.personLabel || serviceRef.person || "").trim();

      for (let i = 0; i < services.length; i += 1) {
        const item = services[i];
        const itemStart = parseDisplayTimeToMinutes(item.startTime || item.start || "");
        const itemEnd   = parseDisplayTimeToMinutes(item.endTime   || item.end   || "");
        if (itemStart === null || itemEnd === null) continue;

        const itemArtist = String(item.assignedArtist || item.artist || "").trim();
        const itemPerson = String(item.personLabel || item.person || "").trim();

        if (targetArtist && itemArtist === targetArtist && rangesOverlap_(startMin, endMin, itemStart, itemEnd)) {
          return targetArtist + " is already booked at that time.";
        }
        if (personName && itemPerson === personName && rangesOverlap_(startMin, endMin, itemStart, itemEnd)) {
          return personName + " already has another service at that time.";
        }
      }

      return "";
    }

    // Clearing artistMeta wipes the setup + packdown rows from the
    // timeline grid. We call this whenever a service moves so the
    // planner doesn't see stale start/finish times that no longer
    // reflect the current service positions. They re-click the
    // "Start / Finish Times" button when ready to lock in the window.
    function clearArtistMetaOnMove_() {
      if (state.workspace && state.workspace.artistMeta) {
        state.workspace.artistMeta = {};
      }
    }

    function applyTimelineMove(serviceId, targetArtist, targetMinutes) {
      pushUndoSnapshot_();
      clearArtistMetaOnMove_();
      const sourceDraft      = findSourceServiceById_(serviceId);
      const workspaceService = findWorkspaceServiceById_(serviceId);
      const reference        = sourceDraft || workspaceService;
      if (!reference) return;

      // Skill check is now WARNING-ONLY (was previously a hard block
      // that silently rejected the drag).
      //
      // The team roster's canHair / canMakeup flags don't always match
      // the artist's actual capability — e.g. an artist tagged "ALL"
      // in the UI may have canHair=false in the underlying data. The
      // skill flags come from team-roster CRM columns that aren't
      // always kept in sync with the location/role chip the UI shows.
      // Blocking the drag in that case stops legitimate planner
      // assignments. Show the warning so Jess can fix the roster
      // record, but let the move proceed.
      if (targetArtist && !canArtistHandleService_(targetArtist, reference)) {
        showBanner(targetArtist + " is not marked for " + describeServiceSkill_(reference) + " in the team roster — proceeding anyway. Update their team record if this is wrong.", "warning");
      }

      if (targetMinutes === null || isNaN(targetMinutes)) {
        showBanner("Drop target time was missing.", "error");
        return;
      }

      const durationMin = getServiceDurationMin_(reference);
      const endMin      = targetMinutes + durationMin;

      // Check for person-level conflict (same person, overlapping time) — these are hard blocks.
      const personName = String(reference.personLabel || reference.person || "").trim();
      if (personName) {
        const services = ((state.workspace && state.workspace.services) || []).filter(function(item) {
          return !item.isSystemBlock && getServiceId_(item) !== String(serviceId);
        });
        for (let i = 0; i < services.length; i += 1) {
          const item = services[i];
          const itemStart = parseDisplayTimeToMinutes(item.startTime || item.start || "");
          const itemEnd   = parseDisplayTimeToMinutes(item.endTime   || item.end   || "");
          if (itemStart === null || itemEnd === null) continue;
          const itemPerson = String(item.personLabel || item.person || "").trim();
          if (itemPerson === personName && rangesOverlap_(targetMinutes, endMin, itemStart, itemEnd)) {
            showBanner(personName + " already has another service at that time.", "error");
            return;
          }
        }
      }

      // Bump any services on the same artist lane that would now overlap.
      if (targetArtist) {
        // Collect all same-artist services (excluding the one being moved) that conflict.
        // Bump them forward by durationMin and cascade — keep bumping until no overlap.
        const sameArtistServices = (state.sourceServicesDraft || []).filter(function(s) {
          const sid = getServiceId_(s);
          if (sid === String(serviceId)) return false;
          const sArtist = String(s.assignedArtist || s.artist || "").trim();
          return sArtist === targetArtist && isServiceTimed_(s);
        });

        // Sort by start time so we cascade in order.
        sameArtistServices.sort(function(a, b) {
          return (a.startMin || 0) - (b.startMin || 0);
        });

        // Iteratively push services that overlap with the insertion gap.
        let gapStart = targetMinutes;
        let gapEnd   = endMin;
        for (let i = 0; i < sameArtistServices.length; i += 1) {
          const s = sameArtistServices[i];
          const sStart = Number(s.startMin);
          const sEnd   = Number(s.endMin);
          if (!rangesOverlap_(gapStart, gapEnd, sStart, sEnd)) continue;
          // This service overlaps — push it so it starts at gapEnd.
          const sDuration = sEnd - sStart;
          const newStart  = gapEnd;
          const newEnd    = newStart + sDuration;
          applyTimedPlacementToService_(s, targetArtist, newStart, newEnd);
          state.timelineOverrides[getServiceId_(s)] = { startMin: newStart, endMin: newEnd, artist: targetArtist };
          // Advance the gap to cover the newly-shifted service.
          gapStart = newStart;
          gapEnd   = newEnd;
        }
      }

      state.timelineOverrides[serviceId] = {
        startMin: targetMinutes,
        endMin:   endMin,
        artist:   targetArtist
      };

      (state.sourceServicesDraft || []).forEach(function(s) {
        if (getServiceId_(s) !== String(serviceId)) return;
        applyTimedPlacementToService_(s, targetArtist, targetMinutes, endMin);
      });

      // Re-sort sourceServicesDraft to match the new temporal order so a subsequent
      // rebuild doesn't re-sequence services based on stale .order values.
      syncWorkspaceServicesFromSourceDraft_();
      const startByServiceId = {};
      (state.sourceServicesDraft || []).forEach(function(s) {
        const sid = getServiceId_(s);
        if (sid) startByServiceId[sid] = isServiceTimed_(s)
          ? Number(s.startMin !== undefined ? s.startMin : (parseDisplayTimeToMinutes(s.start || s.startTime || "") || 0))
          : Number.MAX_SAFE_INTEGER;
      });
      state.sourceServicesDraft.sort(function(a, b) {
        const aMin = startByServiceId[getServiceId_(a)] !== undefined ? startByServiceId[getServiceId_(a)] : (a.order || 0);
        const bMin = startByServiceId[getServiceId_(b)] !== undefined ? startByServiceId[getServiceId_(b)] : (b.order || 0);
        return aMin - bMin;
      });
      state.sourceServicesDraft.forEach(function(s, i) { s.order = i + 1; });
      rederiveDraftState_();
      state.timelineDirty = true;
      showBanner("Service moved. Any conflicting services on the same lane were bumped forward. Click Recalculate Preview to reflow.", "warning");
      renderStage();
    }

    function moveServiceToArtistWorkingArea_(serviceId, targetArtist, opts) {
      delete state.timelineOverrides[serviceId];
      // Start Again's bulk sweep opts out of the artistMeta clear (2026-07-19):
      // the confirm modal promises booking details are kept, and each
      // artist's individually-set setup/pack-down times are a booking detail
      // by that standard. Every other caller (single-service drag/drop, the
      // move-service modal) keeps clearing it exactly as before.
      if (!(opts && opts.skipArtistMetaClear)) {
        clearArtistMetaOnMove_();
      }

      (state.sourceServicesDraft || []).forEach(function(service) {
        if (getServiceId_(service) !== String(serviceId)) return;
        service.artist = String(targetArtist || "").trim();
        service.assignedArtist = service.artist;
        // Clear source so isServiceTimed_ no longer treats it as sheet-timed
        if (service.source === "runsheet_tab") service.source = "draft";
        clearServiceTimingFields_(service, true);
      });
    }

    function clearServiceTimingFields_(service, clearOverride) {
      if (!service) return;
      service.start = "";
      service.startTime = "";
      service.end = "";
      service.endTime = "";
      service.startMin = null;
      service.endMin = null;
      if (clearOverride) {
        service.startTimeOverride = "";
      }
    }

    function applyTimedPlacementToService_(service, targetArtist, startMin, endMin) {
      if (!service) return;
      const startLabel = formatMinutesAsDisplayTime_(startMin);
      const endLabel = formatMinutesAsDisplayTime_(endMin);
      service.artist = String(targetArtist || "").trim();
      service.assignedArtist = service.artist;
      service.startMin = startMin;
      service.endMin = endMin;
      service.start = startLabel;
      service.startTime = startLabel;
      service.end = endLabel;
      service.endTime = endLabel;
      service.startTimeOverride = startLabel;
    }

    function getLaneNames() {
      // Derive lane columns directly from skill pickers — never from workspace suggestions
      // or activeTeam which can be overwritten by backend on rebuild.
      return uniqueNames(
        [state.skillSelections.makeup, state.skillSelections.hair]
          .concat(state.skillSelections.additional || [])
          .filter(Boolean)
      );
    }

    function buildSchedulingOrder_(services) {
      const regular = [];
      let brideHair = null;
      let brideMakeup = null;

      (services || []).forEach(function(service) {
        const isBride = !!service.isBride || String(service.personLabel || service.person || "").trim().toLowerCase() === "bride";
        const label = String(service.serviceLabel || service.service || "").toLowerCase();
        if (isBride && label.indexOf("hair") > -1) {
          brideHair = service;
          return;
        }
        if (isBride && label.indexOf("make") > -1) {
          brideMakeup = service;
          return;
        }
        regular.push(service);
      });

      if (brideHair) regular.push(brideHair);
      if (brideMakeup) regular.push(brideMakeup);
      return regular;
    }

    function normaliseEditableServices(services) {
      return (services || []).map(function(service, index) {
        const id = String(service.id || service.itemId || "");
        const person = String(service.personLabel || service.person || "").trim();
        const serviceLabel = String(service.serviceLabel || service.service || "").trim();
        const assignedArtist = String(service.assignedArtist || service.artist || "").trim();
        // Fall back to startTimeOverride when start/startTime are empty (runsheet_tab source)
        var _resolvedStart = String(service.start || service.startTime || service.startTimeOverride || "").trim();
        var _resolvedEnd = String(service.end || service.endTime || "").trim();
        var _resolvedStartMin = (service.startMin !== undefined && service.startMin !== null && service.startMin !== "")
          ? Number(service.startMin)
          : parseDisplayTimeToMinutes(_resolvedStart);
        var _resolvedEndMin = (service.endMin !== undefined && service.endMin !== null && service.endMin !== "")
          ? Number(service.endMin)
          : parseDisplayTimeToMinutes(_resolvedEnd);
        return {
  id: id,
  itemId: id,
  pmId: String(service.pmId || service.pm_id || "").trim(),
  serviceInstanceId: String(service.serviceInstanceId || "").trim(),
  scheduleEntryId: String(service.scheduleEntryId || "").trim(),
  personId: String(service.personId || "").trim(),
  order: Number(service.order || (index + 1)),
  person: person,
  personLabel: person,
  role: String(service.role || "").trim(),
  service: serviceLabel,
  serviceLabel: serviceLabel,
  category: String(service.category || "").trim(),
  durationMin: Number(service.durationMin || service.duration || 0),
  assignedArtist: assignedArtist,
  artist: assignedArtist,
  start: _resolvedStart,
  startTime: _resolvedStart,
  end: _resolvedEnd,
  endTime: _resolvedEnd,
  startMin: _resolvedStartMin,
  endMin: _resolvedEndMin,
  isBride: !!service.isBride,
  notes: String(service.notes || "").trim(),
  source: String(service.source || "").trim(),
  hairArtist: String(service.hairArtist || "").trim(),
  makeupArtist: String(service.makeupArtist || "").trim(),
inspirationFolder: String(service.inspirationFolder || "").trim(),
inspirationSubmitted: !!service.inspirationSubmitted,
startTimeOverride: String(service.startTimeOverride || "").trim(),
isCustomBlock: !!service.isCustomBlock
};
      }).sort(function(a, b) {
        return Number(a.order || 0) - Number(b.order || 0);
      });
    }

    function normalisePayloadServices(services) {
      return (services || []).map(function(service, index) {
        return {
  id: String(service.id || service.itemId || ""),
  itemId: String(service.id || service.itemId || ""),
  pmId: String(service.pmId || service.pm_id || "").trim(),
  serviceInstanceId: String(service.serviceInstanceId || "").trim(),
  scheduleEntryId: String(service.scheduleEntryId || "").trim(),
  personId: String(service.personId || "").trim(),
  order: Number(service.order || (index + 1)),
  person: String(service.personLabel || service.person || "").trim(),
  personLabel: String(service.personLabel || service.person || "").trim(),
  role: String(service.role || "").trim(),
  service: String(service.serviceLabel || service.service || "").trim(),
  serviceLabel: String(service.serviceLabel || service.service || "").trim(),
  category: String(service.category || "").trim(),
  durationMin: Number(service.durationMin || 0),
  assignedArtist: String(service.assignedArtist || service.artist || "").trim(),
  artist: String(service.assignedArtist || service.artist || "").trim(),
  start: String(service.start || service.startTime || service.startTimeOverride || "").trim(),
  startTime: String(service.startTime || service.start || service.startTimeOverride || "").trim(),
  end: String(service.end || service.endTime || "").trim(),
  endTime: String(service.end || service.endTime || "").trim(),
  startMin: service.startMin !== undefined && service.startMin !== null && service.startMin !== "" ? Number(service.startMin) : null,
  endMin: service.endMin !== undefined && service.endMin !== null && service.endMin !== "" ? Number(service.endMin) : null,
  isBride: !!service.isBride,
  notes: String(service.notes || "").trim(),
  source: String(service.source || "").trim(),
  hairArtist: String(service.hairArtist || "").trim(),
  makeupArtist: String(service.makeupArtist || "").trim(),
inspirationFolder: String(service.inspirationFolder || "").trim(),
inspirationSubmitted: !!service.inspirationSubmitted,
startTimeOverride: String(service.startTimeOverride || "").trim(),
isCustomBlock: !!service.isCustomBlock
};
      });
    }

    function buildMetaLine(workspace) {
      const bits = [];
      if (workspace.weddingDate) bits.push(formatDateDisplay_(workspace.weddingDate));
      if (workspace.location) bits.push(workspace.location);
      if (workspace.readyBy) bits.push("ready by " + workspace.readyBy);
      bits.push(String(state.sourceServicesDraft.length || 0) + " source services");
      if (workspace.services && workspace.services.length) {
        bits.push(String(workspace.services.length) + " timed rows");
      }
      return bits.join(" • ");
    }

    function displayTimeToInput(value) {
      const mins = parseDisplayTimeToMinutes(value);
      if (mins === null) return "";
      const hours = Math.floor(mins / 60);
      const minutes = mins % 60;
      return String(hours).padStart(2, "0") + ":" + String(minutes).padStart(2, "0");
    }

    function inputTimeToDisplay(value) {
      const raw = String(value || "").trim();
      if (!raw || raw.indexOf(":") === -1) return "";
      const parts = raw.split(":").map(Number);
      if (parts.length !== 2 || isNaN(parts[0]) || isNaN(parts[1])) return "";
      let hours = parts[0];
      const minutes = parts[1];
      const suffix = hours >= 12 ? "PM" : "AM";
      if (hours === 0) hours = 12;
      if (hours > 12) hours -= 12;
      return hours + ":" + String(minutes).padStart(2, "0") + " " + suffix;
    }

    function formatMinutesAsDisplayTime_(value) {
      if (value == null || isNaN(value)) return "";
      const safe = Math.max(0, Number(value));
      return inputTimeToDisplay(
        String(Math.floor(safe / 60)).padStart(2, "0") + ":" + String(safe % 60).padStart(2, "0")
      );
    }

    function parseDisplayTimeToMinutes(value) {
      const match = String(value || "").trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
      if (!match) return null;

      let hours = Number(match[1]);
      const minutes = Number(match[2]);
      const suffix = match[3].toUpperCase();

      if (suffix === "AM" && hours === 12) hours = 0;
      if (suffix === "PM" && hours !== 12) hours += 12;

      return (hours * 60) + minutes;
    }

    function findEarliestStart(services) {
      const starts = (services || [])
        .map(function(item) { return parseDisplayTimeToMinutes(item.start || item.startTime || ""); })
        .filter(function(item) { return item !== null; });

      if (!starts.length) return "";
      const min = Math.min.apply(null, starts);
      return inputTimeToDisplay(
        String(Math.floor(min / 60)).padStart(2, "0") + ":" + String(min % 60).padStart(2, "0")
      );
    }

    function findLatestFinish(services) {
      const ends = (services || [])
        .map(function(item) { return parseDisplayTimeToMinutes(item.end || item.endTime || ""); })
        .filter(function(item) { return item !== null; });

      if (!ends.length) return "";
      const max = Math.max.apply(null, ends);
      return inputTimeToDisplay(
        String(Math.floor(max / 60)).padStart(2, "0") + ":" + String(max % 60).padStart(2, "0")
      );
    }

    function uniqueNames(values) {
      const out = [];
      (values || []).forEach(function(value) {
        const safe = String(value || "").trim();
        if (safe && out.indexOf(safe) === -1) out.push(safe);
      });
      return out;
    }

    function buildHydrateDebug_(workspace) {
      const safe = workspace || {};

      // Bucket counts
      var srcDraft = state.sourceServicesDraft || [];
      var timedCount = 0;
      var workingCount = 0;
      var unscheduledCount = 0;
      var runsheetTabCount = 0;
      var minBound = null;
      var maxBound = null;

      srcDraft.forEach(function(s) {
        if (s.isSystemBlock || s.isCustomBlock) return;
        var hasArtist = !!String(s.assignedArtist || s.artist || "").trim();
        var timed = isServiceTimed_(s);
        if (s.source === "runsheet_tab") runsheetTabCount++;
        if (hasArtist && timed) {
          timedCount++;
          var sMin = s.startMin != null ? Number(s.startMin) : parseDisplayTimeToMinutes(s.startTime || s.start || "");
          var eMin = s.endMin != null ? Number(s.endMin) : parseDisplayTimeToMinutes(s.endTime || s.end || "");
          if (sMin !== null && (minBound === null || sMin < minBound)) minBound = sMin;
          if (eMin !== null && (maxBound === null || eMin > maxBound)) maxBound = eMin;
        } else if (hasArtist && !timed) {
          workingCount++;
        } else if (!hasArtist) {
          unscheduledCount++;
        }
      });

      return {
        state: workspace ? "hydrated" : "empty",
        hydratedFrom: String(safe.hydratedFrom || "").trim(),
        brideName: String(safe.brideName || state.currentBride || "").trim(),
        weddingDate: String(safe.weddingDate || "").trim(),
        location: String(safe.location || "").trim(),
        readyBy: String(safe.readyBy || "").trim(),
        sourceServices: srcDraft.length,
        timedServices: timedCount,
        workingServices: workingCount,
        unscheduledServices: unscheduledCount,
        runsheetTabServices: runsheetTabCount,
        timingBounds: minBound !== null
          ? formatMinutesAsDisplayTime_(minBound) + " — " + formatMinutesAsDisplayTime_(maxBound || minBound)
          : "—",
        recommendedArtistCount: Number(safe.recommendedArtistCount || 0),
        selectedArtists: Array.isArray(safe.selectedArtists) ? safe.selectedArtists.join(", ") : "",
        leadArtist: String(safe.leadArtist || "").trim(),
        phone: String(safe.phone || "").trim(),
        message: workspace ? "Workspace received by browser" : "No workspace payload received"
      };
    }

    function describeArtist(artist) {
      const bits = [];
      if (artist.canLead) bits.push("Lead");
      if (artist.canHair && artist.canMakeup) bits.push("Hair + Makeup");
      else if (artist.canHair) bits.push("Hair");
      else if (artist.canMakeup) bits.push("Makeup");
      if (artist.region) bits.push(artist.region);
      return bits.join(" • ") || "Available artist";
    }

    // Canonical availability status for an artist on THIS wedding date.
    // Merges the poll response (artist.availability) with the self block-out
    // signal (artist.blockedOnDate). A block-out is a hard self-declared no,
    // so it forces "unavailable" regardless of any poll answer.
    // Returns one of: "available", "maybe", "unknown", "unavailable".
    function artistAvailStatus_(artist) {
      if (artist && artist.blockedOnDate) return "unavailable";
      var status = String((artist && artist.availability) || "").trim().toUpperCase();
      if (status === "AVAILABLE") return "available";
      if (status === "UNAVAILABLE") return "unavailable";
      if (status === "MAYBE") return "maybe";
      return "unknown";
    }

    // Sort rank: available first, maybe/unknown middle, unavailable last.
    function artistAvailRank_(artist) {
      var s = artistAvailStatus_(artist);
      if (s === "available") return 0;
      if (s === "unavailable") return 2;
      return 1; // maybe + unknown
    }

    // Stable sort a filtered artist list by availability (available to unavailable),
    // preserving original order within the same rank.
    function sortArtistsByAvailability_(list) {
      return (list || [])
        .map(function(a, i) { return { a: a, i: i }; })
        .sort(function(x, y) {
          var r = artistAvailRank_(x.a) - artistAvailRank_(y.a);
          return r !== 0 ? r : (x.i - y.i);
        })
        .map(function(x) { return x.a; });
    }

    function renderAvailBadge_(artist) {
      var s = artistAvailStatus_(artist);
      if (artist && artist.blockedOnDate) return '<span class="avail-badge avail-no">Blocked out</span>';
      if (s === "available") return '<span class="avail-badge avail-yes">Available</span>';
      if (s === "unavailable") return '<span class="avail-badge avail-no">Unavailable</span>';
      if (s === "maybe") return '<span class="avail-badge avail-maybe">Maybe</span>';
      return '<span class="avail-badge avail-pending">No Response</span>';
    }

    // Text suffix for <option> labels. NO emoji (brand rule: emoji only in SMS).
    function availDropdownLabel_(artist) {
      if (artist && artist.blockedOnDate) return " (Blocked out)";
      var s = artistAvailStatus_(artist);
      if (s === "unavailable") return " (Unavailable)";
      if (s === "maybe") return " (Maybe)";
      return "";
    }

    function renderAvailSummaryChips_(teamOptions) {
      var team = teamOptions || [];
      if (!team.length) return '';
      // Determine whether an availability poll has been fired for this wedding.
      // The workspace availability payload has responses/pendingArtists only when
      // a poll exists. If neither list is populated the poll has not been sent yet,
      // so "N No Response" would be contradictory. Suppress it in that case.
      var w = state.workspace || {};
      var avail = w.availability || {};
      var pollHasFired = (
        (Array.isArray(avail.responses) && avail.responses.length > 0) ||
        (Array.isArray(avail.pendingArtists) && avail.pendingArtists.length > 0)
      );
      var yes = 0, no = 0, maybe = 0, pending = 0;
      team.forEach(function(a) {
        var s = String(a.availability || "").trim().toUpperCase();
        if (s === "AVAILABLE") yes++;
        else if (s === "UNAVAILABLE") no++;
        else if (s === "MAYBE") maybe++;
        else pending++;
      });
      var chips = [];
      if (yes) chips.push('<span class="avail-badge avail-yes">' + yes + ' Available</span>');
      if (maybe) chips.push('<span class="avail-badge avail-maybe">' + maybe + ' Maybe</span>');
      if (no) chips.push('<span class="avail-badge avail-no">' + no + ' Unavailable</span>');
      // Only show the "No Response" badge when a poll exists; otherwise show
      // "Not polled" so the message matches the Team Availability panel below.
      if (pending) {
        chips.push(pollHasFired
          ? '<span class="avail-badge avail-pending">' + pending + ' No Response</span>'
          : '<span class="avail-badge avail-pending">Not polled</span>');
      }
      if (!chips.length) return '';
      return '<div class="chip-row" style="margin-top:6px;">' + chips.join(' ') + '</div>';
    }

    // Team availability poll panel for the runsheet builder.
    // Renders the Available / Maybe / Unavailable / Waiting-on breakdown from the
    // workspace availability payload (appGetAvailabilityForResponse_ via
    // team_app_get_availability). Zero new GAS calls on load, no comms fired here.
    // To send or re-fire a poll, links to the admin CRM calendar drawer.
    function renderTeamAvailabilityPanel_() {
      var w = state.workspace || {};
      var avail = w.availability || {};
      var responses = Array.isArray(avail.responses) ? avail.responses : [];
      var pending = Array.isArray(avail.pendingArtists) ? avail.pendingArtists : [];
      var total = avail.totalArtists || (responses.length + pending.length);

      // No poll sent yet: gentle empty state with a link to start one.
      if (!responses.length && !pending.length) {
        return (
          '<div class="rs-avail-panel is-empty">' +
            '<div class="rs-avail-head">' +
              '<span class="rs-avail-title">Team availability</span>' +
            '</div>' +
            '<p class="rs-avail-empty">No availability poll has gone out for this wedding yet. ' +
              'Fire one from the ' + renderPollManageLink_('calendar assignment screen') + ' and responses will show here.</p>' +
          '</div>'
        );
      }

      var buckets = { AVAILABLE: [], MAYBE: [], UNAVAILABLE: [] };
      responses.forEach(function(r) {
        var key = String(r.response || '').toUpperCase();
        if (buckets[key]) buckets[key].push(r.artistName || '');
      });

      function group(label, names, variant) {
        var body = names.length
          ? names.map(function(n) {
              return '<span class="avail-badge avail-' + variant + '">' + escapeHtml(n) + '</span>';
            }).join(' ')
          : '<span class="avail-badge avail-pending">None</span>';
        return (
          '<div class="rs-avail-group">' +
            '<div class="rs-avail-group-label">' + escapeHtml(label) + '</div>' +
            '<div class="chip-row">' + body + '</div>' +
          '</div>'
        );
      }

      return (
        '<div class="rs-avail-panel">' +
          '<div class="rs-avail-head">' +
            '<span class="rs-avail-title">Team availability</span>' +
            '<span class="rs-avail-count">' + responses.length + ' of ' + total + ' responded</span>' +
          '</div>' +
          group('Available', buckets.AVAILABLE, 'yes') +
          group('Maybe', buckets.MAYBE, 'maybe') +
          group('Unavailable', buckets.UNAVAILABLE, 'no') +
          group('Waiting on', pending, 'pending') +
          '<p class="rs-avail-foot">Need to send or re-send the poll? Use the ' +
            renderPollManageLink_('calendar assignment screen') + '.</p>' +
        '</div>'
      );
    }

    // Link to the admin CRM calendar drawer where the fire-poll flow lives.
    // Uses the #calendar hash so it opens the assign panel directly, not the
    // read-only standalone /pages/calendar page.
    function renderPollManageLink_(label) {
      return '<a href="/pages/admin-crm#calendar" target="_blank" rel="noopener" class="rs-avail-link">' +
        escapeHtml(label) + '</a>';
    }

    function getProvenance(workspace, key) {
      return (workspace && workspace.provenance && workspace.provenance[key]) || {};
    }

    var _bannerTimeout = null;
    // When a persistent roster-changed-since-publish WARNING is showing, transient
    // "success" banners (e.g. the profile-loader "Bride & party details loaded"
    // note that fires AFTER renderStatus) must NOT overwrite it. Otherwise the
    // roster nudge is clobbered and auto-hidden before the planner ever sees it.
    // Set in renderStatus, respected here. (Bug 2 fix, 2026-07-17.)
    var _rosterWarningActive = false;
    function showBanner(message, type, rawHtml) {
      // Don't let a transient success message clobber an active roster warning.
      if ((type || "success") === "success" && _rosterWarningActive) return;
      const banner = document.getElementById("statusBanner");
      banner.className = "status-banner " + (type || "success");
      banner.innerHTML =
        '<i class="fa-solid ' + (
          type === "error"
            ? 'fa-triangle-exclamation'
            : type === "warning"
              ? 'fa-circle-exclamation'
              : 'fa-circle-check'
        ) + '"></i>' +
        "<span>" + (rawHtml ? message : escapeHtml(message)) + "</span>";
      if (_bannerTimeout) clearTimeout(_bannerTimeout);
      if (type === "success") {
        _bannerTimeout = setTimeout(function() { hideBanner(); }, 5000);
      }
    }

    function hideBanner() {
      const banner = document.getElementById("statusBanner");
      banner.className = "status-banner";
      banner.innerHTML = "";
    }

    // ─── Published runsheet: public viewer link card ─────────────────────────────
    // Persistent (does not auto-dismiss like the success banner) so Jess can copy /
    // open the no-login viewer after a publish. Lives in .workspace, a sibling of
    // stageContent, so re-rendering the workspace does not wipe it. Cleared on a
    // genuine bride switch (see loadWorkspace).
    // Phase 3b: on opening an already-published bride, show the published-state
    // affordance so the admin knows she is editing the LIVE version and can jump
    // to exactly what the bride sees (no second tab). Reuses the existing
    // showPublishedViewerLink_ card + a one-time re-publish reminder banner.
    function showPublishedStateOnLoad_(workspace) {
      workspace = workspace || {};
      var isPublished = !!(workspace.isPublished ||
        String(workspace.runsheetLink || workspace.runsheetUrl || '').trim());
      if (!isPublished) return;
      // STALE-LINK FIX (Jess 2026-07-23): the tokenised no-login VIEWER page is the
      // correct destination, NOT the old Shopify PDF. Previously this preferred
      // workspace.runsheetViewLink (the CRM RUNSHEET VIEW LINK column = the old
      // PDF), so "View published" sent you to a stale PDF after a fresh publish.
      // Build the viewer URL from the runsheet viewer TOKEN first (workspace field
      // or the CDN island), and only fall back to the stored links if no token.
      var viewerUrl = '';
      var tok = String(workspace.runsheetViewerToken || workspace.viewerToken || '').trim();
      if (!tok) { try { tok = rsbViewerTokenFromIsland_(workspace.email || '') || ''; } catch (e) { tok = ''; } }
      if (tok) {
        viewerUrl = 'https://jessicavegas.com/pages/runsheet-view?token=' + encodeURIComponent(tok);
      } else {
        // FAIL CLOSED (2026-08-07). This used to fall back to
        // runsheetViewLink / runsheetLink / runsheetUrl. That is WRONG for a
        // bride-facing share link: runsheetLink is the CRM "RUNSHEET LINK"
        // column, which appBuildRunsheetUrl_ (RunsheetEngineV4.gs:3700-3702)
        // builds as an INTERNAL docs.google.com/spreadsheets/... URL. Handing a
        // bride that link gives her a Google sign-in wall for a sheet she can
        // never open, and it exposes an internal tool URL.
        //
        // Found by the 2026-08-07 readiness audit: both "View published
        // runsheet" and "Copy link" landed on the raw Runsheet spreadsheet for
        // a bride with no viewer token, so there was NO working way to send a
        // bride her link.
        //
        // A wrong link is worse than no link, because the admin cannot tell it
        // is wrong by looking at the button. So when there is no token we show
        // nothing and say why, rather than offering a destination that cannot
        // work. Same fail-closed reasoning as the token-viewer cache key: an
        // unidentifiable target must not silently resolve to a plausible wrong
        // one.
        //
        // rsbShareLinkUnavailable_ is only called from the on-load path; the
        // token is normally present, so this is the exception, not the norm.
        rsbShareLinkUnavailable_();
      }
      // Published-badge fix (Option A, BRIEFS/SCOPE-published-badge-must-
      // reflect-real-blob-state-2026-09-11.md). Only the REOPEN path (here)
      // has had time since the last publish for the deferred hook to have
      // verified the bride-facing blob write. The publish-success path
      // above fires immediately, before that verification can have run, so
      // it stays optimistic-only by design and does not pass this flag.
      if (viewerUrl) showPublishedViewerLink_(viewerUrl, workspace.runsheetBlobVerifiedAt);
      var when = String(workspace.publishedAt || '').trim();
      showBanner(
        'This runsheet is already published' + (when ? (' (' + when + ')') : '') +
        '. You are editing the live version the bride sees. Make your changes, then Publish again to update her.',
        'warning'
      );
    }

    // Shown instead of a share card when the bride has no runsheet viewer token
    // (2026-08-07). Deliberately explains the situation rather than silently
    // rendering nothing, so an admin knows the link is genuinely unavailable and
    // does not go hunting for a button that was never there.
    function rsbShareLinkUnavailable_() {
      try {
        showBanner(
          'No shareable bride link for this runsheet yet. Publish her again to ' +
          'generate one. Do not send the internal runsheet URL, the bride cannot open it.',
          'warning'
        );
      } catch (_) {}
    }

    // Published-badge fix (Option A, BRIEFS/SCOPE-published-badge-must-
    // reflect-real-blob-state-2026-09-11.md). `verifiedAt` is
    // workspace.runsheetBlobVerifiedAt: blank means unknown (legacy bride,
    // pre-fix, or the publish-success optimistic call site which never
    // passes it), an ISO timestamp means the bride-facing blob write was
    // confirmed, and 'FAILED:<iso>' means it was attempted and did NOT
    // land, i.e. the bride's own link currently shows the empty "coming
    // soon" state despite this badge saying "published". Only the FAILED
    // case changes rendering. Blank is treated the same as confirmed
    // (silent success) rather than shown as broken, since a legacy bride
    // predating this fix has no way to ever get a non-blank value and must
    // not be permanently flagged as broken for that reason alone.
    function showPublishedViewerLink_(url, verifiedAt) {
      if (!url) return;
      var workspace = document.querySelector(".jv-runsheet-builder .workspace");
      if (!workspace) return;
      var existing = document.getElementById("publishedViewerLink");
      if (existing) existing.remove();
      var isConfirmedBroken = String(verifiedAt || '').trim().indexOf('FAILED:') === 0;
      var card = document.createElement("div");
      card.id = "publishedViewerLink";
      card.className = "published-viewer-link" + (isConfirmedBroken ? " published-viewer-link--broken" : "");
      if (isConfirmedBroken) {
        card.innerHTML =
          '<div class="pvl-text">' +
            '<div class="pvl-title"><i class="fa-solid fa-triangle-exclamation"></i> Runsheet published, but the bride\'s link is not working</div>' +
            '<div class="pvl-sub">The published-runsheet write to her link did not confirm. She would currently see "coming soon" if she opened it. Publish again to retry, or check with the team if this keeps happening.</div>' +
          '</div>' +
          '<div class="pvl-actions">' +
            '<a class="btn btn-ghost" id="pvlOpenLink" href="' + escapeHtml(url) + '" target="_blank" rel="noopener">' +
              '<i class="fa-solid fa-up-right-from-square"></i> Open anyway</a>' +
          '</div>';
      } else {
        card.innerHTML =
          '<div class="pvl-text">' +
            '<div class="pvl-title"><i class="fa-solid fa-circle-check"></i> Runsheet published</div>' +
            '<div class="pvl-sub">Share the live, no-login viewer with the bride or your team.</div>' +
          '</div>' +
          '<div class="pvl-actions">' +
            '<a class="btn btn-primary" id="pvlOpenLink" href="' + escapeHtml(url) + '" target="_blank" rel="noopener">' +
              '<i class="fa-solid fa-up-right-from-square"></i> View published runsheet</a>' +
            '<button type="button" class="btn btn-ghost" id="pvlCopyBtn">' +
              '<i class="fa-solid fa-link"></i> Copy link</button>' +
          '</div>';
      }
      var hero = document.getElementById("heroStrip");
      if (hero && hero.parentNode === workspace) {
        workspace.insertBefore(card, hero.nextSibling);
      } else {
        workspace.insertBefore(card, workspace.firstChild);
      }
      var copyBtn = document.getElementById("pvlCopyBtn");
      if (copyBtn) {
        copyBtn.addEventListener("click", function() { pvlCopyLink_(url, copyBtn); });
      }
    }

    function pvlCopyLink_(url, btn) {
      function done() {
        if (!btn) return;
        var orig = btn.innerHTML;
        btn.innerHTML = '<i class="fa-solid fa-check"></i> Copied';
        setTimeout(function() { btn.innerHTML = orig; }, 2000);
      }
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(url).then(done, function() { pvlCopyFallback_(url, done); });
        } else {
          pvlCopyFallback_(url, done);
        }
      } catch (e) {
        pvlCopyFallback_(url, done);
      }
    }

    function pvlCopyFallback_(url, cb) {
      try {
        var ta = document.createElement("textarea");
        ta.value = url;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      } catch (e) {}
      if (cb) cb();
    }

    function getErrorMessage(err) {
      if (!err) return "Unknown error";
      if (typeof err === "string") return err;
      return err.message || String(err);
    }

    function escapeHtml(value) {
      return String(value == null ? "" : value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/\"/g, "&quot;")
        .replace(/'/g, "&#39;");
    }
    // ─── UNSCHEDULED SERVICES PANEL ──────────────────────────────────────────────

    function renderUnscheduledPanel_() {
  const orphans = (state.sourceServicesDraft || []).filter(function(s) {
    if (s.isSystemBlock || s.isCustomBlock) return false;
    var hasArtist = !!String(s.assignedArtist || s.artist || "").trim();
    // No artist → always show (even if timed) so nothing disappears from UI.
    if (!hasArtist) return true;
    // Has artist + timed → on the timeline, not here.
    if (isServiceTimed_(s)) return false;
    return false;
  });
      if (!orphans.length) return "";
      const cards = orphans.map(function(s) {
        const sid = escapeHtml(String(s.id || s.itemId || ""));
        return (
          '<div class="unscheduled-service-card" draggable="true"' +
            ' data-service-id="' + sid + '"' +
            ' data-service-source="unassigned"' +
            ' ondragstart="handleTimelineDragStart(event)"' +
            ' ondragend="clearDragState()">' +
            '<div class="working-service-topline">' +
              '<div class="unscheduled-card-meta">' + escapeHtml(s.category || "") + (s.durationMin ? ' • ' + s.durationMin + ' min' : '') + '</div>' +
              '<div class="service-action-group">' + renderServiceMoveButton_(sid) + '</div>' +
            '</div>' +
            '<div class="unscheduled-card-title">' + escapeHtml(resolveServiceDisplayName_(s)) + ' — ' + escapeHtml(s.serviceLabel || s.service || "Service") + '</div>' +
            renderServiceInspirationMeta_(s) +
          '</div>'
        );
      }).join('');
      return (
  '<div class="unscheduled-panel">' +
    '<div class="unscheduled-panel-head">' +
      '<i class="fa-solid fa-inbox"></i>' +
      '<div><strong>Unscheduled Services</strong><div class="panel-sub">These services are not currently assigned to a visible artist lane.</div></div>' +
    '</div>' +
    '<div class="timeline-empty-dropzone" ' +
      'data-drop-type="unscheduled" ' +
      'ondragover="handleDropOver(event)" ' +
      'ondragleave="handleDropLeave(event)" ' +
      'ondrop="handleUnscheduledDrop(event)" ' +
      'style="margin-bottom:12px;">Drop here to unschedule a service</div>' +
    '<div class="unscheduled-card-list">' + cards + '</div>' +
  '</div>'
);
    }

    // ─── TIMELINE CONFLICT ALERTS ────────────────────────────────────────────────

    function detectTimelineAlerts_() {
      const alerts = [];
      const services = (state.workspace && state.workspace.services) || [];
      const byPerson = {};
      const byArtist = {};

      const systemLabels = ["arrival & set up", "touch-ups & pack down"];
      services.forEach(function(s) {
        if (s.isSystemBlock) return;
        if (s.category === "system") return;
        const lbl = String(s.serviceLabel || s.service || "").toLowerCase();
        if (systemLabels.indexOf(lbl) > -1) return;
        const start = parseDisplayTimeToMinutes(s.startTime || s.start || "");
        const end   = parseDisplayTimeToMinutes(s.endTime   || s.end   || "");
        if (start === null || end === null) return;

        const person = String(s.personLabel || s.person || "").trim();
        const artist = String(s.assignedArtist || s.artist || "").trim();
        if (!person || person.toLowerCase() === "team") return;

        // MALFORMED-TIME GUARD (2026-08-21). A row with end <= start (e.g. a
        // corrupted "8:00 AM-7:00 AM" block) silently broke the overlap math
        // below: its own broken "end" value gets used as the comparison point
        // for the NEXT item in the sorted-by-start list, so a genuine overlap
        // with a DIFFERENT service can slip through undetected. Root incident,
        // confirmed live 2026-08-21: Georgia C's runsheet published with the
        // bride getting Makeup from BOTH Jessica and Mia at 8-9am simultaneously,
        // because the Mia row's corrupted end time (7:00am, before its own
        // 8:00am start) meant this same-person overlap check never fired, and
        // the publish-blocking conflict gate (publishConflictGate_) let it
        // through. Fail loud instead: flag the malformed row directly as its
        // own error (so it is visible and MUST be fixed) rather than silently
        // treating it as if it had no duration and letting it corrupt the
        // comparison for whichever service sorts next after it.
        if (end <= start) {
          alerts.push({
            severity: 'error',
            message: person + ': "' + (s.serviceLabel || s.service || 'service') +
              '" has an invalid time range (' + (s.startTime || s.start || '?') +
              ' to ' + (s.endTime || s.end || '?') + ', ends before or at its own start). Fix this time before publishing.'
          });
          return;
        }

        if (!byPerson[person]) byPerson[person] = [];
        byPerson[person].push({ start: start, end: end, label: s.serviceLabel || s.service || "" });
        if (artist) {
          if (!byArtist[artist]) byArtist[artist] = [];
          byArtist[artist].push({ start: start, end: end, label: s.serviceLabel || s.service || "", person: person });
        }
      });

      Object.keys(byPerson).forEach(function(person) {
        const items = byPerson[person].slice().sort(function(a, b) { return a.start - b.start; });
        for (var i = 1; i < items.length; i++) {
          if (items[i].start < items[i - 1].end) {
            alerts.push({ severity: 'error', message: person + ': timing clash between "' + items[i - 1].label + '" and "' + items[i].label + '".' });
          }
        }
      });

      Object.keys(byArtist).forEach(function(artist) {
        const items = byArtist[artist].slice().sort(function(a, b) { return a.start - b.start; });
        for (var i = 1; i < items.length; i++) {
          if (items[i].start < items[i - 1].end) {
            alerts.push({ severity: "error", message: artist + " is double-booked: " + items[i - 1].person + " and " + items[i].person + " overlap." });
          }
        }
      });

      return alerts;
    }

    function renderTimelineAlerts_() {
      const alerts = detectTimelineAlerts_();
      if (!alerts.length) return "";
      return (
        '<div class="timeline-alerts">' +
          alerts.map(function(a) {
            return '<div class="alert-card ' + escapeHtml(a.severity || "warning") + '">' +
              '<i class="fa-solid ' + (a.severity === "error" ? "fa-circle-xmark" : "fa-triangle-exclamation") + '" style="margin-right:6px;"></i>' +
              escapeHtml(a.message) +
            '</div>';
          }).join("") +
        '</div>'
      );
    }

    // ─── FINISH TIME MISMATCH ALERT ──────────────────────────────────────────────

    function renderFinishTimeMismatchAlert_() {
      var w = state.workspace;
      if (!w || !w.readyBy) return "";
      var readyByMin = parseDisplayTimeToMinutes(w.readyBy);
      if (readyByMin === null) return "";

      // Find the actual last real service end (excluding setup/packdown)
      var realServices = (w.services || []).filter(function(s) { return !s.isSystemBlock; });
      if (!realServices.length) return "";

      var lastEndMins = realServices.map(function(s) {
        return parseDisplayTimeToMinutes(s.endTime || s.end || "");
      }).filter(function(v) { return v !== null; });
      if (!lastEndMins.length) return "";

      var actualFinish = Math.max.apply(null, lastEndMins);
      var diff = actualFinish - readyByMin;

      // Allow 15 min tolerance
      if (Math.abs(diff) <= 15) return "";

      var actualFinishStr = formatMinutesAsDisplayTime_(actualFinish);
      var direction = diff > 0 ? "later" : "earlier";
      var diffMins = Math.abs(diff);

      return (
        '<div style="padding:14px 18px;border-radius:var(--radius-sm);background:var(--warn-bg);border:1px solid var(--warn-border-strong);color:var(--warn-text);margin-bottom:14px;font-size:13px;line-height:1.5;">' +
          '<div style="margin-bottom:8px;">' +
            '<i class="fa-solid fa-triangle-exclamation" style="margin-right:6px;"></i>' +
            '<strong>Finish time mismatch:</strong> Last service ends at ' + escapeHtml(actualFinishStr) +
            ' (' + diffMins + ' min ' + direction + ' than Ready By ' + escapeHtml(w.readyBy) + ')' +
          '</div>' +
          '<div style="display:flex;flex-wrap:wrap;gap:8px;">' +
            '<button class="btn btn-soft" style="font-size:11px;padding:6px 14px;" onclick="overrideReadyByToActual_()">' +
              '<i class="fa-solid fa-clock" style="margin-right:4px;"></i> Update Ready By to ' + escapeHtml(actualFinishStr) +
            '</button>' +
            '<button class="btn btn-soft" style="font-size:11px;padding:6px 14px;" onclick="shiftScheduleToReadyBy_()">' +
              '<i class="fa-solid fa-arrows-left-right" style="margin-right:4px;"></i> Shift schedule to finish by ' + escapeHtml(w.readyBy) +
            '</button>' +
          '</div>' +
        '</div>'
      );
    }

    function overrideReadyByToActual_() {
      var realServices = ((state.workspace && state.workspace.services) || []).filter(function(s) { return !s.isSystemBlock; });
      var lastEndMins = realServices.map(function(s) {
        return parseDisplayTimeToMinutes(s.endTime || s.end || "");
      }).filter(function(v) { return v !== null; });
      if (!lastEndMins.length) return;

      var actualFinish = Math.max.apply(null, lastEndMins);
      var newReadyBy = formatMinutesAsDisplayTime_(actualFinish);
      state.workspace.readyBy = newReadyBy;
      state.timelineDirty = true;
      document.getElementById("bMeta").innerText = buildMetaLine(state.workspace);
      renderHeroStrip();
      renderStage();
      showBanner("Ready By updated to " + newReadyBy + ". Click Save Draft to persist.", "success");
    }

    function shiftScheduleToReadyBy_() {
      var w = state.workspace;
      if (!w || !w.readyBy) return;
      var targetMin = parseDisplayTimeToMinutes(w.readyBy);
      if (targetMin === null) return;

      // Find current actual finish
      var realServices = (state.sourceServicesDraft || []).filter(function(s) {
        return !s.isSystemBlock && !s.isCustomBlock && isServiceTimed_(s);
      });
      var lastEndMins = realServices.map(function(s) {
        return s.endMin !== null && s.endMin !== undefined ? Number(s.endMin) : parseDisplayTimeToMinutes(s.end || s.endTime || "");
      }).filter(function(v) { return v !== null && Number.isFinite(v); });
      if (!lastEndMins.length) return;

      var currentFinish = Math.max.apply(null, lastEndMins);
      var shiftBy = targetMin - currentFinish;
      if (shiftBy === 0) return;

      // Shift all timed services by the delta
      (state.sourceServicesDraft || []).forEach(function(s) {
        if (!isServiceTimed_(s)) return;
        var startMin = s.startMin !== null && s.startMin !== undefined ? Number(s.startMin) : parseDisplayTimeToMinutes(s.start || s.startTime || "");
        var endMin = s.endMin !== null && s.endMin !== undefined ? Number(s.endMin) : parseDisplayTimeToMinutes(s.end || s.endTime || "");
        if (startMin === null || endMin === null) return;

        var newStart = Math.max(0, startMin + shiftBy);
        var newEnd = Math.max(newStart + 15, endMin + shiftBy);
        applyTimedPlacementToService_(s, s.assignedArtist || s.artist || "", newStart, newEnd);
      });

      rederiveDraftState_();
      state.timelineDirty = true;
      document.getElementById("bMeta").innerText = buildMetaLine(state.workspace);
      renderHeroStrip();
      renderStage();
      showBanner("Schedule shifted by " + (shiftBy > 0 ? "+" : "") + shiftBy + " minutes to finish by " + w.readyBy + ". Click Save Draft to persist.", "success");
    }

    // ─── RIGHT-CLICK CONTEXT MENU ─────────────────────────────────────────────────

    function openServiceContextMenu(event, serviceId) {
      event.preventDefault();
      event.stopPropagation();

      state.contextMenu.open = true;
      state.contextMenu.serviceId = serviceId;

      const menu = document.getElementById("svcContextMenu");
      if (!menu) return;

      // Position near cursor/button, keep within viewport
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const menuW = 210;
      const menuH = 180;
      const x = Math.min(event.clientX, vw - menuW - 8);
      const y = Math.min(event.clientY, vh - menuH - 8);

      menu.style.left = x + "px";
      menu.style.top  = y + "px";
      menu.className = "context-menu open";
    }

    function closeServiceContextMenu() {
      state.contextMenu.open = false;
      state.contextMenu.serviceId = "";
      const menu = document.getElementById("svcContextMenu");
      if (menu) { menu.className = "context-menu"; }
    }

    function ctxEditService() {
      const id = state.contextMenu.serviceId;
      closeServiceContextMenu();
      openServiceEditModal(id);
    }

    function inlineDurationOverride_(id) {
      var match = (state.sourceServicesDraft || []).find(function(s) { return (s.id || s.itemId) === id; });
      if (!match) return;
      openDurationOverrideModal_(id, match.durationMin || match.duration || 60);
    }

    function ctxOverrideTiming() {
      const id = state.contextMenu.serviceId;
      closeServiceContextMenu();
      const match = (state.sourceServicesDraft || []).find(function(s) { return (s.id || s.itemId) === id; });
      if (!match) return;
      openDurationOverrideModal_(id, match.durationMin || 45);
    }

    function ctxEditNote() {
      const id = state.contextMenu.serviceId;
      closeServiceContextMenu();
      const match = (state.sourceServicesDraft || []).find(function(s) { return (s.id || s.itemId) === id; });
      if (!match) return;
      openNoteEditModal_(id, match.notes || "");
    }

    // --- Duration Override Modal helpers ---
    var _durationOverrideTargetId = "";
    function openDurationOverrideModal_(serviceId, currentDuration) {
      _durationOverrideTargetId = serviceId;
      document.getElementById("durationOverrideInput").value = currentDuration;
      document.getElementById("durationOverrideModal").classList.add("open");
      document.getElementById("durationOverrideInput").focus();
    }
    function closeDurationOverrideModal_() {
      document.getElementById("durationOverrideModal").classList.remove("open");
      _durationOverrideTargetId = "";
    }
    function confirmDurationOverride_() {
      var num = parseInt(document.getElementById("durationOverrideInput").value, 10);
      if (isNaN(num) || num < 1) { showBanner("Enter a valid duration in minutes.", "error"); return; }
      var id = _durationOverrideTargetId;
      closeDurationOverrideModal_();
      pushUndoSnapshot_();
      var match = (state.sourceServicesDraft || []).find(function(s) { return (s.id || s.itemId) === id; });
      if (match) { match.durationMin = num; match.duration = num; }
      ((state.workspace && state.workspace.services) || []).forEach(function(s) {
        if ((s.id || s.itemId) === id) { s.durationMin = num; s.duration = num; }
      });
      syncWorkspaceServicesFromSourceDraft_();
      state.timelineDirty = true;
      renderStage();
      showBanner("Duration set to " + num + "min. Recalculate to update timing.", "warning");
    }

    // --- Note Edit Modal helpers ---
    var _noteEditTargetId = "";
    function openNoteEditModal_(serviceId, currentNote) {
      _noteEditTargetId = serviceId;
      document.getElementById("noteEditInput").value = currentNote;
      updateNoteCharCount_();
      document.getElementById("noteEditModal").classList.add("open");
      document.getElementById("noteEditInput").focus();
    }
    function closeNoteEditModal_() {
      document.getElementById("noteEditModal").classList.remove("open");
      _noteEditTargetId = "";
    }
    function updateNoteCharCount_() {
      var len = (document.getElementById("noteEditInput").value || "").length;
      document.getElementById("noteCharCount").textContent = len + "/200";
    }
    function confirmNoteEdit_() {
      var id = _noteEditTargetId;
      var note = String(document.getElementById("noteEditInput").value || "").trim();
      closeNoteEditModal_();
      var match = (state.sourceServicesDraft || []).find(function(s) { return (s.id || s.itemId) === id; });
      if (!match) return;
      match.notes = note;
      ((state.workspace && state.workspace.services) || []).forEach(function(s) {
        if ((s.id || s.itemId) === id) s.notes = note;
      });
      syncWorkspaceServicesFromSourceDraft_();
      renderStage();
    }

    async function ctxRemoveService() {
      const id = state.contextMenu.serviceId;
      closeServiceContextMenu();
      var removeOk = await confirmModal_("Remove this service from the timeline draft?", "Remove", "Cancel");
      if (!removeOk) return;
      pushUndoSnapshot_();
      state.sourceServicesDraft = (state.sourceServicesDraft || []).filter(function(s) {
        return (s.id || s.itemId) !== id;
      });
      state.customBlocksDraft = (state.customBlocksDraft || []).filter(function(s) {
        return (s.id || s.itemId) !== id;
      });
      rederiveDraftState_();
      state.timelineDirty = true;
      renderStage();
      showBanner("Service removed. Click Recalculate Preview to recalculate.", "warning");
    }

    // Close context menu on any outside click
    document.addEventListener("click", function() {
      if (state.contextMenu.open) closeServiceContextMenu();
    });

    // ─── SKILL PICKERS ────────────────────────────────────────────────────────────

    function renderSkillPickerCard_(label, selectId, artists, selectedValue) {
      const selected = String(selectedValue || "").trim();
      var selectedArtist = selected ? (artists || []).find(function(a) { return a.name === selected; }) : null;
      var skillKey = selectId === 'skillMakeupSelect' ? 'makeup' : 'hair';
      return (
        '<div class="skill-picker-card">' +
          '<h5>' + escapeHtml(label) + '</h5>' +
          '<select id="' + escapeHtml(selectId) + '" onchange="handleSkillPickerChange()">' +
            '<option value="">none</option>' +
            sortArtistsByAvailability_(artists || []).map(function(a) {
              const sel = a.name === selected ? ' selected' : '';
              return '<option value="' + escapeHtml(a.name) + '"' + sel + '>' + escapeHtml(a.name) + availDropdownLabel_(a) + '</option>';
            }).join('') +
          '</select>' +
          '<div class="skill-picker-current' + (selected ? '' : ' empty') + '">' +
            (selected
              ? renderArtistAvatar_(selected, "artist-avatar--md") + '<span>' + escapeHtml(selected) + '</span>' + renderAvailBadge_(selectedArtist) +
                '<a href="#" class="skill-picker-remove" onclick="unpickSkillArtist(\'' + escapeHtml(skillKey) + '\');return false;" title="Remove from team">&times;</a>'
              : '<span>No artist selected yet</span>') +
          '</div>' +
        '</div>'
      );
    }

    function handleSkillPickerChange() {
  state._hasUserEditedStaffing = true;

  const makeup = document.getElementById("skillMakeupSelect");
  const hair   = document.getElementById("skillHairSelect");

  if (makeup) state.skillSelections.makeup = String(makeup.value || "").trim();
  if (hair)   state.skillSelections.hair   = String(hair.value || "").trim();

  rebuildActiveTeamFromSkillSelections_();
  sanitizeSourceServicesDraftAssignments_();
  syncArtistsIntoBridalPartyDraft_();
  syncWorkspaceServicesFromSourceDraft_();
  refreshLeadDropdownOptions_();
  refreshAdditionalArtistCards_();
renderStage();
}

async function unpickSkillArtist(skillKey) {
  var artistName = state.skillSelections[skillKey];
  if (!artistName) return;

  // Check if artist has assigned services
  var assigned = (state.sourceServicesDraft || []).filter(function(s) {
    var a = String(s.assignedArtist || s.artist || "").trim();
    return a === artistName;
  });

  if (assigned.length) {
    var serviceLabels = assigned.map(function(s) {
      return s.serviceLabel || s.service || "service";
    }).join(", ");
    var unpickOk = await confirmModal_(artistName + " is assigned to: " + serviceLabels + ".\n\nRemoving them will unassign those services. Continue?", "Remove", "Cancel");
    if (!unpickOk) return;
  }

  state._hasUserEditedStaffing = true;
  state.skillSelections[skillKey] = "";

  rebuildActiveTeamFromSkillSelections_();
  sanitizeSourceServicesDraftAssignments_();
  syncArtistsIntoBridalPartyDraft_();
  syncWorkspaceServicesFromSourceDraft_();
  refreshLeadDropdownOptions_();
  refreshAdditionalArtistCards_();
  renderStage();
}

function refreshLeadDropdownOptions_() {
  const sel = document.getElementById("leadArtistSelect");
  if (!sel) return;

  const names = uniqueNames(state.activeTeam || []);
  if (!names.length) {
    sel.innerHTML = '<option value="">Select artists above first</option>';
    state.leadArtist = "";
    return;
  }

  if (!state.leadArtist || names.indexOf(state.leadArtist) === -1) {
    state.leadArtist = names[0];
  }

  sel.innerHTML = names.map(function(name) {
    return '<option value="' + escapeHtml(name) + '"' + (name === state.leadArtist ? ' selected' : '') + '>' + escapeHtml(name) + '</option>';
  }).join('');
}

function refreshAdditionalArtistCards_() {
  const host = document.getElementById("additionalArtistCards");
  if (!host || !state.workspace) return;
  host.innerHTML = renderAdditionalArtistCards_(Array.isArray(state.workspace.team) ? state.workspace.team : []);
}

    function rebuildActiveTeamFromSkillSelections_() {
      const next = [];
      if (state.skillSelections.makeup) next.push(state.skillSelections.makeup);
      if (state.skillSelections.hair && next.indexOf(state.skillSelections.hair) === -1) {
        next.push(state.skillSelections.hair);
      }
      (state.skillSelections.additional || []).forEach(function(name) {
        if (name && next.indexOf(name) === -1) next.push(name);
      });

      state.activeTeam = uniqueNames(next);

      if (state.leadArtist && state.activeTeam.indexOf(state.leadArtist) === -1) {
        state.leadArtist = state.activeTeam[0] || "";
      }
      if (!state.leadArtist && state.activeTeam.length) {
        state.leadArtist = state.activeTeam[0];
      }
    }

  function toggleAdditionalArtist(name) {
  state._hasUserEditedStaffing = true;

  const additional = (state.skillSelections.additional || []).slice();
  const idx = additional.indexOf(name);

  if (idx > -1) additional.splice(idx, 1);
  else additional.push(name);

  state.skillSelections.additional = additional;
  rebuildActiveTeamFromSkillSelections_();
  sanitizeSourceServicesDraftAssignments_();
  syncArtistsIntoBridalPartyDraft_();
  syncWorkspaceServicesFromSourceDraft_();
  refreshLeadDropdownOptions_();
  refreshAdditionalArtistCards_();
  renderStage();
}

async function openAddArtistModal() {
  const next = await promptModal_("Type the artist name to add to this run:", "e.g. Sarah");
  if (next === null) return;
  const safe = String(next || "").trim();
  if (!safe) return;

  state._hasUserEditedStaffing = true;
  if (!state.skillSelections.additional) state.skillSelections.additional = [];
  if (state.skillSelections.additional.indexOf(safe) === -1 &&
      safe !== state.skillSelections.makeup &&
      safe !== state.skillSelections.hair) {
    state.skillSelections.additional.push(safe);
  }

  rebuildActiveTeamFromSkillSelections_();
  refreshLeadDropdownOptions_();
  refreshAdditionalArtistCards_();
}

function renderArtistTimingControls_() {
  const artists = state.activeTeam || [];
  if (!artists.length) return "";

  return (
    '<div class="panel" style="margin-bottom:16px;">' +
      '<h4>Setup &amp; Pack Down</h4>' +
      '<div class="panel-sub">Automatically sets each artist\'s setup 15 min before their first service and pack down 15 min after their last service.</div>' +
      '<div style="margin-top:12px;">' +
        '<button class="btn btn-soft" onclick="applyArtistSetupPackdown_()">' +
          '<i class="fa fa-clock"></i> Apply Start / Finish Times' +
        '</button>' +
      '</div>' +
    '</div>'
  );
}

function updateArtistMetaTime_(artistName, field, value) {
  if (!state.workspace) return;
  if (!state.workspace.artistMeta) state.workspace.artistMeta = {};
  if (!state.workspace.artistMeta[artistName]) state.workspace.artistMeta[artistName] = {};

  state.workspace.artistMeta[artistName][field] = value ? inputTimeToDisplay(value) : "";
  state.timelineDirty = true;
}

function renderAdditionalArtistCards_(teamOptions) {
      const skillArtists = [state.skillSelections.makeup, state.skillSelections.hair].filter(Boolean);
      const additional = state.skillSelections.additional || [];
      const extras = (teamOptions || []).filter(function(a) {
        return skillArtists.indexOf(a.name) === -1;
      });

      if (!extras.length) {
        var allPicked = (teamOptions || []).filter(function(a) {
          return skillArtists.indexOf(a.name) > -1 || additional.indexOf(a.name) > -1;
        });
        return (
          '<div class="team-auto-summary">' +
            '<h4>All team members assigned</h4>' +
            '<p>All <strong>' + allPicked.length + '</strong> available artist' + (allPicked.length !== 1 ? 's are' : ' is') +
              ' already slotted into services for this wedding. To change someone\'s role, edit their service below.</p>' +
            '<div class="team-auto-chips">' +
              allPicked.map(function(a) {
                return (
                  '<span class="artist-pill">' +
                    renderArtistAvatar_(a.name, "artist-avatar--sm") +
                    '<span>' + escapeHtml(a.name) + '</span>' +
                  '</span>'
                );
              }).join('') +
            '</div>' +
          '</div>'
        );
      }

      return extras.map(function(a) {
        const isChecked = additional.indexOf(a.name) > -1;
        return (
          '<label class="team-card' + (isChecked ? ' active' : '') + '" style="cursor:pointer;">' +
            '<input type="checkbox"' + (isChecked ? ' checked' : '') +
              ' onchange="toggleAdditionalArtist(\'' + escapeHtml(a.name) + '\')">' +
            renderArtistAvatar_(a.name, "artist-avatar--md") +
            '<div>' +
              '<div class="team-name">' + escapeHtml(a.name) + ' ' + renderAvailBadge_(a) + '</div>' +
              '<div class="team-notes">' + escapeHtml(describeArtist(a)) + '</div>' +
            '</div>' +
          '</label>'
        );
      }).join('');
    }

    // ─── BRIDAL PARTY BUILDER ────────────────────────────────────────────────────

   function groupServicesIntoBridalPartyDraft_(services) {
  const people = {};
  const ordered = [];

  // Blank-name party-member split (CODE fix 2026-07-10).
  // Party service rows carry personLabel = the member's name and NO personId
  // (see runsheetPersonSourcer_buildSourceServices_). When two distinct party
  // members have a blank name, they key to the SAME "|role" bucket below and
  // collapse into one card, so the second member's service blocks are silently
  // merged away by uniqueStrings_. The hub sourcer numbers blank slots
  // "Bridesmaid 1/2", but a saved-draft or published-tab roster is NOT
  // re-synthesised, so we do it here (the shared funnel for every source path).
  // Each blank member gets a stable synthetic label + personId so distinct
  // members never merge, and the split survives the draft round-trip (personId
  // persists via flattenBridalPartyDraftToServices_).
  //
  // Distinct-member detection: a single member never has the same service block
  // twice (one "Hair", one "Makeup"), so within a run of same-role blank rows a
  // REPEATED service label marks the start of the next member. Rows arrive
  // order-sorted and a member's blocks are emitted contiguously by the backend,
  // so this is deterministic.
  const _syntheticByRow = {};
  (function assignSyntheticIdentities_() {
    const runCounter = {};            // role => members synthesised so far
    const currentMember = {};         // role => { index, seenLabels:{} }
    (services || []).forEach(function(service, index) {
      const safe = service || {};
      if (safe.isCustomBlock || safe.isSystemBlock) return;
      if (String(safe.personId || "").trim()) return;      // real identity, leave alone
      const rowPerson = String(safe.personLabel || safe.person || "").trim();
      if (rowPerson) return;                               // named members key fine on their name
      const rowRole = normalisePartyRole_(
        String(safe.role || inferRoleFromPerson_(rowPerson, { isBride: !!safe.isBride }) || "").trim(),
        rowPerson,
        !!safe.isBride
      );
      if (rowRole === "Bride") return;                     // bride handled separately
      const label = String(safe.serviceLabel || safe.service || "").trim().toLowerCase();
      let cur = currentMember[rowRole];
      const isNewMember = !cur || (label && cur.seenLabels[label]);
      if (isNewMember) {
        runCounter[rowRole] = (runCounter[rowRole] || 0) + 1;
        cur = { index: runCounter[rowRole], seenLabels: {} };
        currentMember[rowRole] = cur;
      }
      if (label) cur.seenLabels[label] = true;
      _syntheticByRow[index] = {
        label: rowRole + " " + cur.index,
        key: "synthparty|" + rowRole.toLowerCase() + "|" + cur.index
      };
    });
  })();

  (services || []).forEach(function(service, index) {
    const safe = service || {};
    if (safe.isCustomBlock || safe.isSystemBlock) return;

    const synthetic = _syntheticByRow[index];
    const rawPerson = synthetic
      ? synthetic.label
      : String(safe.personLabel || safe.person || "").trim();
    const rawRole = String(
      safe.role ||
      inferRoleFromPerson_(rawPerson, { isBride: !!safe.isBride }) ||
      ""
    ).trim();

    const normalisedRole = normalisePartyRole_(rawRole, rawPerson, !!safe.isBride);

    // Force all bride-like rows into ONE bride bucket
    const personKey = normalisedRole === "Bride"
      ? "bride"
      : (synthetic
          ? synthetic.key
          : String(safe.personId || buildPartyPersonKey_(rawPerson, normalisedRole)).trim());

    if (!people[personKey]) {
      people[personKey] = {
        id: personKey,
        personId: personKey,
        pmId: String(safe.pmId || safe.pm_id || "").trim(),
        personLabel: normalisedRole === "Bride"
          ? (rawPerson || (state.workspace && state.workspace.brideName) || "Bride")
          : rawPerson,
        name: normalisedRole === "Bride"
          ? (rawPerson || (state.workspace && state.workspace.brideName) || "Bride")
          : rawPerson,
        profileName: String(safe.profileName || "").trim(),
        role: normalisedRole,
        isBride: normalisedRole === "Bride",
        servicesBooked: [],
        hairstyleChosen: String(safe.hairstyleChosen || "").trim(),
        hairArtist: String(safe.hairArtist || "").trim(),
        makeupArtist: String(safe.makeupArtist || "").trim(),
        assignedArtist: String(safe.assignedArtist || safe.artist || "").trim(),
        inspirationFolder: String(safe.inspirationFolder || safe.folderUrl || "").trim(),
        inspirationSubmitted: !!safe.inspirationSubmitted,
        inspirationGallery: Array.isArray(safe.inspirationGallery) ? safe.inspirationGallery.slice() : [],
        trialFolderUrl: String(safe.trialFolderUrl || "").trim(),
        trialGallery: Array.isArray(safe.trialGallery) ? safe.trialGallery.slice() : [],
        trialAssistSubmitted: safe.trialAssistSubmitted,
        notes: String(safe.notes || "").trim(),
        sourceOrder: index
      };
      ordered.push(people[personKey]);
    }

    const person = people[personKey];
    if (!String(person.pmId || "").trim() && String(safe.pmId || safe.pm_id || "").trim()) {
      person.pmId = String(safe.pmId || safe.pm_id || "").trim();
    }
    const serviceLabel = String(safe.serviceLabel || safe.service || "").trim();

    if (serviceLabel && person.servicesBooked.indexOf(serviceLabel) === -1) {
      person.servicesBooked.push(serviceLabel);
    }

    // Prefer richer values as we merge more rows into same person
    if (!person.profileName && safe.profileName) person.profileName = String(safe.profileName).trim();
    if (!person.hairstyleChosen && safe.hairstyleChosen) person.hairstyleChosen = String(safe.hairstyleChosen).trim();
    if (!person.hairArtist && safe.hairArtist) person.hairArtist = String(safe.hairArtist).trim();
    if (!person.makeupArtist && safe.makeupArtist) person.makeupArtist = String(safe.makeupArtist).trim();
    if (!person.assignedArtist && (safe.assignedArtist || safe.artist)) {
      person.assignedArtist = String(safe.assignedArtist || safe.artist).trim();
    }
    if (!person.inspirationFolder && (safe.inspirationFolder || safe.folderUrl)) {
      person.inspirationFolder = String(safe.inspirationFolder || safe.folderUrl).trim();
    }
    if (!person.inspirationSubmitted && safe.inspirationSubmitted) {
      person.inspirationSubmitted = true;
    }
    if ((!person.inspirationGallery || !person.inspirationGallery.length) && Array.isArray(safe.inspirationGallery) && safe.inspirationGallery.length) {
      person.inspirationGallery = safe.inspirationGallery.slice();
    }
    if (!person.trialFolderUrl && safe.trialFolderUrl) {
      person.trialFolderUrl = String(safe.trialFolderUrl).trim();
    }
    if ((!person.trialGallery || !person.trialGallery.length) && Array.isArray(safe.trialGallery) && safe.trialGallery.length) {
      person.trialGallery = safe.trialGallery.slice();
    }
    if (person.trialAssistSubmitted === undefined && safe.trialAssistSubmitted !== undefined) {
      person.trialAssistSubmitted = safe.trialAssistSubmitted;
    }
    if (!person.notes && safe.notes) {
      person.notes = String(safe.notes).trim();
    }

    // Keep bride label synced to workspace bride name if available
    if (person.role === "Bride") {
      const workspaceBrideName = String((state.workspace && state.workspace.brideName) || "").trim();
      if (workspaceBrideName) {
        person.personLabel = workspaceBrideName;
        person.name = workspaceBrideName;
      }
    }
  });

  return ordered.sort(function(a, b) {
    // Case-insensitive bride detection — saved drafts from the legacy
    // store can carry role: 'bride' lowercase, while hub-sourced uses
    // 'Bride'. Also fall back to isBride flag.
    var aIsBride = !!a.isBride || String(a.role || '').trim().toLowerCase() === 'bride';
    var bIsBride = !!b.isBride || String(b.role || '').trim().toLowerCase() === 'bride';
    if (aIsBride && !bIsBride) return -1;
    if (!aIsBride && bIsBride) return 1;
    return Number(a.sourceOrder || 0) - Number(b.sourceOrder || 0);
  });
}
  
    function flattenBridalPartyDraftToServices_(draft) {
      const out = [];
      let ord = 1;

      (draft || []).forEach(function(person) {
        const rawServices = person.servicesBooked && person.servicesBooked.length
          ? person.servicesBooked
          : ["Service"];
        // Hair Prep always comes first — it's a setup block at the start of the morning
        const services = rawServices.slice().sort(function(a, b) {
          if (a === "Hair Prep") return -1;
          if (b === "Hair Prep") return 1;
          return 0;
        });

        // Use the stable personId preserved from backend when available.
        const personId = person.personId || person.id || "";

        // SERVICE-ID COLLISION GUARD (2026-08-20). When personId is blank
        // (a not-yet-onboarded party member has no pm_id yet, e.g. Georgia
        // C's own placeholder cards, confirmed earlier tonight), svcId used
        // to fall back to the bare service slug alone ("makeup", "hair",
        // etc.) with NO person component. Every ID-less person sharing the
        // same service category then collapsed onto the SAME svcId, so
        // findSourceServiceById_ / findWorkspaceServiceById_ (both keyed
        // purely on this string) resolved a drag on one person's row to
        // WHICHEVER colliding row happened to sit first in the array. Root
        // incident, confirmed live via a draft-workspace dump: an artist who
        // was never on the bride's team appeared repeated across multiple
        // different party members' rows after a live editing session.
        // Fix: reuse buildPartyPersonKey_'s existing fallback (personLabel
        // + role, lowercased) instead of inventing a second convention, the
        // same key this file already uses elsewhere (line ~6646, ~8025) to
        // identify a person when no backend id exists yet.
        const personKey = personId || buildPartyPersonKey_(person.personLabel, person.role, "");

        services.forEach(function(label) {
          const cat = inferCategoryFromLabel_(label);

          // Ownership: skill-picker global defaults are the source of truth.
          // Per-person assignedArtist is only used as a fallback when no skill
          // picker covers this service's category.
          var artist = "";
if (cat === "makeup") {
  artist = person.makeupArtist || ((state.skillSelections && state.skillSelections.makeup) || "");
} else if (cat === "hair") {
  artist = person.hairArtist || ((state.skillSelections && state.skillSelections.hair) || "");
}
if (!artist) artist = person.assignedArtist || "";

          // Derive stable service ID matching the backend pattern (personKey|serviceSlug).
          const slug  = label.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
          const svcId = personKey + "|" + slug;

          out.push({
  id:                svcId,
  itemId:            svcId,
  pmId:              person.pmId || person.pm_id || "",
  serviceInstanceId: person.serviceInstanceId || "",
  scheduleEntryId:   person.scheduleEntryId || "",
  order:             ord++,
  person:            person.personLabel,
  personLabel:       person.personLabel,
  personId:          personId,
  role:              person.role || "",
  service:           label,
  serviceLabel:      label,
  category:          cat,
  durationMin:       inferDurationFromLabel_(label),
  assignedArtist:    artist,
  artist:            artist,
  isBride:           String(person.role || "").toLowerCase() === "bride",
  notes:             person.notes || "",
  source:            "override",
  hairstyleChosen:   person.hairstyleChosen || "",
  inspirationFolder: person.inspirationFolder || "",
  inspirationSubmitted: !!person.inspirationSubmitted,
  hairArtist:        person.hairArtist || "",
  makeupArtist:      person.makeupArtist || ""
    });
        });
      });

      return out;
    }

    function inferRoleFromPerson_(personLabel, service) {
      // Prefer explicit role from backend — most reliable source.
      // Substring match (not equality) so canonical CRM strings like
      // "Mother of the Bride" / "Maid of Honour" / "Mother of the Groom"
      // bucket correctly. Mirrors normalisePartyRole_.
      const explicitRole = String((service && service.role) || "").trim().toLowerCase();
      if (explicitRole === "bride") return "Bride";
      if (explicitRole.indexOf("bridesmaid") > -1 || explicitRole === "maid of honour" || explicitRole === "maid of honor" || explicitRole === "moh" || explicitRole === "matron of honour" || explicitRole === "matron of honor") return "Bridesmaid";
      if (explicitRole.indexOf("mother") > -1 || explicitRole === "mum" || explicitRole === "mob" || explicitRole === "mog") return "Mother";
      if (explicitRole.indexOf("flower") > -1) return "Flower Girl";
      if (explicitRole.indexOf("guest") > -1) return "Guest";

      // Check isBride flag
      if (!!service.isBride) return "Bride";

      // Bride detection by name match against state.workspace.brideName —
      // saved-draft restoration can drop isBride flag + role, leaving the
      // bride classed as catch-all "Bridal Party". She isn't in
      // partyMembers (party only) so the hub lookup below misses her.
      const raw = String(personLabel || "").toLowerCase().trim();
      try {
        var wsBrideName = String((state.workspace && state.workspace.brideName) || '').trim().toLowerCase();
        if (wsBrideName && raw === wsBrideName) return "Bride";
      } catch (_) {}

      // Fall back to label matching — use startsWith to avoid false positives
      // (e.g. "Bridget" should NOT match "bride")
      if (raw === "bride" || raw.startsWith("bride ") && !raw.startsWith("bridesmaid")) return "Bride";
      if (raw.startsWith("bridesmaid")) return "Bridesmaid";
      if (raw.startsWith("mother") || raw.startsWith("mum") || raw === "mob" || raw === "mog") return "Mother";
      if (raw.startsWith("flower girl") || raw.startsWith("flowergirl")) return "Flower Girl";
      if (raw.startsWith("guest")) return "Guest";

      // Hub-blob name lookup before catch-all (same fallback pattern as
      // normalisePartyRole_). Recovers role for saved-draft-restored
      // services where service.role was dropped to "" or "Bridal Party"
      // (the previous catch-all) and personLabel is a plain name.
      try {
        var cards = (state.brideCardsData && state.brideCardsData.partyMembers) || [];
        var match = cards.find(function(c) {
          return String(c.name || '').trim().toLowerCase() === raw;
        });
        if (match && match.role) {
          var mr = String(match.role).trim().toLowerCase();
          if (mr.indexOf("bridesmaid") > -1) return "Bridesmaid";
          if (mr.indexOf("mother") > -1) return "Mother";
          if (mr.indexOf("flower") > -1) return "Flower Girl";
          if (mr.indexOf("guest") > -1) return "Guest";
        }
      } catch (_) {}

      return "Bridal Party";
}

// drive.google.com/thumbnail redirects to sign-in when embedded cross-origin (Shopify page);
// lh3.googleusercontent.com/d/{id}=wN is the CDN-served variant that serves without auth cookies.
function toEmbeddableThumb_(url) {
  if (!url) return url;
  const m = String(url).match(/drive\.google\.com\/thumbnail\?id=([^&]+)(?:&sz=w(\d+))?/);
  if (!m) return url;
  const size = m[2] || '400';
  return 'https://lh3.googleusercontent.com/d/' + m[1] + '=w' + size;
}

/**
 * Canonical inspo thumb renderer — consumes Array<InspirationItem> matching
 * DOCS/inspiration-store-data-contract.md. Mirror of the Liquid snippet
 * snippets/jv-inspo-thumbs.liquid — both must stay in lockstep.
 *
 * @typedef {Object} InspirationItem
 * @property {string}                      fileId        Bare Drive file ID.
 * @property {string}                      thumbUrl      lh3 200px embed URL.
 * @property {string}                      fullUrl       lh3 1600px embed URL.
 * @property {?string}                     driveViewUrl  Drive viewer URL.
 * @property {?('hair'|'makeup')}          category      Inferred from store.
 * @property {('metafield'|'sheet'|'drive')} source       Which store fetched.
 * @property {?string}                     name          Drive file name.
 * @property {?string}                     mimeType      Drive mime type.
 *
 * @param {Array<InspirationItem>} items
 * @param {Object} [opts]
 * @param {number} [opts.limit]       Truncate after N items (default 8).
 * @param {boolean} [opts.groupByCategory] Render as hair/makeup sub-strips (default false).
 * @param {string} [opts.emptyLabel]  Text shown when items is empty.
 * @returns {string} HTML
 */
function renderInspoThumbs_(items, opts) {
  opts = opts || {};
  var safeItems = Array.isArray(items) ? items.filter(function(it) { return it && it.fileId; }) : [];
  if (!safeItems.length) {
    return '<div class="gallery-empty">' + escapeHtml(opts.emptyLabel || 'No inspiration files found yet.') + '</div>';
  }
  var limit = typeof opts.limit === 'number' ? opts.limit : 8;
  var trimmed = safeItems.slice(0, limit);

  function tile(item) {
    var label = escapeHtml(item.name || (item.category ? (item.category === 'hair' ? 'Hair inspiration' : 'Makeup inspiration') : 'Inspiration'));
    var thumb = escapeHtml(item.thumbUrl || '');
    var href = escapeHtml(item.fullUrl || item.driveViewUrl || '#');
    return (
      '<a class="gallery-tile" href="' + href + '" target="_blank" rel="noopener">' +
        '<div class="gallery-tile-media">' +
          (thumb ? '<img src="' + thumb + '" alt="' + label + '" loading="lazy" referrerpolicy="no-referrer">' : '<i class="fa-regular fa-file-image"></i>') +
        '</div>' +
      '</a>'
    );
  }

  if (opts.groupByCategory) {
    var hair = trimmed.filter(function(it) { return it.category === 'hair'; });
    var makeup = trimmed.filter(function(it) { return it.category === 'makeup'; });
    var other = trimmed.filter(function(it) { return it.category !== 'hair' && it.category !== 'makeup'; });
    var parts = [];
    if (hair.length)   parts.push('<div class="gallery-group"><div class="gallery-group-label">Hair</div><div class="gallery-strip">'   + hair.map(tile).join('')   + '</div></div>');
    if (makeup.length) parts.push('<div class="gallery-group"><div class="gallery-group-label">Makeup</div><div class="gallery-strip">' + makeup.map(tile).join('') + '</div></div>');
    if (other.length)  parts.push('<div class="gallery-group"><div class="gallery-strip">' + other.map(tile).join('') + '</div></div>');
    return '<div class="gallery-grouped">' + parts.join('') + '</div>';
  }

  return '<div class="gallery-strip">' + trimmed.map(tile).join('') + '</div>';
}

function renderGalleryStrip_(items, emptyLabel) {
  const safeItems = Array.isArray(items) ? items : [];
  if (!safeItems.length) {
    return '<div class="gallery-empty">' + escapeHtml(emptyLabel || "No files found yet.") + '</div>';
  }

  return (
    '<div class="gallery-strip">' +
      safeItems.slice(0, 6).map(function(item) {
        const href = escapeHtml(item.url || item.previewUrl || "#");
        const label = escapeHtml(item.name || "Gallery file");
        const thumb = toEmbeddableThumb_(item.thumbnailUrl);
        const media = thumb
          ? '<img src="' + escapeHtml(thumb) + '" alt="' + label + '" loading="lazy" referrerpolicy="no-referrer">'
          : '<i class="fa-regular fa-file-image"></i>';

        return (
          '<a class="gallery-tile" href="' + href + '" target="_blank" rel="noopener">' +
            '<div class="gallery-tile-media">' + media + '</div>' +
            '<div class="gallery-tile-meta">' + label + '</div>' +
          '</a>'
        );
      }).join('') +
    '</div>'
  );
}

    function inferCategoryFromLabel_(label) {
      const raw = String(label || "").toLowerCase();
      if (raw.includes("hair") && raw.includes("make")) return "hair_and_makeup";
      if (raw.includes("make")) return "makeup";
      if (raw.includes("hair") || raw.includes("wave") || raw.includes("blow")) return "hair";
      return "";
    }

    function inferDurationFromLabel_(label) {
      const raw = String(label || "").toLowerCase();
      if (raw.includes("hair & makeup") || raw.includes("hair and makeup")) return 120;
      if (raw === "hair prep" || raw.includes("hair prep")) return 60;
      if (raw.includes("makeup")) return 60;
      if (raw.includes("hair")) return 60;
      if (raw.includes("touch up") || raw.includes("touch-up")) return 15;
      return 60;
    }

  // Look up the hub-side data for a given party card person, by matching
  // name+role against state.brideCardsData.partyMembers. Returns null if
  // the brideCardsData hasn't arrived yet or no match found.
  function getHubMemberForPerson_(person) {
    var cards = state.brideCardsData && state.brideCardsData.partyMembers;
    if (!cards || !cards.length) return null;
    var nameLow = String(person.name || person.profileName || person.personLabel || '').trim().toLowerCase();
    var roleLow = String(person.role || '').trim().toLowerCase();
    if (!nameLow) return null;
    var byNameRole = null;
    var byName = null;
    for (var i = 0; i < cards.length; i++) {
      var c = cards[i];
      var cn = String(c.name || '').trim().toLowerCase();
      var cr = String(c.role || '').trim().toLowerCase();
      if (cn === nameLow && cr === roleLow) { byNameRole = c; break; }
      if (cn === nameLow) byName = byName || c;
    }
    return byNameRole || byName;
  }

  // Convert a Drive URL (any of the common shapes) into an embeddable
  // lh3.googleusercontent.com/d/<fileId>=w<size> URL. Mirrors the
  // bridal hub's normalisation (sections/jv-bridal-hub.liquid). The
  // raw blob URLs are often viewer URLs (drive.google.com/file/d/...)
  // which don't render in <img src> — without this conversion the
  // thumbs come back as broken images.
  function hubThumbSrc_(url, size) {
    var raw = String(url || '').trim();
    if (!raw) return '';
    size = size || 200;
    var m = raw.match(/lh3\.googleusercontent\.com\/d\/([A-Za-z0-9_-]+)/) ||
            raw.match(/drive\.google\.com\/file\/d\/([A-Za-z0-9_-]+)/) ||
            raw.match(/drive\.google\.com\/(?:uc|open|thumbnail)\?(?:[^#]*[?&])?id=([A-Za-z0-9_-]+)/);
    if (m && m[1]) return 'https://lh3.googleusercontent.com/d/' + m[1] + '=w' + size;
    // Bare Drive file ID (no URL wrapper) — brideInspo.hair[] / makeup[] store
    // just the ID. Drive file IDs are 25-44 chars of [A-Za-z0-9_-] with no slash/dot.
    if (/^[A-Za-z0-9_-]{20,60}$/.test(raw)) {
      return 'https://lh3.googleusercontent.com/d/' + raw + '=w' + size;
    }
    return raw;
  }

  // Render a small "From bridal hub" panel showing artist-relevant hub
  // fields: colour palette, skin concerns, allergies, inspo thumbs.
  // Read-only in Pass 1 — editable + writeback lands in Pass 2.
  //
  // For party members: sources from the jv_party_members_json blob.
  // For the bride card: pass `isBride=true` and bride-shape data
  // (brideProfile + brideInspo) — different field names.
  function renderHubMemberDetails_(hub, opts) {
    opts = opts || {};
    // Pass 2: render editable inputs for all 5 hub fields. Always rendered (even
    // when blank) so planner can ADD data. Saves via savePartyMemberHubField_.
    // personIndex required so the save handler can resolve member identity.
    var personIndex = (typeof opts.personIndex === 'number') ? opts.personIndex : -1;
    var canEdit = (personIndex >= 0);
    hub = hub || {};

    // Always-rendered editable rows (order matches Pass 1)
    var rows = [
      { label: 'Allergies',      key: 'allergies',      value: hub.allergies      || '', severity: 'high', multiline: false },
      { label: 'Skin concerns',  key: 'skin_concerns',  value: hub.skin_concerns  || '', severity: 'warn', multiline: false },
      { label: 'Colour palette', key: 'colour_palette', value: hub.colour_palette || '', severity: '',     multiline: false },
      { label: 'Hair notes',     key: 'hair_notes',     value: hub.hair_notes     || '', severity: '',     multiline: true  },
      { label: 'Inspo notes',    key: 'inspo_notes',    value: hub.inspo_notes    || '', severity: '',     multiline: true  }
    ];

    var hairUrls = String(hub.hair_inspo_urls || hub.hairInspoUrls || '').split(',').map(function(s) { return s.trim(); }).filter(Boolean);
    var makeupUrls = String(hub.makeup_inspo_urls || hub.makeupInspoUrls || '').split(',').map(function(s) { return s.trim(); }).filter(Boolean);
    var allThumbs = hairUrls.concat(makeupUrls).slice(0, 8);

    var rowsHtml = rows.map(function(r) {
      var chipClass = r.severity === 'high' ? 'chip missing'
                    : r.severity === 'warn' ? 'chip soft'
                    : 'chip';
      if (!canEdit) {
        if (!r.value) return '';
        return '<div style="display:flex;gap:8px;align-items:flex-start;padding:6px 0;border-top:1px solid var(--line);">' +
          '<span class="' + chipClass + '" style="flex:0 0 auto;">' + escapeHtml(r.label) + '</span>' +
          '<span style="font-size:13px;color:var(--brand-text);line-height:1.4;">' + escapeHtml(r.value) + '</span>' +
          '</div>';
      }
      var inputStyle = 'flex:1;min-width:0;font-size:13px;font-family:inherit;border:1px solid var(--line);border-radius:6px;padding:4px 6px;background:#fff;line-height:1.4;';
      var onChange = "savePartyMemberHubField_(" + personIndex + ", '" + r.key + "', this.value)";
      var inputHtml = r.multiline
        ? '<textarea rows="2" style="' + inputStyle + 'resize:vertical;" placeholder="Add " onchange="' + onChange + '">' + escapeHtml(r.value) + '</textarea>'
        : '<input type="text" style="' + inputStyle + '" value="' + escapeHtml(r.value) + '" placeholder="—" onchange="' + onChange + '">';
      // Save-status indicator (Cassandra Healey P1(4), 2026-10-27). Id must
      // match hubFieldStatusId_party_(personIndex, r.key) exactly.
      var statusId = hubFieldStatusId_party_(personIndex, r.key);
      var statusHtml = statusId
        ? '<span id="' + statusId + '" class="jv-hubfield-status" style="flex:0 0 auto;align-self:center;font-size:11px;min-width:56px;text-align:right;"></span>'
        : '';
      return '<div style="display:flex;gap:8px;align-items:flex-start;padding:6px 0;border-top:1px solid var(--line);">' +
        '<span class="' + chipClass + '" style="flex:0 0 auto;">' + escapeHtml(r.label) + '</span>' +
        inputHtml +
        statusHtml +
        '</div>';
    }).join('');

    // Thumbnails intentionally NOT rendered here, the existing
    // "Inspiration Gallery" section above the panel already shows
    // them. Duplicating here just clutters the card. The hub panel
    // is for textual fields only (allergies, skin, palette, notes).

    return (
      '<div class="person-card-section" style="background:var(--brand-bg-soft);border-radius:12px;padding:10px 12px;margin-top:10px;">' +
        '<h5 style="margin:0 0 4px;font-size:11px;text-transform:uppercase;letter-spacing:0.08em;color:var(--brand-primary);">From bridal hub</h5>' +
        rowsHtml +
      '</div>'
    );
  }

  // Bride-side hub details. brideInspo stores hair + makeup as arrays
  // of Drive file IDs (not URLs), so build embeddable URLs from the
  // IDs directly. Pulls profile fields from brideProfile + brideBrief.
  function renderHubBrideDetails_(brideProfile, brideBrief, brideInspo) {
    brideProfile = brideProfile || {};
    brideBrief   = brideBrief   || {};
    brideInspo   = brideInspo   || {};

    // Pass 2 — read-only fields (set during onboarding, rarely change)
    var partnerName = String(brideProfile.partnerName || '').trim();
    var visionWords = Array.isArray(brideProfile.visionWords) ? brideProfile.visionWords : [];

    // Editable fields — beautyNotes + makeupVibe. partnerName + visionWords stay
    // read-only per Pass 2 brief (those belong to the bride's own onboarding).
    var editableRows = [
      { label: 'Beauty notes', key: 'beautyNotes', value: String(brideProfile.beautyNotes || ''), multiline: true  },
      { label: 'Makeup vibe',  key: 'makeupVibe',  value: String(brideBrief.makeupVibe    || ''), multiline: false }
    ];

    var readonlyRowsHtml = '';
    if (partnerName) {
      readonlyRowsHtml = '<div style="display:flex;gap:8px;align-items:flex-start;padding:6px 0;border-top:1px solid var(--line);">' +
        '<span class="chip" style="flex:0 0 auto;">Partner</span>' +
        '<span style="font-size:13px;color:var(--brand-text);line-height:1.4;">' + escapeHtml(partnerName) + '</span>' +
        '</div>';
    }

    var rowsHtml = readonlyRowsHtml + editableRows.map(function(r) {
      var inputStyle = 'flex:1;min-width:0;font-size:13px;font-family:inherit;border:1px solid var(--line);border-radius:6px;padding:4px 6px;background:#fff;line-height:1.4;';
      var onChange = "saveBrideHubField_('" + r.key + "', this.value)";
      var inputHtml = r.multiline
        ? '<textarea rows="2" style="' + inputStyle + 'resize:vertical;" placeholder="—" onchange="' + onChange + '">' + escapeHtml(r.value) + '</textarea>'
        : '<input type="text" style="' + inputStyle + '" value="' + escapeHtml(r.value) + '" placeholder="—" onchange="' + onChange + '">';
      // Save-status indicator (Cassandra Healey P1(4), 2026-10-27). Id must
      // match hubFieldStatusId_bride_(r.key) exactly.
      var statusId = hubFieldStatusId_bride_(r.key);
      var statusHtml = statusId
        ? '<span id="' + statusId + '" class="jv-hubfield-status" style="flex:0 0 auto;align-self:center;font-size:11px;min-width:56px;text-align:right;"></span>'
        : '';
      return '<div style="display:flex;gap:8px;align-items:flex-start;padding:6px 0;border-top:1px solid var(--line);">' +
        '<span class="chip" style="flex:0 0 auto;">' + escapeHtml(r.label) + '</span>' +
        inputHtml +
        statusHtml +
        '</div>';
    }).join('');

    var visionHtml = visionWords.length
      ? '<div style="margin-top:6px;display:flex;flex-wrap:wrap;gap:4px;">' +
          visionWords.map(function(w) { return '<span class="chip soft">' + escapeHtml(w) + '</span>'; }).join('') +
        '</div>'
      : '';

    // Thumbnails intentionally NOT rendered here — matches party-member behaviour.
    // The "Inspiration Gallery" section above now renders the bride's Drive files
    // via the same folder-scan + INSPIRATION FOLDER (CRM col 27) path that party
    // members use, so the hub-panel thumbnails were a duplicate render.
    return (
      '<div class="person-card-section" style="background:var(--brand-bg-soft);border-radius:12px;padding:10px 12px;margin-top:10px;">' +
        '<h5 style="margin:0 0 4px;font-size:11px;text-transform:uppercase;letter-spacing:0.08em;color:var(--brand-primary);">From bridal hub</h5>' +
        rowsHtml +
        visionHtml +
      '</div>'
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  // STAGE 1 roster redesign (2026-09-13). See state/findings/TRACE-runsheet-
  // page2-matching-2026-09-13.md for the engineering trace this build is
  // based on, and BRIEFS/SCOPING-runsheet-page2-redesign-combined-2026-09-13.md
  // for the agreed scope. ONLY the low-risk pieces from that doc's "ready to
  // build now" list: compact roster + selected-person detail panel, collapsed
  // secondary sections, a one-line summary bar, and correctly distinguishing
  // "needs artist assigned" from "not matched to a real person" (two
  // different problems the OLD layout showed the same vague way).
  //
  // Deliberately NOT built here (stage 2, gated on Jess reviewing stage 1
  // live first, per the scoping doc's explicit staged-approval answer):
  // inline matching UI, inline person creation, any change to Publish, save/
  // autosave timing, the Remove-person confirmation dialog, or the underlying
  // Person data fields. This wrapper reuses renderBridalPartyCards_'s
  // EXISTING card render + save logic UNCHANGED, only choosing which single
  // card is left expanded via the pre-existing collapsedPartyCards mechanism
  // (state.collapsedPartyCards / togglePartyCard_ already collapse every
  // card but index 0 on load, this build only surfaces that as a real
  // roster instead of an implicit collapse-all-but-first default).
  // ═══════════════════════════════════════════════════════════════════════

  /**
   * Mirrors PartyMemberAdminTools.gs's pmAdmin_isGenericSlotLabel_ regex
   * exactly (same role-name-plus-trailing-number / "add a name" pattern),
   * client-side only, read-only, zero network calls. This is a DISPLAY
   * classification for the roster's status flag, not a matching decision,
   * so it carries no identity-matching risk. If the real server-side
   * definition ever changes, this display heuristic may drift, but it
   * never blocks or performs a write, so drift here is cosmetic only.
   * @param {string} label
   * @returns {boolean}
   */
  function rsbLooksLikeGenericSlotLabel_(label) {
    var l = String(label || '').trim().toLowerCase();
    if (!l) return true;
    if (l === 'add a name') return true;
    return /^(bride|groom|partner|mum|mother|dad|father|bridesmaid|junior bridesmaid|maid of honou?r|matron of honou?r|flower girl|page boy|guest|groomsman|best man|usher|family|friend)\s*\d*(\s*of the (bride|groom))?$/.test(l);
  }

  // Canonical checkbox-driven service labels. Per the RsbPerson contract at
  // the top of this file, servicesBooked is documented to hold ONLY these.
  var RSB_CANONICAL_SERVICE_LABELS_ = { 'Hair': true, 'Hair Prep': true, 'Makeup': true };

  /**
   * Normalises servicesBooked for DISPLAY ONLY (never mutates the array,
   * never touches save/publish/flatten logic). Fixes the "legacy labels
   * concatenated oddly" bug (Cassandra Healey P1(6), 2026-10-27): a
   * free-text legacy label (e.g. "Hair TBC", "1/2 Updo") predates the
   * canonical Hair/Hair Prep/Makeup checkboxes and can sit in the SAME
   * array as the canonical "Hair" entry once a planner checks that box,
   * producing a visibly duplicated roster summary ("Hair TBC + Makeup +
   * Hair"). Once ANY canonical label is present, every non-canonical label
   * is superseded debris and is dropped from display. If NO canonical
   * label is present, every label is kept as-is (no information loss, the
   * legacy label may be the only record a service is booked at all).
   * @param {string[]} services
   * @returns {string[]}
   */
  function rsbNormalizeServicesForDisplay_(services) {
    var list = uniqueStrings_(services || []);
    var hasAnyCanonical = list.some(function(label) { return !!RSB_CANONICAL_SERVICE_LABELS_[label]; });
    if (!hasAnyCanonical) return list;
    return list.filter(function(label) { return !!RSB_CANONICAL_SERVICE_LABELS_[label]; });
  }

  /**
   * Computes the roster status for one person: "not_matched" (generic slot
   * label, a DIFFERENT problem from below), "needs_artist" (real person, at
   * least one booked service has no artist assigned), or "all_set". Checked
   * in this order because a generic-label slot with no artist yet is still
   * fundamentally an identity problem first, not an assignment problem.
   * @param {Object} person
   * @returns {{code:string,label:string}}
   */
  function rsbPersonRosterStatus_(person) {
    var displayName = person.profileName || person.name || person.personLabel || '';
    if (rsbLooksLikeGenericSlotLabel_(displayName)) {
      return { code: 'not_matched', label: 'Not matched' };
    }
    var services = person.servicesBooked || [];
    var needsHairArtist = services.indexOf('Hair') > -1 && !String(person.hairArtist || person.assignedArtist || '').trim();
    var needsMakeupArtist = services.indexOf('Makeup') > -1 && !String(person.makeupArtist || person.assignedArtist || '').trim();
    if (services.length && (needsHairArtist || needsMakeupArtist)) {
      return { code: 'needs_artist', label: 'Needs artist' };
    }
    return { code: 'all_set', label: 'All set' };
  }

  // CLICK-TO-EXPAND FIX (2026-09-14): tracks which selectedPartyPersonKey the
  // collapse-forcing logic in renderPartyRosterAndDetail_ last actually
  // applied, so it can tell a genuine roster-row selection change (force the
  // new card open) apart from an unrelated re-render of the same selection
  // (leave togglePartyCard_'s own state alone). Pure render-sequencing
  // bookkeeping, not sent to the server. Self-heals on a bride switch even
  // though nothing explicitly resets it: the new bride's roster[0].cardKey
  // is a different string (a different personId), so it naturally diverges
  // from whatever key this var held for the PREVIOUS bride, and the force-
  // open branch fires correctly for her first card.
  var _rsbLastForcedSelectionKey_ = null;

  /**
   * The compact roster list + wedding-level summary bar + the (single)
   * selected person's detail panel. Computes cardKey identically to
   * renderBridalPartyCards_ (person.personId || person.id || index) so
   * clicking a roster row addresses the SAME key the existing collapse
   * mechanism already understands.
   * @param {Array} teamOptions
   * @returns {string}
   */
  function renderPartyRosterAndDetail_(teamOptions) {
    if (!state.bridalPartyDraft.length) {
      return '<div class="empty-state"><i class="fa-solid fa-file-circle-question"></i><div>No bridal party records found yet. Load a bride from the sidebar.</div></div>';
    }

    var roster = state.bridalPartyDraft.map(function(person, index) {
      var cardKey = String(person.personId || person.id || index);
      var displayName = person.profileName || person.name || person.personLabel ||
        (String(person.role || '').toLowerCase() === 'bride' ? (state.workspace && state.workspace.brideName) : '') || 'Unnamed';
      var status = rsbPersonRosterStatus_(person);
      var services = person.servicesBooked || [];
      var displayServices = rsbNormalizeServicesForDisplay_(services);
      var servicesLabel = displayServices.length ? displayServices.join(' + ') : 'No services booked';
      var artistLabel = String(person.assignedArtist || person.hairArtist || person.makeupArtist || '').trim() || 'Unassigned';
      // Computed start time: earliest timed service belonging to this person,
      // matched by personId first (stable), falling back to personLabel string
      // for legacy rows with no id. Read-only display, no state mutation.
      var personId = String(person.personId || person.id || '').trim();
      var personLabel = String(person.personLabel || person.name || '').trim();
      var earliestMin = null;
      (state.sourceServicesDraft || []).forEach(function(svc) {
        var belongsToPerson = personId
          ? String(svc.personId || '').trim() === personId
          : String(svc.personLabel || svc.person || '').trim() === personLabel;
        if (!belongsToPerson) return;
        var startMin = parseDisplayTimeToMinutes(svc.startTime || svc.start || svc.startTimeOverride || '');
        if (startMin === null) return;
        if (earliestMin === null || startMin < earliestMin) earliestMin = startMin;
      });
      var startTimeLabel = earliestMin !== null ? minutesToDisplayTime_(earliestMin) : 'Not scheduled';

      return {
        cardKey: cardKey, displayName: displayName, status: status,
        servicesLabel: servicesLabel, artistLabel: artistLabel, startTimeLabel: startTimeLabel
      };
    });

    // Default selection: first person, unless a selection already exists AND
    // still resolves to a real roster row (a person could have been removed).
    var selectedKey = state.selectedPartyPersonKey;
    var selectedExists = roster.some(function(r) { return r.cardKey === selectedKey; });
    if (!selectedExists) {
      selectedKey = roster[0].cardKey;
      state.selectedPartyPersonKey = selectedKey;
    }

    // CLICK-TO-EXPAND FIX (2026-09-14, direct Jess feedback: "The click to
    // expand works much better" than the forced-open behaviour this
    // replaced it with). Reuses the SAME state.collapsedPartyCards mechanism
    // togglePartyCard_ already owns, still not a new expand/collapse system
    // -- but only forces the roster's OWN choice (which card opens when a
    // DIFFERENT roster row is clicked) on an actual selection CHANGE, not on
    // every render. Previously this ran unconditionally on every render,
    // which meant a click on the visible card's own head (togglePartyCard_,
    // unchanged, locked function) was immediately overwritten by this same
    // forced reassignment on the very next render -- a real functional
    // regression, not just cosmetic, exactly as flagged. Tracked via a
    // module-scope var (not state.*, this is pure render-sequencing
    // bookkeeping, never sent to the server) so a genuine roster-row click
    // still opens that card, while re-renders from autosave/edits/etc. leave
    // whatever the admin last did with expand/collapse alone.
    if (selectedKey !== _rsbLastForcedSelectionKey_) {
      roster.forEach(function(r) {
        state.collapsedPartyCards[r.cardKey] = (r.cardKey !== selectedKey);
      });
      _rsbLastForcedSelectionKey_ = selectedKey;
    }

    var needAttentionCount = roster.filter(function(r) { return r.status.code !== 'all_set'; }).length;
    var totalServicesCount = state.bridalPartyDraft.reduce(function(sum, p) { return sum + (p.servicesBooked || []).length; }, 0);
    var assignedCount = state.bridalPartyDraft.filter(function(p) {
      var services = p.servicesBooked || [];
      if (!services.length) return false;
      var needsHair = services.indexOf('Hair') > -1 && !String(p.hairArtist || p.assignedArtist || '').trim();
      var needsMakeup = services.indexOf('Makeup') > -1 && !String(p.makeupArtist || p.assignedArtist || '').trim();
      return !(needsHair || needsMakeup);
    }).length;

    var summaryBar =
      '<div class="rsb-roster-summary">' +
        '<span>' + escapeHtml(String(roster.length)) + ' ' + (roster.length === 1 ? 'person' : 'people') + '</span>' +
        '<span class="rsb-roster-summary-dot">&middot;</span>' +
        '<span>' + escapeHtml(String(totalServicesCount)) + ' ' + (totalServicesCount === 1 ? 'service' : 'services') + '</span>' +
        '<span class="rsb-roster-summary-dot">&middot;</span>' +
        '<span>' + escapeHtml(String(assignedCount)) + ' assigned</span>' +
        '<span class="rsb-roster-summary-dot">&middot;</span>' +
        '<span' + (needAttentionCount ? ' class="rsb-roster-summary-attention"' : '') + '>' +
          escapeHtml(String(needAttentionCount)) + ' need' + (needAttentionCount === 1 ? 's' : '') + ' attention' +
        '</span>' +
      '</div>';

    var rosterListHtml = '<div class="rsb-roster-list">' + roster.map(function(r) {
      var isSelected = r.cardKey === selectedKey;
      var statusClass = 'rsb-roster-status-' + r.status.code;
      return (
        '<button type="button" class="rsb-roster-row' + (isSelected ? ' rsb-roster-row-selected' : '') + '" ' +
          'onclick="rsbSelectPartyPerson_(\'' + escapeHtml(r.cardKey) + '\')">' +
          '<span class="rsb-roster-row-name">' + escapeHtml(r.displayName) + '</span>' +
          '<span class="rsb-roster-row-meta">' + escapeHtml(r.servicesLabel) + '</span>' +
          '<span class="rsb-roster-row-meta">' + escapeHtml(r.artistLabel) + ' &middot; ' + escapeHtml(r.startTimeLabel) + '</span>' +
          '<span class="rsb-roster-status ' + statusClass + '">' + escapeHtml(r.status.label) + '</span>' +
        '</button>'
      );
    }).join('') + '</div>';

    var detailHtml = '<div class="rsb-roster-detail">' + renderBridalPartyCards_(teamOptions) + '</div>';

    return (
      summaryBar +
      '<div class="rsb-roster-layout">' +
        rosterListHtml +
        detailHtml +
      '</div>'
    );
  }

  /**
   * Roster-row click handler: selects a person (drives which single card
   * stays expanded next render) and re-renders. No save, no network call,
   * no write of any kind, purely a client-side selection change.
   * @param {string} cardKey
   */
  function rsbSelectPartyPerson_(cardKey) {
    state.selectedPartyPersonKey = String(cardKey || '');
    renderStage();
  }

  /**
   * Formats a minutes-since-midnight integer as "h:mm AM/PM", matching the
   * display convention already used throughout this file (e.g. buildMetaLine).
   * @param {number} mins
   * @returns {string}
   */
  function minutesToDisplayTime_(mins) {
    var h = Math.floor(mins / 60) % 24;
    var m = mins % 60;
    var suffix = h >= 12 ? 'PM' : 'AM';
    var h12 = h % 12; if (h12 === 0) h12 = 12;
    return h12 + ':' + (m < 10 ? '0' : '') + m + ' ' + suffix;
  }

  function renderBridalPartyCards_(teamOptions) {
  if (!state.bridalPartyDraft.length) {
    return '<div class="empty-state" style="grid-column:1/-1;"><i class="fa-solid fa-file-circle-question"></i><div>No bridal party records found yet. Load a bride from the sidebar.</div></div>';
  }

    const SERVICE_OPTIONS = ["Hair", "Hair Prep", "Makeup"];
    const HAIRSTYLE_OPTIONS = rsbHairstyleOptions_();

  return state.bridalPartyDraft.map(function(person, index) {
    const cardKey = String(person.personId || person.id || index);
    const isCollapsed = !!state.collapsedPartyCards[cardKey];
    const displayName =
  person.profileName ||
  person.name ||
  person.personLabel ||
  (person.role === "Bride" ? state.workspace.brideName : "");
    const showProfileSubtitle = !!person.profileName && person.profileName !== person.personLabel;
    const isBrideCard = String(person.role || "").toLowerCase() === "bride";
    const hasHair = (person.servicesBooked || []).indexOf("Hair") > -1;

    // Hub data for this person — null if hub cards haven't loaded yet
    // or no match found. For bride card, sourced from brideCardsData.brideProfile + brideBrief.
    const hubMember = !isBrideCard ? getHubMemberForPerson_(person) : null;
    const hubBride = isBrideCard ? (state.brideCardsData && state.brideCardsData.brideProfile) : null;
    const hubBrideBrief = isBrideCard ? (state.brideCardsData && state.brideCardsData.brideBrief) : null;
    const initials = String(displayName || '?').split(' ').map(function(p) { return p.charAt(0); }).join('').slice(0, 2).toUpperCase();
    // Profile pic — try every known source. Different sync paths populate
    // different storage tiers, so be generous about where we look.
    var avatarPicUrl = '';
    if (hubMember && (hubMember.profile_pic_url || hubMember.profilePicUrl)) {
      avatarPicUrl = hubMember.profile_pic_url || hubMember.profilePicUrl;
    }
    if (!avatarPicUrl && hubBride && hubBride.profilePicUrl) {
      avatarPicUrl = hubBride.profilePicUrl;
    }
    if (!avatarPicUrl && person.profilePicUrl) {
      avatarPicUrl = person.profilePicUrl;
    }
    // Party-member fallback: state.partyProfiles is the GraphQL metaobject
    // fetch (different sync path from the blob). For members whose blob
    // sync hasn't run yet but who have a metaobject profile_pic file_ref,
    // this catches them.
    if (!avatarPicUrl && !isBrideCard && Array.isArray(state.partyProfiles)) {
      var nameLow = String(displayName || '').trim().toLowerCase();
      var profile = state.partyProfiles.find(function(pp) {
        return String(pp.name || '').trim().toLowerCase() === nameLow;
      });
      if (profile && profile.profilePicUrl) avatarPicUrl = profile.profilePicUrl;
    }
    // Bride fallback: workspace.people[isBride].profilePicUrl is annotated
    // by the hub sourcer from CRM.BRIDE_PROFILE_PIC_URL — final safety net.
    if (!avatarPicUrl && isBrideCard && state.workspace && Array.isArray(state.workspace.people)) {
      var bridePerson = state.workspace.people.find(function(p) {
        return p && String(p.role || '').trim().toLowerCase() === 'bride';
      });
      if (bridePerson && bridePerson.profilePicUrl) avatarPicUrl = bridePerson.profilePicUrl;
    }
    const avatarHtml = '<div class="party-card-avatar" style="width:48px;height:48px;border-radius:50%;flex:0 0 auto;display:flex;align-items:center;justify-content:center;font-size:14px;font-weight:600;color:var(--brand-primary-deep);background:var(--brand-bg-soft);' +
      (avatarPicUrl ? 'background-image:url(' + escapeHtml(avatarPicUrl) + ');background-size:cover;background-position:center;color:transparent;' : '') +
      '">' + (avatarPicUrl ? '' : escapeHtml(initials)) + '</div>';

    return (
      '<div class="person-card">' +
        '<div class="person-card-head" style="cursor:pointer;display:flex;align-items:center;gap:12px;" onclick="togglePartyCard_(\'' + escapeHtml(cardKey) + '\')">' +
          avatarHtml +
          '<div style="flex:1;min-width:0;">' +
            '<div class="person-role-badge">' + escapeHtml(person.role || "Bridal Party") + '</div>' +
            '<div class="person-card-name">' + escapeHtml(displayName) + '</div>' +
            (showProfileSubtitle
              ? '<div class="person-card-subtitle">Runsheet label: ' + escapeHtml(person.personLabel || "") + '</div>'
              : '') +
          '</div>' +
          '<div class="person-chip-row">' +
            (person.inspirationFolder
              ? '<span class="chip high" title="' + escapeHtml(person.inspirationFolder) + '">Folder linked</span>'
              : '<span class="chip soft">No folder</span>') +
            // "Form submitted" is now sourced from isInspoCompleteForPerson_
            // which counts EITHER the legacy Google-form flag OR hub
            // onboarding (hairstyle picked + ≥1 inspo photo actually
            // uploaded). Pre-fix this read raw person.inspirationSubmitted
            // and silently treated members with no uploads as "complete"
            // when their hub onboarding was partially filled.
            (isInspoCompleteForPerson_(person)
              ? '<span class="chip high">Inspo submitted</span>'
              : '<span class="chip missing">Inspo missing</span>') +
            (isBrideCard && person.trialAssistSubmitted === false
              ? '<span class="chip missing">Trial assist missing</span>'
              : '') +
          '</div>' +
        '</div>' +

        (
          isCollapsed
            ? '<div class="panel-sub">Click to expand</div>'
            :
              '<div id="party-safety-alert-' + escapeHtml(cardKey) + '"></div>' +
              '<div class="person-card-grid-2">' +
                '<div class="field"><label>Name</label>' +
 '<input type="text" value="' + escapeHtml(displayName || "") + '" onchange="updatePartyField(' + index + ', \'personLabel\', this.value)">' +
'</div>' +
                '<div class="field"><label>Role</label>' +
                  '<select onchange="updatePartyField(' + index + ', \'role\', this.value)">' +
                    ['Bride','Bridesmaid','Mother','Guest','Bridal Party'].map(function(r) {
                      return '<option value="' + r + '"' + (r === person.role ? ' selected' : '') + '>' + r + '</option>';
                    }).join('') +
                  '</select>' +
                '</div>' +
              '</div>' +

              '<div class="field"><label>Services</label>' +
                '<div class="service-checklist">' +
                  SERVICE_OPTIONS.map(function(svc) {
                    const active = (person.servicesBooked || []).indexOf(svc) > -1;
                    return '<label class="service-pill-check' + (active ? ' active' : '') + '">' +
                      '<input type="checkbox"' + (active ? ' checked' : '') +
                      ' onchange="togglePartyService(' + index + ', \'' + escapeHtml(svc) + '\', this.checked)">' +
                      escapeHtml(svc) +
                    '</label>';
                  }).join('') +
                '</div>' +
              '</div>' +

              (hasHair
                ? '<div class="person-card-grid-2">' +
                    '<div class="field"><label>Preferred Hairstyle</label>' +
                      '<select onchange="updatePartyHairstyle_(' + index + ', this.value)">' +
                        (function() {
                          // Preference order:
                          //   1. person.hairstyleChosen (planner edit, freshest) —
                          //      treat "TBC" as no-value so hub fallback fires.
                          //   2. hub member's hair_style (jv_party_members_json blob)
                          //   3. state.partyProfiles[] hairStyle (metaobject GraphQL —
                          //      different sync path, catches members whose blob is stale)
                          //   4. bride's hairstylePref (bride_brief_json metafield)
                          //   5. "TBC" fallback
                          var hubHair = (hubMember && hubMember.hair_style) || '';
                          if (!hubHair && !isBrideCard && Array.isArray(state.partyProfiles)) {
                            var nameLow = String(displayName || '').trim().toLowerCase();
                            var pp = state.partyProfiles.find(function(x) {
                              return String(x.name || '').trim().toLowerCase() === nameLow;
                            });
                            if (pp && pp.hairStyle) hubHair = pp.hairStyle;
                          }
                          if (!hubHair && isBrideCard && hubBrideBrief && hubBrideBrief.hairstylePref) {
                            hubHair = hubBrideBrief.hairstylePref;
                          }
                          var plannerChoice = String(person.hairstyleChosen || '').trim();
                          if (plannerChoice.toLowerCase() === 'tbc') plannerChoice = '';
                          var cur = plannerChoice || hubHair || "TBC";
                          // Ensure the current value is in the option list even if it's
                          // a non-canonical free-text response (legacy data).
                          var opts = HAIRSTYLE_OPTIONS.slice();
                          if (cur && opts.indexOf(cur) === -1) opts.push(cur);
                          return opts.map(function(opt) {
                            return '<option value="' + escapeHtml(opt) + '"' + (opt === cur ? ' selected' : '') + '>' + escapeHtml(opt) + '</option>';
                          }).join('');
                        })() +
                      '</select>' +
                      '<span id="jv-hubstat-hairstyle-' + index + '" class="jv-hubfield-status" style="font-size:11px;"></span>' +
                    '</div>' +
                    '<div class="field"><label>Hairstylist</label>' +
                      renderPerPersonArtistSelect_(person, 'hair', index) +
                    '</div>' +
                  '</div>'
                : '') +

              '<div class="person-card-grid-2">' +
                '<div class="field"><label>Makeup Artist</label>' +
                  renderPerPersonArtistSelect_(person, 'makeup', index) +
                '</div>' +
                '<div class="field"><label>Inspiration Folder</label>' +
                  '<input type="text" value="' + escapeHtml(person.inspirationFolder || "") + '" placeholder="Paste folder link" onchange="updatePartyField(' + index + ', \'inspirationFolder\', this.value)">' +
                  // Sub-text matches the header chip — both use isInspoCompleteForPerson_
                  // which counts the legacy Google-form flag OR hub onboarding completion
                  // (hairstyle picked + ≥1 inspo photo uploaded). Pre-fix, the chip
                  // could say "Inspo submitted" while the sub-text said "not found yet".
                  '<small>' + (isInspoCompleteForPerson_(person)
                    ? 'Form submitted.'
                    : 'No inspiration form submission found yet.') + '</small>' +
                '</div>' +
              '</div>' +

              // STAGE 1 roster redesign (2026-09-13): the inspiration gallery and
              // the curated hub fields (allergies/skin/colour/notes) are secondary
              // reference content per the scoping doc's Tier 2/3 classification,
              // collapsed behind "Show more" by default. This wraps the EXISTING
              // renderInspoThumbs_ / renderGalleryStrip_ / renderHubBrideDetails_ /
              // renderHubMemberDetails_ output unchanged, no edit to those
              // functions' internals, purely a visibility toggle around them.
              (function() {
                var secondaryExpanded = !!state.rsbSecondaryExpanded[cardKey];
                var inspoHtml =
                  '<div class="person-card-section">' +
                    '<h5>Inspiration Gallery</h5>' +
                    // InspirationStore (session 2): when the new fetcher flag is on,
                    // the GAS response includes a canonical InspirationItem[] under
                    // bride.inspirationItems / partyMembers[].inspirationItems.
                    // Use it via the new renderer; fall back to legacy V4 gallery
                    // when the new shape is absent (flag off).
                    (function() {
                      var newItems = isBrideCard
                        ? (state.brideCardsData && state.brideCardsData.brideInspirationItems)
                        : (hubMember && hubMember.inspirationItems);
                      if (Array.isArray(newItems)) {
                        return renderInspoThumbs_(newItems, {
                          limit: 12,
                          groupByCategory: true,
                          emptyLabel: 'No inspiration files found yet.'
                        });
                      }
                      return renderGalleryStrip_(person.inspirationGallery || [], 'No inspiration files found in Drive yet.');
                    })() +
                  '</div>';
                // Curated hub fields, Pass 2: editable inline with writeback via
                // saveBrideHubField_ / savePartyMemberHubField_ (gated by feature flag
                // JV_RUNSHEET_BUILDER_WRITEBACK_ENABLED on the GAS side).
                var hubFieldsHtml = isBrideCard
                  ? renderHubBrideDetails_(hubBride, hubBrideBrief, state.brideCardsData && state.brideCardsData.brideInspo)
                  : renderHubMemberDetails_(hubMember, { personIndex: index });
                return (
                  '<button type="button" class="rsb-show-more-toggle" onclick="rsbToggleSecondary_(\'' + escapeHtml(cardKey) + '\')">' +
                    (secondaryExpanded ? 'Show less' : 'Show more') +
                  '</button>' +
                  (secondaryExpanded ? ('<div class="rsb-secondary-content">' + inspoHtml + hubFieldsHtml + '</div>') : '')
                );
              })() +

              (
                isBrideCard
                  ? '<div class="person-card-section">' +
                      '<h5>Trial Gallery</h5>' +
                      renderGalleryStrip_(person.trialGallery || [], 'No trial files found in Trial Photos/Feedback yet.') +
                      '<div class="person-chip-row" style="justify-content:flex-start;margin-top:10px;">' +
                        (person.trialFolderUrl
                          ? '<span class="chip high" title="' + escapeHtml(person.trialFolderUrl) + '">Trial folder linked</span>'
                          : '<span class="chip soft">No trial folder</span>') +
                        (person.trialAssistSubmitted === false
                          ? '<span class="chip missing">Trial assist form missing</span>'
                          : '<span class="chip high">Trial assist submitted</span>') +
                      '</div>' +
                    '</div>'
                  : ''
              ) +

              '<div class="field"><label>Notes</label>' +
  '<input type="text" value="' + escapeHtml(person.notes || "") + '" placeholder="Veil removal, early start, etc." onchange="updatePartyField(' + index + ', \'notes\', this.value)">' +
'</div>' +

'<div style="display:flex;justify-content:flex-end;align-items:center;gap:8px;margin-top:6px;">' +
  '<button type="button" class="btn btn-soft" style="background:var(--error-bg);color:var(--error-text);" onclick="removePersonServicesFromCard_(' + index + ')">' +
    '<i class="fa-solid fa-trash-can"></i> Remove Bridal Party Service(s)' +
  '</button>' +
  (!isBrideCard
    ? '<span style="width:1px;align-self:stretch;background:var(--line);margin:0 2px;"></span>' +
      '<button type="button" class="btn btn-danger-solid" title="Permanently removes this person -- revokes their invite and deletes their record. Cannot be undone from here." onclick="removePartyMemberFromRoster_(' + index + ')">' +
        '<i class="fa-solid fa-triangle-exclamation"></i> Remove Person (Permanent)' +
      '</button>'
    : '') +
'</div>'
        ) +
      '</div>'
    );
  }).join("");
}

function normalisePartyRole_(role, personLabel, isBride) {
  if (isBride) return "Bride";

  const rawRole = String(role || "").trim().toLowerCase();
  const rawPerson = String(personLabel || "").trim().toLowerCase();

  // Bride detection by name match — saved-draft restoration drops both
  // service.isBride flag AND service.role, leaving the bride classed as
  // catch-all "Bridal Party". She isn't in state.brideCardsData.partyMembers
  // (that's party only — bride lives in brideProfile). So we match her
  // personLabel against state.workspace.brideName instead.
  try {
    var workspaceBride = String((typeof state !== 'undefined' && state.workspace && state.workspace.brideName) || '').trim().toLowerCase();
    if (workspaceBride && rawPerson === workspaceBride) return "Bride";
  } catch (_) {}

  if (rawRole === "bride" || rawPerson === "bride") return "Bride";
  if (rawRole.indexOf("bridesmaid") > -1 || rawPerson.indexOf("bridesmaid") > -1 || rawRole === "moh" || rawRole === "maid of honour" || rawRole === "maid of honor" || rawRole === "matron of honour" || rawRole === "matron of honor") return "Bridesmaid";
  if (rawRole.indexOf("mother") > -1 || rawRole === "mum" || rawRole === "mob" || rawRole === "mog" || rawPerson.indexOf("mother") > -1 || rawPerson.indexOf("mum") > -1) return "Mother";
  if (rawRole.indexOf("guest") > -1 || rawPerson.indexOf("guest") > -1) return "Guest";

  // Role-by-name lookup before catch-all. Saved-draft restoration can
  // drop service.role. Without this, every drafted member would
  // normalise to "Bridal Party" (catch-all), wiping out the Bridal
  // Party Units counter + breaking downstream role-sensitive logic.
  // state.brideCardsData.partyMembers is the canonical hub blob with
  // accurate roles per name.
  try {
    var cards = (typeof state !== 'undefined' && state.brideCardsData && state.brideCardsData.partyMembers) || [];
    var nameLow = rawPerson;
    var match = cards.find(function(c) {
      return String(c.name || '').trim().toLowerCase() === nameLow;
    });
    if (match && match.role) {
      var matchRole = String(match.role).trim().toLowerCase();
      if (matchRole.indexOf("bridesmaid") > -1) return "Bridesmaid";
      if (matchRole.indexOf("mother") > -1) return "Mother";
      if (matchRole.indexOf("guest") > -1) return "Guest";
      if (matchRole.indexOf("flower") > -1) return "Flower Girl";
    }
  } catch (_) {}

  return "Bridal Party";
}

// HARDENED MATCH (2026-09-13, state/findings/runsheet-seat-rename-no-validation-
// 2026-09-13.md): shared by both removal cleanup filters below. Previously each
// filter matched on `samePersonId || samePersonLabel` independently, so a service
// whose person had been renamed to collide with someone else's label (now
// blocked at the source by updatePartyField's new collision check, but a
// belt-and-braces fix must not rely solely on that) could be matched and
// removed by the label half alone even when personId was populated and
// genuinely different. Rule: if BOTH the target person and the service being
// tested carry a real personId, the personId is authoritative and label is
// ignored. Label is only consulted as a fallback when one side's personId is
// genuinely blank (legacy/manually-added rows with no id yet).
function runsheetServiceBelongsToPerson_(service, personId, personLabel) {
  const svcPersonId = String(service.personId || "").trim();
  if (personId && svcPersonId) return svcPersonId === personId;
  const svcPersonLabel = String(service.personLabel || service.person || "").trim();
  return svcPersonLabel === personLabel;
}

async function removePersonServicesFromCard_(index) {
  var person = state.bridalPartyDraft[index];
  if (!person) return;

  // WRONG-PERSON GUARD (2026-10-26). `index` is a raw array index captured at
  // render time. A background team-list refresh re-sorts/mutates
  // state.bridalPartyDraft while the user is still looking at the old render,
  // so by the time this click handler runs, index can point at a completely
  // different person than the card the user actually clicked. Confirmed live:
  // opening Jacqui Wright's card on Cassandra Healey's runsheet and clicking
  // "Remove Bridal Party Service(s)" produced a confirm dialog for Cassandra
  // Healey (the bride, index 0) instead of Jacqui. Same bug class already
  // fixed once in this file for savePartyMemberHubField_ (commit 1bf8547b2,
  // "resolve by stable personId, not array index"); applying the same
  // pattern here. Re-resolve by stable personId/id before trusting the
  // captured index, falling back to the index only if that person has
  // genuinely vanished from the draft.
  var capturedPersonId0 = String(person.personId || person.id || "").trim();
  if (capturedPersonId0) {
    var reResolved0 = state.bridalPartyDraft.find(function(p) {
      return String((p && (p.personId || p.id)) || "").trim() === capturedPersonId0;
    });
    if (reResolved0) {
      if (reResolved0 !== person) {
        console.warn('removePersonServicesFromCard_: array reordered since render, re-resolved "' +
          (reResolved0.profileName || reResolved0.name || reResolved0.personLabel) +
          '" by stable id instead of trusting index ' + index);
      }
      person = reResolved0;
    }
  }

  const personId = String(person.personId || person.id || "").trim();
  const personLabel = String(person.personLabel || "").trim();

  var removeOk = await confirmModal_("Remove all services for " + personLabel + "?", "Remove", "Cancel");
  if (!removeOk) return;

  state.sourceServicesDraft = (state.sourceServicesDraft || []).filter(function(service) {
    return !runsheetServiceBelongsToPerson_(service, personId, personLabel);
  });

  rederiveDraftState_();
  state.timelineDirty = true;
  renderStage();
  showBanner("Removed services for " + personLabel + ".", "success");
}

// Remove a party member entirely (2026-07-19, BRIEF-12 item 2 Track B).
// Distinct from removePersonServicesFromCard_ above (client-side services-only
// clear, does not persist). This calls the SAME proven cascading delete the
// Admin CRM party-member drawer already uses (adminCrm_deletePartyMember in
// PartyMemberAdminTools.gs): soft-deletes the _PartyMembers row, revokes any
// active invite token, deletes the Shopify jv_bridal_party_member metaobject,
// writes an audit log entry, and rebuilds the jv_party_members_json blob.
// Reused as-is, not reimplemented. Blocked (no force escape hatch here) if
// the member has an active runsheet slot; Jess can force-delete from the
// Admin CRM drawer instead if that's genuinely needed.
async function removePartyMemberFromRoster_(index) {
  var person = state.bridalPartyDraft[index];
  if (!person) return;

  // WRONG-PERSON GUARD (2026-10-26). `index` is a raw array index captured at
  // render time. A background team-list refresh re-sorts/mutates
  // state.bridalPartyDraft while the user is still looking at the old render,
  // so by the time this click handler runs, index can point at a completely
  // different person than the card the user actually clicked. Confirmed live:
  // opening Jacqui Wright's card on Cassandra Healey's runsheet and clicking
  // "Remove Bridal Party Service(s)" produced a confirm dialog for Cassandra
  // Healey (the bride, index 0) instead of Jacqui. Same bug class already
  // fixed once in this file for savePartyMemberHubField_ (commit 1bf8547b2,
  // "resolve by stable personId, not array index"); applying the same
  // pattern here. Re-resolve by stable personId/id before trusting the
  // captured index, falling back to the index only if that person has
  // genuinely vanished from the draft.
  var capturedPersonId1 = String(person.personId || person.id || "").trim();
  if (capturedPersonId1) {
    var reResolved1 = state.bridalPartyDraft.find(function(p) {
      return String((p && (p.personId || p.id)) || "").trim() === capturedPersonId1;
    });
    if (reResolved1) {
      if (reResolved1 !== person) {
        console.warn('removePartyMemberFromRoster_: array reordered since render, re-resolved "' +
          (reResolved1.profileName || reResolved1.name || reResolved1.personLabel) +
          '" by stable id instead of trusting index ' + index);
      }
      person = reResolved1;
    }
  }

  const brideEmail = state.workspace && state.workspace.email;
  if (!brideEmail) {
    showBanner("Can't remove this person, no bride email on this workspace.", "error");
    return;
  }

  // Resolve the pm_id, refreshing the card cache once if we miss (2026-08-06).
  //
  // getHubMemberForPerson_ matches against state.brideCardsData.partyMembers,
  // which is a CACHE fetched once when the workspace loads. Its own comment
  // says it "returns null if the brideCardsData hasn't arrived yet". A member
  // added through the Admin CRM drawer after that fetch is not in the cache, so
  // the lookup missed and the old code gave up here. That is why Remove Person
  // read as a dead button, and why the audit could not delete its own test
  // member from any surface.
  //
  // The pm_id DOES exist: partyMember_createOrUpdate generates one via
  // partyMember_generateId_ (PartyMemberAccess.gs:742) when it creates the row.
  // So this was never missing data, only a stale client cache. Re-fetching and
  // retrying keeps the full cascading delete (invite token revoked, Shopify
  // metaobject removed, audit log written, blob rebuilt) instead of falling
  // back to a weaker name-keyed remove that would orphan both the token and the
  // metaobject.
  let hubMember = getHubMemberForPerson_(person);
  let pmId = hubMember && String(hubMember.pm_id || "").trim();

  // state.partyProfiles is the RELIABLE source of pm_id (2026-08-08).
  //
  // getHubMemberForPerson_ reads state.brideCardsData.partyMembers, which comes
  // from bridge_admin_getBrideCardsData. That route builds its list from the
  // Shopify jv_party_members_json metafield (BrideCardsApi.gs:43), and for
  // Jo Test that blob is EMPTY while _PartyMembers holds 21 real rows: the
  // 2026-08-06 diagnostic returned partyMembers 0 against 21, with populated
  // ids. Two surfaces, two different stores.
  //
  // bridge_runsheet_getPartyProfiles reads the store that actually has the
  // members (partyMember_getForBrideEmail_) and, since commit 508c544d, returns
  // pm_id on BOTH of its assembly paths. The builder already calls that route on
  // every workspace load, so this needs no extra call.
  //
  // Checked before the cache refresh below because it is already in memory: no
  // network, no ~15s wait, and it is the source more likely to be populated.
  if (!pmId && Array.isArray(state.partyProfiles)) {
    const nameLow = String(person.name || person.profileName || person.personLabel || '').trim().toLowerCase();
    const roleLow = String(person.role || '').trim().toLowerCase();
    if (nameLow) {
      // Prefer an exact name+role match, then fall back to name alone, mirroring
      // getHubMemberForPerson_ so both sources resolve a person the same way.
      let byNameRole = null, byName = null;
      for (let i = 0; i < state.partyProfiles.length; i++) {
        const p = state.partyProfiles[i];
        const pn = String(p.name || '').trim().toLowerCase();
        if (pn !== nameLow) continue;
        if (String(p.role || '').trim().toLowerCase() === roleLow) { byNameRole = p; break; }
        byName = byName || p;
      }
      const match = byNameRole || byName;
      const fromProfiles = match && String(match.pm_id || '').trim();
      if (fromProfiles) {
        pmId = fromProfiles;
        hubMember = match;
      }
    }
  }

  // state.workspace.people is the THIRD source, and the most direct one
  // (2026-08-08). RunsheetPersonSourcer.gs sets `pmId` on every person straight
  // from the _PartyMembers PARTY_MEMBER_ID column (:281, :396), which is the
  // exact value pmAdmin_findRow_ matches on server-side
  // (PartyMemberAdminTools.gs:1013). So this is the same id, from the same
  // column, already in memory.
  //
  // The 2026-08-08 audit proved this is the missing link: with the email gate
  // finally cleared, removal still failed with "no party-member record found to
  // delete" for members the audit could SEE in the workspace people array with a
  // real pmId (pm_486f8e08 for QA-G3-WARM-1813). The record existed, the id
  // existed, and this function simply never read it: it checked partyProfiles
  // and brideCardsData, and gave up when both missed.
  //
  // Reproduced on a member never touched before, so it was not corruption from a
  // failed earlier attempt, and a full page reload did not clear it.
  //
  // Checked before the network refresh below for the same reason as
  // partyProfiles: it costs nothing, it is already loaded, and it is the source
  // the workspace itself is built from.
  if (!pmId && state.workspace && Array.isArray(state.workspace.people)) {
    const wsNameLow = String(person.name || person.profileName || person.personLabel || '').trim().toLowerCase();
    const wsPersonId = String(person.personId || person.id || '').trim();
    for (let i = 0; i < state.workspace.people.length; i++) {
      const wp = state.workspace.people[i];
      if (!wp) continue;
      // personId is the stronger match where present: it is the identity the
      // card itself was rendered from, so it cannot collide on a shared name.
      const idMatch = wsPersonId && String(wp.personId || wp.id || '').trim() === wsPersonId;
      const nameMatch = wsNameLow && String(wp.name || '').trim().toLowerCase() === wsNameLow;
      if (!idMatch && !nameMatch) continue;
      const wsPmId = String(wp.pmId || wp.pm_id || '').trim();
      if (wsPmId) { pmId = wsPmId; hubMember = hubMember || wp; break; }
    }
  }

  if (!pmId) {
    try {
      const cardsRoute = rsbV2On_() ? 'bridge_runsheet_v2_brideCards' : 'bridge_admin_getBrideCardsData';
      const cardsRes = await jvBridge.callAdmin(cardsRoute, { brideEmail: brideEmail });
      if (cardsRes && cardsRes.ok && (!rsbV2On_() || cardsRes.mode === 'primary') && cardsRes.partyMembers) {
        state.brideCardsData = state.brideCardsData || {};
        state.brideCardsData.partyMembers = cardsRes.partyMembers;
        hubMember = getHubMemberForPerson_(person);
        pmId = hubMember && String(hubMember.pm_id || "").trim();
      }
    } catch (refreshErr) {
      // Fall through to the error below. A failed refresh is reported as an
      // inability to resolve the record, never silently swallowed.
      console.warn('[runsheet] party-member cache refresh failed', refreshErr);
    }
  }

  if (!pmId) {
    showBanner("Can't remove this person, no party-member record found to delete. Try reloading the page.", "error");
    return;
  }

  const displayName = String(person.personLabel || person.name || "this person").trim();
  const removeOk = await confirmModal_(
    'Permanently remove "' + displayName + '" from this wedding? This revokes their invite link so it stops working, removes their party-member record from Shopify, and takes them off this bridal party for good. This is different from the "Remove Bridal Party Service(s)" button above, which only clears their services from this draft. This cannot be undone from here -- if you remove the wrong person, ask Jess to restore the record.\n\nIf they already have a runsheet slot, this is blocked -- merge their slot into another person first.',
    "Yes, remove them permanently",
    "Keep them in the party"
  );
  if (!removeOk) return;

  try {
    const res = await jvBridge.callAdmin('bridge_admin_generic', {
      function: 'adminCrm_deletePartyMember',
      args: { brideEmail: brideEmail, pmId: pmId }
    });
    const d = (res && res.data) || res;
    if (d && d.ok) {
      const personId = String(person.personId || person.id || "").trim();
      const personLabel = String(person.personLabel || "").trim();
      state.bridalPartyDraft = state.bridalPartyDraft.filter(function(_, i) { return i !== index; });
      state.sourceServicesDraft = (state.sourceServicesDraft || []).filter(function(service) {
        return !runsheetServiceBelongsToPerson_(service, personId, personLabel);
      });
      rederiveDraftState_();
      state.timelineDirty = true;
      renderStage();
      showBanner("Removed " + displayName + ".", "success");
    } else if (d && d.blocked) {
      showBanner("Cannot delete: " + d.reason, "error");
    } else {
      showBanner("Delete failed: " + ((d && d.error) || "unknown error"), "error");
    }
  } catch (err) {
    showBanner("Error: " + String((err && err.message) || err), "error");
  }
}

// ─── Add Person to Bridal Party (Prompt 270) ────────────────────────────────

function openAddPersonModal_() {
  var artistOptions = (state.activeTeam || []).map(function(name) {
    return '<option value="' + escapeHtml(name) + '">' + escapeHtml(name) + '</option>';
  }).join('');
  document.getElementById("addPersonRole").value = "Bridesmaid";
  document.getElementById("addPersonLabel").value = "";
  document.getElementById("addPersonDuration").value = "60";
  document.getElementById("addPersonArtist").innerHTML = '<option value="">— auto —</option>' + artistOptions;
  document.getElementById("addPersonNotes").value = "";

  // Reset service checkboxes — only Makeup checked by default
  var checks = document.querySelectorAll("#addPersonServices input[type='checkbox']");
  for (var i = 0; i < checks.length; i++) {
    checks[i].checked = (checks[i].value === "Makeup");
    var pill = checks[i].closest(".service-pill-check");
    if (pill) pill.classList.toggle("active", checks[i].checked);
  }
  // Bind pill toggle styling
  for (var j = 0; j < checks.length; j++) {
    checks[j].onchange = function() {
      var p = this.closest(".service-pill-check");
      if (p) p.classList.toggle("active", this.checked);
    };
  }

  document.getElementById("addPersonModal").classList.add("open");
  document.getElementById("addPersonLabel").focus();
}

function closeAddPersonModal_() {
  document.getElementById("addPersonModal").classList.remove("open");
}

// PARTY-MODEL PROMOTION (2026-07-30)
// BRIEFS/BRIEF-runsheet-builder-orphaned-manual-persons-2026-07-30.md
//
// Manually-added runsheet people used to be a dead end: person_manual_* ids
// were never written to _PartyMembers, so the person was invisible on the
// party grid, the bridal hub, invitations, and onboarding (the Shari
// Charrington / Brittany Rock bug class). Fix: call the real party-model
// promotion route the moment someone is added, and swap the throwaway id
// for the returned canonical pm_ id everywhere it was used.
//
// Fires in the background, not blocking the modal close/render. The
// throwaway id renders immediately (same speed as before), then gets
// swapped in place once the promotion call resolves. This is safe because
// the backend is fully idempotent (same bride+name always returns the same
// pm_ id), so a slow network or a repeat add can never create a duplicate
// or leave the draft in a broken state. Worst case on failure, the person
// stays on the throwaway id and a loud banner says so, exactly as before
// this fix shipped, never a silent regression.
function promoteManualPersonInBackground_(throwawayPersonId, memberName, memberRole) {
  if (!hasBridge) return; // no bridge available (e.g. local dev), leave throwaway id in place
  jvBridge.callAdmin('bridge_runsheet_promoteManualPerson', {
    brideName: state.currentBride,
    memberName: memberName,
    memberRole: memberRole || 'Party Member'
  }).then(function(r) {
    var data = r && r.data;
    if (!r || !r.ok || !data || !data.ok || !data.partyMemberId) {
      showBanner(escapeHtml(memberName) + " was added to the runsheet, but could not be linked to the party model (" +
        ((data && data.error) || (r && r.error) || "unknown error") +
        "). She will not appear on the party grid or bridal hub until this is retried.", "error");
      return;
    }

    var newId = data.partyMemberId;
    var swapped = 0;
    (state.sourceServicesDraft || []).forEach(function(s) {
      if (s.personId === throwawayPersonId) {
        var slugPart = String(s.id || s.itemId || "").split("|")[1] || "";
        s.personId = newId;
        s.id = newId + (slugPart ? "|" + slugPart : "");
        s.itemId = s.id;
        swapped++;
      }
    });

    if (swapped) {
      rederiveDraftState_();
      state.timelineDirty = true;
      renderStage();
      showBanner(escapeHtml(memberName) + " is now a real party member, she'll appear on the party grid and can be invited to the hub.", "success");
    }
  }).catch(function(err) {
    showBanner(escapeHtml(memberName) + " was added to the runsheet, but the party-model link failed (" +
      getErrorMessage(err) + "). She will not appear on the party grid or bridal hub until this is retried.", "error");
  });
}

function confirmAddPerson_() {
  var role     = document.getElementById("addPersonRole").value.trim();
  var label    = document.getElementById("addPersonLabel").value.trim();
  var duration = parseInt(document.getElementById("addPersonDuration").value, 10) || 60;
  var artist   = document.getElementById("addPersonArtist").value.trim();
  var notes    = document.getElementById("addPersonNotes").value.trim();

  // Gather checked services
  var services = [];
  var checks = document.querySelectorAll("#addPersonServices input[type='checkbox']:checked");
  for (var i = 0; i < checks.length; i++) {
    services.push(checks[i].value);
  }

  if (!label) { showBanner("Person label is required.", "error"); return; }
  if (!services.length) { showBanner("Select at least one service.", "error"); return; }

  pushUndoSnapshot_();

  var personSlug = label.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
  var personId   = "person_manual_" + personSlug + "_" + Date.now();
  var baseOrder  = (state.sourceServicesDraft || []).length + 1;

  // Compute a running cursor so each new service slots after the previous one.
  // Start from the latest end time of existing timed services for the resolved artist,
  // or fall back to the workspace draftStartTime, or 9:00 AM as absolute fallback.
  var addPersonCursor = null; // minutes — will be set on first service

  services.forEach(function(svcLabel, idx) {
    var cat  = inferCategoryFromLabel_(svcLabel);
    var slug = svcLabel.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
    var svcId = personId + "|" + slug;

    var assignedArtist = artist;
    if (!assignedArtist) {
      if (cat === "makeup") assignedArtist = (state.skillSelections && state.skillSelections.makeup) || "";
      else if (cat === "hair") assignedArtist = (state.skillSelections && state.skillSelections.hair) || "";
    }

    // Compute start time for the new service (only once per Add Person batch,
    // then advance the cursor by duration for each subsequent service).
    if (addPersonCursor === null) {
      // Find the latest endMin among existing timed services for this artist
      var latestEnd = 0;
      var foundTimed = false;
      (state.sourceServicesDraft || []).forEach(function(s) {
        if (!isServiceTimed_(s)) return;
        var sArtist = String(s.assignedArtist || s.artist || "").trim();
        if (assignedArtist && sArtist === assignedArtist) {
          var end = Number(s.endMin);
          if (!isNaN(end) && end > latestEnd) { latestEnd = end; foundTimed = true; }
          // Also check startMin + durationMin as fallback
          var sStart = Number(s.startMin);
          var sDur = Number(s.durationMin || s.duration || 0);
          if (!isNaN(sStart) && sStart + sDur > latestEnd) { latestEnd = sStart + sDur; foundTimed = true; }
        }
      });

      if (!foundTimed) {
        // No timed services for this artist — try draftStartTime from workspace
        var wsDraft = (state.workspace && state.workspace.draftStartTime)
          ? parseDisplayTimeToMinutes(state.workspace.draftStartTime)
          : null;
        if (wsDraft !== null) {
          addPersonCursor = wsDraft;
        } else {
          // Absolute fallback: 9:00 AM (540 minutes)
          addPersonCursor = 540;
        }
      } else {
        addPersonCursor = latestEnd;
      }
    }

    var svcStartMin = addPersonCursor;
    var svcEndMin   = svcStartMin + duration;
    var svcStartTime = formatMinutesAsDisplayTime_(svcStartMin);
    var svcEndTime   = formatMinutesAsDisplayTime_(svcEndMin);

    // Advance cursor for the next service in this batch
    addPersonCursor = svcEndMin;

    state.sourceServicesDraft.push({
      id:                svcId,
      itemId:            svcId,
      personId:          personId,
      order:             baseOrder + idx,
      person:            label,
      personLabel:       label,
      profileName:       label,
      role:              role,
      service:           svcLabel,
      serviceLabel:      svcLabel,
      category:          cat,
      durationMin:       duration,
      duration:          duration,
      assignedArtist:    assignedArtist,
      artist:            assignedArtist,
      startTime:         svcStartTime,
      start:             svcStartTime,
      endTime:           svcEndTime,
      end:               svcEndTime,
      startMin:          svcStartMin,
      endMin:            svcEndMin,
      isBride:           role === "Bride",
      notes:             notes,
      source:            "manual_builder_add",
      hairArtist:        "",
      makeupArtist:      "",
      hairstyleChosen:   "",
      inspirationFolder: "",
      inspirationSubmitted: false
    });
  });

  // Re-index orders
  state.sourceServicesDraft.forEach(function(s, i) { s.order = i + 1; });

  rederiveDraftState_();
  state.timelineDirty = true;
  closeAddPersonModal_();
  renderStage();
  showBanner(escapeHtml(label) + " added with " + services.length + " service(s). Linking her to the party model...", "success");

  // Promote to a real party-model record in the background (2026-07-30).
  // Does not block the modal close or render above; the throwaway id is
  // swapped for the real pm_ id once this resolves. See
  // promoteManualPersonInBackground_ for full context.
  promoteManualPersonInBackground_(personId, label, role);
}

function togglePartyCard_(personId) {
  const key = String(personId || "");
  state.collapsedPartyCards[key] = !state.collapsedPartyCards[key];
  renderStage();
}

/**
 * STAGE 1 roster redesign (2026-09-13). Toggles the secondary hub-content
 * "show more" region for one person, keyed separately from
 * collapsedPartyCards so it never touches the main card expand/collapse
 * state. No save, no network call, purely a client-side display toggle.
 * @param {string} cardKey
 */
function rsbToggleSecondary_(cardKey) {
  const key = String(cardKey || "");
  state.rsbSecondaryExpanded[key] = !state.rsbSecondaryExpanded[key];
  renderStage();
}

// ─── GUEST SAFETY ALERT BANNER (Prompt 135) ──────────────────────────────────

function loadGuestSafetyAlert(brideId, guestEmail, targetContainerId) {
  if (!brideId || !guestEmail) return;
  jvBridge.callAdmin('bridge_runsheet_getAlerts', { brideId: brideId, guestEmail: guestEmail })
    .then(function(res) {
      if (res.ok) {
        var result = res.data || {};
        if (result && result.hasAlert) {
          renderSafetyBanner(result.alerts, targetContainerId);
        }
      }
    })
    .catch(function() { /* silent fail — safety data is advisory */ });
}

function renderSafetyBanner(alerts, containerId) {
  var container = document.getElementById(containerId);
  if (!container) return;

  var existing = container.querySelector('.jv-safety-banner');
  if (existing) existing.remove();

  var high = alerts.filter(function(a) { return a.level === 'HIGH'; });
  var std  = alerts.filter(function(a) { return a.level === 'STANDARD'; });

  var banner = document.createElement('div');
  banner.className = 'jv-safety-banner';
  banner.style.cssText = [
    'background:var(--alert-critical-bg)',
    'border:2px solid var(--alert-critical-text)',
    'border-radius:10px',
    'padding:12px 16px',
    'margin:10px 0',
    'font-size:0.82rem',
    'line-height:1.5'
  ].join(';');

  var html = '';
  if (high.length) {
    html += '<strong style="color:var(--alert-critical-text);">SAFETY ALERT</strong><br>';
    html += high.map(function(a) {
      return 'Allergens: ' + a.allergens.join(', ');
    }).join('<br>');
  }
  if (std.length) {
    if (high.length) html += '<hr style="border-color:var(--alert-critical-line);margin:8px 0">';
    html += '<strong>Client Note:</strong> ';
    html += std.map(function(a) {
      return a.allergens.join(', ');
    }).join(', ');
  }

  banner.innerHTML = html;
  container.insertBefore(banner, container.firstChild);
}

function schedulePartySafetyAlertsLoad_() {
  var brideId = state.workspace && state.workspace.email;
  if (!brideId) return;
  state.bridalPartyDraft.forEach(function(person, index) {
    var cardKey = String(person.personId || person.id || index);
    var isCollapsed = !!state.collapsedPartyCards[cardKey];
    if (isCollapsed) return;
    var containerId = 'party-safety-alert-' + cardKey;
    loadGuestSafetyAlert(brideId, person.email, containerId);
  });
}

// ─────────────────────────────────────────────────────────────────────────────

function renderPerPersonArtistSelect_(person, skillType, index) {
  const teamOptions = Array.isArray(state.workspace && state.workspace.team) ? state.workspace.team : [];
  const activeTeam = uniqueNames(state.activeTeam || []);

  const allowedRaw = teamOptions.filter(function(artist) {
    if (activeTeam.indexOf(artist.name) === -1) return false;
    if (skillType === "hair") return !!artist.canHair;
    if (skillType === "makeup") return !!artist.canMakeup;
    return false;
  });
  // Sort available artists to the top, unavailable (poll "no" or self block-out)
  // to the bottom. No artist is removed (Jess always picks).
  const allowed = sortArtistsByAvailability_(allowedRaw);

  const currentValue = skillType === "hair"
    ? String(person.hairArtist || "").trim()
    : String(person.makeupArtist || "").trim();

  const fieldName = skillType === "hair" ? "hairArtist" : "makeupArtist";

  return (
    '<select onchange="updatePartyField(' + index + ', \'' + fieldName + '\', this.value)">' +
      '<option value="">Planner default</option>' +
      allowed.map(function(artist) {
        return '<option value="' + escapeHtml(artist.name) + '"' +
          (artist.name === currentValue ? ' selected' : '') + '>' +
          escapeHtml(artist.name) + availDropdownLabel_(artist) +
        '</option>';
      }).join('') +
    '</select>'
  );
}

function buildPartyPersonKey_(personLabel, role, personId) {
  const explicitId = String(personId || "").trim();
  if (explicitId) return explicitId;
  return [
    String(personLabel || "").trim().toLowerCase(),
    String(role || "").trim().toLowerCase()
  ].join("|");
}

function ensureBrideServiceRow_() {
  var ws = state.workspace || {};
  var brideName = String(ws.brideName || state.currentBride || "").trim();
  if (!brideName) return;
  var draft = state.sourceServicesDraft || [];
  var brideLower = brideName.toLowerCase();
  var hasBride = draft.some(function(s) {
    if (s && s.isBride) return true;
    if (String((s && s.role) || "").trim().toLowerCase() === "bride") return true;
    var label = String((s && (s.personLabel || s.person)) || "").trim().toLowerCase();
    return label === brideLower || label === "bride";
  });
  if (hasBride) return;
  var requested = String(ws.servicesRequested || ws.services_requested || ws.packageLabel || "").toLowerCase();
  var wantsHair = requested.indexOf("hair") > -1;
  var wantsMakeup = requested.indexOf("makeup") > -1 || !requested;
  var labels = [];
  if (wantsHair && wantsMakeup) labels.push("Hair & Makeup");
  else if (wantsHair) labels.push("Hair");
  else labels.push("Makeup");
  var personSlug = brideName.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
  var personId = "person_bride_" + personSlug;
  var duration = 60;
  var toUnshift = [];
  labels.forEach(function(svcLabel, idx) {
    var cat = (typeof inferCategoryFromLabel_ === "function") ? inferCategoryFromLabel_(svcLabel) : "";
    var slug = svcLabel.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
    var svcId = personId + "|" + slug;
    toUnshift.push({
      id: svcId,
      itemId: svcId,
      personId: personId,
      order: idx,
      person: brideName,
      personLabel: brideName,
      profileName: brideName,
      role: "Bride",
      service: svcLabel,
      serviceLabel: svcLabel,
      category: cat,
      durationMin: duration,
      duration: duration,
      assignedArtist: "",
      artist: "",
      isBride: true,
      notes: "",
      source: "auto_seed_bride",
      hairArtist: "",
      makeupArtist: "",
      hairstyleChosen: "",
      inspirationFolder: "",
      inspirationSubmitted: false
    });
  });
  state.sourceServicesDraft = toUnshift.concat(draft);
  state.sourceServicesDraft.forEach(function(s, i) { s.order = i + 1; });
}

function syncBridalPartyToSourceServices_() {
  const peopleByKey = {};

  (state.bridalPartyDraft || []).forEach(function(person, index) {
    const idKey = buildPartyPersonKey_(person.personLabel, person.role, person.personId || person.id || index);
    const labelKey = buildPartyPersonKey_(person.personLabel, person.role, "");
    peopleByKey[idKey] = person;
    peopleByKey[labelKey] = person;
  });

  state.sourceServicesDraft = (state.sourceServicesDraft || []).map(function(service) {
    const key = buildPartyPersonKey_(
      service.personLabel || service.person || "",
      service.role || inferRoleFromPerson_(service.personLabel || service.person || "", service),
      service.personId || ""
    );
    const fallbackKey = buildPartyPersonKey_(
      service.personLabel || service.person || "",
      service.role || inferRoleFromPerson_(service.personLabel || service.person || "", service),
      ""
    );
    const person = peopleByKey[key] || peopleByKey[fallbackKey];
    if (!person) return service;

    const clone = Object.assign({}, service);
    clone.inspirationFolder = String(person.inspirationFolder || "").trim();
    clone.inspirationSubmitted = !!person.inspirationSubmitted;
    return clone;
  });
}


  function updatePartyField(index, field, value) {
  const person = state.bridalPartyDraft[index];
  if (!person) return;

  const safeValue = String(value || "").trim();
  const isBride = String(person.role || "").trim().toLowerCase() === "bride";

  if (field === "personLabel") {
    // VALIDATION (2026-09-13, state/findings/runsheet-seat-rename-no-validation-
    // 2026-09-13.md): this used to accept ANY string with zero checks, including
    // empty. An empty label plus removePartyMemberFromRoster_'s personLabel-string
    // fallback (used when personId is blank on a record) created a real silent
    // data-loss path: rename person A to collide with person B's exact label,
    // then remove A -- B's services got silently stripped too via the label
    // match. Two checks only, minimal and targeted per the trace's own scope:
    if (!safeValue) {
      showBanner("Name can't be empty. Keeping the previous name.", "warning");
      renderStage();
      return;
    }
    var collidesWith = (state.bridalPartyDraft || []).some(function(other, otherIndex) {
      if (otherIndex === index) return false;
      return String(other.personLabel || "").trim().toLowerCase() === safeValue.toLowerCase();
    });
    if (collidesWith) {
      showBanner('Another person in this party is already named "' + safeValue + '". Use a different name (e.g. add a last initial) to keep them distinguishable.', "warning");
      renderStage();
      return;
    }
    person.personLabel = safeValue;
    person.name = safeValue;
    person.profileName = safeValue;
  } else if (field === "role") {
    person.role = safeValue;
  } else if (field === "hairstyleChosen") {
    person.hairstyleChosen = safeValue;
  } else if (field === "inspirationFolder") {
    person.inspirationFolder = safeValue;
    // Bride's folder URL is the canonical CRM col 27, persisted via the hub
    // writeback bridge so future workspace loads see it. Party-member folder
    // URLs persist via the same writeback bridge into _PartyMembers, the
    // jv_party_members_json blob, and the jv_bridal_party_member metaobject's
    // inspiration_folder field (added so Shopify has the party member's
    // folder link too, not just the bride's).
    if (isBride) {
      try { saveBrideHubField_('inspirationFolder', safeValue); }
      catch (e) { console.warn('saveBrideHubField_(inspirationFolder) failed:', e); }
    } else {
      try { savePartyMemberHubField_(index, 'inspirationFolder', safeValue); }
      catch (e) { console.warn('savePartyMemberHubField_(inspirationFolder) failed:', e); }
    }
  } else if (field === "notes") {
    person.notes = safeValue;
  } else if (field === "assignedArtist") {
    person.assignedArtist = safeValue;
  } else if (field === "hairArtist") {
    person.hairArtist = safeValue;
  } else if (field === "makeupArtist") {
    person.makeupArtist = safeValue;
  } else {
    person[field] = safeValue;
  }

  syncBridalPartyToSourceDraft_();
  syncWorkspaceServicesFromSourceDraft_();

  ((state.workspace && state.workspace.services) || []).forEach(function(service) {
    const samePerson =
      String(service.personId || "").trim() === String(person.personId || "").trim() ||
      String(service.personLabel || service.person || "").trim() === String(person.personLabel || "").trim();

    if (!samePerson) return;

    service.person = person.personLabel || service.person;
    service.personLabel = person.personLabel || service.personLabel;
    service.role = person.role || service.role || "";
    service.notes = person.notes || "";
    service.hairArtist = person.hairArtist || "";
    service.makeupArtist = person.makeupArtist || "";
    service.inspirationFolder = person.inspirationFolder || "";
    service.inspirationSubmitted = !!person.inspirationSubmitted;

    // Sync artist assignments back into workspace services
    if (field === "assignedArtist") {
      service.assignedArtist = person.assignedArtist || "";
      service.artist = person.assignedArtist || "";
    }
    if (field === "hairArtist") {
      service.hairArtist = person.hairArtist || "";
    }
    if (field === "makeupArtist") {
      service.makeupArtist = person.makeupArtist || "";
    }
  });

  state.timelineDirty = true;
  renderStage();
}

    function togglePartyService(index, label, checked) {
  const person = state.bridalPartyDraft[index];
  if (!person) return;

  const list = (person.servicesBooked || []).slice();
  const pos = list.indexOf(label);

  if (checked && pos === -1) list.push(label);
  if (!checked && pos > -1) list.splice(pos, 1);

  // If Hair is being unchecked, clear the hairstyle choice and remove Hair Prep if present
  if (label === "Hair" && !checked) {
    person.hairstyleChosen = "";
    const prepPos = list.indexOf("Hair Prep");
    if (prepPos > -1) list.splice(prepPos, 1);
  }

  person.servicesBooked = list;
  syncBridalPartyToSourceDraft_();
  syncWorkspaceServicesFromSourceDraft_();
  state.timelineDirty = true;
  renderStage();
}

    function updatePartyHairstyle_(index, value) {
  const person = state.bridalPartyDraft[index];
  if (!person) return;

  const prev = person.hairstyleChosen || "";
  person.hairstyleChosen = value;

  const list = (person.servicesBooked || []).slice();
  if (value === "Hollywood Waves") {
    if (list.indexOf("Hair Prep") === -1) list.push("Hair Prep");
  } else if (prev === "Hollywood Waves") {
    const pos = list.indexOf("Hair Prep");
    if (pos > -1) list.splice(pos, 1);
  }
  person.servicesBooked = list;

  syncBridalPartyToSourceDraft_();
  syncWorkspaceServicesFromSourceDraft_();
  state.timelineDirty = true;
  renderStage();

  // Pass 2 — also writeback to the hub. Bride → bride_brief_json.hairstylePref,
  // party member → _PartyMembers.HAIR_STYLE + blob + metaobject.
  // Suppress the "TBC" sentinel — it's a UI placeholder, not a real choice.
  var hairValue = String(value || '').trim();
  if (hairValue && hairValue.toLowerCase() !== 'tbc') {
    var isBride = String(person.role || "").trim().toLowerCase() === "bride";
    // Status span id matches the one rendered next to the Preferred
    // Hairstyle select in renderBridalPartyCards_ (Cassandra Healey P1(4)).
    var hairStatusId = 'jv-hubstat-hairstyle-' + index;
    if (isBride) {
      saveBrideHubField_('hairstylePref', hairValue, hairStatusId);
    } else {
      savePartyMemberHubField_(index, 'hair_style', hairValue, hairStatusId);
    }
  }
}

// ─── Pass 2 hub-field writeback (calls bridge_runsheet_save*HubFields) ───
// Optimistic: writes to state.brideCardsData immediately, fires bridge, alerts
// + reverts on error. Saves on blur (onchange), no debounce needed.
//
// LOCAL-FIRST RETRY QUEUE (2026-08-20, per CLAUDE.md BUILD DOCTRINE #2, "a
// confirmed item must NEVER be silently lost, background failure = retry
// and fail LOUD to admin, never to the user"). Before this, a GAS timeout
// under slow-instance load (confirmed live: 20-30s+ per call some evenings)
// reverted the field on screen with only a transient toast banner, easy to
// miss during a real multi-field editing session, and the edit was then
// genuinely gone with no record it ever happened. Root incident: Georgia C's
// runsheet edit session, 2026-08-20, a hairstyle save hit the 30s relay
// ceiling and reverted silently while other saves in the same session
// succeeded at 20-23s, masking the loss.
//
// Every failed save (network error OR a non-ok response, EXCLUDING the
// intentional 'writeback disabled' feature-flag-off case) is queued to
// localStorage instead of reverted immediately. A background drain retries
// with backoff. The field is only reverted on screen once the queue drains
// without success, at which point a persistent (non-auto-dismissing) failure
// banner fires, so a save can still fail, but it can never fail invisibly.
var HUB_FIELD_RETRY_KEY_ = 'jv_runsheet_hubfield_retryqueue';
var HUB_FIELD_RETRY_MAX_ATTEMPTS_ = 5;
var HUB_FIELD_RETRY_BASE_DELAY_MS_ = 4000; // 4s, 8s, 16s, 32s, 64s
var _hubFieldRetryTimer_ = null;

function hubFieldRetryQueue_read_() {
  try {
    var raw = localStorage.getItem(HUB_FIELD_RETRY_KEY_);
    return raw ? JSON.parse(raw) : [];
  } catch (e) { return []; }
}

function hubFieldRetryQueue_write_(queue) {
  try { localStorage.setItem(HUB_FIELD_RETRY_KEY_, JSON.stringify(queue)); }
  catch (e) { console.warn('hubFieldRetryQueue_write_: localStorage write failed', e); }
}

/**
 * Queue a failed hub-field save for background retry. Never throws, a
 * broken queue must not compound the failure it exists to protect against.
 * @param {'bride'|'party'} kind
 * @param {string} route      'bridge_runsheet_saveBrideHubFields' or '...savePartyMemberHubFields'
 * @param {Object} payload    Exact payload already built for the bridge call.
 * @param {Function} onFinalFailure  Called (msg) if retries exhaust, caller reverts the field and shows the persistent banner.
 */
function hubFieldRetryQueue_enqueue_(kind, route, payload, onFinalFailure, statusId) {
  try {
    var queue = hubFieldRetryQueue_read_();
    var entry = {
      id: kind + '_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8),
      kind: kind,
      route: route,
      payload: payload,
      attempts: 0,
      queuedAt: new Date().toISOString(),
      brideName: state.currentBride || '',
      statusId: statusId || null // display-only, see hubFieldStatus_set_ above
    };
    queue.push(entry);
    hubFieldRetryQueue_write_(queue);
    _hubFieldRetryOnFailureCallbacks_[entry.id] = onFinalFailure;
    hubFieldRetryQueue_scheduleDrain_(HUB_FIELD_RETRY_BASE_DELAY_MS_);
  } catch (e) {
    console.error('hubFieldRetryQueue_enqueue_ failed, save cannot be retried, reverting now:', e);
    if (typeof onFinalFailure === 'function') onFinalFailure('retry queue itself failed: ' + (e && e.message));
  }
}

// In-memory only (not persisted, a page reload re-derives "final failure" by
// simply leaving the queued entry for the NEXT session's drain to retry, so
// losing these callbacks on reload is safe; the underlying data is not lost,
// only the currently-open tab's revert-the-field UI hook for THIS attempt).
var _hubFieldRetryOnFailureCallbacks_ = {};

function hubFieldRetryQueue_scheduleDrain_(delayMs) {
  if (_hubFieldRetryTimer_) return; // a drain is already scheduled
  _hubFieldRetryTimer_ = setTimeout(function() {
    _hubFieldRetryTimer_ = null;
    hubFieldRetryQueue_drain_();
  }, delayMs);
}

function hubFieldRetryQueue_drain_() {
  if (!hasBridge) { hubFieldRetryQueue_scheduleDrain_(HUB_FIELD_RETRY_BASE_DELAY_MS_); return; }
  var queue = hubFieldRetryQueue_read_();
  if (!queue.length) return;

  var entry = queue[0];
  entry.attempts++;

  jvBridge.callAdmin(entry.route, entry.payload)
    .then(function(res) {
      var remaining = hubFieldRetryQueue_read_().filter(function(e) { return e.id !== entry.id; });
      if (res && res.ok) {
        hubFieldRetryQueue_write_(remaining);
        delete _hubFieldRetryOnFailureCallbacks_[entry.id];
        hubFieldStatus_set_(entry.statusId, 'saved');
        showBanner('Saved (recovered after a slow-server retry).', 'success');
        if (remaining.length) hubFieldRetryQueue_scheduleDrain_(500);
        return;
      }
      var msg = (res && res.error) || 'unknown error';
      if (msg === 'writeback disabled') {
        // Intentional server state, drop from queue, no alert, no revert.
        hubFieldRetryQueue_write_(remaining);
        delete _hubFieldRetryOnFailureCallbacks_[entry.id];
        hubFieldStatus_set_(entry.statusId, 'clear');
        return;
      }
      hubFieldRetryQueue_handleAttemptFailure_(entry, msg);
    })
    .catch(function(err) {
      hubFieldRetryQueue_handleAttemptFailure_(entry, err && err.message ? err.message : String(err));
    });
}

function hubFieldRetryQueue_handleAttemptFailure_(entry, msg) {
  var queue = hubFieldRetryQueue_read_();
  var idx = queue.findIndex(function(e) { return e.id === entry.id; });
  if (idx === -1) return; // already resolved by another path

  if (entry.attempts >= HUB_FIELD_RETRY_MAX_ATTEMPTS_) {
    queue.splice(idx, 1);
    hubFieldRetryQueue_write_(queue);
    var cb = _hubFieldRetryOnFailureCallbacks_[entry.id];
    delete _hubFieldRetryOnFailureCallbacks_[entry.id];
    hubFieldStatus_set_(entry.statusId, 'failed');
    // FAIL LOUD, per doctrine, a persistent banner (no auto-dismiss timeout,
    // see showBanner's success-only auto-clear), not a toast that scrolls away.
    showBanner(
      'Could not save "' + hubFieldRetryQueue_describeEntry_(entry) + '" after ' + entry.attempts +
      ' attempts (server may be overloaded). This edit has been REVERTED, please try again once things speed up.',
      'error'
    );
    if (typeof cb === 'function') cb(msg);
  } else {
    queue[idx] = entry;
    hubFieldRetryQueue_write_(queue);
    hubFieldStatus_set_(entry.statusId, 'retrying');
    var delay = HUB_FIELD_RETRY_BASE_DELAY_MS_ * Math.pow(2, entry.attempts - 1);
    hubFieldRetryQueue_scheduleDrain_(delay);
  }
}

function hubFieldRetryQueue_describeEntry_(entry) {
  var fieldKeys = Object.keys((entry.payload && entry.payload.fields) || {});
  var field = fieldKeys[0] || 'field';
  if (entry.kind === 'party') {
    return field + ' for ' + (entry.payload.memberName || 'party member');
  }
  return field + ' (bride)';
}

/**
 * Resume any saves left queued from a previous page load (e.g. the tab was
 * closed mid-retry). Called once on init. A stale entry just resumes its
 * backoff from attempt 0, safe, since the server-side write is idempotent
 * per bride+field (last-write-wins on the same field).
 */
function hubFieldRetryQueue_resumeOnLoad_() {
  var queue = hubFieldRetryQueue_read_();
  if (queue.length) {
    console.info('[hubFieldRetryQueue] resuming ' + queue.length + ' queued save(s) from a previous session');
    hubFieldRetryQueue_scheduleDrain_(HUB_FIELD_RETRY_BASE_DELAY_MS_);
  }
}
try { hubFieldRetryQueue_resumeOnLoad_(); } catch (_) {}

/**
 * D1-first fast path for party-member/bride hub field saves (2026-08-20).
 *
 * Spec: BRIEFS/SPEC-party-member-writes-cloudflare-first-2026-08-20.md
 * Handoff: AGENT-PROMPTS/handoffs/HANDOFF-wire-party-member-d1-fast-path-to-runsheet-builder-2026-08-20.md
 * Worker: DEPOLYMENT/jv-admin-saves/worker.js
 * Relay route: bridge_runsheet_savePartyMemberFieldD1 (DEPOLYMENT/
 * cloudflare-cors-relay/worker.js), which forwards to the Worker above over
 * a service binding after passing the relay's own admin-key gate.
 *
 * WHY THIS EXISTS. The existing synchronous saveBrideHubField_ /
 * savePartyMemberHubField_ calls (below) run bridge_runsheet_
 * saveBrideHubFields_ / ...savePartyMemberHubFields_ directly against GAS,
 * observed 8.4-40.8s per call live (some genuine 504s) during Georgia C's
 * runsheet edit session, 2026-08-20 (the trigger for the whole build). This
 * function attempts a durable Cloudflare D1 write FIRST, which the Worker
 * confirms in well under a second, before GAS is ever touched. GAS still
 * gets the real write within about a minute via a background drain
 * (PartyMemberOutboxDrain_2026_08_20.gs), this is "instant confirmation,
 * async backup," not a replacement for the GAS write.
 *
 * FIELD KEY TRANSLATION. This file's own field keys (hairstylePref,
 * makeupVibe, beautyNotes, inspirationFolder for the bride; hair_style,
 * allergies, skin_concerns, colour_palette, hair_notes, inspo_notes,
 * inspirationFolder for a party member) do not perfectly match the D1
 * outbox's own field vocabulary (016_party_member_write_outbox.sql /
 * jv-admin-saves/worker.js ALLOWED_FIELDS), specifically inspirationFolder
 * (frontend/GAS spelling) vs inspiration_folder_url (party member) /
 * inspiration_folder (bride) on the D1 side, and the bride fields are
 * snake_case there (hairstyle_pref/makeup_vibe/beauty_notes) while camelCase
 * here. Translated explicitly below rather than assumed identical, a
 * silent mismatch would mean the outbox accepts a field name the drain's
 * own PARTY_MEMBER_OUTBOX_BRIDE_FIELDS_/..._MEMBER_FIELDS_ maps do not
 * recognise, failing the DRAIN side invisibly to this frontend (see that
 * file's own "field not recognised" reason string).
 *
 * REFUSE, DON'T ATTEMPT BLIND. Requires state.workspace.brideUid (surfaced
 * live in RunsheetEngineV4.gs, confirmed present at appBuildSidebarWorkspaceFromStoredResponse_
 * as of 2026-08-20) and a resolvable pm_id for party-member fields (reusing
 * the SAME synchronous lookup chain removePartyMemberFromRoster_ already
 * proves live, via getHubMemberForPerson_ + state.partyProfiles +
 * state.workspace.people, all synchronous, the async network-refresh
 * fourth fallback that function has is deliberately NOT reused here: this is
 * a FAST PATH, and a fast path that itself waits on a slow network call to
 * even attempt would defeat its own purpose). Missing either value skips the
 * D1 attempt entirely and falls straight to the existing GAS call, which is
 * always correct, just not instant.
 *
 * @param {'bride'|'party'} kind
 * @param {Object} opts - { brideUid, pmId?, brideEmail, memberName? }
 * @param {string} fieldKey - this file's own key spelling
 * @param {string} value
 * @returns {Promise<boolean>} resolves true if the D1 fast path accepted
 *   the write (caller should still let the existing GAS call proceed
 *   unchanged, this is a parallel confirmation channel, not a gate on it),
 *   false if the fast path was skipped or declined (caller's existing GAS
 *   path is the ONLY save in that case, exactly as before this build).
 */
var HUB_FIELD_D1_BRIDE_MAP_ = {
  hairstylePref: 'hairstyle_pref',
  makeupVibe: 'makeup_vibe',
  beautyNotes: 'beauty_notes',
  inspirationFolder: 'inspiration_folder'
};
var HUB_FIELD_D1_MEMBER_MAP_ = {
  hair_style: 'hair_style',
  allergies: 'allergies',
  skin_concerns: 'skin_concerns',
  colour_palette: 'colour_palette',
  hair_notes: 'hair_notes',
  inspo_notes: 'inspo_notes',
  inspirationFolder: 'inspiration_folder_url'
};

/**
 * Resolve pm_id for a party-card person, synchronously, from whatever is
 * already in memory. Deliberately mirrors ONLY the first three (synchronous)
 * sources removePartyMemberFromRoster_ uses, in the same order, for the same
 * reasoning documented there (getHubMemberForPerson_ cache, then
 * state.partyProfiles, then state.workspace.people), see that function's
 * own comments for why each source exists and in what order. Does NOT
 * include that function's fourth (async network-refresh) fallback: this is
 * a fast-path resolver, a cache miss here just means "skip D1, use the
 * existing GAS call," never "wait on a network round-trip to find out."
 */
function resolvePmIdSync_(person) {
  if (!person) return '';

  var hubMember = (typeof getHubMemberForPerson_ === 'function') ? getHubMemberForPerson_(person) : null;
  var pmId = hubMember && String(hubMember.pm_id || '').trim();
  if (pmId) return pmId;

  if (Array.isArray(state.partyProfiles)) {
    var nameLow = String(person.name || person.profileName || person.personLabel || '').trim().toLowerCase();
    var roleLow = String(person.role || '').trim().toLowerCase();
    if (nameLow) {
      var byNameRole = null, byName = null;
      for (var i = 0; i < state.partyProfiles.length; i++) {
        var p = state.partyProfiles[i];
        var pn = String(p.name || '').trim().toLowerCase();
        if (pn !== nameLow) continue;
        if (String(p.role || '').trim().toLowerCase() === roleLow) { byNameRole = p; break; }
        byName = byName || p;
      }
      var match = byNameRole || byName;
      var fromProfiles = match && String(match.pm_id || '').trim();
      if (fromProfiles) return fromProfiles;
    }
  }

  if (state.workspace && Array.isArray(state.workspace.people)) {
    var wsNameLow = String(person.name || person.profileName || person.personLabel || '').trim().toLowerCase();
    var wsPersonId = String(person.personId || person.id || '').trim();
    for (var j = 0; j < state.workspace.people.length; j++) {
      var wp = state.workspace.people[j];
      if (!wp) continue;
      var idMatch = wsPersonId && String(wp.personId || wp.id || '').trim() === wsPersonId;
      var nameMatch = wsNameLow && String(wp.name || '').trim().toLowerCase() === wsNameLow;
      if (!idMatch && !nameMatch) continue;
      var wsPmId = String(wp.pmId || wp.pm_id || '').trim();
      if (wsPmId) return wsPmId;
    }
  }

  return '';
}

function saveHubFieldD1Fast_(kind, opts, fieldKey, value) {
  var brideUid = String((opts && opts.brideUid) || '').trim();
  if (!brideUid) return Promise.resolve(false); // no fast path without it, see file header

  var d1Field;
  if (kind === 'bride') {
    d1Field = HUB_FIELD_D1_BRIDE_MAP_[fieldKey];
  } else {
    d1Field = HUB_FIELD_D1_MEMBER_MAP_[fieldKey];
  }
  if (!d1Field) return Promise.resolve(false); // field not on the D1 allowlist, skip silently

  var pmId = String((opts && opts.pmId) || '').trim();
  if (kind === 'party' && !pmId) return Promise.resolve(false); // can't write without it

  var payload = {
    bride_uid: brideUid,
    pm_id: kind === 'party' ? pmId : (brideUid + ':bride'),
    field: d1Field,
    value: value,
    idempotency_key: brideUid + '|' + (pmId || 'bride') + '|' + d1Field + '|' + Date.now() + '|' + Math.random().toString(36).slice(2),
    bride_email: (opts && opts.brideEmail) || null,
    member_name: kind === 'party' ? ((opts && opts.memberName) || null) : null
  };

  return jvBridge.callAdmin('bridge_runsheet_savePartyMemberFieldD1', payload)
    .then(function(res) {
      // ok:true means D1 durably accepted the write, the drain will apply
      // it to CRM/_PartyMembers within ~1min. fallback:'gas' (per the
      // Worker's own contract) or any other non-ok is a clean, expected
      // "skip the fast path" signal, not an error, the caller's existing
      // GAS call is unaffected either way, so nothing here needs to alert,
      // revert, or retry. This channel is purely additive.
      return !!(res && res.ok);
    })
    .catch(function() {
      // Network/relay failure on the FAST path only. Never surfaced to the
      // user, never retried here, the existing GAS call (which the caller
      // runs regardless of this promise's result) is the real save and was
      // never blocked by this attempt.
      return false;
    });
}

// --- Per-field save-status indicator (Cassandra Healey P1(4), 2026-10-27) ---
// BRIEFS/HANDOFF-runsheet-builder-cassandra-findings-2026-10-26.md P1(4):
// "party hubfield writes time out and queue silently with no per-member
// confirmation." The hubFieldRetryQueue_* system above (2026-08-20) already
// retries with backoff and fails LOUD on exhaustion, this layer is purely
// the missing VISIBLE signal while a save is pending/retrying, plus a brief
// confirmation once it actually lands. It never changes what gets saved,
// retried, or reverted, the retry queue remains the single source of truth
// for whether a write landed, this only decorates the matching input.
// Looked up fresh by DOM id every time (never a cached node reference) so it
// keeps working across any renderStage() that happens while a save is still
// in flight. Missing node (card collapsed, person removed, a later
// re-render dropped it) is a silent no-op.
function hubFieldStatus_set_(statusId, mode) {
  if (!statusId) return;
  try {
    var el = document.getElementById(statusId);
    if (!el) return;
    var myVersion = (parseInt(el.getAttribute('data-jv-status-v') || '0', 10) || 0) + 1;
    el.setAttribute('data-jv-status-v', String(myVersion));
    var CFG = {
      pending:  { text: 'Saving...',             color: '#8a7a6a' },
      retrying: { text: 'Retrying...',           color: '#b9772e' },
      saved:    { text: 'Saved',                 color: '#1f6b46' },
      failed:   { text: 'Not saved, reverted',   color: '#9a2020' }
    };
    var cfg = CFG[mode];
    if (!cfg) { el.textContent = ''; return; } // unknown/empty mode = clear
    el.textContent = cfg.text;
    el.style.color = cfg.color;
    if (mode === 'saved') {
      // Auto-clear the confirmation after a moment so it doesn't linger as
      // permanent clutter, but only if nothing newer (a later pending/
      // retrying/failed call) has stamped a higher version in the meantime.
      setTimeout(function() {
        try {
          var cur = document.getElementById(statusId);
          if (cur && cur.getAttribute('data-jv-status-v') === String(myVersion)) cur.textContent = '';
        } catch (_) {}
      }, 2500);
    }
  } catch (_) {}
}

// Default status-span id for a bride hub field (renderHubBrideDetails_'s
// editable rows). hairstylePref's own status span lives on the Preferred
// Hairstyle select in the main card (renderBridalPartyCards_), not here,
// callers pass an explicit statusIdOverride for that field instead.
function hubFieldStatusId_bride_(fieldKey) {
  if (fieldKey === 'beautyNotes' || fieldKey === 'makeupVibe') return 'jv-hubstat-bride-' + fieldKey;
  return null;
}

// Default status-span id for a party-member hub field (renderHubMemberDetails_'s
// editable rows). hair_style's own status span lives on the Preferred
// Hairstyle select in the main card, callers pass an explicit
// statusIdOverride for that field instead. Keyed on personIndex (the same
// render-time index baked into that input's own onchange handler), so a
// cosmetic mismatch can only happen if the roster reorders while this exact
// save is still in flight, the save itself is still resolved against the
// stable personId by the WRONG-PERSON GUARD below, this id is display-only.
function hubFieldStatusId_party_(personIndex, fieldKey) {
  var MAPPED = { allergies: 1, skin_concerns: 1, colour_palette: 1, hair_notes: 1, inspo_notes: 1 };
  if (MAPPED[fieldKey]) return 'jv-hubstat-party-' + personIndex + '-' + fieldKey;
  return null;
}

function saveBrideHubField_(fieldKey, value, statusIdOverride) {
  if (!hasBridge) { console.warn('saveBrideHubField_: no bridge'); return; }
  var _statusId = statusIdOverride || hubFieldStatusId_bride_(fieldKey);
  var brideEmail = state.workspace && state.workspace.email;
  if (!brideEmail) { showBanner('Cannot save: bride email missing from workspace.', 'error'); return; }
  hubFieldStatus_set_(_statusId, 'pending');

  var newValue = String(value == null ? '' : value);
  var d = state.brideCardsData || {};
  d.brideProfile = d.brideProfile || {};
  d.brideBrief   = d.brideBrief   || {};

  // Find the bride entry in the local draft so we can optimistically update
  // person.inspirationFolder + revert on failure (mirrors party-member flow).
  var brideDraftEntry = null;
  if (Array.isArray(state.bridalPartyDraft)) {
    for (var _bi = 0; _bi < state.bridalPartyDraft.length; _bi++) {
      var _p = state.bridalPartyDraft[_bi];
      if (_p && String(_p.role || '').trim().toLowerCase() === 'bride') {
        brideDraftEntry = _p; break;
      }
    }
  }

  // Capture prev for revert
  var prev;
  if (fieldKey === 'beautyNotes') {
    prev = d.brideProfile.beautyNotes;
    d.brideProfile.beautyNotes = newValue;
  } else if (fieldKey === 'makeupVibe') {
    prev = d.brideBrief.makeupVibe;
    d.brideBrief.makeupVibe = newValue;
  } else if (fieldKey === 'hairstylePref') {
    prev = d.brideBrief.hairstylePref;
    d.brideBrief.hairstylePref = newValue;
  } else if (fieldKey === 'inspirationFolder') {
    // CRM col 27 write — the draft entry already holds the value (set by
    // updatePartyField) so prev tracking is for the bridge-failure revert.
    prev = brideDraftEntry ? brideDraftEntry.inspirationFolder : '';
    if (brideDraftEntry) brideDraftEntry.inspirationFolder = newValue;
  } else {
    console.warn('saveBrideHubField_: unknown field', fieldKey);
    hubFieldStatus_set_(_statusId, 'clear');
    return;
  }

  var payload = { brideEmail: brideEmail, fields: {} };
  payload.fields[fieldKey] = newValue;

  // D1-first fast path (2026-08-20). Fire-and-forget: the existing
  // jvBridge.callAdmin('bridge_runsheet_saveBrideHubFields', ...) call below
  // is UNCHANGED and remains the real save either way. This just gives the
  // admin an instant-feeling confirmation channel when it succeeds, and is a
  // silent no-op when it doesn't (missing brideUid, unrecognised field, or a
  // Worker/relay failure).
  var d1BrideUid = state.workspace && state.workspace.brideUid;
  saveHubFieldD1Fast_('bride', { brideUid: d1BrideUid, brideEmail: brideEmail }, fieldKey, newValue);

  function revertBrideField_() {
    if (fieldKey === 'beautyNotes')   d.brideProfile.beautyNotes   = prev;
    if (fieldKey === 'makeupVibe')    d.brideBrief.makeupVibe      = prev;
    if (fieldKey === 'hairstylePref') d.brideBrief.hairstylePref   = prev;
    if (fieldKey === 'inspirationFolder' && brideDraftEntry) brideDraftEntry.inspirationFolder = prev;
    renderStage();
  }

  jvBridge.callAdmin('bridge_runsheet_saveBrideHubFields', payload)
    .then(function(res) {
      if (!res || !res.ok) {
        var msg = (res && res.error) || 'unknown error';
        // Writeback-disabled is an expected, intentional server state (feature
        // flag off), not a user-facing error. Revert quietly, no alert/banner.
        if (msg === 'writeback disabled') {
          console.info('saveBrideHubField_: writeback disabled, reverting silently');
          hubFieldStatus_set_(_statusId, 'clear');
          revertBrideField_();
        } else {
          console.warn('saveBrideHubField_ failed, queueing for background retry:', msg);
          hubFieldStatus_set_(_statusId, 'retrying');
          hubFieldRetryQueue_enqueue_('bride', 'bridge_runsheet_saveBrideHubFields', payload, revertBrideField_, _statusId);
        }
      } else {
        hubFieldStatus_set_(_statusId, 'saved');
      }
    })
    .catch(function(err) {
      console.warn('saveBrideHubField_ exception, queueing for background retry:', err);
      hubFieldStatus_set_(_statusId, 'retrying');
      hubFieldRetryQueue_enqueue_('bride', 'bridge_runsheet_saveBrideHubFields', payload, revertBrideField_, _statusId);
    });
}

function savePartyMemberHubField_(personIndex, fieldKey, value, statusIdOverride) {
  if (!hasBridge) { console.warn('savePartyMemberHubField_: no bridge'); return; }
  var _statusId = statusIdOverride || hubFieldStatusId_party_(personIndex, fieldKey);
  var brideEmail = state.workspace && state.workspace.email;
  if (!brideEmail) { showBanner('Cannot save: bride email missing from workspace.', 'error'); return; }
  hubFieldStatus_set_(_statusId, 'pending');

  // WRONG-PERSON GUARD (2026-08-20). This function used to trust personIndex
  // as a stable pointer into state.bridalPartyDraft. It is not stable: the
  // array is re-sorted/re-rendered on every drag-and-drop (syncBridalPartyToSourceDraft_
  // plus renderStage() run on every drop), and under slow GAS (confirmed live
  // 20-40s+ per call some evenings) a save can still be in flight, or sitting
  // in the retry queue, well after several drags have reshuffled the array.
  // By the time it resolves, index N may point at a DIFFERENT person than the
  // one the user actually edited. Root incident: Georgia C's runsheet edit
  // session, 2026-08-20, repeated saves for "Anna" fired while other people's
  // services were being dragged, producing a builder-vs-published-preview
  // mismatch (confirmed via console trace, not theorised).
  //
  // Fix: resolve the person by their stable personId (or id) captured at the
  // moment the field's onchange fired, the same identifier every other caller
  // in this file already treats as canonical (see person.personId||person.id
  // used throughout). Fall back to the index ONLY for a brand-new manually
  // added person who has no id yet (a real, narrow case, not the common path).
  var person = state.bridalPartyDraft[personIndex];
  var capturedPersonId = person ? String(person.personId || person.id || '').trim() : '';
  if (capturedPersonId) {
    var reResolved = state.bridalPartyDraft.find(function(p) {
      return String((p && (p.personId || p.id)) || '').trim() === capturedPersonId;
    });
    if (reResolved) {
      if (reResolved !== person) {
        console.warn('savePartyMemberHubField_: array reordered since edit, re-resolved "' +
          (reResolved.profileName || reResolved.name || reResolved.personLabel) +
          '" by stable id instead of trusting index ' + personIndex);
      }
      person = reResolved;
    }
    // else: the id-carrying person genuinely vanished from the draft (removed
    // mid-edit), fall through to the index-based person as the closest
    // available signal, same as the pre-fix behaviour for that edge case.
  }
  if (!person) {
    console.warn('savePartyMemberHubField_: no person at index', personIndex);
    hubFieldStatus_set_(_statusId, 'clear');
    return;
  }

  var memberName = String(person.profileName || person.name || person.personLabel || '').trim();
  var memberRole = String(person.role || '').trim();
  if (!memberName) {
    showBanner('Cannot save: member name missing.', 'error');
    hubFieldStatus_set_(_statusId, 'clear');
    return;
  }

  var newValue = String(value == null ? '' : value);

  // Find the matching hub member in state.brideCardsData.partyMembers by name+role
  // and update optimistically. If no match, write the field directly to the local
  // cards array so re-render shows it (the GAS save will create the row server-side).
  var cards = (state.brideCardsData && state.brideCardsData.partyMembers) || [];
  var nameLow = memberName.toLowerCase();
  var roleLow = memberRole.toLowerCase();
  var match = cards.find(function(m) {
    var n = String(m.name || '').trim().toLowerCase();
    var r = String(m.role || '').trim().toLowerCase();
    return n === nameLow && (!roleLow || r === roleLow);
  });
  if (!match) {
    match = { name: memberName, role: memberRole };
    cards.push(match);
    if (state.brideCardsData) state.brideCardsData.partyMembers = cards;
  }
  var prev = match[fieldKey];
  match[fieldKey] = newValue;

  var payload = {
    brideEmail: brideEmail,
    memberName: memberName,
    memberRole: memberRole,
    fields: {}
  };
  payload.fields[fieldKey] = newValue;

  // D1-first fast path (2026-08-20). Same fire-and-forget shape as the
  // bride version, see saveBrideHubField_ for the full reasoning.
  var d1BrideUid2 = state.workspace && state.workspace.brideUid;
  var d1PmId = resolvePmIdSync_(person);
  saveHubFieldD1Fast_('party', {
    brideUid: d1BrideUid2,
    pmId: d1PmId,
    brideEmail: brideEmail,
    memberName: memberName
  }, fieldKey, newValue);

  function revertPartyField_() {
    match[fieldKey] = prev;
    renderStage();
  }

  jvBridge.callAdmin('bridge_runsheet_savePartyMemberHubFields', payload)
    .then(function(res) {
      if (!res || !res.ok) {
        var msg = (res && res.error) || 'unknown error';
        // Writeback-disabled is an expected, intentional server state (feature
        // flag off), not a user-facing error. Revert quietly, no alert/banner.
        if (msg === 'writeback disabled') {
          console.info('savePartyMemberHubField_: writeback disabled, reverting silently');
          hubFieldStatus_set_(_statusId, 'clear');
          revertPartyField_();
        } else {
          console.warn('savePartyMemberHubField_ failed, queueing for background retry:', msg);
          hubFieldStatus_set_(_statusId, 'retrying');
          hubFieldRetryQueue_enqueue_('party', 'bridge_runsheet_savePartyMemberHubFields', payload, revertPartyField_, _statusId);
        }
      } else {
        hubFieldStatus_set_(_statusId, 'saved');
      }
    })
    .catch(function(err) {
      console.warn('savePartyMemberHubField_ exception, queueing for background retry:', err);
      hubFieldStatus_set_(_statusId, 'retrying');
      hubFieldRetryQueue_enqueue_('party', 'bridge_runsheet_savePartyMemberHubFields', payload, revertPartyField_, _statusId);
    });
}

    function syncBridalPartyToSourceDraft_() {
  const previousById = {};
  (state.sourceServicesDraft || []).forEach(function(svc) {
    const id = getServiceId_(svc);
    if (id) previousById[id] = svc;
  });

  const rebuilt = flattenBridalPartyDraftToServices_(state.bridalPartyDraft).map(function(svc) {
    const existing = previousById[getServiceId_(svc)];
    if (!existing) return svc;

    const clone = Object.assign({}, existing, svc);

    // authoritative person/service ownership from the rebuilt draft must win
    clone.person = svc.person;
    clone.personLabel = svc.personLabel;
    clone.personId = svc.personId;
    clone.role = svc.role || "";
    clone.service = svc.service;
    clone.serviceLabel = svc.serviceLabel;
    clone.category = svc.category;
    clone.assignedArtist = svc.assignedArtist;
    clone.artist = svc.artist;
    clone.hairArtist = svc.hairArtist || "";
    clone.makeupArtist = svc.makeupArtist || "";
    clone.inspirationFolder = svc.inspirationFolder || "";
    clone.inspirationSubmitted = !!svc.inspirationSubmitted;

    // STALE-TIMING GUARD (2026-08-21, forensic audit P1). flattenBridalPartyDraftToServices_
    // rebuilds services from scratch on every party-edit and never carries timing
    // fields, so the Object.assign above lets startTime/endTime/startMin/endMin/
    // startTimeOverride survive from `existing` even when the artist assignment
    // just changed underneath it. That is the confirmed mechanism for Georgia C's
    // class of bug: a regenerated row inherits a stale artist's old time slot.
    // If the artist actually changed, the old timing no longer describes a real
    // booking, so clear it and let autoTimeUntimedAssignedServices_ re-time it
    // fresh instead of silently keeping a slot that belonged to a different artist.
    var existingArtist = String(existing.assignedArtist || existing.artist || "").trim();
    var newArtist = String(clone.assignedArtist || clone.artist || "").trim();
    if (existingArtist && newArtist && existingArtist !== newArtist) {
      clone.startTime = "";
      clone.endTime = "";
      clone.start = "";
      clone.end = "";
      clone.startMin = undefined;
      clone.endMin = undefined;
      clone.startTimeOverride = "";
    }

    // Preserve manual_builder_add source marker through rebuilds
    if (existing.source === "manual_builder_add") clone.source = "manual_builder_add";

    return clone;
  });

  // Auto-assign timing to any new service that has an artist but no timing.
  // Without timing, isServiceTimed_() returns false and the service lands in
  // unscheduled — invisible on the timeline and dropped on publish.
  autoTimeUntimedAssignedServices_(rebuilt);

  state.sourceServicesDraft = rebuilt.concat(state.customBlocksDraft || []);
sanitizeSourceServicesDraftAssignments_();
}

// Give any artist-assigned-but-untimed service a provisional start/end so it
// promotes out of the invisible "Working Area" into its artist lane. Appends
// each service after the artist's latest existing end (falling back to the
// draft start / 9:00 AM). Mutates `list` in place. Services already timed, or
// with no artist, are left untouched. Shared by syncBridalPartyToSourceDraft_
// (edit paths) and rederiveDraftState_ (initial load — fixes Bug B, 2026-07-03
// audit: services wouldn't bucket into an artist lane until "Refresh Timeline"
// was clicked, because only a full GAS round-trip assigned their timing).
function autoTimeUntimedAssignedServices_(list) {
  const items = Array.isArray(list) ? list : [];
  items.forEach(function(svc) {
    var artist = String(svc.assignedArtist || svc.artist || "").trim();
    if (!artist) return; // no artist — will be unscheduled intentionally
    if (isServiceTimed_(svc)) return; // already has timing

    // Find the latest end time among existing timed services for this artist
    var latestEnd = 0;
    var foundTimed = false;
    items.forEach(function(s) {
      if (s === svc) return;
      if (!isServiceTimed_(s)) return;
      var sArtist = String(s.assignedArtist || s.artist || "").trim();
      if (sArtist !== artist) return;
      var end = Number(s.endMin);
      if (!isNaN(end) && end > latestEnd) { latestEnd = end; foundTimed = true; }
      var sStart = Number(s.startMin);
      var sDur = Number(s.durationMin || s.duration || 0);
      if (!isNaN(sStart) && sStart + sDur > latestEnd) { latestEnd = sStart + sDur; foundTimed = true; }
    });

    var cursor;
    if (!foundTimed) {
      var wsDraft = (state.workspace && state.workspace.draftStartTime)
        ? parseDisplayTimeToMinutes(state.workspace.draftStartTime)
        : null;
      cursor = wsDraft !== null ? wsDraft : 540; // fallback 9:00 AM
    } else {
      cursor = latestEnd;
    }

    var dur = Number(svc.durationMin || svc.duration || 60);
    svc.startMin = cursor;
    svc.endMin = cursor + dur;
    svc.startTime = svc.start = formatMinutesAsDisplayTime_(cursor);
    svc.endTime = svc.end = formatMinutesAsDisplayTime_(cursor + dur);
  });
  return items;
}

function syncArtistsIntoBridalPartyDraft_() {
  (state.bridalPartyDraft || []).forEach(function(person) {
    if (person.assignedArtist && state.activeTeam.indexOf(person.assignedArtist) === -1) {
      person.assignedArtist = "";
    }
    if (person.hairArtist && state.activeTeam.indexOf(person.hairArtist) === -1) {
      person.hairArtist = "";
    }
    if (person.makeupArtist && state.activeTeam.indexOf(person.makeupArtist) === -1) {
      person.makeupArtist = "";
    }
  });
  syncBridalPartyToSourceDraft_();
  syncWorkspaceServicesFromSourceDraft_();
}

    function handleServiceEditButtonMouseDown(event) {
      event.preventDefault();
      event.stopPropagation();
    }

    function handleServiceEditButtonClick(event, serviceId) {
      event.preventDefault();
      event.stopPropagation();
      openServiceEditModal(serviceId);
      return false;
    }

    function handleServiceMoveButtonClick(event, serviceId) {
      event.preventDefault();
      event.stopPropagation();
      openServiceMoveModal(serviceId);
      return false;
    }

    function openServiceMoveModal(serviceId) {
      const service = findSourceServiceById_(serviceId) || findWorkspaceServiceById_(serviceId);
      if (!service) return;

      state.movingServiceId = String(serviceId || "");
      document.getElementById("svcMoveLabel").innerText =
        String(service.personLabel || service.person || "Service").trim() +
        " — " +
        String(service.serviceLabel || service.service || "").trim();

      const artistSelect = document.getElementById("svcMoveArtist");
      const laneNames = getLaneNames();
      artistSelect.innerHTML = ['<option value="">Select artist</option>'].concat(
        laneNames.map(function(name) {
          return '<option value="' + escapeHtml(name) + '">' + escapeHtml(name) + '</option>';
        })
      ).join("");

      const currentArtist = String(service.assignedArtist || service.artist || "").trim();
      const currentStart = displayTimeToInput(service.startTime || service.start || "");
      const action = !currentArtist
        ? "unscheduled"
        : (isServiceTimed_(service) ? "timeline" : "working");

      artistSelect.value = currentArtist;
      document.getElementById("svcMoveAction").value = action;
      document.getElementById("svcMoveTime").value = currentStart;
      updateServiceMoveModalState_();
      document.getElementById("svcMoveModal").classList.add("open");
    }

    function closeServiceMoveModal() {
      state.movingServiceId = null;
      document.getElementById("svcMoveModal").classList.remove("open");
    }

    function updateServiceMoveModalState_() {
      const action = String(document.getElementById("svcMoveAction").value || "").trim();
      const artistField = document.getElementById("svcMoveArtistField");
      const timeField = document.getElementById("svcMoveTimeField");

      artistField.style.display = action === "unscheduled" ? "none" : "";
      timeField.style.display = action === "timeline" ? "" : "none";
    }

    function parseInputTimeValueToMinutes_(value) {
      const raw = String(value || "").trim();
      if (!raw || raw.indexOf(":") === -1) return null;
      const parts = raw.split(":");
      if (parts.length !== 2) return null;
      const hours = Number(parts[0]);
      const minutes = Number(parts[1]);
      if (isNaN(hours) || isNaN(minutes)) return null;
      return (hours * 60) + minutes;
    }

    function saveServiceMove() {
      const serviceId = state.movingServiceId;
      if (!serviceId) return;

      const action = String(document.getElementById("svcMoveAction").value || "").trim();
      const artist = String(document.getElementById("svcMoveArtist").value || "").trim();
      const timeMin = parseInputTimeValueToMinutes_(document.getElementById("svcMoveTime").value);

      closeServiceMoveModal();

      if (action === "unscheduled") {
        moveServiceToArtistWorkingArea_(serviceId, "");
        rederiveDraftState_();
        state.timelineDirty = true;
        renderStage();
        showBanner("Service moved to Unscheduled.", "warning");
        return;
      }

      if (!artist) {
        showBanner("Choose an artist first.", "error");
        return;
      }

      if (action === "working") {
        moveServiceToArtistWorkingArea_(serviceId, artist);
        rederiveDraftState_();
        state.timelineDirty = true;
        renderStage();
        showBanner("Service moved into the Working Area.", "warning");
        return;
      }

      if (action === "timeline") {
        if (timeMin === null) {
          showBanner("Choose a timeline start time.", "error");
          return;
        }
        applyTimelineMove(serviceId, artist, timeMin);
      }
    }

    // ─── SERVICE EDIT MODAL ──────────────────────────────────────────────────────

  // ── Swipeable card modal state ──
  var svcCardIndex = 0;
  var svcCardCount = 0;

  function openServiceEditModal(serviceId) {
  if (state.dragStarted) {
    state.dragStarted = false;
    return;
  }

  const service = findWorkspaceServiceById_(serviceId) || findSourceServiceById_(serviceId);
  if (!service || service.isSystemBlock) return;

  state.editingServiceId = serviceId;

  var person = resolveServicePersonProfile_(service);
  var displayName = person.profileName || person.personLabel || service.personLabel || service.person || "";
  var startTime = service.startTimeOverride || service.startTime || service.start || "";
  var endTime = service.endTime || service.end || "";
  var artistName = service.assignedArtist || service.artist || "Unassigned";

  // ── Populate sticky header ──
  document.getElementById("svcDetailRole").textContent = person.role || "Bridal Party";
  // Prepend an avatar to the name so the modal matches the Page 2 / timeline
  // visual identity. Uses the same lookup as the timeline card. innerHTML
  // because we're injecting markup; safe because displayName is escaped.
  var modalPicUrl = getProfilePicForService_(service);
  var modalInitials = String(displayName || '?').split(' ').map(function(p) { return p.charAt(0); }).join('').slice(0, 2).toUpperCase() || '?';
  var modalAvatarStyle = 'display:inline-flex;align-items:center;justify-content:center;width:32px;height:32px;border-radius:50%;flex:0 0 auto;font-size:13px;font-weight:600;color:var(--brand-primary-deep);background:var(--brand-bg-soft);vertical-align:middle;margin-right:8px;';
  if (modalPicUrl) modalAvatarStyle += 'background-image:url(' + modalPicUrl.replace(/"/g, '%22') + ');background-size:cover;background-position:center;color:transparent;';
  document.getElementById("svcDetailName").innerHTML =
    '<span style="' + modalAvatarStyle + '">' + (modalPicUrl ? '' : escapeHtml(modalInitials)) + '</span>' +
    '<span>' + escapeHtml(displayName) + '</span>';
  document.getElementById("svcDetailSubtitle").innerHTML =
    escapeHtml(service.serviceLabel || service.service || "") + " &middot; " +
    escapeHtml(artistName) +
    (startTime ? " &middot; " + escapeHtml(startTime) + (endTime ? " – " + escapeHtml(endTime) : "") : "");

  // ── Build swipeable cards ──
  var $track = document.getElementById("svcCardTrack");
  var $dots = document.getElementById("svcCardDots");
  $track.innerHTML = "";
  $dots.innerHTML = "";
  svcCardIndex = 0;

  // Merge gallery data: person (from bridalPartyDraft) may have it,
  // but also check the service source and workspace.people directly
  var mergedPerson = Object.assign({}, person);
  // Fallback: pull from service source
  if ((!mergedPerson.inspirationGallery || !mergedPerson.inspirationGallery.length) && Array.isArray(service.inspirationGallery) && service.inspirationGallery.length) {
    mergedPerson.inspirationGallery = service.inspirationGallery;
  }
  if ((!mergedPerson.trialGallery || !mergedPerson.trialGallery.length) && Array.isArray(service.trialGallery) && service.trialGallery.length) {
    mergedPerson.trialGallery = service.trialGallery;
  }
  if (!mergedPerson.inspirationFolder && (service.inspirationFolder || service.folderUrl)) {
    mergedPerson.inspirationFolder = service.inspirationFolder || service.folderUrl;
  }
  if (!mergedPerson.trialFolderUrl && service.trialFolderUrl) {
    mergedPerson.trialFolderUrl = service.trialFolderUrl;
  }
  // Fallback: pull from workspace.people directly
  var wsPeople = (state.workspace && state.workspace.people) || [];
  var pLabel = (service.personLabel || service.person || "").trim().toLowerCase();
  for (var wp = 0; wp < wsPeople.length; wp++) {
    var wpName = String(wsPeople[wp].name || wsPeople[wp].profileName || "").trim().toLowerCase();
    if (wpName && pLabel && (wpName === pLabel || wpName.indexOf(pLabel) > -1 || pLabel.indexOf(wpName) > -1)) {
      if ((!mergedPerson.inspirationGallery || !mergedPerson.inspirationGallery.length) && Array.isArray(wsPeople[wp].inspirationGallery) && wsPeople[wp].inspirationGallery.length) {
        mergedPerson.inspirationGallery = wsPeople[wp].inspirationGallery;
      }
      if ((!mergedPerson.trialGallery || !mergedPerson.trialGallery.length) && Array.isArray(wsPeople[wp].trialGallery) && wsPeople[wp].trialGallery.length) {
        mergedPerson.trialGallery = wsPeople[wp].trialGallery;
      }
      if (!mergedPerson.inspirationFolder && wsPeople[wp].inspirationFolder) {
        mergedPerson.inspirationFolder = wsPeople[wp].inspirationFolder;
      }
      if (!mergedPerson.trialFolderUrl && wsPeople[wp].trialFolderUrl) {
        mergedPerson.trialFolderUrl = wsPeople[wp].trialFolderUrl;
      }
      break;
    }
  }

  var cards = [];

  // Card 1: Overview
  cards.push(buildSvcOverviewCard_(service, mergedPerson, artistName, startTime, endTime));

  // Card 2: Inspiration (conditional)
  var inspoCard = buildSvcInspirationCard_(mergedPerson);
  if (inspoCard) cards.push(inspoCard);

  // Card 3: Trial (conditional)
  var trialCard = buildSvcTrialCard_(mergedPerson);
  if (trialCard) cards.push(trialCard);

  svcCardCount = cards.length;
  for (var i = 0; i < cards.length; i++) $track.appendChild(cards[i]);

  // Build dots
  if (cards.length > 1) {
    for (var d = 0; d < cards.length; d++) {
      var dot = document.createElement("button");
      dot.className = "svc-card-dot" + (d === 0 ? " svc-card-dot--active" : "");
      dot.dataset.index = d;
      dot.setAttribute("aria-label", "Go to card " + (d + 1));
      dot.addEventListener("click", function() {
        svcGoToCard_(parseInt(this.dataset.index, 10));
      });
      $dots.appendChild(dot);
    }
  }

  setupSvcCardSwipe_();
  svcGoToCard_(0);

  // ── Populate EDIT view fields ──
  document.getElementById("svcEditPerson").value = displayName;
  var svcLabel = service.serviceLabel || service.service || "";
  var svcSelect = document.getElementById("svcEditServiceSelect");
  var svcText = document.getElementById("svcEditService");
  if (svcSelect && svcText) {
    var optMatch = Array.from(svcSelect.options).some(function(o) { return o.value === svcLabel && o.value !== "__custom__"; });
    if (optMatch) {
      svcSelect.value = svcLabel;
      svcText.style.display = "none";
      svcText.value = svcLabel;
    } else {
      svcSelect.value = "__custom__";
      svcText.style.display = "";
      svcText.value = svcLabel;
    }
  }
  document.getElementById("svcEditDuration").value = service.durationMin || service.duration || "";
  document.getElementById("svcEditNotes").value = service.notes || "";

  const artistField = document.getElementById("svcEditArtist");
  if (artistField) {
    const artistOptions = ['<option value="">— unassigned —</option>']
      .concat((state.activeTeam || []).map(function(name) {
        return '<option value="' + escapeHtml(name) + '">' + escapeHtml(name) + '</option>';
      }));
    artistField.innerHTML = artistOptions.join("");
    artistField.value = service.assignedArtist || service.artist || "";
  }

  const startField = document.getElementById("svcEditStartTime");
  if (startField) {
    startField.value = displayTimeToInput(startTime);
  }

  document.getElementById("svcEditDeleteBtn").style.display = service.isSystemBlock ? "none" : "";

  toggleServiceEditMode_(false);
  document.getElementById("svcEditModal").classList.add("open");
}

// ── Card builders ──

function buildSvcOverviewCard_(service, person, artistName, startTime, endTime) {
  var card = document.createElement("div");
  card.className = "svc-card";

  // Label
  var label = document.createElement("div");
  label.className = "svc-card-label";
  label.textContent = "Overview";
  card.appendChild(label);

  // Artist row
  var artistRow = document.createElement("div");
  artistRow.className = "svc-card-artist";
  var avatar = document.createElement("div");
  avatar.className = "svc-card-artist-avatar";
  avatar.textContent = (artistName || "U").charAt(0).toUpperCase();
  var nameSpan = document.createElement("span");
  nameSpan.textContent = artistName;
  artistRow.appendChild(avatar);
  artistRow.appendChild(nameSpan);
  card.appendChild(artistRow);

  // Status chips
  var chipsDiv = document.createElement("div");
  chipsDiv.className = "svc-card-chips";
  if (person.inspirationFolder) chipsDiv.innerHTML += '<span class="chip high">Folder linked</span>';
  if (person.inspirationSubmitted) chipsDiv.innerHTML += '<span class="chip high">Inspo submitted</span>';
  else chipsDiv.innerHTML += '<span class="chip missing">Inspo missing</span>';
  if (person.isBride && person.trialAssistSubmitted === false) chipsDiv.innerHTML += '<span class="chip missing">Trial assist missing</span>';
  card.appendChild(chipsDiv);

  // Detail rows
  var details = document.createElement("div");
  details.className = "svc-card-details";
  details.appendChild(buildSvcDetailRow_("Service", service.serviceLabel || service.service || ""));
  details.appendChild(buildSvcDetailRow_("Artist", artistName));
  details.appendChild(buildSvcDetailRow_("Duration", (service.durationMin || service.duration || 0) + " min"));
  details.appendChild(buildSvcDetailRow_("Time", startTime ? startTime + (endTime ? " – " + endTime : "") : "Not timed"));
  if (person.hairstyleChosen) details.appendChild(buildSvcDetailRow_("Hairstyle", person.hairstyleChosen));
  card.appendChild(details);

  // Notes
  if (service.notes) {
    var notesDiv = document.createElement("div");
    notesDiv.className = "svc-card-notes-block";
    notesDiv.textContent = service.notes;
    card.appendChild(notesDiv);
  }

  // Folder links
  var links = document.createElement("div");
  links.className = "svc-card-links";
  if (person.inspirationFolder) links.appendChild(buildSvcFolderLink_("Inspiration Folder", person.inspirationFolder));
  if (person.trialFolderUrl) links.appendChild(buildSvcFolderLink_("Trial Folder", person.trialFolderUrl));
  if (links.children.length) card.appendChild(links);

  return card;
}

function buildSvcInspirationCard_(person) {
  var gallery = person.inspirationGallery || [];
  var folder = person.inspirationFolder || "";
  if (!gallery.length && !folder) return null;

  var card = document.createElement("div");
  card.className = "svc-card";

  var label = document.createElement("div");
  label.className = "svc-card-label";
  label.textContent = "Inspiration (" + gallery.length + ")";
  card.appendChild(label);

  if (gallery.length) {
    var grid = document.createElement("div");
    grid.className = "svc-card-gallery";
    gallery.forEach(function(item) {
      var thumb = item.thumbnailUrl || item.previewUrl || item.url || "";
      if (!thumb) return;
      var img = document.createElement("img");
      img.className = "svc-card-gallery-thumb";
      img.src = thumb;
      img.alt = item.name || "Inspiration";
      img.loading = "lazy";
      if (item.url) {
        img.style.cursor = "pointer";
        img.dataset.openUrl = item.url;
      }
      grid.appendChild(img);
    });
    card.appendChild(grid);
  } else {
    var msg = document.createElement("p");
    msg.className = "svc-card-empty-msg";
    msg.textContent = "No inspiration photos yet.";
    card.appendChild(msg);
  }

  if (folder) card.appendChild(buildSvcFolderLink_("Open folder in Drive", folder));

  return card;
}

function buildSvcTrialCard_(person) {
  var gallery = person.trialGallery || [];
  var folder = person.trialFolderUrl || "";
  if (!gallery.length && !folder) return null;

  var card = document.createElement("div");
  card.className = "svc-card";

  var label = document.createElement("div");
  label.className = "svc-card-label";
  label.textContent = "Trial Photos (" + gallery.length + ")";
  card.appendChild(label);

  if (gallery.length) {
    var grid = document.createElement("div");
    grid.className = "svc-card-gallery";
    gallery.forEach(function(item) {
      var thumb = item.thumbnailUrl || item.previewUrl || item.url || "";
      if (!thumb) return;
      var img = document.createElement("img");
      img.className = "svc-card-gallery-thumb";
      img.src = thumb;
      img.alt = item.name || "Trial photo";
      img.loading = "lazy";
      if (item.url) {
        img.style.cursor = "pointer";
        img.dataset.openUrl = item.url;
      }
      grid.appendChild(img);
    });
    card.appendChild(grid);
  } else {
    var msg = document.createElement("p");
    msg.className = "svc-card-empty-msg";
    msg.textContent = "No trial photos uploaded yet.";
    card.appendChild(msg);
  }

  if (folder) card.appendChild(buildSvcFolderLink_("Open trial folder in Drive", folder));

  return card;
}

// ── Card helpers ──

function buildSvcDetailRow_(labelText, value) {
  var row = document.createElement("div");
  row.className = "svc-card-detail-row";
  var lbl = document.createElement("span");
  lbl.className = "svc-card-detail-label";
  lbl.textContent = labelText;
  var val = document.createElement("span");
  val.className = "svc-card-detail-value";
  val.textContent = value;
  row.appendChild(lbl);
  row.appendChild(val);
  return row;
}

function buildSvcFolderLink_(text, url) {
  var a = document.createElement("a");
  a.className = "svc-card-folder-link";
  a.href = url;
  a.target = "_blank";
  a.innerHTML = '<svg viewBox="0 0 24 24" width="14" height="14"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" fill="none" stroke="currentColor" stroke-width="2"/></svg> ' + escapeHtml(text);
  return a;
}

// ── Card navigation + swipe ──

function svcGoToCard_(idx) {
  if (idx < 0 || idx >= svcCardCount) return;
  svcCardIndex = idx;
  var $track = document.getElementById("svcCardTrack");
  if ($track) $track.style.transform = "translateX(-" + (idx * 100) + "%)";
  var dots = document.querySelectorAll("#svcCardDots .svc-card-dot");
  for (var i = 0; i < dots.length; i++) {
    dots[i].classList.toggle("svc-card-dot--active", i === idx);
  }
}

function setupSvcCardSwipe_() {
  var $track = document.getElementById("svcCardTrack");
  if (!$track) return;
  var startX = 0, startY = 0, tracking = false;

  // Remove old listeners by cloning
  var newTrack = $track.cloneNode(true);
  $track.parentNode.replaceChild(newTrack, $track);

  // Re-append card content (cloneNode copies children)
  newTrack.addEventListener("touchstart", function(e) {
    if (e.touches.length !== 1) return;
    startX = e.touches[0].clientX;
    startY = e.touches[0].clientY;
    tracking = true;
  }, { passive: true });

  newTrack.addEventListener("touchend", function(e) {
    if (!tracking) return;
    tracking = false;
    var endX = e.changedTouches[0].clientX;
    var endY = e.changedTouches[0].clientY;
    var dx = endX - startX;
    var dy = endY - startY;
    if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 50) {
      if (dx < 0) svcGoToCard_(svcCardIndex + 1);
      else svcGoToCard_(svcCardIndex - 1);
    }
  }, { passive: true });

  // Delegated click for gallery thumbs (cloneNode doesn't copy listeners)
  newTrack.addEventListener("click", function(e) {
    var url = e.target && e.target.dataset && e.target.dataset.openUrl;
    if (url) window.open(url, "_blank");
  });
}

function toggleServiceEditMode_(showEdit) {
  document.getElementById("svcDetailView").style.display = showEdit ? "none" : "";
  document.getElementById("svcEditView").style.display = showEdit ? "" : "none";
}

function resolveServicePersonProfile_(service) {
  var personId = String(service.personId || "").trim();
  var personLabel = String(service.personLabel || service.person || "").trim();
  var personLabelLower = personLabel.toLowerCase();
  var match = null;

  // Tier 1: exact ID or exact label match
  (state.bridalPartyDraft || []).forEach(function(p) {
    if (match) return;
    var pId = String(p.personId || p.id || "").trim();
    var pLabel = String(p.personLabel || p.name || "").trim();
    if ((personId && pId === personId) || pLabel === personLabel) {
      match = p;
    }
  });

  // Tier 2: case-insensitive label match
  if (!match) {
    (state.bridalPartyDraft || []).forEach(function(p) {
      if (match) return;
      var pLabel = String(p.personLabel || p.name || "").trim().toLowerCase();
      if (pLabel && personLabelLower && pLabel === personLabelLower) {
        match = p;
      }
    });
  }

  // Tier 3: role-based match (bride, mother)
  if (!match) {
    (state.bridalPartyDraft || []).forEach(function(p) {
      if (match) return;
      if (personLabelLower === "bride" && String(p.role || "").toLowerCase() === "bride") {
        match = p;
      } else if ((personLabelLower === "mum" || personLabelLower === "mother") && String(p.role || "").toLowerCase() === "mother") {
        match = p;
      }
    });
  }

  // Tier 4: fuzzy — partial name match (handles "Sezen" matching "Sezen Ilker")
  if (!match && personLabelLower) {
    (state.bridalPartyDraft || []).forEach(function(p) {
      if (match) return;
      var pLabel = String(p.personLabel || p.name || "").trim().toLowerCase();
      if (pLabel && (pLabel.indexOf(personLabelLower) > -1 || personLabelLower.indexOf(pLabel) > -1)) {
        match = p;
      }
    });
  }

  // Tier 5: if service is for the bride (by isBride flag), match the bride person
  if (!match && service.isBride) {
    (state.bridalPartyDraft || []).forEach(function(p) {
      if (match) return;
      if (String(p.role || "").toLowerCase() === "bride") match = p;
    });
  }

  return match || {
    personLabel: personLabel,
    profileName: "",
    role: service.role || "",
    isBride: !!service.isBride,
    inspirationFolder: service.inspirationFolder || "",
    inspirationSubmitted: !!service.inspirationSubmitted,
    inspirationGallery: [],
    trialGallery: [],
    trialAssistSubmitted: true,
    hairstyleChosen: service.hairstyleChosen || "",
    notes: service.notes || ""
  };
}

function openWarningsModal_() {
  var warnings = (state.workspace && state.workspace.warnings) || [];
  var list = document.getElementById("warningsModalList");
  if (!list) return;

  if (!warnings.length) {
    list.innerHTML = '<div style="padding:16px;color:var(--muted);font-size:13px;text-align:center;">No warnings.</div>';
  } else {
    list.innerHTML = warnings.map(function(w) {
      var msg = String(w.message || "").trim();
      var isInspo = msg.toLowerCase().indexOf("inspiration") > -1;
      var personName = "";
      if (isInspo) {
        var match = msg.match(/^(.+?)\s+inspiration/i);
        personName = match ? match[1].trim() : "";
      }
      var actionHtml = "";
      if (isInspo && personName) {
        actionHtml = '<div style="margin-top:8px;">' +
          '<button class="btn btn-soft" style="font-size:11px;padding:5px 12px;" onclick="sendPartyInviteLinkRequest_(\'' + escapeHtml(personName) + '\')">' +
            '<i class="fa-solid fa-paper-plane" style="margin-right:4px;"></i> Get portal invite link for ' + escapeHtml(personName) +
          '</button>' +
        '</div>';
      }
      var icon = String(w.severity || "").toLowerCase() === "error" ? "fa-circle-xmark" : "fa-triangle-exclamation";
      var bgColor = String(w.severity || "").toLowerCase() === "error" ? "var(--error-bg)" : "var(--warn-bg)";
      var textColor = String(w.severity || "").toLowerCase() === "error" ? "var(--error-text)" : "var(--warn-text)";
      return '<div style="padding:12px 14px;border-radius:var(--radius-sm);background:' + bgColor + ';color:' + textColor + ';margin-bottom:8px;font-size:13px;line-height:1.5;">' +
        '<div><i class="fa-solid ' + icon + '" style="margin-right:6px;"></i>' + escapeHtml(msg) + '</div>' +
        actionHtml +
      '</div>';
    }).join("");
  }

  document.getElementById("warningsModal").classList.add("open");
}

function closeWarningsModal_() {
  document.getElementById("warningsModal").classList.remove("open");
}

function renderPartyInviteLinkResult_(res, personName) {
  var subtitleBits = ['Portal invite link for ' + escapeHtml(personName || state.currentBride)];
  if (res.memberRole) subtitleBits.push(escapeHtml(res.memberRole));
  var subtitle = subtitleBits.join(' · ');
  var statusNote = res.isNew
    ? 'New invite token generated.'
    : 'Existing active invite token returned.';
  var html = '<div style="margin-bottom:14px;">' +
    '<div style="font-size:13px;font-weight:600;margin-bottom:4px;">' + subtitle + '</div>' +
    '<div style="font-size:11px;color:var(--muted);margin-bottom:8px;">' + statusNote + ' Paste into SMS or email to send.</div>' +
    '<input type="text" value="' + escapeHtml(res.url) + '" readonly ' +
      'style="width:100%;padding:10px 12px;border:1px solid var(--line);border-radius:10px;font-size:12px;background:var(--brand-bg-soft);color:var(--brand-text);" ' +
      'onclick="this.select()" id="inspoFormUrlInput">' +
    '<div style="display:flex;gap:8px;margin-top:10px;">' +
      '<button class="btn btn-primary" style="font-size:12px;padding:8px 16px;" onclick="copyInspoFormUrl_()"><i class="fa-solid fa-copy" style="margin-right:4px;"></i> Copy Link</button>' +
      '<a class="btn btn-soft" style="font-size:12px;padding:8px 16px;text-decoration:none;" href="' + escapeHtml(res.url) + '" target="_blank" rel="noopener"><i class="fa-solid fa-external-link" style="margin-right:4px;"></i> Open</a>' +
    '</div>' +
  '</div>';
  document.getElementById("warningsModalList").innerHTML = html;
  document.getElementById("warningsModal").classList.add("open");
}

// CF-first relay path (Issue #11, Sub-E, se-party-invite-link-admin-relay-2026-10-27). Builds the
// /partner/party-invite-link-admin/generate URL exactly like rsbLocationCfUrl_ does for the address
// save path, reusing the same App Proxy base. Returns '' (falsy) when the flag is off or the base
// is unconfigured -- callers treat that as "go straight to GAS", no network call attempted.
function rsbPartyInviteLinkAdminCfUrl_() {
  if (window.jvRsbPartyInviteLinkAdminCf !== true) return '';
  var base = String(window.jvRsbPartyInviteLinkAdminCfBase || '').replace(/\/+$/, '');
  return base ? base + '/partner/party-invite-link-admin/generate' : '';
}

// Best-effort client-side role/email lookup so the CF-first path carries the same memberRole the
// GAS path resolves server-side from _PartyMembers (RUNSHEET.gs shopifyBridge_runsheetGeneratePartyInviteLink_).
// A miss just means the Worker defaults memberRole to 'Party Member', same as the GAS path's own
// default when no _PartyMembers row matches by name -- never a hard failure either way.
function rsbResolvePartyMemberRoleEmail_(personName) {
  var nameLow = String(personName || '').trim().toLowerCase();
  var out = { role: '', email: '' };
  if (!nameLow) return out;
  var pools = [(state.bridalPartyDraft || []), ((state.workspace && state.workspace.people) || [])];
  for (var p = 0; p < pools.length; p++) {
    var list = pools[p];
    for (var i = 0; i < list.length; i++) {
      var person = list[i] || {};
      var pn = String(person.name || person.profileName || person.personLabel || '').trim().toLowerCase();
      if (pn !== nameLow) continue;
      if (!out.role && person.role) out.role = String(person.role).trim();
      if (!out.email && (person.email || person.memberEmail)) out.email = String(person.email || person.memberEmail).trim().toLowerCase();
    }
  }
  return out;
}

function sendPartyInviteLinkRequest_(personName) {
  if (!state.currentBride) {
    showBanner("No bride selected.", "error");
    return;
  }
  closeWarningsModal_();
  showBanner("Generating portal invite link...", "success");

  function viaGas() {
    jvBridge.callAdmin('bridge_runsheet_generatePartyInviteLink', {
      brideName: state.currentBride,
      memberName: personName
    })
      .then(function(res) {
        if (!res.ok || !res.url) {
          showBanner(res.error || "Could not generate portal invite link.", "error");
          return;
        }
        renderPartyInviteLinkResult_(res, personName);
      })
      .catch(function(err) {
        showBanner(getErrorMessage(err), "error");
      });
  }

  var cfUrl = rsbPartyInviteLinkAdminCfUrl_();
  if (!cfUrl) { viaGas(); return; }

  var brideUid = (state.workspace && state.workspace.brideUid) || '';
  var brideEmail = (state.workspace && state.workspace.email) || '';
  if (!brideUid && !brideEmail) { viaGas(); return; }

  var roleEmail = rsbResolvePartyMemberRoleEmail_(personName);
  var nf = (typeof window._jvNativeFetch === 'function' ? window._jvNativeFetch : window.fetch).bind(window);
  var ctrl = (typeof AbortController === 'function') ? new AbortController() : null;
  var timer = ctrl ? setTimeout(function() { ctrl.abort(); }, 6000) : null;

  nf(cfUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'text/plain' },
    credentials: 'same-origin',
    signal: ctrl ? ctrl.signal : undefined,
    body: JSON.stringify({
      brideUid: brideUid,
      email: brideEmail,
      memberName: personName,
      memberRole: roleEmail.role || undefined,
      memberEmail: roleEmail.email || undefined
    })
  })
    .then(function(r) { return r.json().then(function(j) { return { httpOk: r.ok, j: j }; }, function() { return { httpOk: false, j: null }; }); })
    .then(function(result) {
      if (timer) clearTimeout(timer);
      var j = result.j;
      // Any non-success shape (404 flag-off, 403 not-admin/bride_identity_required,
      // fallback:'gas' from the handler, missing url, etc.) falls straight to GAS --
      // never shown to Jess as an error, since GAS is the proven working path.
      if (!result.httpOk || !j || j.ok !== true || !j.url) { viaGas(); return; }
      renderPartyInviteLinkResult_(j, personName);
    })
    .catch(function() {
      if (timer) clearTimeout(timer);
      viaGas();
    });
}

function copyInspoFormUrl_() {
  var input = document.getElementById("inspoFormUrlInput");
  if (!input) return;
  input.select();
  try {
    document.execCommand("copy");
    showBanner("Link copied to clipboard.", "success");
  } catch (e) {
    showBanner("Select the link and copy manually.", "warning");
  }
}

function handleEditServiceSelectChange_() {
  var sel = document.getElementById("svcEditServiceSelect");
  var txt = document.getElementById("svcEditService");
  if (!sel || !txt) return;
  if (sel.value === "__custom__") {
    txt.style.display = "";
    txt.value = "";
    txt.focus();
  } else {
    txt.style.display = "none";
    txt.value = sel.value;
  }
}

function renderDetailRow_(label, value) {
  return '<div style="display:flex;justify-content:space-between;padding:8px 0;border-bottom:1px solid var(--line);font-size:13px;">' +
    '<span style="font-size:11px;font-weight:800;text-transform:uppercase;letter-spacing:0.08em;color:var(--muted);">' + escapeHtml(label) + '</span>' +
    '<span style="font-weight:600;color:var(--brand-text);text-align:right;">' + escapeHtml(value || "—") + '</span>' +
  '</div>';
}

    function closeServiceEditModal() {
      state.editingServiceId = null;
      document.getElementById("svcEditModal").classList.remove("open");
    }

    function saveServiceEdit() {
  const id = state.editingServiceId;
  if (!id || !state.workspace) return;

  clearArtistMetaOnMove_();

  const person   = document.getElementById("svcEditPerson").value.trim();
  var svcSel = document.getElementById("svcEditServiceSelect");
  const service  = (svcSel && svcSel.value !== "__custom__") ? svcSel.value.trim() : document.getElementById("svcEditService").value.trim();
  const duration = parseInt(document.getElementById("svcEditDuration").value, 10);
  const notes    = document.getElementById("svcEditNotes").value.trim();
  const artist   = document.getElementById("svcEditArtist").value.trim();
  const startRaw = document.getElementById("svcEditStartTime").value.trim();
  const startOvr = startRaw ? inputTimeToDisplay(startRaw) : "";

  // ONLY mutate sourceServicesDraft — workspace.services is derived via rederive.
  (state.sourceServicesDraft || []).forEach(function(s) {
    if ((s.id || s.itemId) === id) {
      if (person) {
        s.personLabel = person;
        s.person = person;
      }
      if (service) {
        s.serviceLabel = service;
        s.service = service;
      }
      if (!isNaN(duration) && duration > 0) {
        s.durationMin = duration;
        s.duration = duration;
      }
      s.assignedArtist = artist;
      s.artist = artist;
      s.startTimeOverride = startOvr;
      // Also set startTime/startMin so isServiceTimed_() includes this service in the timeline.
      // Recompute end fields from start+duration so the card doesn't stretch when start moves.
      // Without this, editing start time from 11:00→10:00 left endTime at 12:00 and the card
      // visually spanned 10:00–12:00 (2 hours) instead of shifting to 10:00–11:00.
      if (startOvr) {
        s.startTime = startOvr;
        s.start     = startOvr;
        s.startMin  = parseDisplayTimeToMinutes(startOvr);
        var effectiveDuration = Number(s.durationMin || s.duration || 0);
        if (s.startMin !== null && !isNaN(s.startMin) && effectiveDuration > 0) {
          s.endMin = s.startMin + effectiveDuration;
          var endLabel = formatMinutesAsDisplayTime_(s.endMin);
          s.endTime = endLabel;
          s.end     = endLabel;
        }
      }
      s.notes = notes;
    }
  });

  rederiveDraftState_();
  state.timelineDirty = true;
  closeServiceEditModal();
  renderStage();
  showBanner("Service updated. Click Save Draft Changes to keep it, or Recalculate Preview to rebuild timing.", "warning");
}

    async function deleteEditingService() {
  const id = state.editingServiceId;
  if (!id) return;
  var delOk = await confirmModal_("Remove this service?", "Remove", "Cancel");
  if (!delOk) return;

  state.sourceServicesDraft = (state.sourceServicesDraft || []).filter(function(s) {
    return getServiceId_(s) !== String(id);
  });

  state.customBlocksDraft = (state.customBlocksDraft || []).filter(function(s) {
    return getServiceId_(s) !== String(id);
  });

  state.sourceServicesDraft = (state.sourceServicesDraft || []).filter(function(s) {
    return !s.isCustomBlock;
  }).concat(state.customBlocksDraft);
  rederiveDraftState_();

  closeServiceEditModal();
  state.timelineDirty = true;
  renderStage();
  showBanner("Service removed. Click Recalculate Preview to rebuild the schedule.", "warning");
}

    // ── Add Service ──────────────────────────────────────────────
    function openAddServiceModal() {
      var artistOptions = (state.activeTeam || []).map(function(name) {
        return '<option value="' + escapeHtml(name) + '">' + escapeHtml(name) + '</option>';
      }).join('');
      document.getElementById("addSvcPerson").value = "";
      document.getElementById("addSvcService").value = "Hair";
      document.getElementById("addSvcCategory").value = "hair";
      document.getElementById("addSvcDuration").value = "60";
      document.getElementById("addSvcArtist").innerHTML = '<option value="">— auto —</option>' + artistOptions;
      document.getElementById("addSvcStartTime").value = "";
      document.getElementById("addSvcNotes").value = "";
      document.getElementById("addSvcModal").classList.add("open");
    }

    function closeAddServiceModal() {
      document.getElementById("addSvcModal").classList.remove("open");
    }

    function saveNewService() {
      pushUndoSnapshot_();
      var person   = document.getElementById("addSvcPerson").value.trim();
      var service  = document.getElementById("addSvcService").value.trim();
      var category = document.getElementById("addSvcCategory").value.trim();
      var duration = parseInt(document.getElementById("addSvcDuration").value, 10) || 60;
      var artist   = document.getElementById("addSvcArtist").value.trim();
      var startOvr = document.getElementById("addSvcStartTime").value.trim();
      var notes    = document.getElementById("addSvcNotes").value.trim();

      if (!person) { showBanner("Person is required.", "error"); return; }
      if (!service) { showBanner("Service name is required.", "error"); return; }

      // Auto-assign artist from skill selections if user left dropdown on "auto"
      var assignedArtist = artist;
      if (!assignedArtist) {
        var cat = (category || "").toLowerCase();
        if (cat === "makeup") assignedArtist = (state.skillSelections && state.skillSelections.makeup) || "";
        else if (cat === "hair") assignedArtist = (state.skillSelections && state.skillSelections.hair) || "";
      }

      // Compute timing: use user-provided start time override if available,
      // otherwise find the latest end time for this artist and slot after it
      // (same cursor logic as confirmAddPerson_).
      var svcStartMin = null;
      if (startOvr) {
        // Parse the HTML time input (HH:MM 24h) to display format then to minutes
        var displayTime = inputTimeToDisplay(startOvr);
        if (displayTime) {
          svcStartMin = parseDisplayTimeToMinutes(displayTime);
        }
      }

      if (svcStartMin === null) {
        // Auto-compute: slot after the latest timed service for the same artist
        var latestEnd = 0;
        var foundTimed = false;
        (state.sourceServicesDraft || []).forEach(function(s) {
          if (!isServiceTimed_(s)) return;
          var sArtist = String(s.assignedArtist || s.artist || "").trim();
          if (assignedArtist && sArtist === assignedArtist) {
            var end = Number(s.endMin);
            if (!isNaN(end) && end > latestEnd) { latestEnd = end; foundTimed = true; }
            var sStart = Number(s.startMin);
            var sDur = Number(s.durationMin || s.duration || 0);
            if (!isNaN(sStart) && sStart + sDur > latestEnd) { latestEnd = sStart + sDur; foundTimed = true; }
          }
        });

        if (!foundTimed) {
          var wsDraft = (state.workspace && state.workspace.draftStartTime)
            ? parseDisplayTimeToMinutes(state.workspace.draftStartTime)
            : null;
          svcStartMin = wsDraft !== null ? wsDraft : 540; // fallback 9:00 AM
        } else {
          svcStartMin = latestEnd;
        }
      }

      var svcEndMin = svcStartMin + duration;
      var svcStartTime = formatMinutesAsDisplayTime_(svcStartMin);
      var svcEndTime = formatMinutesAsDisplayTime_(svcEndMin);

      var personSlug = person.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
var serviceSlug = service.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
// 2026-08-26 (BRIEFS/BUILD-REQUEST-service-identity-mint-and-backfill-2026-08-26.md):
// personId had no uniqueness suffix, unlike confirmAddPerson_'s matching
// personId (which appends Date.now()). Calling "Add Service" twice for the
// same person+service label produced the exact same id/itemId both times,
// so the second add silently collided with/overwrote the first instead of
// creating a second line item. Date.now() matches confirmAddPerson_'s real
// (not the brief's originally-assumed) pattern.
var personId = "person_manual_" + personSlug + "_" + Date.now();
var id = personId + "|" + serviceSlug;
var isBride = /^bride$/i.test(person.trim());

var newSvc = {
  id: id,
  itemId: id,
  personId: personId,
  order: (state.sourceServicesDraft.length + 1),
  person: person,
  personLabel: person,
  role: inferRoleFromPerson_(person, { isBride: isBride }),
  service: service,
  serviceLabel: service,
  category: category,
  durationMin: duration,
  duration: duration,
  assignedArtist: assignedArtist,
  artist: assignedArtist,
  startTime: svcStartTime,
  start: svcStartTime,
  endTime: svcEndTime,
  end: svcEndTime,
  startMin: svcStartMin,
  endMin: svcEndMin,
  isBride: isBride,
  notes: notes,
  source: "manual"
};
     if (startOvr) { newSvc.startTimeOverride = inputTimeToDisplay(startOvr); }

      state.sourceServicesDraft.push(newSvc);
state.sourceServicesDraft.forEach(function(s, i) { s.order = i + 1; });
rederiveDraftState_();
state.timelineDirty = true;
closeAddServiceModal();
renderStage();
showBanner("New service added for " + person + ".", "success");

      // Promote to a real party-model record in the background (2026-07-30),
      // same as confirmAddPerson_. This modal can also introduce a brand-new
      // person_manual_* id via free-text entry, so it needs the same fix.
      // Idempotent on the backend, so this is safe even if "person" text
      // happens to match an existing real party member elsewhere.
      promoteManualPersonInBackground_(personId, person, newSvc.role);
    }

    // ── Add Custom Block ─────────────────────────────────────────
    function openAddCustomBlockModal() {
      var artistOptions = (state.activeTeam || []).map(function(name) {
        return '<option value="' + escapeHtml(name) + '">' + escapeHtml(name) + '</option>';
      }).join('');
      document.getElementById("addBlockTitle").value = "";
      document.getElementById("addBlockArtist").innerHTML = '<option value="">— all artists —</option>' + artistOptions;
      document.getElementById("addBlockDuration").value = "15";
      document.getElementById("addBlockStartTime").value = "";
      document.getElementById("addBlockNotes").value = "";
      document.getElementById("addBlockModal").classList.add("open");
    }

    function closeAddCustomBlockModal() {
      document.getElementById("addBlockModal").classList.remove("open");
    }

function saveNewCustomBlock() {
  pushUndoSnapshot_();
  var title = document.getElementById("addBlockTitleCustom").value.trim() || document.getElementById("addBlockTitle").value.trim();
  var artist = document.getElementById("addBlockArtist").value.trim();
  var duration = parseInt(document.getElementById("addBlockDuration").value, 10) || 15;
  var startOvr = document.getElementById("addBlockStartTime").value.trim();
  var notes = document.getElementById("addBlockNotes").value.trim();

  if (!title) {
    showBanner("Block title is required.", "error");
    return;
  }

  var slug = title.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
  var id = "block_" + Date.now() + "_" + slug;

  var startDisplay = startOvr ? inputTimeToDisplay(startOvr) : "";

  var newBlock = {
    id: id,
    itemId: id,
    personId: "block_" + slug,
    order: (state.sourceServicesDraft || []).length + 1,
    person: title,
    personLabel: title,
    role: "",
    service: title,
    serviceLabel: title,
    category: "custom",
    durationMin: duration,
    duration: duration,
    assignedArtist: artist,
    artist: artist,
    isBride: false,
    isCustomBlock: true,
    notes: notes,
    source: "manual",
    startTimeOverride: startDisplay
  };

  state.customBlocksDraft = state.customBlocksDraft || [];
  state.customBlocksDraft.push(newBlock);

  state.sourceServicesDraft = (state.sourceServicesDraft || [])
    .filter(function(service) { return !service.isCustomBlock; })
    .concat(state.customBlocksDraft);

  state.sourceServicesDraft.forEach(function(service, i) {
    service.order = i + 1;
  });

  syncWorkspaceServicesFromSourceDraft_();
  state.timelineDirty = true;

  closeAddCustomBlockModal();
  renderStage();
  showBanner('Custom block "' + title + '" added. Click Recalculate Preview to place it at the chosen time.', "success");
}

function dedupeBridalPartyDraft_(people) {
  const safe = Array.isArray(people) ? people : [];
  const byKey = {};

  safe.forEach(function(person) {
    const role = String(person.role || "").trim().toLowerCase();
    const rawName =
      String(person.profileName || person.name || person.personLabel || "").trim();

    // Prefer a stable synthetic personId as the dedupe key so two blank-name
    // members split by groupServicesIntoBridalPartyDraft_ are never re-merged
    // here (CODE fix 2026-07-10). Synthetic ids are "synthparty|role|N".
    const pid = String(person.personId || person.id || "").trim();
    let key = (pid && pid.indexOf("synthparty|") === 0)
      ? pid
      : role + "|" + rawName.toLowerCase();

    // Force all bride variants into one record
    if (role === "bride") {
      key = "bride";
    }

    if (!byKey[key]) {
      byKey[key] = JSON.parse(JSON.stringify(person));
      return;
    }

    const existing = byKey[key];

    // Prefer richer profile data
    if (!existing.profileName && person.profileName) existing.profileName = person.profileName;
    if ((!existing.name || existing.name === "Bride") && person.name) existing.name = person.name;
    if ((!existing.personLabel || existing.personLabel === "Bride") && person.personLabel) existing.personLabel = person.personLabel;
    if (!existing.inspirationFolder && person.inspirationFolder) existing.inspirationFolder = person.inspirationFolder;
    if (!existing.inspirationGallery || !existing.inspirationGallery.length) existing.inspirationGallery = person.inspirationGallery || [];
    if (!existing.trialGallery || !existing.trialGallery.length) existing.trialGallery = person.trialGallery || [];
    if (person.inspirationSubmitted) existing.inspirationSubmitted = true;
    if (person.trialAssistSubmitted === false) existing.trialAssistSubmitted = false;

    existing.servicesBooked = uniqueStrings_((existing.servicesBooked || []).concat(person.servicesBooked || []));
  });

  return Object.keys(byKey).map(function(key) {
    return byKey[key];
  });
}

function uniqueStrings_(arr) {
  const out = [];
  (arr || []).forEach(function(v) {
    const s = String(v || "").trim();
    if (s && out.indexOf(s) === -1) out.push(s);
  });
  return out;
}

// --- Placeholder-duplicate collapse (Cassandra Healey P1(5), 2026-10-27) ---
// A placeholder person (a bare first name only, e.g. "Leanne") can coexist
// in the source data alongside the real party member once their full name
// is known (e.g. "Leanne Healey"). dedupeBridalPartyDraft_ above keys on the
// EXACT lowercased name, so these are different keys and both survive as
// separate roster rows, the placeholder can then resurface mid-edit as an
// extra person with duplicated services (confirmed live, see
// BRIEFS/RESULTS-cassandra-healey-runsheet-edit-attempt-2026-10-26.md).
//
// This is a render-side safety net, it never touches the upstream source
// of the duplicate (that root cause is undiagnosed in this pass, see the
// handoff). It collapses a bare-first-name person into a same-role person
// whose name starts with "<bareName> " (a true word-boundary prefix match,
// never a substring match, "Kate" never matches "Katelyn" since there is
// no space after "Kate" inside "Katelyn"). Never touches the bride bucket.
// Never touches a synthetic-id placeholder from groupServicesIntoBridalPartyDraft_
// ("Bridesmaid 1"/"Bridesmaid 2" blank-name splits), those are deliberately
// distinct placeholders, not duplicates of each other.
function collapsePlaceholderDuplicates_(people) {
  const safe = Array.isArray(people) ? people : [];
  const bride = [];
  const rest = [];
  safe.forEach(function(p) {
    if (p && String(p.role || "").trim().toLowerCase() === "bride") bride.push(p);
    else rest.push(p);
  });

  // Group by normalised role so a placeholder is never merged across roles
  // (e.g. a "Bridesmaid" placeholder into a similarly-named "Mother").
  const byRole = {};
  rest.forEach(function(p) {
    const role = String(p.role || "").trim().toLowerCase();
    byRole[role] = byRole[role] || [];
    byRole[role].push(p);
  });

  Object.keys(byRole).forEach(function(role) {
    const bucket = byRole[role];
    bucket.forEach(function(candidate) {
      const candName = String(candidate.profileName || candidate.name || candidate.personLabel || "").trim().toLowerCase();
      if (!candName || candName.indexOf(" ") !== -1) return; // only a bare single-word name is a placeholder candidate
      const pid = String(candidate.personId || candidate.id || "").trim();
      if (pid.indexOf("synthparty|") === 0) return; // deliberate blank-name split, not a duplicate

      var anchor = null;
      for (var i = 0; i < bucket.length; i++) {
        var other = bucket[i];
        if (other === candidate) continue;
        var otherName = String(other.profileName || other.name || other.personLabel || "").trim().toLowerCase();
        if (otherName.indexOf(candName + " ") === 0) { anchor = other; break; }
      }
      if (!anchor) return;

      anchor.servicesBooked = uniqueStrings_((anchor.servicesBooked || []).concat(candidate.servicesBooked || []));
      if (!anchor.inspirationFolder && candidate.inspirationFolder) anchor.inspirationFolder = candidate.inspirationFolder;
      if ((!anchor.inspirationGallery || !anchor.inspirationGallery.length) && candidate.inspirationGallery && candidate.inspirationGallery.length) {
        anchor.inspirationGallery = candidate.inspirationGallery;
      }
      try {
        console.warn('[runsheet-builder] collapsed placeholder person "' + candName + '" into "' +
          String(anchor.profileName || anchor.name || anchor.personLabel || '').trim() + '" (role: ' + role + ')');
      } catch (_) {}
      candidate.__jvCollapsedPlaceholder = true;
    });
  });

  return bride.concat(rest.filter(function(p) { return !p.__jvCollapsedPlaceholder; }));
}

function applyArtistSetupPackdown_() {
  if (!state.workspace) return;

  const timed = (state.workspace.services || []).filter(function(s) {
    return !s.isSystemBlock && !!String(s.assignedArtist || s.artist || "").trim();
  });

  const byArtist = {};
  timed.forEach(function(service) {
    const artist = String(service.assignedArtist || service.artist || "").trim();
    if (!artist) return;
    if (!byArtist[artist]) byArtist[artist] = [];
    byArtist[artist].push(service);
  });

  state.workspace.artistMeta = state.workspace.artistMeta || {};

  Object.keys(byArtist).forEach(function(artist) {
    const items = byArtist[artist].slice().sort(function(a, b) {
      return Number(a.startMin || parseDisplayTimeToMinutes(a.startTime || a.start || "")) -
             Number(b.startMin || parseDisplayTimeToMinutes(b.startTime || b.start || ""));
    });

    const first = items[0];
    const last = items[items.length - 1];

    const firstStart = Number(first.startMin || parseDisplayTimeToMinutes(first.startTime || first.start || ""));
    const lastEnd = Number(last.endMin || parseDisplayTimeToMinutes(last.endTime || last.end || ""));

    state.workspace.artistMeta[artist] = state.workspace.artistMeta[artist] || {};
    state.workspace.artistMeta[artist].startTime = formatMinutesAsDisplayTime_(Math.max(0, firstStart - 15));
    state.workspace.artistMeta[artist].endTime = formatMinutesAsDisplayTime_(lastEnd + 15);
  });

  state.timelineDirty = true;
  renderStage();
  showBanner("Artist setup and pack down times applied.", "success");
}

// ── Stage A: stable pm_id from D1 ───────────────────────────────────────────────
//
// PURELY ADDITIVE. This adds a BETTER SOURCE of pm_id; it removes nothing. Every
// existing name-keyed fallback (getPmIdForPerson_ :7593/:7636, and the ~27 other
// name-resolution sites) stays exactly as it was. If this read returns nothing,
// behaviour is byte-identical to before it existed.
//
// WHY (BRIEFS/HANDOFF-party-identity-stage-a-to-runsheet-builder-2026-08-28.md):
// party-member identity in this file is a display-name string, not a stable id.
// That one root cause produced duplicate seats, bridesmaid_3/"Bridesmaid 1"
// mismatches, service-ids re-minting on rename, and a name-keyed GAS lookup that
// CREATES a row on a miss. D1 already solves it: party_members.pm_id is
// NOT NULL with UNIQUE (bride_id, pm_id), 458 rows across 131 brides, zero
// blanks. The builder simply never asked for it (0 calls to this route before
// this change).
//
// WHERE THIS ATTACHES, AND WHY IT IS NOT WHERE THE HANDOFF SUGGESTED:
// the handoff proposed the Promise.all at what was then :10250. That call site
// is inside loadBrideAndPartyProfilesViaGas_, the LAST-RESORT GAS path, reached
// only when BOTH CDN tiers miss. Attaching there would have skipped the D1 read
// for exactly the brides the CDN serves (the common case). Instead this hangs
// off rsbApplyPartyMemberIdsFromD1_, called from BOTH hydration paths:
//   - the CDN path  (rsbApplyCardDataEntry_)
//   - the GAS path  (loadBrideAndPartyProfilesViaGas_'s .then)
// so pm_id lands regardless of which one served the bride.
//
// SEPARATE REAL GAP FOUND WHILE WIRING THIS: the CDN path's state.partyProfiles
// mapping (in rsbApplyCardDataEntry_) never carried pm_id at all -- it maps 10
// named fields and pm_id is not among them. So on a CDN hit, the ":7593
// partyProfiles is the RELIABLE source of pm_id" fallback had no pm_id to read
// and silently degraded to the weaker sources. That mapping now passes pm_id
// through. Noted rather than glossed: this is a pre-existing bug, not one this
// change introduced.
function rsbApplyPartyMemberIdsFromD1_(brideUid) {
  if (window.JV_RSB_D1_PMID === false) return Promise.resolve(false); // kill flag
  if (!hasBridge) return Promise.resolve(false);

  // Per the handoff's "not verified" note: the builder's bride_uid availability
  // needed checking rather than deriving from a name. It IS available --
  // state.workspace.brideUid, set by RunsheetEngineV4.gs:907 from the CRM
  // BRIDE_UID column, and already used by the D1 hub-field fast path (:8666).
  // If it is genuinely absent we REFUSE rather than fall back to a name key --
  // a name key is the very thing this stage exists to stop relying on.
  var uid = String(brideUid || (state.workspace && state.workspace.brideUid) || '').trim();
  if (!uid) {
    _rsbD1PmIdStats_.noBrideUid++;
    return Promise.resolve(false);
  }

  return jvBridge.callAdmin('bridge_runsheet_getPartyMembersD1', { bride_uid: uid })
    .then(function(res) {
      // An unmirrored bride returns {ok:true, rowCount:0, rows:[]} -- a clean
      // "no id available", NOT an error. Treat it as a miss and fall through to
      // today's logic, exactly as the handoff requires.
      if (!res || !res.ok || !Array.isArray(res.rows) || !res.rows.length) {
        _rsbD1PmIdStats_.emptyOrMiss++;
        return false;
      }
      _rsbD1PmIdStats_.d1RowsSeen += res.rows.length;
      rsbMergeD1PartyIds_(res.rows);
      return true;
    })
    .catch(function(err) {
      // Never blank or block the builder on this read. Worst case we are exactly
      // as well off as before this function existed.
      _rsbD1PmIdStats_.errors++;
      try { console.warn('[runsheet][stageA] D1 pm_id read failed', err); } catch (_) {}
      return false;
    });
}

// Instrumentation (Stage B depends on this data). Counts how often the D1 id was
// available vs the name-keyed fallback having to do the work, and -- the finding
// the handoff explicitly asked to surface rather than silently resolve -- how
// often D1's id DISAGREES with the name-matched one for the same person.
var _rsbD1PmIdStats_ = {
  d1RowsSeen: 0,       // rows returned by D1
  appliedToProfiles: 0,// partyProfiles entries that gained a pm_id from D1
  alreadyHadSame: 0,   // already had the same id (D1 confirmed it)
  DISAGREED: 0,        // already had a DIFFERENT id -- real finding, see below
  unmatchedD1Rows: 0,  // D1 rows no local person matched by name
  blankNamedSeats: 0,  // D1 rows with a real role but no name (legit unnamed seats)
  noBrideUid: 0,
  emptyOrMiss: 0,
  errors: 0
};

// Merge D1's stable ids onto the in-memory roster. NON-DESTRUCTIVE by design:
// this only ever FILLS a blank pm_id. Where a local pm_id already exists and
// differs from D1's, it does NOT overwrite -- it records the disagreement and
// leaves today's value untouched, because the handoff is explicit that a
// D1-vs-_PartyMembers disagreement is a REAL FINDING to surface, not something
// for this stage to silently pick a winner on. (D1's 458 rows have not been
// reconciled against _PartyMembers, the GAS system of record; only the D1 side
// was measured.)
function rsbMergeD1PartyIds_(rows) {
  var byName = {};
  for (var i = 0; i < rows.length; i++) {
    var r = rows[i] || {};
    var pmId = String(r.pm_id || '').trim();
    if (!pmId) continue; // schema says NOT NULL, but never trust, verify
    var nm = String(r.name || '').trim().toLowerCase();
    if (!nm) {
      // The 164 blank-name rows across the fleet are UNNAMED SEATS, not people
      // whose names went missing: every one carries a real role and NONE carry
      // any contact detail or hub data (a record that lost its name would still
      // have her email or her hub answers). They are legitimate booked seats --
      // a bride who booked 6 bridesmaids and named 4. Do NOT clean these up and
      // do NOT try to name-match them. Evidence:
      // BRIEFS/RESULTS-party-identity-expanded-scope-d1-checks-2026-08-28.md
      _rsbD1PmIdStats_.blankNamedSeats++;
      continue;
    }
    var roleLow = String(r.role || '').trim().toLowerCase();
    // Key on name+role first (mirrors how :7593 resolves), name alone as backup,
    // so a "Mother of the Bride" whose NAME is also the role string still lands.
    byName[nm + '|' + roleLow] = byName[nm + '|' + roleLow] || r;
    byName[nm] = byName[nm] || r;
  }

  var matchedD1 = {};
  if (Array.isArray(state.partyProfiles)) {
    for (var j = 0; j < state.partyProfiles.length; j++) {
      var p = state.partyProfiles[j];
      if (!p) continue;
      var pn = String(p.name || '').trim().toLowerCase();
      if (!pn) continue;
      var pr = String(p.role || '').trim().toLowerCase();
      var hit = byName[pn + '|' + pr] || byName[pn];
      if (!hit) continue;
      matchedD1[String(hit.pm_id)] = true;
      var d1Id = String(hit.pm_id || '').trim();
      var existing = String(p.pm_id || '').trim();
      if (!existing) {
        p.pm_id = d1Id;                       // fill the blank -- the whole point
        _rsbD1PmIdStats_.appliedToProfiles++;
      } else if (existing === d1Id) {
        _rsbD1PmIdStats_.alreadyHadSame++;
      } else {
        // DO NOT silently prefer one. Surface it loudly and keep today's value.
        _rsbD1PmIdStats_.DISAGREED++;
        try {
          console.warn(
            '[runsheet][stageA] pm_id DISAGREEMENT for "' + (p.name || '') +
            '" (role "' + (p.role || '') + '") -- local=' + existing +
            ' D1=' + d1Id + '. Keeping local. This is a real finding: D1 has not ' +
            'been reconciled against _PartyMembers (the GAS system of record).'
          );
        } catch (_) {}
      }
    }
  }

  for (var k = 0; k < rows.length; k++) {
    var rr = rows[k] || {};
    var rid = String(rr.pm_id || '').trim();
    if (rid && !matchedD1[rid] && String(rr.name || '').trim()) {
      _rsbD1PmIdStats_.unmatchedD1Rows++;
    }
  }

  try {
    console.info('[runsheet][stageA] pm_id merge:', JSON.parse(JSON.stringify(_rsbD1PmIdStats_)));
  } catch (_) {}
}

// Readable from the console for Stage B's dead-fallback analysis.
window.jvRsbD1PmIdStats = function () { return _rsbD1PmIdStats_; };

// ── Bride profile + party profiles ──────────────────────────────────────────────

// CDN card-data fast-path parser + shaper (2026-07-07, Part B).
// Reads the server-rendered `#jv-rsb-carddata` island (populated from the
// public `bridal_card_data` metaobject by BridalCardDataSync.gs) and, for a
// bride present in it, hydrates state.brideProfile / state.partyProfiles /
// state.brideCardsData to be byte-for-byte equivalent to what the 3 GAS
// profile calls set (see the .then in loadBrideAndPartyProfiles_). Returns
// true when it fully hydrated (caller then skips the GAS calls); returns
// false on any miss, parse error or incomplete data so the caller falls
// through to the existing live GAS path (cards are never blanked).
function rsbBuildCardIndex_() {
  if (window._jvRsbCardIndex) return window._jvRsbCardIndex;
  var idx = {};
  try {
    var el = document.getElementById('jv-rsb-carddata');
    if (!el) { window._jvRsbCardIndex = idx; return idx; }
    var parsed = JSON.parse(el.textContent || el.innerText || '{}');
    var brides = (parsed && parsed.brides) || [];
    for (var i = 0; i < brides.length; i++) {
      var b = brides[i];
      var key = String((b && b.email) || '').trim().toLowerCase();
      if (key) idx[key] = b;
    }
  } catch (_) {
    // Parse failure => empty index => every bride falls through to GAS.
    idx = {};
  }
  window._jvRsbCardIndex = idx;
  return idx;
}

// Win B (2026-07-12): look up a bride's published runsheet viewer token from the
// CDN card-data island by email. The token is written to the `bridal_card_data`
// metaobject's viewer_token field (BridalCardDataSync.gs) and mirrored into the
// island as `viewerToken`. Returns '' when the island is absent, the bride is not
// in it, or the bride has no token yet (unpublished or pre-backfill), and the
// caller then falls through to the live bridge_runsheet_getPreviewUrl GAS path.
// Kill flag: window.JV_RSB_FASTPATH === false disables the CDN read entirely.
function rsbViewerTokenFromIsland_(email) {
  if (window.JV_RSB_FASTPATH === false) return '';
  if (!email) return '';
  try {
    var idx = rsbBuildCardIndex_();
    var entry = idx[String(email).trim().toLowerCase()];
    var tok = entry && entry.viewerToken ? String(entry.viewerToken).trim() : '';
    return tok;
  } catch (_) { return ''; }
}

// Defensively parse an island JSON-string field. Each may be '', 'null',
// undefined, or a JSON string. Returns the fallback on any failure.
function rsbParseJsonField_(raw, fallback) {
  if (raw === null || raw === undefined) return fallback;
  // Already-parsed object or array (Shopify JSON field via Liquid | json): pass through.
  if (typeof raw === 'object') return raw;
  var s = String(raw).trim();
  if (!s || s === 'null' || s === '""') return fallback;
  try {
    var v = JSON.parse(s);
    return (v === null || v === undefined) ? fallback : v;
  } catch (_) {
    return fallback;
  }
}

// Two-tier CDN card-data read (2026-07-08).
//   Tier 1 (this fn, synchronous): the shop-wide `#jv-rsb-carddata` island
//     server-rendered by sections/jv-runsheet-builder.liquid. Shopify Liquid
//     caps shop.metaobjects.<type>.values at 50 entries, so the island only
//     covers the first ~50 brides; it is a fast same-page fallback.
//   Tier 2 (rsbFetchCardDataViaWorker_, async): a per-bride Storefront read of
//     the PUBLIC_READ `bridal_card_data` metaobject by handle via the Cloudflare
//     relay route `jv_read_card_data` (~200ms). Covers every bride, including
//     those past #50 that the island drops.
// Both paths hydrate identical state shapes through rsbApplyCardDataEntry_ and
// skip the 3 slow GAS profile calls. The LOCKED Step-2 card render is untouched.
function rsbTryCardDataFastPath_(email) {
  if (window.JV_RSB_FASTPATH === false) return false;
  if (!email) return false;

  var idx = rsbBuildCardIndex_();
  var entry = idx[String(email).trim().toLowerCase()];
  if (!entry) return false;

  return rsbApplyCardDataEntry_(entry);
}

// Hydrate state + render from a single card-data entry object. The entry carries
// the SAME camelCase keys whether it came from the island index (rsbBuildCardIndex_)
// or the worker (jv_read_card_data): email, name, weddingDate, partyMembersJson,
// brideProfileJson, brideBriefJson, brideInspoJson, brideSocialJson, vendorsJson.
// Returns true when it hydrated, false on the incompleteness guard so the caller
// can fall through. State shapes are byte-identical to the 3 GAS profile calls
// (LOCKED Step-2 cards depend on them, do NOT change field mappings).
function rsbApplyCardDataEntry_(entry) {
  if (!entry) return false;

  // Parse the inner JSON strings defensively.
  var partyBlob   = rsbParseJsonField_(entry.partyMembersJson, null);
  var brideProfile = rsbParseJsonField_(entry.brideProfileJson, null);
  var brideBrief   = rsbParseJsonField_(entry.brideBriefJson,   null);
  var brideInspo   = rsbParseJsonField_(entry.brideInspoJson,   null);
  var social       = rsbParseJsonField_(entry.brideSocialJson,  null) || {};
  var vendorsArr   = rsbParseJsonField_(entry.vendorsJson,      null);

  // The party blob mirrors custom.jv_party_members_json: { members: [...] }
  // (or, defensively, a bare array). The members carry snake_case fields
  // (name/role/hair_style/allergies/skin_concerns/colour_palette/hair_notes/
  // inspo_notes/profile_pic_url/hair_inspo_urls/makeup_inspo_urls/form_status/
  // email/phone) which is exactly what jvPcRenderCards_ + getHubMemberForPerson_
  // read, so this array feeds brideCardsData.partyMembers directly.
  var members = [];
  if (partyBlob && Array.isArray(partyBlob.members)) members = partyBlob.members;
  else if (Array.isArray(partyBlob)) members = partyBlob;

  // Incompleteness guard: if we have neither a usable party roster nor any
  // bride profile/brief data, fall through to GAS rather than render blank
  // cards. (An unpublished / pre-backfill bride can have an empty entry.)
  if (!members.length && !brideProfile && !brideBrief) return false;

  // ── state.brideProfile ── identical shape to bridge_runsheet_getBrideProfile
  // ({ instagram, pinterest, vendors }). Vendors resolved at write time (Part A).
  state.brideProfile = {
    instagram: (social && social.instagram) || '',
    pinterest: (social && social.pinterest) || '',
    vendors:   Array.isArray(vendorsArr) ? vendorsArr : []
  };

  // ── state.brideCardsData ── identical shape to bridge_admin_getBrideCardsData
  // mapped in the GAS .then: brideProfile=cardsRes.bride.profile,
  // brideBrief=cardsRes.bride.brief, brideInspo=cardsRes.bride.inspo,
  // partyMembers=cardsRes.partyMembers. brideInspirationItems is the optional
  // InspirationStore array (null when the GAS flag is off) so null here matches.
  state.brideCardsData = {
    brideProfile: brideProfile || null,
    brideBrief:   brideBrief   || null,
    brideInspo:   brideInspo   || null,
    brideInspirationItems: null,
    partyMembers: members
  };

  // ── state.partyProfiles ── the GraphQL-metaobject fallback list. We derive
  // it from the same blob members so the existing fallback lookups (which key
  // on lowercased name and read camelCase fields) still resolve. Maps
  // snake_case blob fields => the camelCase keys those fallbacks read.
  state.partyProfiles = members.map(function(m) {
    m = m || {};
    return {
      // pm_id passthrough (Stage A, 2026-08-28). PRE-EXISTING BUG, not one this
      // change introduced: this mapping carried 10 named fields and pm_id was
      // not among them, so on a CDN hit the ":7593 partyProfiles is the RELIABLE
      // source of pm_id" fallback had nothing to read and silently degraded to
      // the weaker sources. Carry whatever the blob already knows; the D1 read
      // below fills the rest.
      pm_id:         m.pm_id || m.pmId || '',
      name:          m.name || '',
      role:          m.role || '',
      email:         m.email || '',
      profilePicUrl: m.profile_pic_url || m.profilePicUrl || '',
      formStatus:    m.form_status || m.formStatus || '',
      hairStyle:     m.hair_style || m.hairStyle || '',
      hairNotes:     m.hair_notes || m.hairNotes || '',
      skinConcerns:  m.skin_concerns || m.skinConcerns || '',
      allergies:     m.allergies || '',
      colourPalette: m.colour_palette || m.colourPalette || ''
    };
  });

  // Render exactly as the GAS .then does (same 3 render calls).
  try { showBanner("Bride & party details loaded.", "success"); } catch (_) {}
  reRenderBrideProfilePanels_();
  renderBuilderBrideCards_();
  try { renderStage(); } catch (_) {}

  // Stage A (2026-08-28): overlay D1's stable pm_id onto the roster we just
  // hydrated. Deliberately AFTER the render calls above and deliberately NOT
  // awaited -- the CDN path's whole point is instant paint, and pm_id is not
  // needed to draw a card, only to act on one. A second render fires only if
  // ids actually landed. On any miss/error nothing changes and this path
  // behaves exactly as it did before.
  rsbApplyPartyMemberIdsFromD1_().then(function(applied) {
    if (applied) { try { renderStage(); } catch (_) {} }
  });

  return true;
}

// Per-bride Storefront read of the `bridal_card_data` metaobject via the
// Cloudflare relay route `jv_read_card_data` (2026-07-08). This is Tier 2 of the
// CDN card-data read: it covers every bride the 50-entry island (Tier 1) drops.
// The worker serves this route edge-side (Storefront API, ~200ms) and never
// forwards to GAS. Returns a Promise<boolean>: true when it hydrated the cards,
// false on any miss/error so the caller falls through to the live GAS path.
function rsbFetchCardDataViaWorker_(email) {
  if (window.JV_RSB_FASTPATH === false) return Promise.resolve(false);
  if (!email || !hasBridge) return Promise.resolve(false);
  return jvBridge.callAdmin('jv_read_card_data', { email: email })
    .then(function(res) {
      if (res && res.ok && res.entry) {
        return rsbApplyCardDataEntry_(res.entry);
      }
      return false;
    })
    .catch(function() {
      // CDN read failed; fall through to GAS. Never blank the cards.
      return false;
    });
}

function loadBrideAndPartyProfiles_(email) {
  if (!email || !hasBridge) return;
  var requestEmail = email;

  // Tier 1 CDN read (synchronous): the `bridal_card_data` island covers the
  // first ~50 brides (Shopify caps shop.metaobjects.<type>.values at 50). On a
  // hit we hydrate the 3 profile state objects and skip the slow GAS calls.
  if (rsbTryCardDataFastPath_(requestEmail)) {
    // 2026-09-30 fix (HANDOFF-runsheet-builder-step01-stale-party-data): a CDN
    // hit here used to be the END of the story for Step 01's Party Profiles
    // panel -- no freshness check against the live roster, ever. That cache is
    // only refreshed by a best-effort, try/catch-wrapped, explicitly "non-
    // fatal" write (cardData_cacheForEmail_, called from JV_partyMember_
    // syncBlob) after a party-member save; if that write ever fails silently,
    // or the page is opened between two saves mid-cleanup, the island freezes
    // on a stale/intermediate roster with nothing to ever correct it -- root
    // cause of Shanice Pitty showing 11 ghost rows (incl. a double "Breayn
    // Cullen" and a blank pre-merge placeholder) while Step 02, which reads
    // the live workspace roster, correctly showed 8. Keep the instant paint
    // (still correct per the CDN-first doctrine), but always also fire a
    // background live re-check and self-heal if it disagrees -- the same
    // paint-then-verify pattern every other CDN cache in this codebase uses.
    rsbVerifyPartyProfilesLive_(requestEmail);
    return;
  }

  // Tier 2 CDN read (async): for every bride past the island cap, read that one
  // bride's metaobject by handle via the worker (Storefront API, ~200ms). On a
  // hit we skip GAS; on any miss/error we fall through to the live GAS path.
  rsbFetchCardDataViaWorker_(requestEmail).then(function(hit) {
    if (hit) { rsbVerifyPartyProfilesLive_(requestEmail); return; }
    loadBrideAndPartyProfilesViaGas_(requestEmail);
  });
}

// ── Live verify-and-reconcile for Step 01 Party Profiles (2026-09-30) ──────────
// See loadBrideAndPartyProfiles_ above for the full rationale. Runs in the
// background after a CDN hit already painted; only re-renders when the live
// roster actually differs from what's shown, so a healthy cache costs nothing
// beyond one extra (already-cheap) GAS call. bridge_runsheet_getPartyProfiles
// reads _PartyMembers (the source of record) fresh on every call, so it is
// always at least as current as Step 02's own roster.
function rsbPartyProfileIdentityKey_(p) {
  p = p || {};
  var pmId = String(p.pm_id || p.pmId || '').trim().toLowerCase();
  if (pmId) return 'id:' + pmId;
  return 'nr:' + String(p.name || '').trim().toLowerCase() + '|' + String(p.role || '').trim().toLowerCase();
}
function rsbPartyProfilesRosterMatches_(a, b) {
  var av = (a || []).map(rsbPartyProfileIdentityKey_).sort();
  var bv = (b || []).map(rsbPartyProfileIdentityKey_).sort();
  if (av.length !== bv.length) return false;
  for (var i = 0; i < av.length; i++) { if (av[i] !== bv[i]) return false; }
  return true;
}
function rsbVerifyPartyProfilesLive_(email) {
  if (!email || !hasBridge) return;
  jvBridge.callAdmin('bridge_runsheet_getPartyProfiles', { email: email }).then(function(partyRes) {
    if (!partyRes || !partyRes.ok || !Array.isArray(partyRes.profiles)) return;
    var live = partyRes.profiles;
    if (rsbPartyProfilesRosterMatches_(state.partyProfiles, live)) return; // cache already agrees
    try {
      console.warn(
        '[runsheet][party-profiles] CDN card-data cache disagreed with the live roster for ' +
        email + ' -- replacing with live data.',
        { cached: (state.partyProfiles || []).map(function(p) { return p.name; }),
          live: live.map(function(p) { return p.name; }) }
      );
    } catch (_) {}
    state.partyProfiles = live;
    try { renderStage(); } catch (_) {}
  }).catch(function() {
    // Verify is best-effort; the CDN paint already rendered something usable.
  });
}

// The live GAS profile path (3 calls). Reached only when BOTH CDN tiers miss.
// Each call pays the ~8s GAS cold-start tax, so it stays the last resort.
function loadBrideAndPartyProfilesViaGas_(requestEmail) {
  if (!requestEmail || !hasBridge) return;

  // Progress feedback (Bug A, 2026-07-03 audit): the timeline is already usable
  // at this point, but these 3 profile calls each pay the ~8s GAS cold-start
  // tax. Without a message the panels look frozen. Show a non-blocking note.
  try { showBanner("Timeline ready, loading bride & party details...", "success"); } catch (_) {}

  Promise.all([
    jvBridge.callAdmin('bridge_runsheet_getBrideProfile',  { email: requestEmail }),
    jvBridge.callAdmin('bridge_runsheet_getPartyProfiles', { email: requestEmail }),
    jvBridge.callAdmin('bridge_admin_getBrideCardsData',   { brideEmail: requestEmail })
  ]).then(function(results) {
    var brideRes = results[0];
    var partyRes = results[1];
    var cardsRes = results[2];

    if (brideRes && brideRes.ok) {
      state.brideProfile = {
        instagram: brideRes.instagram || '',
        pinterest: brideRes.pinterest || '',
        vendors:   brideRes.vendors   || []
      };
    }
    if (partyRes && partyRes.ok) {
      state.partyProfiles = partyRes.profiles || [];
    }
    if (cardsRes && cardsRes.ok) {
      state.brideCardsData = {
        brideProfile: cardsRes.bride && cardsRes.bride.profile,
        brideBrief:   cardsRes.bride && cardsRes.bride.brief,
        brideInspo:   cardsRes.bride && cardsRes.bride.inspo,
        // InspirationStore session 2: canonical Array<InspirationItem> from
        // the new GAS fetcher. null when feature flag is off.
        // Contract: DOCS/inspiration-store-data-contract.md
        brideInspirationItems: cardsRes.bride && cardsRes.bride.inspirationItems,
        partyMembers: cardsRes.partyMembers || []
      };
    }

    // Stage A (2026-08-28): overlay D1's stable pm_id onto the roster this GAS
    // path just set. Same call as the CDN path uses -- one implementation, both
    // hydration routes, so pm_id lands regardless of which one served the bride
    // (the handoff's suggested call site was THIS path only, which would have
    // skipped every CDN-served bride; see rsbApplyPartyMemberIdsFromD1_'s
    // docblock). Not awaited: the renders below must not wait on it.
    rsbApplyPartyMemberIdsFromD1_().then(function(applied) {
      if (applied) { try { renderStage(); } catch (_) {} }
    });

    reRenderBrideProfilePanels_();
    renderBuilderBrideCards_();
    // Re-render the Page 2 party cards so the hub fields (profile pic,
    // hairstyle pre-fill, hub details panel) appear once brideCardsData
    // arrives. The initial renderStage on workspace hydrate happens
    // before this async fetch returns, so without this call the cards
    // would only pick up hub data on the next manual interaction.
    try { renderStage(); } catch (_) {}
  }).catch(function() {
    // Profiles are supplementary; swallow errors silently.
  });
}

function reRenderBrideProfilePanels_() {
  var bridePanel = document.getElementById('brideProfilePanel');
  if (bridePanel) bridePanel.innerHTML = renderBrideProfilePanel_();
  var partyPanel = document.getElementById('partyProfilesPanel');
  if (partyPanel) partyPanel.innerHTML = renderPartyProfilesPanel_();
}

function renderBuilderBrideCards_() {
  var panel = document.getElementById('brideCardsPanel');
  if (!panel) return;
  var d = state.brideCardsData;
  if (!d) { panel.innerHTML = ''; return; }

  var brideName = (state.workspace && state.workspace.brideName) || state.currentBride || '';
  var hasContent = d.brideProfile || d.brideBrief || d.brideInspo || (d.partyMembers && d.partyMembers.length);
  if (!hasContent) { panel.innerHTML = ''; return; }

  var html = '<div class="panel panel--bride-cards">';
  html += '<h4 style="font-size:0.72rem;font-weight:700;letter-spacing:0.1em;text-transform:uppercase;color:var(--brand-primary);margin:0 0 0.75rem;">Vision &amp; Party</h4>';
  html += jvPcRenderCards_(d.brideProfile, d.brideBrief, d.brideInspo, d.partyMembers || [], brideName);
  html += '</div>';
  panel.innerHTML = html;

  _jvPcCardData_ = {
    brideProfile: d.brideProfile,
    brideBrief:   d.brideBrief,
    brideInspo:   d.brideInspo,
    partyMembers: d.partyMembers || [],
    brideName:    brideName
  };
  jvPcInitCards_('brideCardsPanel');
}

// ── Vision & Party card helpers (shared pattern with jv-admin-crm.js) ──────────

function jvPcEsc_(s) { return String(s || '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;'); }

function jvPcThumbsHtml_(hairCsv, makeupCsv, limit) {
  var urls = [];
  (String(hairCsv || '').split(',').concat(String(makeupCsv || '').split(','))).forEach(function(u) {
    u = u.trim();
    if (u && urls.length < (limit || 4)) urls.push(u);
  });
  if (!urls.length) return '';
  return '<div class="jv-pc-thumb-strip">' +
    urls.map(function(u) {
      return '<a href="' + jvPcEsc_(u) + '" target="_blank" rel="noopener" class="jv-party-hub__thumb-link">' +
        '<img src="' + jvPcEsc_(u) + '" class="jv-party-hub__thumb" loading="lazy" alt="Inspiration"></a>';
    }).join('') +
  '</div>';
}

function jvPcModalBodyForBride_(profile, brief, inspo, name) {
  var html = '<p class="jv-pc-modal__name">' + jvPcEsc_(name) + '</p><p class="jv-pc-modal__role">Bride</p>';
  var words = (profile && profile.visionWords) || [];
  if (words.length) {
    html += '<p class="jv-party-hub__sub-heading">Wedding Vision</p><div style="display:flex;flex-wrap:wrap;gap:0.4rem;margin-bottom:0.75rem;">';
    words.forEach(function(w) { html += '<span class="jv-vision-word-badge">' + jvPcEsc_(w) + '</span>'; });
    html += '</div>';
  }
  html += '<p class="jv-party-hub__sub-heading">Details</p><div class="jv-party-hub__detail-grid">';
  [['Partner', profile && profile.partnerName],['Beauty notes', profile && profile.beautyNotes],['Hairstyle', brief && brief.hairstylePref],['Makeup vibe', brief && brief.makeupVibe]].forEach(function(r) {
    if (!r[1]) return;
    html += '<div class="jv-party-hub__detail-row"><span class="jv-party-hub__detail-label">' + jvPcEsc_(r[0]) + '</span><span class="jv-party-hub__detail-value">' + jvPcEsc_(r[1]).replace(/\n/g,'<br>') + '</span></div>';
  });
  html += '</div>';
  var hairIds = (inspo && inspo.hair) || [], makeupIds = (inspo && inspo.makeup) || [];
  if (hairIds.length) {
    html += '<p class="jv-party-hub__sub-heading">Hair Inspiration</p><div class="jv-pc-modal__inspo-grid">';
    hairIds.forEach(function(fid) { var u='https://lh3.googleusercontent.com/d/'+jvPcEsc_(fid); html+='<a href="'+u+'" target="_blank" rel="noopener"><img src="'+u+'" loading="lazy" alt="Hair inspiration"></a>'; });
    html += '</div>';
  }
  if (makeupIds.length) {
    html += '<p class="jv-party-hub__sub-heading">Makeup Inspiration</p><div class="jv-pc-modal__inspo-grid">';
    makeupIds.forEach(function(fid) { var u='https://lh3.googleusercontent.com/d/'+jvPcEsc_(fid); html+='<a href="'+u+'" target="_blank" rel="noopener"><img src="'+u+'" loading="lazy" alt="Makeup inspiration"></a>'; });
    html += '</div>';
  }
  return html;
}

function jvPcModalBodyForMember_(member) {
  var name = member.name || 'Party Member';
  var role = member.role || '';
  var html = '<p class="jv-pc-modal__name">' + jvPcEsc_(name) + '</p>' + (role ? '<p class="jv-pc-modal__role">' + jvPcEsc_(role) + '</p>' : '');
  var fields = [['Hairstyle',member.hair_style||member.hairStyle],['Colour palette',member.colour_palette||member.colourPalette],['Skin concerns',member.skin_concerns||member.skinConcerns],['Allergies',member.allergies],['Notes',member.inspo_notes||member.inspoNotes]];
  var hasDetails = fields.some(function(f){return!!f[1];});
  if (hasDetails) {
    html += '<p class="jv-party-hub__sub-heading">Details</p><div class="jv-party-hub__detail-grid">';
    fields.forEach(function(r){if(!r[1])return;html+='<div class="jv-party-hub__detail-row"><span class="jv-party-hub__detail-label">'+jvPcEsc_(r[0])+'</span><span class="jv-party-hub__detail-value">'+jvPcEsc_(r[1]).replace(/\n/g,'<br>')+'</span></div>';});
    html += '</div>';
  }
  var hairUrls=String(member.hair_inspo_urls||member.hairInspoUrls||'').split(',').map(function(u){return u.trim();}).filter(Boolean);
  var makeupUrls=String(member.makeup_inspo_urls||member.makeupInspoUrls||'').split(',').map(function(u){return u.trim();}).filter(Boolean);
  if (hairUrls.length) { html+='<p class="jv-party-hub__sub-heading">Hair Inspiration</p><div class="jv-pc-modal__inspo-grid">'; hairUrls.forEach(function(u){html+='<a href="'+jvPcEsc_(u)+'" target="_blank" rel="noopener"><img src="'+jvPcEsc_(u)+'" loading="lazy" alt="Hair inspiration"></a>';}); html+='</div>'; }
  if (makeupUrls.length) { html+='<p class="jv-party-hub__sub-heading">Makeup Inspiration</p><div class="jv-pc-modal__inspo-grid">'; makeupUrls.forEach(function(u){html+='<a href="'+jvPcEsc_(u)+'" target="_blank" rel="noopener"><img src="'+jvPcEsc_(u)+'" loading="lazy" alt="Makeup inspiration"></a>';}); html+='</div>'; }
  return html;
}

function jvPcRenderCards_(brideProfile, brideBrief, brideInspo, partyMembers, brideName) {
  var html = '';
  var brideInitials = (brideName || 'B').split(' ').map(function(p){return p.charAt(0);}).join('').slice(0,2).toUpperCase() || 'B';
  var bridePicUrl = brideProfile && brideProfile.profilePicUrl;
  var brideHairIds = (brideInspo && brideInspo.hair) || [];
  var brideMakeupIds = (brideInspo && brideInspo.makeup) || [];
  var brideThumbUrls = brideHairIds.concat(brideMakeupIds).slice(0,4).map(function(fid){return 'https://lh3.googleusercontent.com/d/'+fid;});
  var brideThumbsHtml = brideThumbUrls.length ? '<div class="jv-pc-thumb-strip">' + brideThumbUrls.map(function(u){return '<a href="'+jvPcEsc_(u)+'" target="_blank" rel="noopener" class="jv-party-hub__thumb-link"><img src="'+jvPcEsc_(u)+'" class="jv-party-hub__thumb" loading="lazy" alt="Inspiration"></a>';}).join('') + '</div>' : '';

  html += '<div class="jv-party-hub__card jv-pc-card" data-jv-pc-type="bride">';
  html += '<div class="jv-party-hub__card-header">';
  html += '<div class="jv-party-hub__avatar">'+(bridePicUrl?'<img class="jv-party-hub__avatar-img" src="'+jvPcEsc_(bridePicUrl)+'" alt="'+jvPcEsc_(brideName)+'" loading="lazy">':jvPcEsc_(brideInitials))+'</div>';
  html += '<div class="jv-party-hub__card-identity"><p class="jv-party-hub__card-name">'+jvPcEsc_(brideName||'Bride')+'</p><p class="jv-party-hub__card-role">Bride</p></div>';
  html += '<div class="jv-party-hub__card-meta">'+brideThumbsHtml+'<button type="button" class="jv-party-hub__expand-btn jv-pc-view-full" data-jv-pc-type="bride">View full</button></div>';
  html += '</div></div>';

  if (partyMembers && partyMembers.length) {
    html += '<div class="jv-party-hub__grid" style="margin-top:0.75rem;">';
    partyMembers.forEach(function(m, idx) {
      var name=m.name||'Party Member', role=m.role||'', picUrl=m.profile_pic_url||m.profilePicUrl||'';
      var initials=(name).split(' ').map(function(p){return p.charAt(0);}).join('').slice(0,2).toUpperCase()||'?';
      var thumbsHtml=jvPcThumbsHtml_(m.hair_inspo_urls||m.hairInspoUrls, m.makeup_inspo_urls||m.makeupInspoUrls, 4);
      html += '<div class="jv-party-hub__card jv-pc-card" data-jv-pc-type="member" data-jv-pc-idx="'+idx+'">';
      html += '<div class="jv-party-hub__card-header">';
      html += '<div class="jv-party-hub__avatar">'+(picUrl?'<img class="jv-party-hub__avatar-img" src="'+jvPcEsc_(picUrl)+'" alt="'+jvPcEsc_(name)+'" loading="lazy">':jvPcEsc_(initials))+'</div>';
      html += '<div class="jv-party-hub__card-identity"><p class="jv-party-hub__card-name">'+jvPcEsc_(name)+'</p>'+(role?'<p class="jv-party-hub__card-role">'+jvPcEsc_(role)+'</p>':'')+'</div>';
      html += '<div class="jv-party-hub__card-meta">'+thumbsHtml+'<button type="button" class="jv-party-hub__expand-btn jv-pc-view-full" data-jv-pc-type="member" data-jv-pc-idx="'+idx+'">View full</button></div>';
      html += '</div></div>';
    });
    html += '</div>';
  }
  return html;
}

var _jvPcCardData_ = null;

function jvPcInitCards_(containerId) {
  var container = document.getElementById(containerId);
  if (!container) return;
  if (!document.getElementById('jv-pc-modal')) {
    var backdrop = document.createElement('div');
    backdrop.className = 'jv-pc-modal-backdrop';
    backdrop.id = 'jv-pc-modal';
    backdrop.style.display = 'none';
    backdrop.innerHTML = '<div class="jv-pc-modal"><button class="jv-pc-modal__close" id="jv-pc-modal-close" aria-label="Close">&times;</button><div class="jv-pc-modal__body" id="jv-pc-modal-body"></div></div>';
    document.body.appendChild(backdrop);
    document.getElementById('jv-pc-modal-close').addEventListener('click', jvPcCloseModal_);
    backdrop.addEventListener('click', function(e){if(e.target===backdrop)jvPcCloseModal_();});
    document.addEventListener('keydown', function(e){if(e.key==='Escape')jvPcCloseModal_();});
  }
  container.querySelectorAll('.jv-pc-view-full').forEach(function(btn) {
    if (btn._jvPcWired) return;
    btn._jvPcWired = true;
    btn.addEventListener('click', function() {
      var type = btn.getAttribute('data-jv-pc-type');
      var bodyEl = document.getElementById('jv-pc-modal-body');
      if (!bodyEl || !_jvPcCardData_) return;
      var d = _jvPcCardData_;
      bodyEl.innerHTML = type === 'bride'
        ? jvPcModalBodyForBride_(d.brideProfile, d.brideBrief, d.brideInspo, d.brideName)
        : jvPcModalBodyForMember_((d.partyMembers||[])[parseInt(btn.getAttribute('data-jv-pc-idx')||'0',10)] || {});
      document.getElementById('jv-pc-modal').style.display = 'flex';
      document.body.style.overflow = 'hidden';
    });
  });
}

function jvPcCloseModal_() {
  var m = document.getElementById('jv-pc-modal');
  if (m) m.style.display = 'none';
  document.body.style.overflow = '';
}

// ── Vendor search / link modal ──────────────────────────────────────────────────

function openVendorModal_() {
  var modal = document.getElementById('vendorLinkModal');
  if (!modal) return;
  modal.style.display = 'flex';
  document.getElementById('vendorSearchInput').value = '';
  document.getElementById('vendorSearchResults').innerHTML = '';
  document.getElementById('vendorSearchInput').focus();
}

function closeVendorModal_() {
  var modal = document.getElementById('vendorLinkModal');
  if (modal) modal.style.display = 'none';
}

function vendorSearchKeyup_(input) {
  var q = input.value.trim();
  if (q.length < 2) { document.getElementById('vendorSearchResults').innerHTML = ''; return; }
  document.getElementById('vendorSearchResults').innerHTML = '<div class="vendor-search-loading">Searching…</div>';
  jvBridge.callAdmin('bridge_runsheet_searchVendors', { query: q })
    .then(function(res) {
      if (!res.ok) { document.getElementById('vendorSearchResults').innerHTML = '<div class="vendor-search-empty">Error loading vendors.</div>'; return; }
      var vendors = res.vendors || [];
      if (!vendors.length) {
        document.getElementById('vendorSearchResults').innerHTML = '<div class="vendor-search-empty">No vendors found for "' + escapeHtml(q) + '"</div>';
        return;
      }
      var currentGids = (state.brideProfile && state.brideProfile.vendors || []).map(function(v) { return v.gid; });
      document.getElementById('vendorSearchResults').innerHTML = vendors.map(function(v) {
        var linked = currentGids.indexOf(v.gid) !== -1;
        return '<div class="vendor-search-row" onclick="toggleVendorLink_(' + JSON.stringify(v.gid) + ',this)">' +
          (v.logoUrl ? '<img src="' + escapeHtml(v.logoUrl) + '" class="vendor-search-logo" onerror="this.style.display=\'none\'">' : '<div class="vendor-search-logo-placeholder">' + escapeHtml((v.name||'?').charAt(0).toUpperCase()) + '</div>') +
          '<div class="vendor-search-info"><div class="vendor-search-name">' + escapeHtml(v.name) + '</div><div class="vendor-search-role">' + escapeHtml(v.role) + '</div></div>' +
          '<div class="vendor-search-toggle ' + (linked ? 'is-linked' : '') + '">' + (linked ? '✓ Linked' : '+ Link') + '</div>' +
        '</div>';
      }).join('');
    }).catch(function() {
      document.getElementById('vendorSearchResults').innerHTML = '<div class="vendor-search-empty">Search failed — try again.</div>';
    });
}

function toggleVendorLink_(gid, rowEl) {
  if (!state.brideProfile) state.brideProfile = { instagram: '', pinterest: '', vendors: [] };
  var vendors = state.brideProfile.vendors || [];
  var idx = vendors.findIndex ? vendors.findIndex(function(v) { return v.gid === gid; }) : -1;
  if (idx === -1) {
    // Need to find full vendor object from the search results
    jvBridge.callAdmin('bridge_runsheet_searchVendors', { query: '' }).then(function(res) {
      var match = (res.vendors || []).filter(function(v) { return v.gid === gid; })[0];
      if (match) {
        state.brideProfile.vendors.push(match);
        saveBrideProfile_();
        reRenderBrideProfilePanels_();
      }
    });
  } else {
    state.brideProfile.vendors.splice(idx, 1);
    saveBrideProfile_();
    reRenderBrideProfilePanels_();
  }
  var toggle = rowEl && rowEl.querySelector('.vendor-search-toggle');
  if (toggle) {
    var isNowLinked = idx === -1;
    toggle.textContent = isNowLinked ? '✓ Linked' : '+ Link';
    toggle.classList.toggle('is-linked', isNowLinked);
  }
}

function saveBrideProfile_() {
  var email = state.workspace && state.workspace.email;
  if (!email || !state.brideProfile) return;
  jvBridge.callAdmin('bridge_runsheet_updateBrideProfile', {
    email:      email,
    instagram:  state.brideProfile.instagram,
    pinterest:  state.brideProfile.pinterest,
    vendorGids: (state.brideProfile.vendors || []).map(function(v) { return v.gid; })
  }).catch(function() {});
}

function saveBrideSocialLinks_() {
  if (!state.brideProfile) state.brideProfile = { instagram: '', pinterest: '', vendors: [] };
  var igEl = document.getElementById('brideInstagramInput');
  var piEl = document.getElementById('bridePinterestInput');
  state.brideProfile.instagram = igEl ? igEl.value.trim() : state.brideProfile.instagram;
  state.brideProfile.pinterest = piEl ? piEl.value.trim() : state.brideProfile.pinterest;
  saveBrideProfile_();
  reRenderBrideProfilePanels_();
  showBanner('Social links saved.', 'success');
}

function unlinkVendor_(gid) {
  if (!state.brideProfile) return;
  state.brideProfile.vendors = (state.brideProfile.vendors || []).filter(function(v) { return v.gid !== gid; });
  saveBrideProfile_();
  reRenderBrideProfilePanels_();
}

// ── Render: bride profile panel ─────────────────────────────────────────────────

function renderBrideProfilePanel_() {
  var profile = state.brideProfile;
  var email   = state.workspace && state.workspace.email;
  if (!email) return '';

  var ig  = (profile && profile.instagram) || '';
  var pin = (profile && profile.pinterest) || '';
  var vendors = (profile && profile.vendors) || [];

  var brideInitials = ((state.workspace && state.workspace.brideName) || 'B').split(' ')
    .map(function(p) { return p.charAt(0); }).join('').slice(0, 2).toUpperCase();

  // Pull bride profile pic — preference order:
  //   1. state.brideCardsData.brideProfile.profilePicUrl  (bride_profile_json metafield)
  //   2. state.workspace.brideProfilePicUrl               (annotated by hub sourcer on workspace.people)
  // Falls back to initials when neither is set.
  var bridePicUrl = '';
  if (state.brideCardsData && state.brideCardsData.brideProfile && state.brideCardsData.brideProfile.profilePicUrl) {
    bridePicUrl = state.brideCardsData.brideProfile.profilePicUrl;
  } else if (state.workspace && Array.isArray(state.workspace.people)) {
    var bridePerson = state.workspace.people.find(function(p) {
      return String((p && p.role) || '').trim().toLowerCase() === 'bride';
    });
    if (bridePerson && bridePerson.profilePicUrl) bridePicUrl = bridePerson.profilePicUrl;
  }

  var socialsHtml = '';
  if (ig || pin) {
    socialsHtml = '<div class="bp-socials">' +
      (ig  ? '<a href="' + escapeHtml(ig.indexOf('http') === 0 ? ig : 'https://instagram.com/' + ig.replace(/^@/, '')) + '" target="_blank" rel="noopener" class="bp-social-link bp-social-link--ig"><i class="fa-brands fa-instagram"></i> ' + escapeHtml(ig.replace(/^https?:\/\/(www\.)?instagram\.com\/?/, '@').replace(/^@+/, '@') || ig) + '</a>' : '') +
      (pin ? '<a href="' + escapeHtml(pin.indexOf('http') === 0 ? pin : 'https://pinterest.com/' + pin.replace(/^@/, '')) + '" target="_blank" rel="noopener" class="bp-social-link bp-social-link--pin"><i class="fa-brands fa-pinterest"></i> Pinterest</a>' : '') +
    '</div>';
  }

  var vendorsHtml = vendors.length
    ? '<div class="bp-vendors">' +
        vendors.map(function(v) {
          return '<div class="bp-vendor-chip">' +
            (v.logoUrl ? '<img src="' + escapeHtml(v.logoUrl) + '" class="bp-vendor-logo" onerror="this.style.display=\'none\'">' : '<div class="bp-vendor-logo-placeholder">' + escapeHtml((v.name||'?').charAt(0).toUpperCase()) + '</div>') +
            '<div><div class="bp-vendor-name">' + escapeHtml(v.name) + '</div><div class="bp-vendor-role">' + escapeHtml(v.role) + '</div></div>' +
            '<button class="bp-vendor-unlink" onclick="unlinkVendor_(' + JSON.stringify(v.gid) + ')" title="Unlink vendor">×</button>' +
          '</div>';
        }).join('') +
      '</div>'
    : '';

  var editFormHtml =
    '<div class="bp-edit-form" id="bpEditForm" style="display:none;">' +
      '<div class="field"><label>Instagram URL or @handle</label><input id="brideInstagramInput" type="text" placeholder="@username or full URL" value="' + escapeHtml(ig) + '"></div>' +
      '<div class="field"><label>Pinterest URL or @handle</label><input id="bridePinterestInput" type="text" placeholder="@username or full URL" value="' + escapeHtml(pin) + '"></div>' +
      '<div style="display:flex;gap:8px;margin-top:8px;">' +
        '<button class="btn btn-primary" style="font-size:11px;padding:6px 14px;" onclick="saveBrideSocialLinks_()">Save</button>' +
        '<button class="btn btn-soft" style="font-size:11px;padding:6px 14px;" onclick="toggleBpEditForm_()">Cancel</button>' +
      '</div>' +
    '</div>';

  return (
    '<div class="panel panel--bride-profile">' +
      '<div class="bp-header">' +
        '<div class="bp-avatar"' + (bridePicUrl ? ' style="background-image:url(' + escapeHtml(bridePicUrl) + ');background-size:cover;background-position:center;color:transparent;"' : '') + '>' + (bridePicUrl ? '' : brideInitials) + '</div>' +
        '<div class="bp-header-info">' +
          '<div class="bp-label">Bride Profile</div>' +
          '<div class="bp-name">' + escapeHtml((state.workspace && state.workspace.brideName) || state.currentBride || '') + '</div>' +
        '</div>' +
        '<div style="display:flex;gap:6px;">' +
          '<button class="btn btn-soft" style="font-size:11px;padding:5px 12px;" onclick="toggleBpEditForm_()" title="Edit social links"><i class="fa-solid fa-pen"></i></button>' +
          '<button class="btn btn-soft" style="font-size:11px;padding:5px 12px;" onclick="openVendorModal_()" title="Link a vendor"><i class="fa-solid fa-link"></i></button>' +
        '</div>' +
      '</div>' +
      editFormHtml +
      (socialsHtml || '<div class="panel-sub" style="margin-top:8px;">No social links yet — click <i class="fa-solid fa-pen"></i> to add Instagram or Pinterest.</div>') +
      vendorsHtml +
    '</div>'
  );
}

function toggleBpEditForm_() {
  var form = document.getElementById('bpEditForm');
  if (!form) return;
  form.style.display = form.style.display === 'none' ? '' : 'none';
  if (form.style.display !== 'none') {
    var inp = document.getElementById('brideInstagramInput');
    if (inp) inp.focus();
  }
}

// ── Render: party profiles panel ────────────────────────────────────────────────

function renderPartyProfilesPanel_() {
  var profiles = state.partyProfiles || [];
  if (!profiles.length) {
    if (!state.workspace || !state.workspace.email) return '';
    return (
      '<div class="panel panel--party-profiles">' +
        '<h4>Party Profiles</h4>' +
        '<div class="panel-sub">No onboarding submissions yet — profiles will appear here once party members complete their forms.</div>' +
      '</div>'
    );
  }

  var cards = profiles.map(function(p) {
    var initials = (p.name || '?').split(' ').map(function(w) { return w.charAt(0); }).join('').slice(0, 2).toUpperCase();
    var statusClass = p.formStatus === 'completed' ? 'pp-status--done' : p.formStatus === 'pending' ? 'pp-status--pending' : 'pp-status--none';
    var statusLabel = p.formStatus === 'completed' ? '✓' : p.formStatus === 'pending' ? '…' : '–';
    var hasWarning  = !!(p.allergies || p.skinConcerns);

    var notesHtml = '';
    if (p.hairStyle)    notesHtml += '<span class="pp-tag pp-tag--hair"><i class="fa-solid fa-scissors"></i> ' + escapeHtml(p.hairStyle) + '</span>';
    if (p.colourPalette) notesHtml += '<span class="pp-tag pp-tag--palette"><i class="fa-solid fa-palette"></i> ' + escapeHtml(p.colourPalette.slice(0, 20)) + '</span>';
    if (hasWarning) {
      // Inline visible alert with the actual text (was tooltip-only before;
      // tooltips don't work on touch and hide critical safety info).
      var alertBits = [];
      if (p.allergies)    alertBits.push('Allergies: ' + p.allergies);
      if (p.skinConcerns) alertBits.push('Skin: ' + p.skinConcerns);
      var alertText = alertBits.join(' · ');
      notesHtml += '<span class="pp-tag pp-tag--warn" style="display:inline-block;white-space:normal;line-height:1.3;">' +
        '<i class="fa-solid fa-triangle-exclamation" style="margin-right:4px;"></i>' +
        escapeHtml(alertText) +
      '</span>';
    }

    return (
      '<div class="pp-card">' +
        '<div class="pp-avatar" style="' + (p.profilePicUrl ? 'background-image:url(' + escapeHtml(p.profilePicUrl) + ');background-size:cover;color:transparent;' : '') + '">' +
          (p.profilePicUrl ? '' : initials) +
        '</div>' +
        '<div class="pp-info">' +
          '<div class="pp-name">' + escapeHtml(p.name) + '</div>' +
          '<div class="pp-role">' + escapeHtml(p.role) + '</div>' +
          (notesHtml ? '<div class="pp-tags">' + notesHtml + '</div>' : '') +
        '</div>' +
        '<div class="pp-status ' + statusClass + '" title="' + escapeHtml(p.formStatus) + '">' + statusLabel + '</div>' +
      '</div>'
    );
  }).join('');

  var completedCount = profiles.filter(function(p) { return p.formStatus === 'completed'; }).length;

  return (
    '<div class="panel panel--party-profiles">' +
      '<div style="display:flex;justify-content:space-between;align-items:baseline;margin-bottom:4px;">' +
        '<h4 style="margin:0;">Party Profiles</h4>' +
        '<span style="font-size:11px;color:var(--muted);">' + completedCount + '/' + profiles.length + ' complete</span>' +
      '</div>' +
      '<div class="pp-list">' + cards + '</div>' +
      // Match Placeholders (moved from the page-3 toolbar to page 1, next to the
      // party profiles, 2026-07-22): re-links runsheet placeholder slots to these
      // real onboarded members. Auto-matches by role, then opens a manual picker
      // for any outliers.
      '<button class="btn btn-soft" style="margin-top:10px;width:100%;justify-content:center;" onclick="matchPlaceholdersToParty()" title="Re-link placeholder runsheet slots to these real party members"><i class="fa-solid fa-link"></i> Match Placeholders to Runsheet</button>' +
    '</div>'
  );
}

